import React, { useState, useEffect } from 'react';
import { TrendingUp, AlertTriangle, Home, DollarSign, Bitcoin, LineChart, Globe, ShieldAlert, ExternalLink, ChevronDown, ClipboardCheck, BookOpen, BarChart3, Search, PiggyBank, Building2, GraduationCap, Heart, Shield, Target, Percent, TrendingDown, Eye, FileText, Briefcase, CreditCard, AlertCircle, ArrowLeftRight, Clock, RefreshCw, LayoutGrid, User, X, Mail, Lock } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, AreaChart, Area } from 'recharts';
import { signUp, logIn, logOut, onAuthChange, saveCalculation as firebaseSaveCalculation, getUserCalculations, saveQuizResults as firebaseSaveQuizResults } from './firebase';

export default function StreetFi() {
  const [currentPage, setCurrentPage] = useState('home');
  const [startHereDropdownOpen, setStartHereDropdownOpen] = useState(false);
  const [calculatorsDropdownOpen, setCalculatorsDropdownOpen] = useState(false);
  const [planningDropdownOpen, setPlanningDropdownOpen] = useState(false);
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = useState(false);
  const [leverageCalcTab, setLeverageCalcTab] = useState('trade'); // For merged calculator tabs
  
  // Auth UI state
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [currentUser, setCurrentUser] = useState(null); // Firebase user object
  const [authError, setAuthError] = useState(''); // Error messages
  const [authLoading, setAuthLoading] = useState(false); // Loading state
  
  // Compound Interest Calculator state
  const [compoundAmount, setCompoundAmount] = useState(10000);
  
  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);
  
  // Firebase Authentication Listener - Check if user is logged in
  useEffect(() => {
    const unsubscribe = onAuthChange((user) => {
      if (user) {
        // User is logged in
        setIsLoggedIn(true);
        setUserEmail(user.email);
        setCurrentUser(user);
      } else {
        // User is logged out
        setIsLoggedIn(false);
        setUserEmail('');
        setCurrentUser(null);
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);
  
  // Calculator state
  const [calcInputs, setCalcInputs] = useState({
    capital: 1000,
    leverage: 10,
    entryPrice: 100,
    exitPrice: 110,
    position: 'long',
    feePercent: 0.1
  });

  // Calculator functions
  const calculateResults = () => {
    const { capital, leverage, entryPrice, exitPrice, position, feePercent } = calcInputs;
    
    const positionSize = capital * leverage;
    const priceChange = position === 'long' 
      ? ((exitPrice - entryPrice) / entryPrice) 
      : ((entryPrice - exitPrice) / entryPrice);
    
    const grossProfitLoss = positionSize * priceChange;
    const fees = (positionSize * feePercent) / 100;
    const netProfitLoss = grossProfitLoss - fees;
    const roi = (netProfitLoss / capital) * 100;
    
    // Liquidation price (assuming 100% loss of capital)
    const maintenanceMargin = 0.5; // 50% of capital before liquidation
    const liquidationPriceChange = (capital * maintenanceMargin) / positionSize;
    const liquidationPrice = position === 'long'
      ? entryPrice * (1 - liquidationPriceChange)
      : entryPrice * (1 + liquidationPriceChange);
    
    // Risk level
    let riskLevel = 'low';
    let riskColor = 'text-green-400';
    if (leverage > 5 && leverage <= 10) {
      riskLevel = 'medium';
      riskColor = 'text-yellow-400';
    } else if (leverage > 10) {
      riskLevel = 'high';
      riskColor = 'text-red-400';
    }
    
    return {
      positionSize: positionSize,
      netProfitLoss: netProfitLoss,
      profitLoss: netProfitLoss.toFixed(2),
      roi: roi,
      liquidationPrice: liquidationPrice,
      fees: fees.toFixed(2),
      riskLevel,
      riskColor,
      priceChangePercent: (priceChange * 100).toFixed(2)
    };
  };

  const results = calculateResults();

  const updateCalcInput = (field, value) => {
    setCalcInputs(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const loadScenario = (scenario) => {
    const scenarios = {
      conservative: { capital: 1000, leverage: 2, entryPrice: 100, exitPrice: 105, position: 'long', feePercent: 0.1 },
      moderate: { capital: 1000, leverage: 10, entryPrice: 100, exitPrice: 110, position: 'long', feePercent: 0.1 },
      aggressive: { capital: 1000, leverage: 50, entryPrice: 100, exitPrice: 102, position: 'long', feePercent: 0.1 },
      loss: { capital: 1000, leverage: 10, entryPrice: 100, exitPrice: 95, position: 'long', feePercent: 0.1 }
    };
    setCalcInputs(scenarios[scenario]);
  };

  // FIREBASE AUTH HANDLERS
  
  const handleSignup = async (email, password) => {
    setAuthLoading(true);
    setAuthError('');
    
    const result = await signUp(email, password);
    
    if (result.success) {
      setShowLoginModal(false);
      setAuthMode('login');
    } else {
      setAuthError(result.error);
    }
    
    setAuthLoading(false);
  };

  const handleLogin = async (email, password) => {
    setAuthLoading(true);
    setAuthError('');
    
    const result = await logIn(email, password);
    
    if (result.success) {
      setShowLoginModal(false);
    } else {
      setAuthError(result.error);
    }
    
    setAuthLoading(false);
  };

  const handleLogout = async () => {
    const result = await logOut();
    
    if (result.success) {
      setCurrentPage('home');
    } else {
      alert('Error logging out: ' + result.error);
    }
  };

  // Save calculation to Firebase
  const handleSaveCalculation = async (calculatorType, inputs, results) => {
    if (!currentUser) {
      alert('Please log in to save calculations');
      setShowLoginModal(true);
      return;
    }

    const result = await firebaseSaveCalculation(
      currentUser.uid,
      calculatorType,
      inputs,
      results
    );

    if (result.success) {
      alert('✅ Calculation saved successfully!');
    } else {
      alert('Error saving calculation: ' + result.error);
    }
  };

  // Save quiz results to Firebase
  const handleSaveQuizResults = async (answers, riskProfile) => {
    if (!currentUser) {
      return; // Silently skip if not logged in
    }

    await firebaseSaveQuizResults(
      currentUser.uid,
      answers,
      riskProfile
    );
  };

  // Quiz state
  const [quizStep, setQuizStep] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizComplete, setQuizComplete] = useState(false);

  const quizQuestions = [
    {
      id: 'experience',
      question: 'What is your level of investing experience?',
      options: [
        { value: 1, label: 'Complete beginner - Never invested before', icon: '🌱' },
        { value: 2, label: 'Novice - Less than 1 year of experience', icon: '📚' },
        { value: 3, label: 'Intermediate - 1-3 years of experience', icon: '📈' },
        { value: 4, label: 'Advanced - 3-5 years of experience', icon: '💼' },
        { value: 5, label: 'Expert - 5+ years, professional trader', icon: '🎯' }
      ]
    },
    {
      id: 'capital',
      question: 'How much capital do you plan to use for leverage trading?',
      options: [
        { value: 1, label: 'Less than $500', icon: '💵' },
        { value: 2, label: '$500 - $2,000', icon: '💰' },
        { value: 3, label: '$2,000 - $10,000', icon: '💸' },
        { value: 4, label: '$10,000 - $50,000', icon: '💼' },
        { value: 5, label: 'More than $50,000', icon: '🏦' }
      ]
    },
    {
      id: 'risk',
      question: 'How do you feel about risk?',
      options: [
        { value: 1, label: 'Very conservative - Protecting capital is priority #1', icon: '🛡️' },
        { value: 2, label: 'Somewhat conservative - Willing to take calculated risks', icon: '⚖️' },
        { value: 3, label: 'Moderate - Balance between growth and safety', icon: '📊' },
        { value: 4, label: 'Aggressive - Growth is more important than safety', icon: '🚀' },
        { value: 5, label: 'Very aggressive - High risk, high reward', icon: '🎰' }
      ]
    },
    {
      id: 'loss',
      question: 'What percentage loss could you handle emotionally?',
      options: [
        { value: 1, label: 'Less than 10%', icon: '😰' },
        { value: 2, label: '10-20%', icon: '😬' },
        { value: 3, label: '20-50%', icon: '😐' },
        { value: 4, label: '50-75%', icon: '😨' },
        { value: 5, label: '75-100% (total loss)', icon: '💀' }
      ]
    },
    {
      id: 'assets',
      question: 'Which asset classes interest you most?',
      options: [
        { value: 1, label: 'Stocks & ETFs', icon: '📈' },
        { value: 2, label: 'Cryptocurrency', icon: '₿' },
        { value: 3, label: 'Forex (currencies)', icon: '💱' },
        { value: 4, label: 'Commodities (gold, oil, etc.)', icon: '⚡' },
        { value: 5, label: 'Multiple/All of the above', icon: '🌐' }
      ]
    },
    {
      id: 'timeline',
      question: 'What is your typical holding period?',
      options: [
        { value: 1, label: 'Minutes to hours (day trading)', icon: '⚡' },
        { value: 2, label: 'Days to weeks (swing trading)', icon: '📊' },
        { value: 3, label: 'Weeks to months (position trading)', icon: '📈' },
        { value: 4, label: 'Months to years (long-term)', icon: '🎯' },
        { value: 5, label: "Haven't decided yet", icon: '🤔' }
      ]
    },
    {
      id: 'knowledge',
      question: 'How well do you understand leverage and margin?',
      options: [
        { value: 1, label: 'No understanding - Just learning', icon: '❓' },
        { value: 2, label: 'Basic - Know the concept', icon: '📖' },
        { value: 3, label: 'Intermediate - Used it a few times', icon: '💡' },
        { value: 4, label: 'Advanced - Regular user', icon: '⚙️' },
        { value: 5, label: 'Expert - Deep technical knowledge', icon: '🎓' }
      ]
    },
    {
      id: 'purpose',
      question: 'What is your primary goal with leverage?',
      options: [
        { value: 1, label: 'Learn about it (education)', icon: '📚' },
        { value: 2, label: 'Supplement income (side money)', icon: '💰' },
        { value: 3, label: 'Build wealth over time', icon: '🏦' },
        { value: 4, label: 'Full-time trading career', icon: '💼' },
        { value: 5, label: 'Quick profits (high risk)', icon: '🎲' }
      ]
    }
  ];

  const handleQuizAnswer = (value) => {
    const currentQ = quizQuestions[quizStep];
    setQuizAnswers(prev => ({
      ...prev,
      [currentQ.id]: value
    }));
    
    if (quizStep < quizQuestions.length - 1) {
      setQuizStep(prev => prev + 1);
    } else {
      setQuizComplete(true);
    }
  };

  const calculateRiskScore = () => {
    const scores = Object.values(quizAnswers);
    const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    return avgScore;
  };

  const getRecommendations = () => {
    const score = calculateRiskScore();
    
    if (score <= 2) {
      return {
        level: 'Conservative',
        color: 'text-green-400',
        description: 'You should focus on low-leverage strategies with strong risk management.',
        recommendedLeverage: '2x - 5x',
        recommendedMethods: ['Margin Trading (Stocks)', 'Portfolio Margin', 'Real Estate (Mortgage)'],
        warnings: [
          'Start with 2x leverage maximum',
          'Never risk more than 1-2% per trade',
          'Focus on education before increasing leverage',
          'Consider paper trading first'
        ]
      };
    } else if (score <= 3.5) {
      return {
        level: 'Moderate',
        color: 'text-yellow-400',
        description: 'You can handle moderate leverage but should remain cautious.',
        recommendedLeverage: '5x - 10x',
        recommendedMethods: ['Futures Contracts', 'Options Trading', 'Forex (Low Leverage)', 'Crypto Perpetuals (Moderate)'],
        warnings: [
          'Keep leverage under 10x initially',
          'Use stop losses on every trade',
          'Diversify across multiple positions',
          'Have an emergency fund separate from trading capital'
        ]
      };
    } else {
      return {
        level: 'Aggressive',
        color: 'text-red-400',
        description: 'You have high risk tolerance, but extreme caution is still required.',
        recommendedLeverage: '10x - 20x (max)',
        recommendedMethods: ['Crypto Perpetuals', 'High-Leverage Forex', 'Options (Aggressive Strategies)', 'DeFi Leverage'],
        warnings: [
          'High leverage = high liquidation risk',
          'Most traders lose money with 20x+ leverage',
          'Never use more than 20x leverage',
          'Be prepared for total loss of capital',
          'Professional risk management is essential'
        ]
      };
    }
  };

  const resetQuiz = () => {
    setQuizStep(0);
    setQuizAnswers({});
    setQuizComplete(false);
  };

  // Cost Calculator State
  const [costInputs, setCostInputs] = useState({
    market: 'crypto',
    positionSize: 10000,
    leverage: 10,
    holdingDays: 30
  });

  const calculateCosts = () => {
    const { market, positionSize, leverage, holdingDays } = costInputs;
    
    let tradingFee = 0;
    let fundingRate = 0;
    let marginInterest = 0;
    let spread = 0;
    
    if (market === 'crypto') {
      tradingFee = positionSize * 0.001; // 0.1% maker/taker
      fundingRate = positionSize * 0.0001 * (holdingDays * 3); // 0.01% per 8h
    } else if (market === 'stocks') {
      tradingFee = positionSize * 0.0005; // 0.05% typical
      marginInterest = (positionSize * (leverage - 1) / leverage) * 0.08 * (holdingDays / 365); // 8% annual
    } else if (market === 'forex') {
      spread = positionSize * 0.0002; // 2 pips typical
      fundingRate = (positionSize * (leverage - 1) / leverage) * 0.05 * (holdingDays / 365); // 5% rollover
    }
    
    const totalCost = tradingFee + fundingRate + marginInterest + spread;
    const costPercent = (totalCost / positionSize) * 100;
    
    return {
      tradingFee: tradingFee.toFixed(2),
      fundingRate: fundingRate.toFixed(2),
      marginInterest: marginInterest.toFixed(2),
      spread: spread.toFixed(2),
      totalCost: totalCost.toFixed(2),
      costPercent: costPercent.toFixed(3),
      breakEvenMove: costPercent.toFixed(3)
    };
  };

  const costResults = calculateCosts();

  // Risk Management Calculator States
  const [positionSizingInputs, setPositionSizingInputs] = useState({
    accountSize: 10000,
    riskPercent: 2,
    stopLossPercent: 5,
    leverage: 5
  });

  const [riskRewardInputs, setRiskRewardInputs] = useState({
    entryPrice: 100,
    stopLoss: 95,
    takeProfit1: 110,
    takeProfit2: 115,
    takeProfit3: 120,
    positionSize: 1000
  });

  const [portfolioPositions, setPortfolioPositions] = useState([
    { id: 1, asset: 'BTC', size: 5000, leverage: 10, entryPrice: 50000, currentPrice: 52000 },
    { id: 2, asset: 'ETH', size: 3000, leverage: 5, entryPrice: 3000, currentPrice: 3100 },
  ]);

  const [liquidationInputs, setLiquidationInputs] = useState({
    entryPrice: 100,
    currentPrice: 105,
    leverage: 10,
    position: 'long',
    maintenanceMargin: 50
  });

  // Financial Planning State
  const [planningInputs, setPlanningInputs] = useState({
    // Income
    annualSalary: 75000,
    sideIncome: 0,
    investmentIncome: 0,
    // Expenses
    housing: 1500,
    transportation: 400,
    food: 600,
    utilities: 200,
    insurance: 300,
    healthcare: 150,
    discretionary: 500,
    otherExpenses: 250,
    // Goals
    emergencyFundGoal: 15000,
    emergencyFundCurrent: 0,
    homeDownPayment: 60000,
    homeDownPaymentCurrent: 0,
    retirementGoal: 1000000,
    retirementCurrent: 50000,
    customGoalName: 'Vacation Fund',
    customGoalAmount: 5000,
    customGoalCurrent: 0,
    // Assumptions
    inflationRate: 3,
    investmentReturn: 7,
    salaryGrowth: 3,
    taxRate: 25,
    yearsToRetirement: 30
  });

  // Monte Carlo Simulation State
  const [showMonteCarlo, setShowMonteCarlo] = useState(false);
  const [monteCarloResults, setMonteCarloResults] = useState(null);
  const [isSimulating, setIsSimulating] = useState(false);

  // Withdrawal Strategy Calculator State
  const [withdrawalInputs, setWithdrawalInputs] = useState({
    // Retirement Accounts
    traditional401k: 500000,
    traditionalIRA: 200000,
    roth401k: 100000,
    rothIRA: 150000,
    taxableBrokerage: 300000,
    // Personal Info
    currentAge: 65,
    retirementAge: 65,
    lifeExpectancy: 90,
    spouseAge: 63,
    spouseLifeExpectancy: 92,
    married: true,
    // Income & Expenses
    socialSecurityMonthly: 2500,
    spouseSocialSecurity: 1800,
    pensionMonthly: 0,
    annualExpenses: 80000,
    // Tax Info
    state: 'california', // for state tax rates
    filingStatus: 'married', // married or single
    // Strategy Options
    withdrawalStrategy: 'proportional', // proportional, tax-efficient, roth-conversion
    rmdStartAge: 73, // RMD start age (updated for SECURE 2.0)
    // Advanced
    inflationRate: 3,
    investmentReturn: 6,
    socialSecurityStartAge: 67,
    spouseSocialSecurityStartAge: 67
  });

  const updateWithdrawalInput = (field, value) => {
    setWithdrawalInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Mortgage Affordability Calculator State
  const [mortgageInputs, setMortgageInputs] = useState({
    annualIncome: 80000,
    monthlyDebts: 500,
    downPayment: 40000,
    interestRate: 6.5,
    loanTerm: 30
  });

  // Mortgage Affordability Calculation Function
  const calculateMortgageAffordability = () => {
    const { annualIncome, monthlyDebts, downPayment, interestRate, loanTerm } = mortgageInputs;
    
    const monthlyIncome = annualIncome / 12;
    const maxMonthlyPayment = monthlyIncome * 0.28; // 28% front-end ratio
    const maxTotalDebt = monthlyIncome * 0.36; // 36% back-end ratio
    const maxHousingPayment = Math.min(maxMonthlyPayment, maxTotalDebt - monthlyDebts);
    
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTerm * 12;
    
    const maxLoanAmount = maxHousingPayment * (1 - Math.pow(1 + monthlyRate, -numPayments)) / monthlyRate;
    const maxHomePrice = maxLoanAmount + downPayment;
    
    const propertyTax = (maxHomePrice * 0.012) / 12;
    const insurance = (maxHomePrice * 0.005) / 12;
    const pmi = maxLoanAmount > maxHomePrice * 0.8 ? (maxLoanAmount * 0.005) / 12 : 0;
    
    const totalMonthlyPayment = maxHousingPayment + propertyTax + insurance + pmi;
    const dti = ((totalMonthlyPayment + monthlyDebts) / monthlyIncome) * 100;
    
    return {
      maxHomePrice: maxHomePrice.toFixed(0),
      maxLoanAmount: maxLoanAmount.toFixed(0),
      monthlyPayment: maxHousingPayment.toFixed(2),
      propertyTax: propertyTax.toFixed(2),
      insurance: insurance.toFixed(2),
      pmi: pmi.toFixed(2),
      totalMonthlyPayment: totalMonthlyPayment.toFixed(2),
      dti: dti.toFixed(1),
      downPaymentPercent: ((downPayment / maxHomePrice) * 100).toFixed(1)
    };
  };

  const mortgageResults = calculateMortgageAffordability();

  // Rental Property Calculator State
  const [rentalInputs, setRentalInputs] = useState({
    purchasePrice: 300000,
    downPayment: 60000,
    interestRate: 6.5,
    loanTerm: 30,
    monthlyRent: 2500,
    vacancy: 5,
    propertyTax: 3600,
    insurance: 1200,
    maintenance: 2400,
    propertyMgmt: 10
  });

  // Rental Property ROI Calculation Function
  const calculateRentalROI = () => {
    const { purchasePrice, downPayment, interestRate, loanTerm, monthlyRent, vacancy, propertyTax, insurance, maintenance, propertyMgmt } = rentalInputs;
    
    const loanAmount = purchasePrice - downPayment;
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTerm * 12;
    const monthlyMortgage = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
    
    const annualRent = monthlyRent * 12;
    const vacancyLoss = annualRent * (vacancy / 100);
    const effectiveIncome = annualRent - vacancyLoss;
    
    const annualMortgage = monthlyMortgage * 12;
    const mgmtFee = annualRent * (propertyMgmt / 100);
    const totalExpenses = annualMortgage + propertyTax + insurance + maintenance + mgmtFee;
    
    const noi = effectiveIncome - (propertyTax + insurance + maintenance + mgmtFee);
    const cashFlow = effectiveIncome - totalExpenses;
    const monthlyCashFlow = cashFlow / 12;
    
    const cashOnCashReturn = (cashFlow / downPayment) * 100;
    const capRate = (noi / purchasePrice) * 100;
    const grossYield = (annualRent / purchasePrice) * 100;
    
    return {
      monthlyMortgage: monthlyMortgage.toFixed(2),
      effectiveIncome: effectiveIncome.toFixed(2),
      totalExpenses: totalExpenses.toFixed(2),
      noi: noi.toFixed(2),
      annualCashFlow: cashFlow.toFixed(2),
      monthlyCashFlow: monthlyCashFlow.toFixed(2),
      cashOnCashReturn: cashOnCashReturn.toFixed(2),
      capRate: capRate.toFixed(2),
      grossYield: grossYield.toFixed(2)
    };
  };

  const rentalResults = calculateRentalROI();

  // Cost Segregation Calculator State
  const [costSegInputs, setCostSegInputs] = useState({
    propertyPrice: 500000,
    propertyType: 'residential', // residential or commercial
    landValue: 100000,
    studyCost: 5000,
    taxBracket: 35,
    yearPlacedInService: 2025
  });

  // Cost Segregation Calculation Function
  const calculateCostSeg = () => {
    const { propertyPrice, landValue, studyCost, taxBracket, propertyType } = costSegInputs;
    
    const depreciableBase = propertyPrice - landValue;
    
    // Component allocation percentages (industry standard estimates)
    const allocations = propertyType === 'residential' ? {
      personalProperty: 0.15,      // 5-year (carpets, appliances, fixtures)
      landImprovements: 0.10,      // 15-year (landscaping, parking, fencing)
      buildingStructure: 0.75      // 27.5-year (walls, roof, foundation)
    } : {
      personalProperty: 0.20,      // 5-year
      landImprovements: 0.15,      // 15-year
      buildingStructure: 0.65      // 39-year for commercial
    };
    
    const personalProperty = depreciableBase * allocations.personalProperty;
    const landImprovements = depreciableBase * allocations.landImprovements;
    const buildingStructure = depreciableBase * allocations.buildingStructure;
    
    // Traditional depreciation (straight-line)
    const traditionalYears = propertyType === 'residential' ? 27.5 : 39;
    const traditionalAnnual = depreciableBase / traditionalYears;
    const traditional5Year = traditionalAnnual * 5;
    
    // Accelerated depreciation with cost seg + 100% bonus depreciation
    const bonusDepYear1 = personalProperty + landImprovements; // 100% bonus on 5 & 15-year property
    const buildingDepYear1 = buildingStructure / traditionalYears;
    const acceleratedYear1 = bonusDepYear1 + buildingDepYear1;
    
    // Remaining depreciation years 2-5
    const buildingDepYears2to5 = (buildingStructure / traditionalYears) * 4;
    const accelerated5Year = acceleratedYear1 + buildingDepYears2to5;
    
    // Tax savings calculations
    const year1TaxSavings = acceleratedYear1 * (taxBracket / 100);
    const traditionalYear1Savings = traditionalAnnual * (taxBracket / 100);
    const additionalYear1Savings = year1TaxSavings - traditionalYear1Savings;
    
    const total5YearSavings = accelerated5Year * (taxBracket / 100);
    const traditional5YearSavings = traditional5Year * (taxBracket / 100);
    const additional5YearSavings = total5YearSavings - traditional5YearSavings;
    
    // ROI on cost seg study
    const netBenefit = additionalYear1Savings - studyCost;
    const roi = (netBenefit / studyCost) * 100;
    
    // NPV calculation (simplified, 7% discount rate)
    const discountRate = 0.07;
    let npvSavings = additionalYear1Savings;
    for (let year = 2; year <= 5; year++) {
      const yearSavings = (accelerated5Year - acceleratedYear1) / 4;
      npvSavings += (yearSavings * (taxBracket / 100)) / Math.pow(1 + discountRate, year - 1);
    }
    const npvNetBenefit = npvSavings - studyCost;
    
    return {
      // Component breakdown
      personalProperty: personalProperty.toFixed(0),
      landImprovements: landImprovements.toFixed(0),
      buildingStructure: buildingStructure.toFixed(0),
      landValue: landValue.toFixed(0),
      
      // Traditional depreciation
      traditionalAnnual: traditionalAnnual.toFixed(0),
      traditional5Year: traditional5Year.toFixed(0),
      traditionalYear1Savings: traditionalYear1Savings.toFixed(0),
      traditional5YearSavings: traditional5YearSavings.toFixed(0),
      
      // Accelerated depreciation
      acceleratedYear1: acceleratedYear1.toFixed(0),
      accelerated5Year: accelerated5Year.toFixed(0),
      year1TaxSavings: year1TaxSavings.toFixed(0),
      total5YearSavings: total5YearSavings.toFixed(0),
      
      // Comparison & ROI
      additionalYear1Savings: additionalYear1Savings.toFixed(0),
      additional5YearSavings: additional5YearSavings.toFixed(0),
      netBenefit: netBenefit.toFixed(0),
      roi: roi.toFixed(0),
      npvNetBenefit: npvNetBenefit.toFixed(0),
      
      // Percentages for display
      bonusDepPercent: ((bonusDepYear1 / depreciableBase) * 100).toFixed(1),
      traditionalYears: traditionalYears
    };
  };

  const costSegResults = calculateCostSeg();

  // Stock Options & QSBS Calculator State
  const [stockOptionsInputs, setStockOptionsInputs] = useState({
    optionType: 'ISO', // ISO or NSO
    strikePrice: 1.00,
    currentFMV: 10.00,
    sharesGranted: 50000,
    vestingSchedule: 4, // years
    exerciseYear: 2025,
    expectedSaleYear: 2030,
    projectedSalePrice: 50.00,
    taxBracket: 37,
    stateTaxRate: 9.3,
    amtRate: 28,
    qsbsEligible: true,
    companyFounded: 2020,
    originalIssueDate: 2020,
    aggregateBasis: 50000 // Cost to exercise
  });

  // Stock Options & QSBS Calculation Function
  const calculateStockOptionsQSBS = () => {
    const { 
      optionType, strikePrice, currentFMV, sharesGranted, 
      exerciseYear, expectedSaleYear, projectedSalePrice,
      taxBracket, stateTaxRate, amtRate, qsbsEligible,
      companyFounded, originalIssueDate, aggregateBasis
    } = stockOptionsInputs;
    
    const exerciseCost = strikePrice * sharesGranted;
    const exerciseSpread = (currentFMV - strikePrice) * sharesGranted;
    const futureSaleValue = projectedSalePrice * sharesGranted;
    const totalGain = futureSaleValue - exerciseCost;
    
    // QSBS Eligibility Checks
    const holdingPeriodYears = expectedSaleYear - exerciseYear;
    const meetsHoldingPeriod = holdingPeriodYears >= 5;
    const companyAge = exerciseYear - companyFounded;
    const isQualifiedCorporation = companyAge <= 5; // Must be < $50M when issued
    
    // QSBS Exclusion Calculation
    const qsbsEligibleGain = qsbsEligible && meetsHoldingPeriod && isQualifiedCorporation 
      ? Math.min(totalGain, 10000000) // $10M cap
      : 0;
    
    const qsbsExcludableGain = qsbsEligibleGain * 1.0; // 100% exclusion for stock acquired after Sept 2010
    const taxableGain = totalGain - qsbsExcludableGain;
    
    // Tax Calculations
    // Federal LTCG tax (20% for high earners + 3.8% NIIT)
    const federalLTCGRate = 0.238; // 20% + 3.8% NIIT
    const federalTax = taxableGain * federalLTCGRate;
    const stateTax = taxableGain * (stateTaxRate / 100);
    const totalTaxWithoutQSBS = totalGain * (federalLTCGRate + stateTaxRate / 100);
    const totalTaxWithQSBS = federalTax + stateTax;
    const qsbsTaxSavings = totalTaxWithoutQSBS - totalTaxWithQSBS;
    
    // ISO vs NSO comparison at exercise
    let exerciseTaxISO = 0;
    let exerciseTaxNSO = 0;
    let amtLiability = 0;
    
    if (optionType === 'ISO') {
      // ISO: No regular tax at exercise, but AMT applies
      amtLiability = exerciseSpread * (amtRate / 100);
      exerciseTaxISO = amtLiability;
    } else {
      // NSO: Ordinary income tax at exercise
      exerciseTaxNSO = exerciseSpread * ((taxBracket + stateTaxRate) / 100);
    }
    
    // Multi-year projection
    const yearlyProjections = [];
    for (let year = exerciseYear; year <= expectedSaleYear; year++) {
      const yearsFromExercise = year - exerciseYear;
      const yearsUntilSale = expectedSaleYear - year;
      
      // Linear price appreciation (simplified)
      const estimatedPrice = currentFMV + ((projectedSalePrice - currentFMV) / (expectedSaleYear - exerciseYear)) * yearsFromExercise;
      const estimatedValue = estimatedPrice * sharesGranted;
      const unrealizedGain = estimatedValue - exerciseCost;
      
      // Check if QSBS qualified at this point
      const holdingYears = year - exerciseYear;
      const isQSBSQualified = holdingYears >= 5 && qsbsEligible && isQualifiedCorporation;
      
      yearlyProjections.push({
        year: year,
        estimatedPrice: estimatedPrice.toFixed(2),
        estimatedValue: estimatedValue.toFixed(0),
        unrealizedGain: unrealizedGain.toFixed(0),
        holdingYears: holdingYears,
        qsbsQualified: isQSBSQualified,
        potentialTax: isQSBSQualified 
          ? Math.max(0, (unrealizedGain - 10000000) * federalLTCGRate).toFixed(0)
          : (unrealizedGain * federalLTCGRate).toFixed(0)
      });
    }
    
    // Optimal exercise strategy
    let optimalStrategy = '';
    if (optionType === 'ISO') {
      if (qsbsEligible && isQualifiedCorporation) {
        optimalStrategy = 'Exercise now, hold 5+ years for QSBS';
      } else if (amtLiability > exerciseCost * 0.1) {
        optimalStrategy = 'Consider early exercise to minimize AMT';
      } else {
        optimalStrategy = 'Exercise and hold for LTCG treatment';
      }
    } else {
      optimalStrategy = 'Exercise close to expiration to defer ordinary income tax';
    }
    
    return {
      // Exercise economics
      exerciseCost: exerciseCost.toFixed(0),
      exerciseSpread: exerciseSpread.toFixed(0),
      exerciseTaxISO: exerciseTaxISO.toFixed(0),
      exerciseTaxNSO: exerciseTaxNSO.toFixed(0),
      amtLiability: amtLiability.toFixed(0),
      
      // Sale projections
      futureSaleValue: futureSaleValue.toFixed(0),
      totalGain: totalGain.toFixed(0),
      
      // QSBS calculations
      qsbsEligibleGain: qsbsEligibleGain.toFixed(0),
      qsbsExcludableGain: qsbsExcludableGain.toFixed(0),
      taxableGain: taxableGain.toFixed(0),
      
      // Tax calculations
      federalTax: federalTax.toFixed(0),
      stateTax: stateTax.toFixed(0),
      totalTaxWithoutQSBS: totalTaxWithoutQSBS.toFixed(0),
      totalTaxWithQSBS: totalTaxWithQSBS.toFixed(0),
      qsbsTaxSavings: qsbsTaxSavings.toFixed(0),
      effectiveTaxRate: ((totalTaxWithQSBS / totalGain) * 100).toFixed(1),
      
      // Net proceeds
      netProceedsWithQSBS: (futureSaleValue - exerciseCost - totalTaxWithQSBS).toFixed(0),
      netProceedsWithoutQSBS: (futureSaleValue - exerciseCost - totalTaxWithoutQSBS).toFixed(0),
      
      // QSBS eligibility
      meetsHoldingPeriod: meetsHoldingPeriod,
      holdingPeriodYears: holdingPeriodYears,
      isQualifiedCorporation: isQualifiedCorporation,
      
      // Multi-year projection
      yearlyProjections: yearlyProjections,
      optimalStrategy: optimalStrategy
    };
  };

  const stockOptionsResults = calculateStockOptionsQSBS();

  // 83(b) Election Calculator State
  const [election83bInputs, setElection83bInputs] = useState({
    // Grant Details
    sharesGranted: 100000,
    strikePrice: 0.10, // What you pay per share
    fmvAtGrant: 0.50, // Fair market value at grant
    vestingYears: 4,
    // Exit Scenario
    expectedFMVAtExit: 25.00, // Expected value per share at exit
    yearsToExit: 6,
    // Tax Info
    ordinaryIncomeTaxRate: 37, // Federal
    capitalGainsTaxRate: 20, // Federal long-term
    stateTaxRate: 9.3, // State (e.g., CA)
    niitRate: 3.8 // Net Investment Income Tax
  });

  const updateElection83bInput = (field, value) => {
    setElection83bInputs(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  // 83(b) Election Calculation Function
  const calculate83bElection = () => {
    const {
      sharesGranted, strikePrice, fmvAtGrant, vestingYears,
      expectedFMVAtExit, yearsToExit,
      ordinaryIncomeTaxRate, capitalGainsTaxRate, stateTaxRate, niitRate
    } = election83bInputs;

    // Basic calculations
    const totalCost = sharesGranted * strikePrice;
    const totalValueAtGrant = sharesGranted * fmvAtGrant;
    const bargainElementAtGrant = totalValueAtGrant - totalCost;
    const totalValueAtExit = sharesGranted * expectedFMVAtExit;
    const totalProfit = totalValueAtExit - totalCost;

    // WITHOUT 83(b) Election
    // Tax at vesting (ordinary income on bargain element at vest, assuming vests over 4 years)
    const vestingTranches = vestingYears * 4; // Quarterly vesting
    const sharesPerTranche = sharesGranted / vestingTranches;
    let totalTaxAtVestingWithout = 0;
    let totalBargainElementAtVesting = 0;
    
    // Simplified: assume linear appreciation from grant to exit
    const appreciationPerYear = (expectedFMVAtExit - fmvAtGrant) / yearsToExit;
    
    for (let i = 1; i <= vestingTranches; i++) {
      const yearsFromGrant = i / 4;
      const fmvAtThisVest = fmvAtGrant + (appreciationPerYear * yearsFromGrant);
      const bargainElement = (fmvAtThisVest - strikePrice) * sharesPerTranche;
      const taxOnTranche = bargainElement * ((ordinaryIncomeTaxRate + stateTaxRate) / 100);
      totalTaxAtVestingWithout += taxOnTranche;
      totalBargainElementAtVesting += bargainElement;
    }

    // Tax at exit (capital gains on appreciation after vesting)
    const costBasisWithoutElection = totalCost + totalBargainElementAtVesting;
    const capitalGainWithout = totalValueAtExit - costBasisWithoutElection;
    const capitalGainsTaxWithout = capitalGainWithout * ((capitalGainsTaxRate + niitRate + stateTaxRate) / 100);
    const totalTaxWithout83b = totalTaxAtVestingWithout + capitalGainsTaxWithout;

    // WITH 83(b) Election
    // Tax at grant (ordinary income on bargain element NOW)
    const taxAtGrant = bargainElementAtGrant * ((ordinaryIncomeTaxRate + stateTaxRate) / 100);
    
    // Tax at exit (ALL appreciation is capital gains)
    const costBasisWithElection = totalValueAtGrant; // New basis = FMV at grant
    const capitalGainWith = totalValueAtExit - costBasisWithElection;
    const capitalGainsTaxWith = capitalGainWith * ((capitalGainsTaxRate + niitRate + stateTaxRate) / 100);
    const totalTaxWith83b = taxAtGrant + capitalGainsTaxWith;

    // Savings
    const taxSavings = totalTaxWithout83b - totalTaxWith83b;
    const savingsPercentage = (taxSavings / totalTaxWithout83b) * 100;

    // Break-even analysis
    const ordinaryRate = (ordinaryIncomeTaxRate + stateTaxRate) / 100;
    const ltcgRate = (capitalGainsTaxRate + niitRate + stateTaxRate) / 100;
    const breakEvenMultiplier = ordinaryRate / (ordinaryRate - ltcgRate);
    const breakEvenPrice = fmvAtGrant * breakEvenMultiplier;

    // Recommendation
    let recommendation = '';
    let recommendationColor = '';
    if (taxSavings > totalProfit * 0.15) { // Saves >15% of profit
      recommendation = 'Strong YES - File 83(b)';
      recommendationColor = 'text-green-400';
    } else if (taxSavings > 0) {
      recommendation = 'YES - File 83(b)';
      recommendationColor = 'text-green-400';
    } else if (taxSavings < 0 && taxSavings > -5000) {
      recommendation = 'Neutral - Marginal benefit';
      recommendationColor = 'text-yellow-400';
    } else {
      recommendation = 'NO - Don\'t file 83(b)';
      recommendationColor = 'text-red-400';
    }

    // Risk assessment
    const upfrontTaxCost = taxAtGrant;
    const riskLevel = upfrontTaxCost > 10000 ? 'High' : upfrontTaxCost > 2000 ? 'Medium' : 'Low';

    return {
      // Basic values
      totalCost: totalCost.toFixed(2),
      totalValueAtGrant: totalValueAtGrant.toFixed(2),
      bargainElementAtGrant: bargainElementAtGrant.toFixed(2),
      totalValueAtExit: totalValueAtExit.toFixed(2),
      totalProfit: totalProfit.toFixed(2),
      
      // Without 83(b)
      totalTaxAtVestingWithout: totalTaxAtVestingWithout.toFixed(2),
      capitalGainsTaxWithout: capitalGainsTaxWithout.toFixed(2),
      totalTaxWithout83b: totalTaxWithout83b.toFixed(2),
      netProceedsWithout: (totalValueAtExit - totalCost - totalTaxWithout83b).toFixed(2),
      
      // With 83(b)
      taxAtGrant: taxAtGrant.toFixed(2),
      capitalGainsTaxWith: capitalGainsTaxWith.toFixed(2),
      totalTaxWith83b: totalTaxWith83b.toFixed(2),
      netProceedsWith: (totalValueAtExit - totalCost - totalTaxWith83b).toFixed(2),
      
      // Comparison
      taxSavings: taxSavings.toFixed(2),
      savingsPercentage: savingsPercentage.toFixed(1),
      breakEvenPrice: breakEvenPrice.toFixed(2),
      
      // Recommendation
      recommendation,
      recommendationColor,
      upfrontTaxCost: upfrontTaxCost.toFixed(2),
      riskLevel,
      
      // Details
      costBasisWithoutElection: costBasisWithoutElection.toFixed(2),
      costBasisWithElection: costBasisWithElection.toFixed(2),
      capitalGainWithout: capitalGainWithout.toFixed(2),
      capitalGainWith: capitalGainWith.toFixed(2)
    };
  };

  const election83bResults = calculate83bElection();

  // 1031 Exchange Calculator State
  const [exchange1031Inputs, setExchange1031Inputs] = useState({
    // Relinquished Property (Property Being Sold)
    originalPurchasePrice: 500000,
    originalPurchaseYear: 2015,
    salePrice: 800000,
    saleYear: 2025,
    depreciationTaken: 72727, // ~27.5 years of depreciation
    sellingCosts: 56000, // 7% (realtor, closing, etc)
    mortgageBalance: 200000,
    
    // Replacement Property (Property Being Acquired)
    replacementPurchasePrice: 900000,
    replacementMortgage: 250000,
    replacementClosingCosts: 18000,
    
    // Tax Information
    federalCapitalGainsTax: 20, // 20% for high earners
    niitRate: 3.8, // Net Investment Income Tax
    stateTaxRate: 9.3, // CA example
    depreciationRecaptureRate: 25,
    
    // Exchange Details
    bootReceived: 0, // Cash or debt relief not reinvested
    qualifiedIntermediaryFee: 2500,
    identificationDeadlineDays: 45,
    closingDeadlineDays: 180
  });

  // 1031 Exchange Calculation Function
  const calculate1031Exchange = () => {
    const {
      originalPurchasePrice, originalPurchaseYear, salePrice, saleYear,
      depreciationTaken, sellingCosts, mortgageBalance,
      replacementPurchasePrice, replacementMortgage, replacementClosingCosts,
      federalCapitalGainsTax, niitRate, stateTaxRate, depreciationRecaptureRate,
      bootReceived, qualifiedIntermediaryFee
    } = exchange1031Inputs;
    
    // Relinquished Property Calculations
    const netSaleProceeds = salePrice - sellingCosts;
    const adjustedBasis = originalPurchasePrice - depreciationTaken;
    const totalGain = Math.max(0, netSaleProceeds - adjustedBasis);
    const capitalGain = Math.max(0, totalGain - depreciationTaken); // Appreciation
    const depreciationRecapture = depreciationTaken; // Depreciation taken is recaptured
    
    // Tax if Sold Outright (No 1031)
    const federalCapGainsTax = capitalGain * ((federalCapitalGainsTax + niitRate) / 100);
    const stateCapGainsTax = capitalGain * (stateTaxRate / 100);
    const depreciationRecaptureTax = depreciationRecapture * (depreciationRecaptureRate / 100);
    const totalTaxWithout1031 = federalCapGainsTax + stateCapGainsTax + depreciationRecaptureTax;
    
    // Net Proceeds if Sold Outright
    const cashAfterSale = netSaleProceeds - mortgageBalance;
    const netProceedsWithout1031 = cashAfterSale - totalTaxWithout1031;
    
    // 1031 Exchange Analysis
    const equityInRelinquished = netSaleProceeds - mortgageBalance;
    const cashNeededForReplacement = replacementPurchasePrice - replacementMortgage + replacementClosingCosts;
    const cashFromRelinquished = netSaleProceeds - mortgageBalance - qualifiedIntermediaryFee;
    
    // Check if exchange meets requirements
    const meetsValueRequirement = replacementPurchasePrice >= salePrice;
    const meetsDebtRequirement = replacementMortgage >= mortgageBalance;
    const cashShortfall = Math.max(0, cashNeededForReplacement - cashFromRelinquished);
    
    // Boot Calculation (taxable portion)
    let actualBoot = bootReceived || 0;
    if (replacementPurchasePrice < salePrice) {
      actualBoot += (salePrice - replacementPurchasePrice);
    }
    if (replacementMortgage < mortgageBalance) {
      actualBoot += (mortgageBalance - replacementMortgage);
    }
    
    // Tax on Boot
    const bootTaxRate = ((federalCapitalGainsTax + niitRate + stateTaxRate) / 100);
    const taxOnBoot = actualBoot * bootTaxRate;
    
    // Net Benefit of 1031
    const taxDeferred = Math.max(0, totalTaxWithout1031 - taxOnBoot);
    const netBenefitYear1 = taxDeferred - qualifiedIntermediaryFee - cashShortfall;
    
    // ROI Calculations
    const roiOn1031 = qualifiedIntermediaryFee > 0 ? ((taxDeferred - qualifiedIntermediaryFee) / qualifiedIntermediaryFee) * 100 : 0;
    
    // Timeline Calculations
    const today = new Date();
    const saleDate = new Date(today);
    const identificationDeadline = new Date(today);
    identificationDeadline.setDate(identificationDeadline.getDate() + 45);
    const closingDeadline = new Date(today);
    closingDeadline.setDate(closingDeadline.getDate() + 180);
    
    // Multi-year projection (showing benefit of deferral)
    const projectedYears = [];
    for (let year = 0; year <= 10; year += 2) {
      // Assume 5% annual appreciation
      const futureValue1031 = replacementPurchasePrice * Math.pow(1.05, year);
      const futureValueSold = netProceedsWithout1031 * Math.pow(1.05, year);
      const difference = futureValue1031 - futureValueSold;
      
      projectedYears.push({
        year: saleYear + year,
        yearsFromExchange: year,
        value1031: futureValue1031.toFixed(0),
        valueSold: futureValueSold.toFixed(0),
        additionalWealth: difference.toFixed(0)
      });
    }
    
    return {
      // Relinquished Property
      netSaleProceeds: (netSaleProceeds || 0).toFixed(0),
      adjustedBasis: (adjustedBasis || 0).toFixed(0),
      totalGain: (totalGain || 0).toFixed(0),
      capitalGain: (capitalGain || 0).toFixed(0),
      depreciationRecapture: (depreciationRecapture || 0).toFixed(0),
      
      // Tax Without 1031
      federalCapGainsTax: (federalCapGainsTax || 0).toFixed(0),
      stateCapGainsTax: (stateCapGainsTax || 0).toFixed(0),
      depreciationRecaptureTax: (depreciationRecaptureTax || 0).toFixed(0),
      totalTaxWithout1031: (totalTaxWithout1031 || 0).toFixed(0),
      
      // Net Proceeds
      cashAfterSale: (cashAfterSale || 0).toFixed(0),
      netProceedsWithout1031: (netProceedsWithout1031 || 0).toFixed(0),
      
      // 1031 Requirements
      equityInRelinquished: (equityInRelinquished || 0).toFixed(0),
      cashNeededForReplacement: (cashNeededForReplacement || 0).toFixed(0),
      cashFromRelinquished: (cashFromRelinquished || 0).toFixed(0),
      cashShortfall: (cashShortfall || 0).toFixed(0),
      meetsValueRequirement: meetsValueRequirement,
      meetsDebtRequirement: meetsDebtRequirement,
      
      // Boot & Tax
      actualBoot: (actualBoot || 0).toFixed(0),
      taxOnBoot: (taxOnBoot || 0).toFixed(0),
      
      // Benefits
      taxDeferred: (taxDeferred || 0).toFixed(0),
      netBenefitYear1: (netBenefitYear1 || 0).toFixed(0),
      roiOn1031: (roiOn1031 || 0).toFixed(0),
      
      // Timeline
      identificationDeadline: identificationDeadline.toLocaleDateString(),
      closingDeadline: closingDeadline.toLocaleDateString(),
      
      // Projections
      projectedYears: projectedYears
    };
  };

  const exchange1031Results = calculate1031Exchange();

  // Stock Sale Tax Calculator State
  const [stockTaxInputs, setStockTaxInputs] = useState({
    purchasePrice: 50,
    salePrice: 75,
    shares: 100,
    holdingPeriod: 'long', // 'short' or 'long'
    filingStatus: 'single', // 'single', 'married', 'hoh'
    annualIncome: 75000,
    stateTaxRate: 5,
    capitalLossCarryover: 0
  });

  // Stock Sale Tax Calculation Function
  const calculateStockTax = () => {
    const { purchasePrice, salePrice, shares, holdingPeriod, filingStatus, annualIncome, stateTaxRate, capitalLossCarryover } = stockTaxInputs;
    
    const costBasis = purchasePrice * shares;
    const saleProceeds = salePrice * shares;
    const capitalGain = saleProceeds - costBasis;
    const gainPerShare = salePrice - purchasePrice;
    const returnPercent = ((salePrice - purchasePrice) / purchasePrice) * 100;
    
    // Apply capital loss carryover
    const netCapitalGain = Math.max(capitalGain - capitalLossCarryover, 0);
    const carryoverUsed = Math.min(capitalLossCarryover, Math.max(capitalGain, 0));
    const remainingCarryover = capitalLossCarryover - carryoverUsed;
    
    // Federal tax rates for 2025
    let federalTaxRate = 0;
    
    if (holdingPeriod === 'short') {
      // Short-term = ordinary income tax rates
      if (filingStatus === 'single') {
        if (annualIncome <= 11600) federalTaxRate = 10;
        else if (annualIncome <= 47150) federalTaxRate = 12;
        else if (annualIncome <= 100525) federalTaxRate = 22;
        else if (annualIncome <= 191950) federalTaxRate = 24;
        else if (annualIncome <= 243725) federalTaxRate = 32;
        else if (annualIncome <= 609350) federalTaxRate = 35;
        else federalTaxRate = 37;
      } else if (filingStatus === 'married') {
        if (annualIncome <= 23200) federalTaxRate = 10;
        else if (annualIncome <= 94300) federalTaxRate = 12;
        else if (annualIncome <= 201050) federalTaxRate = 22;
        else if (annualIncome <= 383900) federalTaxRate = 24;
        else if (annualIncome <= 487450) federalTaxRate = 32;
        else if (annualIncome <= 731200) federalTaxRate = 35;
        else federalTaxRate = 37;
      } else { // head of household
        if (annualIncome <= 16550) federalTaxRate = 10;
        else if (annualIncome <= 63100) federalTaxRate = 12;
        else if (annualIncome <= 100500) federalTaxRate = 22;
        else if (annualIncome <= 191950) federalTaxRate = 24;
        else if (annualIncome <= 243700) federalTaxRate = 32;
        else if (annualIncome <= 609350) federalTaxRate = 35;
        else federalTaxRate = 37;
      }
    } else {
      // Long-term capital gains rates (0%, 15%, 20%)
      if (filingStatus === 'single') {
        if (annualIncome <= 47025) federalTaxRate = 0;
        else if (annualIncome <= 518900) federalTaxRate = 15;
        else federalTaxRate = 20;
      } else if (filingStatus === 'married') {
        if (annualIncome <= 94050) federalTaxRate = 0;
        else if (annualIncome <= 583750) federalTaxRate = 15;
        else federalTaxRate = 20;
      } else { // head of household
        if (annualIncome <= 63000) federalTaxRate = 0;
        else if (annualIncome <= 551350) federalTaxRate = 15;
        else federalTaxRate = 20;
      }
    }
    
    // Add NIIT (Net Investment Income Tax) for high earners
    let niitRate = 0;
    const niitThreshold = filingStatus === 'married' ? 250000 : 200000;
    if (annualIncome > niitThreshold) {
      niitRate = 3.8;
    }
    
    const totalFederalRate = federalTaxRate + niitRate;
    const federalTax = (netCapitalGain * totalFederalRate) / 100;
    const stateTax = (netCapitalGain * stateTaxRate) / 100;
    const totalTax = federalTax + stateTax;
    
    const netProceeds = saleProceeds - totalTax;
    const netGain = netProceeds - costBasis;
    const effectiveTaxRate = capitalGain > 0 ? (totalTax / capitalGain) * 100 : 0;
    
    // Break-even calculation
    const breakEvenPrice = purchasePrice * (1 + (totalFederalRate + stateTaxRate) / 100);
    
    return {
      costBasis: costBasis.toFixed(2),
      saleProceeds: saleProceeds.toFixed(2),
      capitalGain: capitalGain.toFixed(2),
      gainPerShare: gainPerShare.toFixed(2),
      returnPercent: returnPercent.toFixed(2),
      carryoverUsed: carryoverUsed.toFixed(2),
      remainingCarryover: remainingCarryover.toFixed(2),
      netCapitalGain: netCapitalGain.toFixed(2),
      federalTaxRate: federalTaxRate.toFixed(1),
      niitRate: niitRate.toFixed(1),
      totalFederalRate: totalFederalRate.toFixed(1),
      federalTax: federalTax.toFixed(2),
      stateTax: stateTax.toFixed(2),
      totalTax: totalTax.toFixed(2),
      netProceeds: netProceeds.toFixed(2),
      netGain: netGain.toFixed(2),
      effectiveTaxRate: effectiveTaxRate.toFixed(2),
      breakEvenPrice: breakEvenPrice.toFixed(2),
      isGain: capitalGain > 0
    };
  };

  const stockTaxResults = calculateStockTax();

  // Financial Planning Calculator Functions
  const calculateFinancialPlan = () => {
    const {
      annualSalary, sideIncome, investmentIncome,
      housing, transportation, food, utilities, insurance, healthcare, discretionary, otherExpenses,
      emergencyFundGoal, emergencyFundCurrent,
      homeDownPayment, homeDownPaymentCurrent,
      retirementGoal, retirementCurrent,
      customGoalName, customGoalAmount, customGoalCurrent,
      inflationRate, investmentReturn, salaryGrowth, taxRate, yearsToRetirement
    } = planningInputs;

    // Calculate total income and expenses
    const totalAnnualIncome = annualSalary + sideIncome + investmentIncome;
    const monthlyIncome = totalAnnualIncome / 12;
    const afterTaxIncome = totalAnnualIncome * (1 - taxRate / 100);
    const monthlyAfterTaxIncome = afterTaxIncome / 12;
    
    const totalMonthlyExpenses = housing + transportation + food + utilities + insurance + healthcare + discretionary + otherExpenses;
    const totalAnnualExpenses = totalMonthlyExpenses * 12;
    
    const monthlySurplus = monthlyAfterTaxIncome - totalMonthlyExpenses;
    const annualSurplus = monthlySurplus * 12;
    const savingsRate = (monthlySurplus / monthlyAfterTaxIncome) * 100;

    // Calculate time and monthly savings needed for each goal
    const emergencyMonthsNeeded = emergencyFundGoal > emergencyFundCurrent 
      ? (emergencyFundGoal - emergencyFundCurrent) / monthlySurplus 
      : 0;
    const emergencyMonthlySavings = emergencyMonthsNeeded > 0 
      ? (emergencyFundGoal - emergencyFundCurrent) / emergencyMonthsNeeded 
      : 0;

    const homeMonthsNeeded = homeDownPayment > homeDownPaymentCurrent
      ? (homeDownPayment - homeDownPaymentCurrent) / (monthlySurplus * (1 + investmentReturn / 100 / 12))
      : 0;
    const homeMonthlySavings = homeMonthsNeeded > 0
      ? (homeDownPayment - homeDownPaymentCurrent) / homeMonthsNeeded
      : 0;

    // Retirement calculation (compound interest)
    const retirementGap = retirementGoal - retirementCurrent;
    const monthlyRate = investmentReturn / 100 / 12;
    const totalMonths = yearsToRetirement * 12;
    const futureValueOfCurrent = retirementCurrent * Math.pow(1 + monthlyRate, totalMonths);
    const stillNeeded = retirementGoal - futureValueOfCurrent;
    
    const retirementMonthlySavings = stillNeeded > 0
      ? (stillNeeded * monthlyRate) / (Math.pow(1 + monthlyRate, totalMonths) - 1)
      : 0;

    const customMonthsNeeded = customGoalAmount > customGoalCurrent
      ? (customGoalAmount - customGoalCurrent) / monthlySurplus
      : 0;
    const customMonthlySavings = customMonthsNeeded > 0
      ? (customGoalAmount - customGoalCurrent) / customMonthsNeeded
      : 0;

    // Total monthly savings needed
    const totalMonthlySavingsNeeded = emergencyMonthlySavings + homeMonthlySavings + retirementMonthlySavings + customMonthlySavings;
    const canAfford = monthlySurplus >= totalMonthlySavingsNeeded;

    // Budget breakdown percentages
    const expenseBreakdown = {
      housing: ((housing / totalMonthlyExpenses) * 100).toFixed(1),
      transportation: ((transportation / totalMonthlyExpenses) * 100).toFixed(1),
      food: ((food / totalMonthlyExpenses) * 100).toFixed(1),
      utilities: ((utilities / totalMonthlyExpenses) * 100).toFixed(1),
      insurance: ((insurance / totalMonthlyExpenses) * 100).toFixed(1),
      healthcare: ((healthcare / totalMonthlyExpenses) * 100).toFixed(1),
      discretionary: ((discretionary / totalMonthlyExpenses) * 100).toFixed(1),
      other: ((otherExpenses / totalMonthlyExpenses) * 100).toFixed(1)
    };

    return {
      totalAnnualIncome: totalAnnualIncome.toLocaleString(),
      monthlyIncome: monthlyIncome.toFixed(2),
      afterTaxIncome: afterTaxIncome.toFixed(2),
      monthlyAfterTaxIncome: monthlyAfterTaxIncome.toFixed(2),
      totalMonthlyExpenses: totalMonthlyExpenses.toFixed(2),
      totalAnnualExpenses: totalAnnualExpenses.toFixed(2),
      monthlySurplus: monthlySurplus.toFixed(2),
      annualSurplus: annualSurplus.toFixed(2),
      savingsRate: savingsRate.toFixed(1),
      emergencyMonthsNeeded: emergencyMonthsNeeded.toFixed(1),
      emergencyMonthlySavings: emergencyMonthlySavings.toFixed(2),
      homeMonthsNeeded: homeMonthsNeeded.toFixed(1),
      homeMonthlySavings: homeMonthlySavings.toFixed(2),
      retirementMonthlySavings: retirementMonthlySavings.toFixed(2),
      customMonthsNeeded: customMonthsNeeded.toFixed(1),
      customMonthlySavings: customMonthlySavings.toFixed(2),
      totalMonthlySavingsNeeded: totalMonthlySavingsNeeded.toFixed(2),
      canAfford,
      expenseBreakdown
    };
  };

  const planResults = calculateFinancialPlan();

  // Monte Carlo Retirement Simulator
  const runMonteCarloSimulation = () => {
    // Safety checks
    const monthlySavings = parseFloat(planResults.monthlySurplus);
    
    if (planningInputs.yearsToRetirement <= 0) {
      alert("Please set years to retirement to at least 1 year");
      return;
    }
    
    if (isNaN(monthlySavings) || isNaN(planningInputs.retirementGoal) || isNaN(planningInputs.retirementCurrent)) {
      alert("Please enter valid numeric values for all fields");
      return;
    }
    
    setIsSimulating(true);
    
    // Run simulation after a small delay to show loading state
    setTimeout(() => {
      try {
        const {
          retirementGoal, retirementCurrent,
          investmentReturn, yearsToRetirement
        } = planningInputs;

        const numSimulations = 1000;
        const simulations = [];
        
        // Standard deviation for returns (realistic market volatility)
        const returnStdDev = 0.15; // 15% annual volatility
        
        for (let i = 0; i < numSimulations; i++) {
          let balance = retirementCurrent;
          
          for (let year = 0; year < yearsToRetirement; year++) {
            // Generate random return using normal distribution
            // Box-Muller transform for normal distribution
            const u1 = Math.random();
            const u2 = Math.random();
            const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
            const annualReturn = (investmentReturn / 100) + (z * returnStdDev);
            
            // Apply annual return and add monthly contributions
            for (let month = 0; month < 12; month++) {
              balance = balance * (1 + annualReturn / 12) + monthlySavings;
            }
          }
          
          simulations.push(balance);
        }
        
        // Sort simulations for percentile calculation
        simulations.sort((a, b) => a - b);
        
        // Calculate percentiles
        const getPercentile = (percent) => {
          const index = Math.floor((percent / 100) * numSimulations);
          return simulations[index];
        };
        
        const p10 = getPercentile(10);
        const p25 = getPercentile(25);
        const p50 = getPercentile(50); // Median
        const p75 = getPercentile(75);
        const p90 = getPercentile(90);
        
        // Calculate success rate (reaching retirement goal)
        const successCount = simulations.filter(s => s >= retirementGoal).length;
        const successRate = (successCount / numSimulations) * 100;
        
        // Calculate failure statistics
        const failures = simulations.filter(s => s < retirementGoal);
        const avgShortfall = failures.length > 0
          ? failures.reduce((sum, val) => sum + (retirementGoal - val), 0) / failures.length
          : 0;
        const worstCase = simulations[0];
        const worstShortfall = retirementGoal - worstCase;
        
        // Calculate recommendations
        const needsMoreSavings = successRate < 90;
        const additionalMonthlyNeeded = needsMoreSavings
          ? calculateAdditionalSavingsNeeded(retirementGoal, retirementCurrent, monthlySavings, yearsToRetirement, investmentReturn / 100, returnStdDev)
          : 0;
        
        const results = {
          simulations: simulations.slice(0, 100), // Store subset for visualization
          successRate: successRate.toFixed(1),
          percentiles: {
            p10: Math.round(p10),
            p25: Math.round(p25),
            p50: Math.round(p50),
            p75: Math.round(p75),
            p90: Math.round(p90)
          },
          failures: {
            count: failures.length,
            avgShortfall: Math.round(avgShortfall),
            worstCase: Math.round(worstCase),
            worstShortfall: Math.round(worstShortfall)
          },
          recommendations: {
            needsMoreSavings,
            additionalMonthlyNeeded: Math.round(additionalMonthlyNeeded),
            additionalYearsNeeded: Math.ceil(additionalMonthlyNeeded > 0 && monthlySavings > 0 ? 2 : 0)
          },
          deterministicResult: Math.round(calculateDeterministicRetirement(retirementCurrent, monthlySavings, yearsToRetirement, investmentReturn / 100))
        };
        
        setMonteCarloResults(results);
        setShowMonteCarlo(true);
        setIsSimulating(false);
      } catch (error) {
        console.error("Monte Carlo simulation error:", error);
        alert("An error occurred running the simulation. Please check your inputs and try again.");
        setIsSimulating(false);
      }
    }, 500);
  };

  // Helper function to calculate additional savings needed for 95% success rate
  const calculateAdditionalSavingsNeeded = (goal, current, monthlySavings, years, avgReturn, stdDev) => {
    // Simplified calculation: increase savings by percentage needed
    const currentProjected = calculateDeterministicRetirement(current, monthlySavings, years, avgReturn);
    if (currentProjected >= goal * 0.95) return 0;
    
    const shortfall = goal * 1.1 - currentProjected; // Target 110% of goal for safety
    const monthlyRate = avgReturn / 12;
    const months = years * 12;
    
    // Calculate additional monthly savings needed using future value of annuity formula
    const additionalNeeded = (shortfall * monthlyRate) / (Math.pow(1 + monthlyRate, months) - 1);
    return Math.max(0, additionalNeeded);
  };

  // Helper function for deterministic retirement calculation
  const calculateDeterministicRetirement = (current, monthlySavings, years, avgReturn) => {
    let balance = current;
    const monthlyRate = avgReturn / 12;
    
    for (let month = 0; month < years * 12; month++) {
      balance = balance * (1 + monthlyRate) + monthlySavings;
    }
    
    return balance;
  };

  // Chart Data Generation Functions
  const getExpenseBreakdownData = () => {
    const { housing, transportation, food, utilities, insurance, healthcare, discretionary, otherExpenses } = planningInputs;
    return [
      { name: 'Housing', value: housing, color: '#3b82f6' },
      { name: 'Transportation', value: transportation, color: '#10b981' },
      { name: 'Food', value: food, color: '#f59e0b' },
      { name: 'Utilities', value: utilities, color: '#8b5cf6' },
      { name: 'Insurance', value: insurance, color: '#ec4899' },
      { name: 'Healthcare', value: healthcare, color: '#06b6d4' },
      { name: 'Discretionary', value: discretionary, color: '#f97316' },
      { name: 'Other', value: otherExpenses, color: '#6366f1' }
    ].filter(item => item.value > 0);
  };

  const getNetWorthProjectionData = () => {
    const {
      annualSalary, sideIncome, investmentIncome,
      housing, transportation, food, utilities, insurance, healthcare, discretionary, otherExpenses,
      emergencyFundCurrent, homeDownPaymentCurrent, retirementCurrent, customGoalCurrent,
      investmentReturn, yearsToRetirement
    } = planningInputs;

    const totalAnnualIncome = annualSalary + sideIncome + investmentIncome;
    const afterTaxIncome = totalAnnualIncome * (1 - planningInputs.taxRate / 100);
    const totalMonthlyExpenses = housing + transportation + food + utilities + insurance + healthcare + discretionary + otherExpenses;
    const monthlySurplus = (afterTaxIncome / 12) - totalMonthlyExpenses;
    const annualSavings = monthlySurplus * 12;

    const currentNetWorth = emergencyFundCurrent + homeDownPaymentCurrent + retirementCurrent + customGoalCurrent;
    const monthlyReturn = investmentReturn / 100 / 12;
    
    const data = [];
    const yearsToProject = Math.min(yearsToRetirement, 30);
    
    for (let year = 0; year <= yearsToProject; year++) {
      let netWorth = currentNetWorth;
      
      // Calculate future value with monthly contributions
      for (let month = 0; month < year * 12; month++) {
        netWorth = netWorth * (1 + monthlyReturn) + monthlySurplus;
      }
      
      data.push({
        year: year,
        netWorth: Math.round(netWorth),
        investments: Math.round(netWorth * 0.7),
        savings: Math.round(netWorth * 0.3)
      });
    }
    
    return data;
  };

  const getGoalProjectionData = () => {
    const {
      emergencyFundGoal, emergencyFundCurrent,
      homeDownPayment, homeDownPaymentCurrent,
      retirementGoal, retirementCurrent,
      customGoalAmount, customGoalCurrent,
      investmentReturn
    } = planningInputs;

    const monthlyReturn = investmentReturn / 100 / 12;
    const monthlySurplus = parseFloat(planResults.monthlySurplus);
    
    const data = [];
    const monthsToProject = 120; // 10 years
    
    for (let month = 0; month <= monthsToProject; month += 6) { // Every 6 months
      let emergency = emergencyFundCurrent;
      let home = homeDownPaymentCurrent;
      let retirement = retirementCurrent;
      let custom = customGoalCurrent;
      
      for (let i = 0; i < month; i++) {
        const monthlyContribution = monthlySurplus / 4; // Split equally among 4 goals
        
        emergency = emergency * (1 + monthlyReturn) + (emergency < emergencyFundGoal ? monthlyContribution : 0);
        home = home * (1 + monthlyReturn) + (home < homeDownPayment ? monthlyContribution : 0);
        retirement = retirement * (1 + monthlyReturn) + monthlyContribution;
        custom = custom * (1 + monthlyReturn) + (custom < customGoalAmount ? monthlyContribution : 0);
      }
      
      data.push({
        month: month,
        year: (month / 12).toFixed(1),
        'Emergency Fund': Math.round(Math.min(emergency, emergencyFundGoal)),
        'Home Fund': Math.round(Math.min(home, homeDownPayment)),
        'Retirement': Math.round(retirement),
        [planningInputs.customGoalName]: Math.round(Math.min(custom, customGoalAmount))
      });
    }
    
    return data;
  };

  const updatePlanningTextInput = (field, value) => {
    setPlanningInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updatePlanningInput = (field, value) => {
    setPlanningInputs(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  // Withdrawal Strategy Calculator Functions
  const calculateWithdrawalStrategy = () => {
    const {
      traditional401k, traditionalIRA, roth401k, rothIRA, taxableBrokerage,
      currentAge, lifeExpectancy, spouseAge, spouseLifeExpectancy, married,
      socialSecurityMonthly, spouseSocialSecurity, pensionMonthly, annualExpenses,
      state, filingStatus, withdrawalStrategy, rmdStartAge,
      inflationRate, investmentReturn, socialSecurityStartAge, spouseSocialSecurityStartAge
    } = withdrawalInputs;

    // Calculate total portfolio
    const totalTraditional = traditional401k + traditionalIRA;
    const totalRoth = roth401k + rothIRA;
    const totalPortfolio = totalTraditional + totalRoth + taxableBrokerage;

    // Years in retirement
    const yearsInRetirement = lifeExpectancy - currentAge;
    const spouseYearsInRetirement = married ? spouseLifeExpectancy - spouseAge : 0;

    // Annual withdrawal needed (before Social Security)
    const yearlyWithdrawalNeeded = annualExpenses;

    // Social Security calculations
    const yearsSocialSecurityActive = currentAge < socialSecurityStartAge ? 0 : 1;
    const annualSocialSecurity = yearsSocialSecurityActive ? socialSecurityMonthly * 12 : 0;
    const annualSpouseSocialSecurity = married && (spouseAge >= spouseSocialSecurityStartAge) ? spouseSocialSecurity * 12 : 0;
    const totalAnnualSocialSecurity = annualSocialSecurity + annualSpouseSocialSecurity;

    // Withdrawal from portfolio (after Social Security)
    const portfolioWithdrawalNeeded = Math.max(0, yearlyWithdrawalNeeded - totalAnnualSocialSecurity - (pensionMonthly * 12));

    // Tax rates (simplified federal brackets for 2025)
    const federalTaxRate = filingStatus === 'married' ? 
      (yearlyWithdrawalNeeded < 89075 ? 12 : yearlyWithdrawalNeeded < 190750 ? 22 : 24) :
      (yearlyWithdrawalNeeded < 44725 ? 12 : yearlyWithdrawalNeeded < 95375 ? 22 : 24);

    // State tax rates (simplified)
    const stateTaxRates = {
      california: 9.3, newyork: 6.5, texas: 0, florida: 0, washington: 0,
      oregon: 9.9, nevada: 0, colorado: 4.4, pennsylvania: 3.07, illinois: 4.95
    };
    const stateTaxRate = stateTaxRates[state] || 5;

    // RMD calculation (simplified - using Uniform Lifetime Table)
    const rmdAge = currentAge >= rmdStartAge ? currentAge : 0;
    const rmdDivisor = rmdAge > 0 ? Math.max(1, 85 - (rmdAge - 73)) : 0; // Simplified divisor
    const rmdAmount = rmdAge > 0 ? totalTraditional / rmdDivisor : 0;

    // Calculate strategy-based withdrawals
    let traditionalWithdrawal = 0;
    let rothWithdrawal = 0;
    let taxableWithdrawal = 0;
    let estimatedTaxes = 0;

    if (withdrawalStrategy === 'proportional') {
      // Withdraw proportionally from all accounts
      const traditionalPct = totalTraditional / totalPortfolio;
      const rothPct = totalRoth / totalPortfolio;
      const taxablePct = taxableBrokerage / totalPortfolio;

      traditionalWithdrawal = portfolioWithdrawalNeeded * traditionalPct;
      rothWithdrawal = portfolioWithdrawalNeeded * rothPct;
      taxableWithdrawal = portfolioWithdrawalNeeded * taxablePct;

      // Tax on traditional + taxable gains (assume 30% is gains)
      estimatedTaxes = (traditionalWithdrawal * (federalTaxRate + stateTaxRate) / 100) +
                       (taxableWithdrawal * 0.3 * 15 / 100); // 15% LTCG on gains portion

    } else if (withdrawalStrategy === 'tax-efficient') {
      // Optimal withdrawal order: Taxable → Traditional (up to low bracket) → Roth
      const lowBracketThreshold = filingStatus === 'married' ? 89075 : 44725;
      
      if (taxableBrokerage > 0) {
        taxableWithdrawal = Math.min(portfolioWithdrawalNeeded, taxableBrokerage);
        estimatedTaxes = taxableWithdrawal * 0.3 * 15 / 100;
      }
      
      const remaining = portfolioWithdrawalNeeded - taxableWithdrawal;
      if (remaining > 0 && totalTraditional > 0) {
        traditionalWithdrawal = Math.min(remaining, Math.min(totalTraditional, lowBracketThreshold));
        estimatedTaxes += traditionalWithdrawal * (federalTaxRate + stateTaxRate) / 100;
      }
      
      const stillRemaining = remaining - traditionalWithdrawal;
      if (stillRemaining > 0 && totalRoth > 0) {
        rothWithdrawal = Math.min(stillRemaining, totalRoth);
        // No tax on Roth withdrawals
      }

    } else if (withdrawalStrategy === 'roth-conversion') {
      // Roth conversion strategy: convert traditional to Roth up to bracket threshold
      const conversionThreshold = filingStatus === 'married' ? 89075 : 44725;
      const conversionAmount = Math.min(conversionThreshold, totalTraditional * 0.1); // 10% per year

      traditionalWithdrawal = Math.min(portfolioWithdrawalNeeded, totalTraditional);
      estimatedTaxes = (traditionalWithdrawal + conversionAmount) * (federalTaxRate + stateTaxRate) / 100;
    }

    // Ensure RMD is met
    if (rmdAmount > traditionalWithdrawal) {
      const additionalRMD = rmdAmount - traditionalWithdrawal;
      traditionalWithdrawal = rmdAmount;
      estimatedTaxes += additionalRMD * (federalTaxRate + stateTaxRate) / 100;
    }

    // Portfolio projections over time
    const portfolioProjection = [];
    let projTraditional = totalTraditional;
    let projRoth = totalRoth;
    let projTaxable = taxableBrokerage;
    let projAge = currentAge;

    for (let year = 0; year <= Math.min(yearsInRetirement, 30); year++) {
      projAge = currentAge + year;
      
      // Growth
      projTraditional *= (1 + investmentReturn / 100);
      projRoth *= (1 + investmentReturn / 100);
      projTaxable *= (1 + investmentReturn / 100);

      // Calculate this year's withdrawals
      let yearTraditional = 0;
      let yearRoth = 0;
      let yearTaxable = 0;
      const yearExpenses = annualExpenses * Math.pow(1 + inflationRate / 100, year);
      const yearSSActive = projAge >= socialSecurityStartAge;
      const yearSpouseSSActive = married && (spouseAge + year >= spouseSocialSecurityStartAge);
      const yearTotalSS = (yearSSActive ? socialSecurityMonthly * 12 : 0) + 
                          (yearSpouseSSActive ? spouseSocialSecurity * 12 : 0);
      const yearNeeded = Math.max(0, yearExpenses - yearTotalSS - (pensionMonthly * 12));

      // Apply strategy
      if (withdrawalStrategy === 'proportional') {
        const total = projTraditional + projRoth + projTaxable;
        yearTraditional = total > 0 ? yearNeeded * (projTraditional / total) : 0;
        yearRoth = total > 0 ? yearNeeded * (projRoth / total) : 0;
        yearTaxable = total > 0 ? yearNeeded * (projTaxable / total) : 0;
      } else if (withdrawalStrategy === 'tax-efficient') {
        if (projTaxable > 0) {
          yearTaxable = Math.min(yearNeeded, projTaxable);
        }
        const rem = yearNeeded - yearTaxable;
        if (rem > 0 && projTraditional > 0) {
          yearTraditional = Math.min(rem, projTraditional);
        }
        const stillRem = rem - yearTraditional;
        if (stillRem > 0 && projRoth > 0) {
          yearRoth = Math.min(stillRem, projRoth);
        }
      }

      // RMD check
      if (projAge >= rmdStartAge) {
        const yearRMDDivisor = Math.max(1, 85 - (projAge - 73));
        const yearRMD = projTraditional / yearRMDDivisor;
        if (yearRMD > yearTraditional) {
          yearTraditional = yearRMD;
        }
      }

      // Withdrawals
      projTraditional -= yearTraditional;
      projRoth -= yearRoth;
      projTaxable -= yearTaxable;

      portfolioProjection.push({
        year: year,
        age: projAge,
        traditional: Math.max(0, projTraditional),
        roth: Math.max(0, projRoth),
        taxable: Math.max(0, projTaxable),
        total: Math.max(0, projTraditional + projRoth + projTaxable),
        withdrawal: yearTraditional + yearRoth + yearTaxable,
        socialSecurity: yearTotalSS
      });
    }

    // Tax savings comparison
    const proportionalTax = (portfolioWithdrawalNeeded * 0.6 * (federalTaxRate + stateTaxRate) / 100);
    const taxSavings = withdrawalStrategy === 'tax-efficient' ? 
      Math.max(0, proportionalTax - estimatedTaxes) : 0;
    const lifetimeTaxSavings = taxSavings * yearsInRetirement;

    // Success metrics
    const finalBalance = portfolioProjection[Math.min(portfolioProjection.length - 1, 29)].total;
    const portfolioLasts = finalBalance > 0;
    const yearsPortfolioLasts = portfolioProjection.findIndex(p => p.total < annualExpenses);

    return {
      totalPortfolio: totalPortfolio.toFixed(0),
      totalTraditional: totalTraditional.toFixed(0),
      totalRoth: totalRoth.toFixed(0),
      taxableBrokerage: taxableBrokerage.toFixed(0),
      annualWithdrawalNeeded: portfolioWithdrawalNeeded.toFixed(0),
      traditionalWithdrawal: traditionalWithdrawal.toFixed(0),
      rothWithdrawal: rothWithdrawal.toFixed(0),
      taxableWithdrawal: taxableWithdrawal.toFixed(0),
      estimatedAnnualTaxes: estimatedTaxes.toFixed(0),
      afterTaxIncome: (portfolioWithdrawalNeeded - estimatedTaxes + totalAnnualSocialSecurity + (pensionMonthly * 12)).toFixed(0),
      effectiveTaxRate: portfolioWithdrawalNeeded > 0 ? ((estimatedTaxes / portfolioWithdrawalNeeded) * 100).toFixed(1) : 0,
      rmdAmount: rmdAmount.toFixed(0),
      rmdRequired: rmdAge > 0,
      yearsInRetirement: yearsInRetirement.toFixed(0),
      taxSavingsAnnual: taxSavings.toFixed(0),
      lifetimeTaxSavings: lifetimeTaxSavings.toFixed(0),
      portfolioProjection: portfolioProjection,
      portfolioLasts: portfolioLasts,
      yearsPortfolioLasts: yearsPortfolioLasts > 0 ? yearsPortfolioLasts : yearsInRetirement,
      socialSecurityAnnual: totalAnnualSocialSecurity.toFixed(0)
    };
  };

  const withdrawalResults = calculateWithdrawalStrategy();

  // Position Sizing Calculator Functions
  const calculatePositionSizing = () => {
    const { accountSize, riskPercent, stopLossPercent, leverage } = positionSizingInputs;
    
    const dollarRisk = accountSize * (riskPercent / 100);
    const positionSize = (dollarRisk / (stopLossPercent / 100)) * leverage;
    const capitalRequired = positionSize / leverage;
    const liquidationBuffer = ((leverage - 1) / leverage) * 100;
    const contracts = Math.floor(positionSize / 100);
    
    return {
      maxPositionSize: positionSize.toFixed(2),
      capitalRequired: capitalRequired.toFixed(2),
      dollarRisk: dollarRisk.toFixed(2),
      liquidationBuffer: liquidationBuffer.toFixed(2),
      contracts: contracts,
      riskPercentOfPosition: (stopLossPercent).toFixed(2)
    };
  };

  // Risk-Reward Calculator Functions
  const calculateRiskReward = () => {
    const { entryPrice, stopLoss, takeProfit1, takeProfit2, takeProfit3, positionSize } = riskRewardInputs;
    
    const risk = Math.abs(entryPrice - stopLoss);
    const reward1 = Math.abs(takeProfit1 - entryPrice);
    const reward2 = Math.abs(takeProfit2 - entryPrice);
    const reward3 = Math.abs(takeProfit3 - entryPrice);
    
    const riskRewardRatio1 = reward1 / risk;
    const riskRewardRatio2 = reward2 / risk;
    const riskRewardRatio3 = reward3 / risk;
    
    const winRateNeeded1 = (1 / (1 + riskRewardRatio1)) * 100;
    const winRateNeeded2 = (1 / (1 + riskRewardRatio2)) * 100;
    const winRateNeeded3 = (1 / (1 + riskRewardRatio3)) * 100;
    
    const dollarRisk = (risk / entryPrice) * positionSize;
    const dollarReward1 = (reward1 / entryPrice) * positionSize;
    const dollarReward2 = (reward2 / entryPrice) * positionSize;
    const dollarReward3 = (reward3 / entryPrice) * positionSize;
    
    return {
      risk: risk.toFixed(2),
      riskPercent: ((risk / entryPrice) * 100).toFixed(2),
      dollarRisk: dollarRisk.toFixed(2),
      rr1: riskRewardRatio1.toFixed(2),
      rr2: riskRewardRatio2.toFixed(2),
      rr3: riskRewardRatio3.toFixed(2),
      winRate1: winRateNeeded1.toFixed(1),
      winRate2: winRateNeeded2.toFixed(1),
      winRate3: winRateNeeded3.toFixed(1),
      reward1: dollarReward1.toFixed(2),
      reward2: dollarReward2.toFixed(2),
      reward3: dollarReward3.toFixed(2)
    };
  };

  // Portfolio Risk Calculator Functions
  const calculatePortfolioRisk = () => {
    const totalCapital = portfolioPositions.reduce((sum, pos) => sum + pos.size, 0);
    const totalExposure = portfolioPositions.reduce((sum, pos) => sum + (pos.size * pos.leverage), 0);
    const totalPnL = portfolioPositions.reduce((sum, pos) => {
      const priceChange = (pos.currentPrice - pos.entryPrice) / pos.entryPrice;
      return sum + (pos.size * pos.leverage * priceChange);
    }, 0);
    
    const avgLeverage = totalExposure / totalCapital;
    const portfolioReturn = (totalPnL / totalCapital) * 100;
    
    return {
      totalCapital: totalCapital.toFixed(2),
      totalExposure: totalExposure.toFixed(2),
      avgLeverage: avgLeverage.toFixed(2),
      totalPnL: totalPnL.toFixed(2),
      portfolioReturn: portfolioReturn.toFixed(2)
    };
  };

  // Liquidation Distance Calculator Functions
  const calculateLiquidationDistance = () => {
    const { entryPrice, currentPrice, leverage, position, maintenanceMargin } = liquidationInputs;
    
    const maintenanceMarginPercent = maintenanceMargin / 100;
    const liquidationPriceChange = maintenanceMarginPercent / leverage;
    
    let liquidationPrice;
    if (position === 'long') {
      liquidationPrice = entryPrice * (1 - liquidationPriceChange);
    } else {
      liquidationPrice = entryPrice * (1 + liquidationPriceChange);
    }
    
    const distanceToLiquidation = Math.abs(currentPrice - liquidationPrice);
    const distancePercent = (distanceToLiquidation / currentPrice) * 100;
    const priceChangeToLiquidation = ((liquidationPrice - currentPrice) / currentPrice) * 100;
    
    let safetyLevel = 'safe';
    let safetyColor = 'text-green-400';
    if (distancePercent < 5) {
      safetyLevel = 'danger';
      safetyColor = 'text-red-400';
    } else if (distancePercent < 15) {
      safetyLevel = 'warning';
      safetyColor = 'text-yellow-400';
    }
    
    return {
      liquidationPrice: liquidationPrice.toFixed(2),
      distanceToLiquidation: distanceToLiquidation.toFixed(2),
      distancePercent: distancePercent.toFixed(2),
      priceChangeToLiquidation: priceChangeToLiquidation.toFixed(2),
      safetyLevel,
      safetyColor
    };
  };

  // Glossary with search
  const [glossarySearch, setGlossarySearch] = useState('');
  const [glossaryCategory, setGlossaryCategory] = useState('all');

  const glossaryTerms = [
    // Basic Terms
    { term: 'Leverage', category: 'basic', definition: 'The use of borrowed capital to increase the potential return of an investment. For example, 10x leverage means you control $10,000 with only $1,000 of your own money.' },
    { term: 'Margin', category: 'basic', definition: 'The amount of equity required to open and maintain a leveraged position. It acts as collateral for the borrowed funds.' },
    { term: 'Liquidation', category: 'basic', definition: 'The forced closure of a position when losses reduce your equity below the maintenance margin requirement. This happens automatically to prevent your account from going negative.' },
    { term: 'Long Position', category: 'basic', definition: 'Buying an asset expecting its price to increase. You profit when the price goes up.' },
    { term: 'Short Position', category: 'basic', definition: 'Selling an asset you don\'t own, expecting its price to decrease. You profit when the price goes down.' },
    
    // Risk Terms
    { term: 'Stop Loss', category: 'risk', definition: 'An order that automatically closes your position at a predetermined price to limit losses.' },
    { term: 'Take Profit', category: 'risk', definition: 'An order that automatically closes your position at a predetermined price to lock in gains.' },
    { term: 'Position Sizing', category: 'risk', definition: 'Determining how much capital to allocate to each trade based on your risk tolerance and account size.' },
    { term: 'Risk-Reward Ratio', category: 'risk', definition: 'The potential profit of a trade compared to its potential loss. A 3:1 ratio means you stand to make $3 for every $1 risked.' },
    { term: 'Drawdown', category: 'risk', definition: 'The peak-to-trough decline in your account value. A 50% drawdown requires a 100% gain to recover.' },
    { term: 'Risk Per Trade', category: 'risk', definition: 'Maximum amount willing to lose on single trade. Professional traders risk 1-2% of account per trade. Prevents blowing up account.' },
    { term: 'Risk Tolerance', category: 'risk', definition: 'Comfort level with potential losses. High tolerance = aggressive strategies, low tolerance = conservative. Should match trading style.' },
    { term: 'Portfolio Risk', category: 'risk', definition: 'Total risk across all positions. Even if individual positions are sized well, too many correlated positions = excessive risk.' },
    { term: 'Correlation', category: 'risk', definition: 'How assets move together. High correlation = similar moves. Diversify with low/negative correlation to reduce portfolio risk.' },
    { term: 'Max Leverage', category: 'risk', definition: 'Highest leverage available on platform (can be 100x+ in crypto). Just because it\'s available doesn\'t mean you should use it.' },
    
    // Trading Terms
    { term: 'Perpetual Futures', category: 'trading', definition: 'Futures contracts with no expiration date, commonly used in crypto. They use funding rates to keep prices aligned with spot markets.' },
    { term: 'Funding Rate', category: 'trading', definition: 'Periodic payments between long and short traders in perpetual futures. When positive, longs pay shorts. When negative, shorts pay longs.' },
    { term: 'Mark Price', category: 'trading', definition: 'A calculated price used to prevent manipulation and determine liquidations. It\'s based on spot price plus a funding basis.' },
    { term: 'Open Interest', category: 'trading', definition: 'The total number of outstanding contracts in a market. High open interest indicates high liquidity.' },
    { term: 'Slippage', category: 'trading', definition: 'The difference between expected price and actual execution price, especially in volatile markets or with large orders.' },
    { term: 'Breakeven Price', category: 'trading', definition: 'Price where gains exactly equal losses after fees and costs. Must exceed this to be profitable. Critical for planning exits.' },
    { term: 'Entry Price', category: 'trading', definition: 'Price at which you open a position. Determines profit/loss calculation. Should be planned before trade, not chosen emotionally.' },
    { term: 'Exit Price', category: 'trading', definition: 'Price at which you close a position. Should be predetermined (take profit or stop loss). Don\'t let emotions decide.' },
    { term: 'P&L', category: 'trading', definition: 'Profit and Loss. Net result of a trade after fees. Positive P&L = profit, negative = loss. Track to measure performance.' },
    { term: 'ROI', category: 'trading', definition: 'Return on Investment. Percentage gain/loss on capital risked. 10% ROI = turned $1,000 into $1,100. Key performance metric.' },
    
    // Margin Types
    { term: 'Initial Margin', category: 'margin', definition: 'The minimum amount required to open a leveraged position. Usually 5-50% depending on leverage used.' },
    { term: 'Maintenance Margin', category: 'margin', definition: 'The minimum equity required to keep a position open. Falls below this and you get liquidated.' },
    { term: 'Cross Margin', category: 'margin', definition: 'All positions share the same margin balance. One position\'s profits can offset another\'s losses.' },
    { term: 'Isolated Margin', category: 'margin', definition: 'Each position has its own dedicated margin. Liquidation of one position doesn\'t affect others.' },
    { term: 'Portfolio Margin', category: 'margin', definition: 'Advanced margin calculation based on overall portfolio risk rather than individual positions. Offers more efficient capital usage.' },
    
    // Order Types
    { term: 'Market Order', category: 'orders', definition: 'Executes immediately at current market price. Guarantees execution but not price.' },
    { term: 'Limit Order', category: 'orders', definition: 'Only executes at your specified price or better. Guarantees price but not execution.' },
    { term: 'Stop-Market Order', category: 'orders', definition: 'Becomes a market order when price reaches your trigger. Used for stop losses.' },
    { term: 'Stop-Limit Order', category: 'orders', definition: 'Becomes a limit order when price reaches your trigger. May not execute in fast markets.' },
    { term: 'Trailing Stop', category: 'orders', definition: 'A stop loss that automatically adjusts as price moves in your favor, locking in profits.' },
    
    // Crypto Specific
    { term: 'CEX', category: 'crypto', definition: 'Centralized Exchange - platforms like Binance, Coinbase, Bybit where you trade through a company.' },
    { term: 'DEX', category: 'crypto', definition: 'Decentralized Exchange - peer-to-peer platforms like Hyperliquid, Vertex where you trade directly from your wallet.' },
    { term: 'Gas Fees', category: 'crypto', definition: 'Transaction fees paid to blockchain miners/validators. Can be high on Ethereum during congestion.' },
    { term: 'Smart Contract Risk', category: 'crypto', definition: 'Risk that DeFi protocol code has bugs or vulnerabilities that could lead to loss of funds.' },
    { term: 'Impermanent Loss', category: 'crypto', definition: 'Loss experienced when providing liquidity to pools compared to just holding assets. Not directly related to leverage but important for DeFi.' },
    
    // Options Terms
    { term: 'Call Option', category: 'options', definition: 'Contract giving right to BUY an asset at strike price. Profits when price rises above strike + premium.' },
    { term: 'Put Option', category: 'options', definition: 'Contract giving right to SELL an asset at strike price. Profits when price falls below strike - premium.' },
    { term: 'Strike Price', category: 'options', definition: 'The price at which an option can be exercised. Key determinant of option value.' },
    { term: 'Premium', category: 'options', definition: 'The price paid to buy an option. Maximum loss for option buyers.' },
    { term: 'Greeks', category: 'options', definition: 'Delta, Gamma, Theta, Vega - metrics measuring option price sensitivity to various factors.' },
    
    // Fee Terms
    { term: 'Maker Fee', category: 'fees', definition: 'Fee paid when adding liquidity (limit orders). Usually lower than taker fee, sometimes negative (rebate).' },
    { term: 'Taker Fee', category: 'fees', definition: 'Fee paid when removing liquidity (market orders). Usually 0.02-0.1% per trade.' },
    { term: 'Rollover Fee', category: 'fees', definition: 'Interest charged for holding leveraged forex positions overnight. Can be positive or negative.' },
    { term: 'Spread', category: 'fees', definition: 'Difference between buy and sell price. Hidden cost in forex and some crypto platforms.' },
    { term: 'Margin Interest', category: 'fees', definition: 'Interest charged on borrowed funds in stock margin accounts. Usually 5-12% annually.' },
    
    // Advanced
    { term: 'Basis', category: 'advanced', definition: 'Difference between futures price and spot price. Important for cash-and-carry arbitrage.' },
    { term: 'Contango', category: 'advanced', definition: 'When futures trade above spot price. Common in crypto perpetuals with positive funding.' },
    { term: 'Backwardation', category: 'advanced', definition: 'When futures trade below spot price. Can occur during extreme bearish sentiment.' },
    { term: 'Gamma Squeeze', category: 'advanced', definition: 'Rapid price movement caused by market makers hedging options positions. Can create explosive moves.' },
    { term: 'Max Pain', category: 'advanced', definition: 'Options strike price where most contracts expire worthless. Some believe price gravitates here at expiry.' },
    
    // Financial Planning Terms
    { term: 'Savings Rate', category: 'planning', definition: 'Percentage of income saved each month. Higher savings rates accelerate wealth building and retirement timelines.' },
    { term: 'Net Worth', category: 'planning', definition: 'Total assets minus total liabilities. Key metric for tracking overall financial health and progress toward goals.' },
    { term: 'Emergency Fund', category: 'planning', definition: 'Savings set aside for unexpected expenses or job loss. Typically 3-6 months of expenses recommended.' },
    { term: 'Compound Interest', category: 'planning', definition: 'Interest earned on both principal and accumulated interest. Einstein called it the "8th wonder of the world."' },
    { term: 'FIRE', category: 'planning', definition: 'Financial Independence, Retire Early. Movement focused on aggressive saving (50-70% rate) to retire decades early.' },
    { term: 'Asset Allocation', category: 'planning', definition: 'Distribution of investments across different asset classes (stocks, bonds, real estate). Critical for risk management.' },
    { term: 'Budget', category: 'planning', definition: 'Plan for income and expenses. Foundation of financial planning. Track actual vs planned spending.' },
    { term: 'Dollar-Cost Averaging', category: 'planning', definition: 'Investing fixed amounts at regular intervals regardless of price. Reduces timing risk and emotional decisions.' },
    { term: 'Inflation Rate', category: 'planning', definition: 'Rate at which prices increase over time. Averages 3% annually. Must factor into retirement planning or lose purchasing power.' },
    { term: 'Down Payment', category: 'planning', definition: 'Initial cash payment for large purchase (house, car). Larger down payment = smaller loan = less interest paid over time.' },
    { term: 'Monthly Surplus', category: 'planning', definition: 'Income minus expenses. Positive surplus enables saving and investing. Negative = deficit requiring budget cuts or income increase.' },
    { term: 'Financial Goals', category: 'planning', definition: 'Specific targets with timelines. Examples: $20K emergency fund in 12 months, $60K down payment in 3 years. Makes planning concrete.' },
    { term: 'Retirement Savings', category: 'planning', definition: 'Money set aside for retirement via 401k, IRA, taxable accounts. Rule of thumb: save 15% of income starting age 25 for comfortable retirement.' },
    
    // Tax Terms
    { term: 'Tax-Loss Harvesting', category: 'tax', definition: 'Selling losing investments to offset capital gains and reduce tax liability. Can save thousands annually.' },
    { term: 'Capital Gains Tax', category: 'tax', definition: 'Tax on profits from selling assets. Short-term (under 1 year) taxed as income; long-term (over 1 year) at lower rates.' },
    { term: 'Short-Term Capital Gains', category: 'tax', definition: 'Profits from assets held less than 1 year. Taxed as ordinary income at rates of 10-37%. Much higher than long-term rates.' },
    { term: 'Long-Term Capital Gains', category: 'tax', definition: 'Profits from assets held over 1 year. Taxed at preferential rates of 0%, 15%, or 20% based on income. Major tax advantage.' },
    { term: 'Wash Sale Rule', category: 'tax', definition: 'Cannot claim tax loss if you buy same/similar security within 30 days before or after sale. Prevents tax gaming.' },
    { term: 'NIIT', category: 'tax', definition: 'Net Investment Income Tax. Additional 3.8% tax on investment income for high earners ($200K+ single, $250K+ married). Applies to capital gains, dividends, interest.' },
    { term: 'Capital Loss Carryover', category: 'tax', definition: 'Unused capital losses from previous years that can offset future gains. Losses carry forward indefinitely until used.' },
    { term: 'Filing Status', category: 'tax', definition: 'Tax category determining rates and deductions. Options: Single, Married Filing Jointly, Head of Household. Married filing jointly gets best rates.' },
    { term: 'Ordinary Income', category: 'tax', definition: 'Income taxed at regular rates (10-37%). Includes wages, short-term capital gains, interest. Higher rates than long-term capital gains.' },
    { term: '401(k)', category: 'tax', definition: 'Employer-sponsored retirement account. Contributions are pre-tax, reducing current taxable income. Grows tax-deferred.' },
    { term: 'Roth IRA', category: 'tax', definition: 'Retirement account with after-tax contributions but tax-free growth and withdrawals. Powerful for young investors.' },
    { term: 'Traditional IRA', category: 'tax', definition: 'Retirement account with pre-tax contributions (deductible). Pay taxes on withdrawals in retirement.' },
    { term: 'HSA', category: 'tax', definition: 'Health Savings Account. Triple tax advantage: deductible contributions, tax-free growth, tax-free medical withdrawals.' },
    { term: 'Cost Basis', category: 'tax', definition: 'Original purchase price of asset used to calculate capital gains. Higher basis = lower taxes when selling.' },
    { term: 'Specific Lot Identification', category: 'tax', definition: 'Choosing exactly which shares to sell for tax optimization. Can minimize gains by selling highest-cost shares first.' },
    
    // Product Terms
    { term: 'Leveraged ETF', category: 'products', definition: 'ETF using derivatives to multiply daily returns (2x or 3x). Examples: TQQQ, SOXL, UPRO. Not for long-term holding due to decay.' },
    { term: 'REIT', category: 'products', definition: 'Real Estate Investment Trust. Companies that own income-producing real estate. Must pay 90% of income as dividends.' },
    { term: 'Index Fund', category: 'products', definition: 'Fund tracking a market index (S&P 500, total market). Low fees, diversification. Core of most portfolios.' },
    { term: 'Blue Chip Stock', category: 'products', definition: 'Large, established, financially stable companies. Examples: AAPL, MSFT, GOOGL. Lower volatility than small caps.' },
    { term: 'Micro Futures', category: 'products', definition: 'Smaller futures contracts (1/10th size). Examples: MES, MNQ. Allow trading with less capital and risk.' },
    { term: 'Stablecoin', category: 'products', definition: 'Cryptocurrency pegged to stable asset (usually USD). Examples: USDT, USDC. Used for DeFi and avoiding volatility.' },
    
    // Real Estate Terms  
    { term: 'LTV', category: 'real-estate', definition: 'Loan-to-Value ratio. Loan amount divided by property value. 80% LTV = $80K loan on $100K property (20% down).' },
    { term: 'Cap Rate', category: 'real-estate', definition: 'Capitalization Rate. Net operating income / property value. Measures return on real estate investment. 8-12% is excellent.' },
    { term: 'Cash Flow', category: 'real-estate', definition: 'Rental income minus all expenses (mortgage, taxes, maintenance). Positive cash flow = property pays for itself.' },
    { term: 'NOI', category: 'real-estate', definition: 'Net Operating Income. Rental income minus operating expenses (excludes mortgage). Used to calculate cap rate. Higher NOI = better property.' },
    { term: 'Cash-on-Cash Return', category: 'real-estate', definition: 'Annual cash flow divided by total cash invested (down payment + closing costs). Shows actual return on your money. Target: 8-12%.' },
    { term: 'Gross Yield', category: 'real-estate', definition: 'Annual rental income / property value. Simple return metric before expenses. Doesn\'t account for costs. Useful for quick screening.' },
    { term: '1% Rule', category: 'real-estate', definition: 'Monthly rent should equal or exceed 1% of purchase price. $300K house should rent for $3K/month. Indicates good cash flow potential.' },
    { term: 'Vacancy Rate', category: 'real-estate', definition: 'Percentage of time property sits empty. Budget 5-10% for most markets. Higher vacancy = lower actual income. Critical for cash flow.' },
    { term: 'DTI', category: 'real-estate', definition: 'Debt-to-Income Ratio. Total monthly debt payments / gross monthly income. Under 36% preferred for mortgages. Under 43% is max for most loans.' },
    { term: 'PMI', category: 'real-estate', definition: 'Private Mortgage Insurance. Required when down payment < 20%. Costs 0.5-1% of loan annually. Can be removed once 20% equity reached.' },
    { term: 'Front-End Ratio', category: 'real-estate', definition: 'Housing costs / gross income. Lenders prefer max 28%. Includes mortgage, taxes, insurance. Part of mortgage qualification.' },
    { term: 'Back-End Ratio', category: 'real-estate', definition: 'Total debt payments / gross income. Lenders prefer max 36%. Includes housing + car loans, credit cards, student loans.' },
    { term: 'Principal & Interest', category: 'real-estate', definition: 'Two components of mortgage payment. Principal reduces loan balance. Interest is cost of borrowing. Early payments mostly interest.' },
    { term: 'Amortization', category: 'real-estate', definition: 'Gradual repayment of loan over time. Early payments mostly interest; later payments mostly principal. 15 or 30 year terms typical.' },
    { term: 'Pre-Approval', category: 'real-estate', definition: 'Lender confirms you qualify for specific loan amount. Shows sellers you\'re serious buyer. Different from pre-qualification (estimate only).' },
    { term: 'Closing Costs', category: 'real-estate', definition: 'Fees to finalize mortgage (2-5% of purchase). Includes appraisal, title insurance, origination fees. Buyer typically pays most.' },
    { term: 'Property Management', category: 'real-estate', definition: 'Company handling tenant screening, rent collection, maintenance. Costs 8-12% of rent. Worth it for out-of-town or busy landlords.' },
    { term: 'House Hacking', category: 'real-estate', definition: 'Living in a property while renting out rooms/units to cover mortgage. Accelerates wealth building with low/no housing costs.' },
    { term: 'BRRRR', category: 'real-estate', definition: 'Buy, Rehab, Rent, Refinance, Repeat. Strategy to build portfolio using minimal cash. Buy undervalued, fix, rent, refinance to pull cash out.' },
    
    // Accounting & Financial Statements (from Reading Fine Print)
    { term: 'OCF', category: 'accounting', definition: 'Operating Cash Flow. Cash generated from core business operations. More reliable than net income because cash doesn\'t lie. OCF should be > Net Income.' },
    { term: 'FCF', category: 'accounting', definition: 'Free Cash Flow. OCF minus capital expenditures. Shows cash available for dividends, buybacks, debt reduction. Key metric for valuation.' },
    { term: 'Net Income', category: 'accounting', definition: 'Bottom line profit after all expenses and taxes. Can be manipulated with accounting tricks. Always compare to operating cash flow.' },
    { term: 'COGS', category: 'accounting', definition: 'Cost of Goods Sold. Direct costs to produce goods. Can be manipulated by understating warranty costs or deferring expenses.' },
    { term: 'EBITDA', category: 'accounting', definition: 'Earnings Before Interest, Taxes, Depreciation, Amortization. "Earnings before all the bad stuff." Often used to make profitability look better than it is.' },
    { term: 'Goodwill', category: 'accounting', definition: 'Intangible asset from acquisitions - the excess paid over book value. Red flag if >30% of total assets. Indicates overpaying for acquisitions.' },
    { term: 'PP&E', category: 'accounting', definition: 'Property, Plant & Equipment. Physical assets like buildings, machinery. Harder to sell quickly than cash or securities. Lower quality than liquid assets.' },
    { term: 'Accounts Receivable', category: 'accounting', definition: 'Money owed by customers for sales made on credit. Red flag if growing faster than revenue - suggests IOUs that may never be collected.' },
    { term: 'Inventory', category: 'accounting', definition: 'Products held for sale. Red flag if building up - suggests products not selling. Can become obsolete. Lower asset quality than receivables.' },
    { term: 'DSO', category: 'accounting', definition: 'Days Sales Outstanding. (Receivables / Revenue) × 365. Measures how long it takes to collect payment. Rising DSO = bad sign.' },
    { term: 'DIO', category: 'accounting', definition: 'Days Inventory Outstanding. (Inventory / COGS) × 365. Measures how long inventory sits. Rising DIO = products not selling fast.' },
    { term: 'Gross Margin', category: 'accounting', definition: '(Revenue - COGS) / Revenue. Shows pricing power. Declining margins = losing competitive advantage or facing price pressure.' },
    { term: 'Asset Quality', category: 'accounting', definition: 'Hierarchy: Cash > Securities > Receivables > Inventory > PP&E > Goodwill. Higher quality assets are more liquid and harder to fake.' },
    { term: 'Capitalizing Expenses', category: 'accounting', definition: 'Moving costs to balance sheet as "assets" instead of expensing immediately. Accounting trick that inflates earnings. Watch for this in footnotes.' },
    { term: 'Revenue Recognition', category: 'accounting', definition: 'When a company records revenue. Can be manipulated by booking future revenue today (channel stuffing) or aggressive multi-year contract accounting.' },
    { term: 'Deferred Revenue', category: 'accounting', definition: 'Cash received but not yet earned (like prepaid subscriptions). Red flag if shrinking - means borrowing from future to boost current revenue.' },
    { term: 'Goodwill Impairment', category: 'accounting', definition: 'Writing down overpaid acquisitions. Massive charges that admit management overpaid. One impairment often leads to more - sign of poor M&A.' },
    { term: 'GAAP vs Non-GAAP', category: 'accounting', definition: 'GAAP = standard accounting. Non-GAAP = company\'s "adjusted" version removing "one-time" charges. Red flag if big divergence from GAAP.' },
    { term: 'Related Party Transactions', category: 'accounting', definition: 'Deals with insiders (CEO\'s wife\'s company, etc.). Major red flag for self-dealing. Look in 10-K footnotes under "Related Party."' },
    { term: 'Auditor Opinion', category: 'accounting', definition: 'Independent accountant\'s assessment. "Unqualified" = good. "Going concern" warning = company may not survive. Auditor resignation = huge red flag.' },
    { term: 'Material Weakness', category: 'accounting', definition: 'Serious problems with internal controls. Means accounting systems are broken. Often precedes fraud revelations. Avoid companies with this designation.' },
    { term: 'Channel Stuffing', category: 'accounting', definition: 'Pushing excess inventory to distributors to inflate revenue. Products will be returned later but revenue is booked now. Classic fraud technique.' },
    { term: '10-K', category: 'accounting', definition: 'Annual report filed with SEC. Contains complete financial statements, risk factors, MD&A. Read cash flow first, then footnotes, then income statement last.' },
    { term: '10-Q', category: 'accounting', definition: 'Quarterly report filed with SEC. Shorter than 10-K but contains same financial statements. Watch for changes quarter-over-quarter.' },
    
    // DeFi & Crypto Protocols (from Crypto Protocols page)
    { term: 'TVL', category: 'defi', definition: 'Total Value Locked. Amount of assets deposited in DeFi protocol. Higher TVL = more trusted/established. AAVE has $10B+ TVL.' },
    { term: 'CDP', category: 'defi', definition: 'Collateralized Debt Position. Lock crypto as collateral to mint stablecoins. Example: Lock ETH in Maker to mint DAI. Must maintain collateral ratio.' },
    { term: 'LTV Ratio', category: 'defi', definition: 'Loan-to-Value. Max you can borrow vs collateral. 75% LTV = borrow $750 per $1,000 collateral. Lower LTV = safer but less leverage.' },
    { term: 'Liquidation Threshold', category: 'defi', definition: 'Collateral ratio where position gets liquidated. Example: 110% in Liquity, 150% in Maker. Stay above this or lose collateral.' },
    { term: 'Smart Contract', category: 'defi', definition: 'Self-executing code on blockchain. Powers DeFi but has bug risk. No insurance - if hacked, funds can be lost. Audit history important.' },
    { term: 'AAVE', category: 'defi', definition: 'Leading DeFi lending protocol. Supply assets to earn interest, borrow against collateral. $10B+ TVL. Supports 100+ tokens across multiple chains.' },
    { term: 'DAI', category: 'defi', definition: 'Decentralized stablecoin pegged to $1. Created by locking ETH as collateral in Maker protocol. Most trusted decentralized stablecoin.' },
    { term: 'LUSD', category: 'defi', definition: 'Liquity\'s stablecoin. 0% interest - pay 0.5% one-time fee then borrow forever. Minimum 110% collateral ratio (highest risk/reward).' },
    { term: 'Leveraged Farming', category: 'defi', definition: 'Borrowing to amplify LP yields. Example: Deposit $1K, borrow $2K on Alpaca, farm with $3K. Amplifies returns but adds liquidation risk.' },
    { term: 'Yield Farming', category: 'defi', definition: 'Providing liquidity to earn fees and rewards. Not leverage itself but can be leveraged. Returns vary 5-100%+ APY depending on risk.' },
    { term: 'LP Token', category: 'defi', definition: 'Liquidity Provider token. Proof of deposit in liquidity pool. Can be used as collateral in some protocols. Represents share of pool.' },
    { term: 'Orderbook DEX', category: 'defi', definition: 'Decentralized exchange using traditional orderbook (like Hyperliquid, dYdX). More CEX-like experience but self-custody. Lower slippage than AMMs.' },
    { term: 'AMM', category: 'defi', definition: 'Automated Market Maker. DEX using liquidity pools instead of orderbooks (Uniswap). More decentralized but higher slippage on large trades.' },
    { term: 'Funding Rate', category: 'defi', definition: 'Periodic payment between perpetual contract traders. Positive = longs pay shorts. Negative = shorts pay longs. Keeps futures pegged to spot price.' },
    { term: 'Perpetual Contract', category: 'defi', definition: 'Futures with no expiration date. Common in crypto (dYdX, Hyperliquid, GMX). Uses funding rates to maintain spot peg. Can hold forever.' },
    { term: 'GLP', category: 'defi', definition: 'GMX Liquidity Provider token. Traders trade against GLP pool. LPs earn trading fees but take opposite side of trades. Novel perpetual model.' },
    { term: 'Oracle', category: 'defi', definition: 'External data feed providing prices to smart contracts. Critical for DeFi liquidations. Oracle manipulation can cause unfair liquidations.' },
    { term: 'Flash Loan', category: 'defi', definition: 'Borrow millions without collateral for single transaction. Must repay in same block or reverts. Used for arbitrage and liquidations. Unique to DeFi.' },
    { term: 'Looping', category: 'defi', definition: 'Repeatedly supplying and borrowing same asset to increase leverage. Example: Supply ETH, borrow ETH, supply again. Can reach 5-10x leverage.' },
    { term: 'Credit Account', category: 'defi', definition: 'Gearbox\'s leveraged wallet. Borrow once, use across multiple DeFi protocols (Uniswap, Curve, etc). Composable leverage layer.' },
    { term: 'Staking Yield', category: 'defi', definition: 'Rewards for locking tokens to secure network. ETH staking earns 3-5% APY. Can be leveraged via protocols like Gearbox or Lido.' },
    { term: 'Collateral Factor', category: 'defi', definition: 'Maximum borrowable per dollar of collateral. 75% factor = borrow $0.75 per $1.00 supplied. Varies by asset risk - stablecoins highest.' },
    { term: 'Borrow APR', category: 'defi', definition: 'Interest rate on borrowed funds in DeFi. Variable based on utilization. Typically 5-15% for stablecoins. Must be < farming yield for profit.' },
    { term: 'Self-Custody', category: 'defi', definition: 'You control private keys (not exchange). DeFi protocols are non-custodial - you keep control but also responsibility. Not your keys = not your crypto.' }
  ];

  const filteredTerms = glossaryTerms.filter(term => {
    const matchesSearch = term.term.toLowerCase().includes(glossarySearch.toLowerCase()) ||
                         term.definition.toLowerCase().includes(glossarySearch.toLowerCase());
    const matchesCategory = glossaryCategory === 'all' || term.category === glossaryCategory;
    return matchesSearch && matchesCategory;
  });

  const glossaryCategories = [
    { value: 'all', label: 'All Terms', count: glossaryTerms.length },
    { value: 'basic', label: 'Basic', count: glossaryTerms.filter(t => t.category === 'basic').length },
    { value: 'risk', label: 'Risk Management', count: glossaryTerms.filter(t => t.category === 'risk').length },
    { value: 'trading', label: 'Trading', count: glossaryTerms.filter(t => t.category === 'trading').length },
    { value: 'margin', label: 'Margin Types', count: glossaryTerms.filter(t => t.category === 'margin').length },
    { value: 'orders', label: 'Order Types', count: glossaryTerms.filter(t => t.category === 'orders').length },
    { value: 'crypto', label: 'Crypto', count: glossaryTerms.filter(t => t.category === 'crypto').length },
    { value: 'defi', label: 'DeFi Protocols', count: glossaryTerms.filter(t => t.category === 'defi').length },
    { value: 'options', label: 'Options', count: glossaryTerms.filter(t => t.category === 'options').length },
    { value: 'fees', label: 'Fees', count: glossaryTerms.filter(t => t.category === 'fees').length },
    { value: 'advanced', label: 'Advanced', count: glossaryTerms.filter(t => t.category === 'advanced').length },
    { value: 'accounting', label: 'Accounting & Statements', count: glossaryTerms.filter(t => t.category === 'accounting').length },
    { value: 'planning', label: 'Financial Planning', count: glossaryTerms.filter(t => t.category === 'planning').length },
    { value: 'tax', label: 'Tax Strategies', count: glossaryTerms.filter(t => t.category === 'tax').length },
    { value: 'products', label: 'Products', count: glossaryTerms.filter(t => t.category === 'products').length },
    { value: 'real-estate', label: 'Real Estate', count: glossaryTerms.filter(t => t.category === 'real-estate').length }
  ];

  // Leverage methods data
  const leverageMethods = [
    {
      id: 1,
      title: 'Margin Trading (Stocks)',
      category: 'stocks',
      risk: 'low-medium',
      maxLeverage: '2x',
      icon: TrendingUp,
      description: 'Borrow from your broker to buy stocks. Most common and regulated.',
      pros: ['Regulated & safe', 'Low leverage = lower risk', 'Easy to understand'],
      cons: ['Margin interest (5-12% annual)', 'Margin calls if stock drops', 'Can\'t short easily'],
      bestFor: 'Conservative investors, beginners',
      minCapital: '$2,000',
      platforms: ['Robinhood', 'TD Ameritrade', 'Interactive Brokers'],
      keyPoints: [
        'Borrow up to 50% of stock value (Reg T)',
        'Popular for blue chips: AAPL, MSFT, GOOGL, NVDA, AMZN',
        'Pay interest on borrowed amount (typically 5-12% annual)',
        'Get margin call if equity falls below 25%',
        'Can amplify both gains AND losses'
      ]
    },
    {
      id: 2,
      title: 'Options Trading',
      category: 'stocks',
      risk: 'medium-high',
      maxLeverage: '10-50x',
      icon: BarChart3,
      description: 'Buy the right (not obligation) to buy/sell stocks at a set price.',
      pros: ['Defined max loss (premium)', 'Huge leverage potential', 'Many strategies available'],
      cons: ['Complex to learn', 'Time decay (theta)', 'Can lose 100% of premium'],
      bestFor: 'Intermediate to advanced traders',
      minCapital: '$1,000+',
      platforms: ['tastyworks', 'TD Ameritrade', 'Interactive Brokers'],
      keyPoints: [
        'Calls = bet on price increase (e.g., SPY calls, AAPL calls, TSLA calls)',
        'Puts = bet on price decrease (e.g., QQQ puts for hedging)',
        'Popular strategies: Covered calls, Cash-secured puts, Spreads',
        'Options expire worthless if not ITM',
        'Greeks (delta, gamma, theta, vega) matter'
      ]
    },
    {
      id: 3,
      title: 'Futures Contracts',
      category: 'commodities',
      risk: 'high',
      maxLeverage: '10-20x',
      icon: LineChart,
      description: 'Contracts to buy/sell assets at future date. Used for stocks, commodities, crypto.',
      pros: ['High leverage', 'Low fees', 'Tax advantages (60/40)'],
      cons: ['Very risky', 'Complex', 'Can lose more than invested'],
      bestFor: 'Experienced traders only',
      minCapital: '$5,000+',
      platforms: ['CME Group', 'Interactive Brokers', 'NinjaTrader'],
      keyPoints: [
        'Popular contracts: ES (S&P 500), NQ (Nasdaq), GC (Gold), CL (Crude Oil)',
        'Micro futures available: MES, MNQ for smaller accounts',
        'Standardized contracts with expiration dates',
        'Daily settlement (mark-to-market)',
        'Futures can go negative (you owe money)',
        'Professional traders use these'
      ]
    },
    {
      id: 4,
      title: 'Crypto Perpetual Futures',
      category: 'crypto',
      risk: 'extreme',
      maxLeverage: '100x+',
      icon: Bitcoin,
      description: 'Futures with no expiry. Unique to crypto. Very popular and very risky.',
      pros: ['No expiration', 'Huge leverage available', '24/7 trading'],
      cons: ['Extremely risky', 'Funding fees', 'Easy to get liquidated'],
      bestFor: 'Very experienced crypto traders',
      minCapital: '$100+',
      platforms: ['Binance', 'Bybit', 'Hyperliquid', 'Vertex'],
      keyPoints: [
        'Trade BTC-PERP, ETH-PERP, SOL-PERP, and 100+ altcoins',
        'Pay/receive funding every 8 hours',
        'Funding keeps price near spot',
        'Most retail traders lose money',
        'Start with 2-5x max until experienced'
      ]
    },
    {
      id: 5,
      title: 'Forex Trading',
      category: 'forex',
      risk: 'high',
      maxLeverage: '50-500x',
      icon: Globe,
      description: 'Trade currency pairs with massive leverage. Largest financial market.',
      pros: ['Huge liquidity', 'Low spreads', '24/5 trading'],
      cons: ['Very high leverage temptation', 'Complex macroeconomics', 'Many scam brokers'],
      bestFor: 'Macro-focused traders',
      minCapital: '$100-$500',
      platforms: ['OANDA', 'Forex.com', 'Interactive Brokers'],
      keyPoints: [
        'Trade major pairs: EUR/USD, GBP/USD, USD/JPY, AUD/USD',
        'Exotic pairs: USD/TRY, USD/ZAR for higher volatility',
        'Leverage 50:1 or higher available',
        'Pays/charges rollover interest overnight (swap rates)',
        'Highly sensitive to news and macro'
      ]
    },
    {
      id: 6,
      title: 'Real Estate Leverage (Mortgages)',
      category: 'real-estate',
      risk: 'low-medium',
      maxLeverage: '5-20x',
      icon: Home,
      description: 'Use mortgages to buy property. Traditional but powerful leverage.',
      pros: ['Low interest rates', 'Property appreciates', 'Tax benefits'],
      cons: ['Illiquid', 'High transaction costs', 'Maintenance required'],
      bestFor: 'Long-term investors',
      minCapital: '$10,000+',
      platforms: ['Local lenders', 'Rocket Mortgage', 'Better.com'],
      keyPoints: [
        '3.5-20% down payment required',
        'FHA loans: 3.5% down, conventional: 5-20% down, VA loans: 0% down',
        'Monthly mortgage payments (principal + interest + taxes + insurance)',
        'Property can appreciate or depreciate',
        'Rental income can cover mortgage (house hacking strategy)'
      ]
    },
    {
      id: 7,
      title: 'Portfolio Margin',
      category: 'stocks',
      risk: 'medium-high',
      maxLeverage: '4-6x',
      icon: BookOpen,
      description: 'Advanced margin based on portfolio risk. More efficient than Reg T.',
      pros: ['More leverage than standard', 'Risk-based (smarter)', 'Lower margin requirements'],
      cons: ['Need $125K minimum', 'Complex', 'Higher liquidation risk'],
      bestFor: 'Wealthy, sophisticated investors',
      minCapital: '$125,000',
      platforms: ['Interactive Brokers', 'TD Ameritrade', 'Fidelity'],
      keyPoints: [
        'Margin based on overall portfolio risk (delta-weighted)',
        'Can use 4-6x leverage instead of 2x',
        'Best for diversified portfolios: stocks + options + bonds',
        'Requires options approval and $125K minimum',
        'Must maintain strict risk controls'
      ]
    },
    {
      id: 8,
      title: 'Leveraged ETFs',
      category: 'stocks',
      risk: 'medium-high',
      maxLeverage: '2-3x',
      icon: TrendingUp,
      description: 'ETFs that use derivatives to multiply daily returns.',
      pros: ['Simple to buy (like stocks)', 'No margin account needed', 'Liquid'],
      cons: ['Decay over time', 'High fees', 'Only 2-3x max'],
      bestFor: 'Day traders, short-term trades',
      minCapital: '$500+',
      platforms: ['Any stock broker', 'Robinhood', 'Fidelity'],
      keyPoints: [
        'Reset daily (volatility decay)',
        'Not for holding long-term',
        'Examples: TQQQ (3x Nasdaq), SPXL (3x S&P), SOXL (3x Semiconductors), UPRO (3x S&P 500)',
        'Inverse ETFs available: SQQQ (3x inverse Nasdaq), SPXS (3x inverse S&P)',
        'Can lose more than expected in sideways markets'
      ]
    },
    {
      id: 9,
      title: 'DeFi Leverage Protocols',
      category: 'crypto',
      risk: 'extreme',
      maxLeverage: '10-20x',
      icon: Bitcoin,
      description: 'Decentralized lending/borrowing for crypto leverage.',
      pros: ['Permissionless', 'No KYC', 'Composable with other DeFi'],
      cons: ['Smart contract risk', 'Gas fees', 'Complexity', 'Liquidation risk'],
      bestFor: 'Advanced DeFi users',
      minCapital: '$1,000+',
      platforms: ['Aave', 'Compound', 'Hyperliquid', 'Morpho'],
      keyPoints: [
        'Borrow against crypto collateral (BTC, ETH, stablecoins)',
        'Popular strategies: Recursive lending, yield farming with leverage',
        'Can be liquidated if collateral drops',
        'Pay variable interest rates (APR changes dynamically)',
        'Smart contract risk is real - audits matter'
      ]
    },
    {
      id: 10,
      title: 'Contracts for Difference (CFDs)',
      category: 'other',
      risk: 'high',
      maxLeverage: '10-100x',
      icon: Globe,
      description: 'Speculate on price without owning asset. Popular in Europe.',
      pros: ['Trade anything', 'High leverage', 'No ownership needed'],
      cons: ['Banned in US', 'Counterparty risk', 'Very risky'],
      bestFor: 'Non-US traders (banned in USA)',
      minCapital: '$500+',
      platforms: ['IG', 'Plus500', 'eToro'],
      keyPoints: [
        'NOT available in the United States',
        'Trade stocks, commodities, indices',
        'Pay spread instead of commission',
        'High risk of total loss'
      ]
    },
    {
      id: 11,
      title: 'REITs with Leverage',
      category: 'real-estate',
      risk: 'medium',
      maxLeverage: '2-5x',
      icon: Building2,
      description: 'Real Estate Investment Trusts bought on margin.',
      pros: ['Liquid (trade like stocks)', 'Diversified real estate', 'Dividends'],
      cons: ['Margin interest', 'Market volatility', 'Less control'],
      bestFor: 'Real estate investors wanting liquidity',
      minCapital: '$2,000+',
      platforms: ['Any stock broker', 'Robinhood', 'Fidelity'],
      keyPoints: [
        'Buy REITs with margin (2x leverage)',
        'Examples: VNQ (Vanguard Real Estate), O (Realty Income), SPG (Simon Property), PLD (Prologis)',
        'Earn dividends minus margin interest',
        'More liquid than physical property',
        'Can use in IRAs (without leverage)'
      ]
    },
    {
      id: 12,
      title: 'Synthetic Leverage (Pairs Trading)',
      category: 'stocks',
      risk: 'medium',
      maxLeverage: '2-4x',
      icon: BarChart3,
      description: 'Long one asset, short another. Creates synthetic leverage.',
      pros: ['Market-neutral potential', 'Lower risk than outright leverage', 'Sophisticated'],
      cons: ['Complex', 'Both sides can lose', 'High fees'],
      bestFor: 'Advanced traders',
      minCapital: '$5,000+',
      platforms: ['Interactive Brokers', 'TD Ameritrade'],
      keyPoints: [
        'Classic pairs: Long AAPL / Short MSFT, Long XLE / Short XLK',
        'Go long undervalued, short overvalued',
        'Reduces market exposure (market-neutral)',
        'Pays when spread converges',
        'Requires significant skill and analysis'
      ]
    },
    {
      id: 13,
      title: 'Private Equity / Business Leverage',
      category: 'other',
      risk: 'high',
      maxLeverage: '5-10x',
      icon: Building2,
      description: 'Borrow to buy/invest in private companies.',
      pros: ['Huge upside potential', 'Tax benefits', 'Control'],
      cons: ['Illiquid', 'High risk', 'Requires expertise'],
      bestFor: 'Entrepreneurs, accredited investors',
      minCapital: '$100,000+',
      platforms: ['Private lenders', 'SBA loans', 'Banks'],
      keyPoints: [
        'Leveraged buyouts (LBOs)',
        'SBA loans for small business',
        'Very illiquid for years',
        'Can create massive wealth or debt'
      ]
    },
    {
      id: 14,
      title: 'Peer-to-Peer Lending (Leveraged)',
      category: 'other',
      risk: 'high',
      maxLeverage: '2-5x',
      icon: DollarSign,
      description: 'Borrow to lend on P2P platforms for amplified returns.',
      pros: ['Higher returns potential', 'Passive income', 'Diversification'],
      cons: ['Default risk', 'Illiquid', 'Complex'],
      bestFor: 'Income-focused investors',
      minCapital: '$5,000+',
      platforms: ['LendingClub', 'Prosper', 'Funding Circle'],
      keyPoints: [
        'Borrow at low rate, lend at higher rate',
        'Diversify across 100+ loans',
        'Borrowers can default',
        'Not suitable for most retail investors'
      ]
    },
    {
      id: 15,
      title: 'Credit Card Arbitrage',
      category: 'other',
      risk: 'medium',
      maxLeverage: '2-10x',
      icon: DollarSign,
      description: 'Use 0% APR credit cards to invest borrowed money.',
      pros: ['Zero interest (temporarily)', 'Easy access', 'Can work if disciplined'],
      cons: ['Very risky', 'Hurts credit score', 'Must pay back'],
      bestFor: 'Nobody (seriously, don\'t do this)',
      minCapital: '$5,000+',
      platforms: ['Credit card issuers'],
      keyPoints: [
        'Technically possible but VERY RISKY',
        'Destroys credit if you can\'t pay',
        'Only works with 0% APR offers',
        'Not recommended by any financial advisor'
      ]
    },
    {
      id: 16,
      title: 'Personal Loan Investing',
      category: 'other',
      risk: 'extreme',
      maxLeverage: '2-5x',
      icon: DollarSign,
      description: 'Take personal loan to invest. Almost always a terrible idea.',
      pros: ['...none really'],
      cons: ['High interest', 'Fixed payments', 'Can\'t discharge in bankruptcy'],
      bestFor: 'Nobody (terrible idea)',
      minCapital: '$1,000+',
      platforms: ['Banks', 'SoFi', 'Upstart'],
      keyPoints: [
        'Pay 7-20% interest on loan',
        'MUST make monthly payments regardless of returns',
        'Can ruin your life if investments fail',
        'Do not do this'
      ]
    },
    {
      id: 17,
      title: 'HELOC Investing',
      category: 'real-estate',
      risk: 'high',
      maxLeverage: '2-5x',
      icon: Home,
      description: 'Home Equity Line of Credit used for investing.',
      pros: ['Low interest rates', 'Tax-deductible (maybe)', 'Large amounts'],
      cons: ['Risk losing your house', 'Variable rates', 'Very risky'],
      bestFor: 'Wealthy investors with high risk tolerance',
      minCapital: '$50,000+',
      platforms: ['Banks', 'Credit unions'],
      keyPoints: [
        'Borrow against home equity',
        'Rates typically 5-8%',
        'Your house is collateral',
        'Can lose your home if investments fail'
      ]
    },
    {
      id: 18,
      title: 'Margin Crypto Spot Trading',
      category: 'crypto',
      risk: 'high',
      maxLeverage: '3-10x',
      icon: Bitcoin,
      description: 'Borrow stablecoins or crypto to trade spot markets.',
      pros: ['Lower risk than perpetuals', 'No funding fees', 'Keep your crypto'],
      cons: ['Still very risky', 'Interest fees', 'Liquidation risk'],
      bestFor: 'Intermediate crypto traders',
      minCapital: '$500+',
      platforms: ['Binance', 'Kraken', 'KuCoin'],
      keyPoints: [
        'Borrow USDT/USDC to buy crypto',
        'Pay hourly/daily interest',
        'Can be liquidated if crypto drops',
        'Safer than perpetuals but still risky'
      ]
    },
    {
      id: 19,
      title: 'Box Spread Financing',
      category: 'stocks',
      risk: 'low',
      maxLeverage: '2-3x',
      icon: BarChart3,
      description: 'Advanced options strategy to borrow money at low rates.',
      pros: ['Lowest interest rates available', 'Sophisticated', 'Tax efficient'],
      cons: ['Very complex', 'Need large capital', 'Assignment risk'],
      bestFor: 'Wealthy, sophisticated options traders',
      minCapital: '$100,000+',
      platforms: ['Interactive Brokers', 'tastyworks'],
      keyPoints: [
        'Create synthetic loan using options',
        'Borrow at ~1-3% (below margin rates)',
        'Requires deep options knowledge',
        'Can go wrong if not done correctly'
      ]
    },
    {
      id: 20,
      title: 'Volatility Products (VIX)',
      category: 'other',
      risk: 'extreme',
      maxLeverage: '2-4x',
      icon: AlertTriangle,
      description: 'Trade volatility itself. Very complex and dangerous.',
      pros: ['Hedge against market crashes', 'Uncorrelated returns'],
      cons: ['Extreme decay', 'Hard to time', 'Most traders lose'],
      bestFor: 'Professional traders only',
      minCapital: '$5,000+',
      platforms: ['Interactive Brokers', 'TD Ameritrade'],
      keyPoints: [
        'UVXY, VXX = leveraged volatility',
        'Decay rapidly over time',
        'Spike during market crashes',
        'Not suitable for retail investors'
      ]
    },
    {
      id: 21,
      title: '401(k) Loan for Investing',
      category: 'other',
      risk: 'high',
      maxLeverage: '2x',
      icon: ShieldAlert,
      description: 'Borrow from your 401(k) to invest elsewhere. Usually unwise.',
      pros: ['Low interest to yourself', 'No credit check', 'Easy approval'],
      cons: ['Opportunity cost', 'Must repay or huge tax bill', 'Very risky'],
      bestFor: 'Almost nobody',
      minCapital: '$10,000+',
      platforms: ['Your 401(k) provider'],
      keyPoints: [
        'Can borrow up to 50% of 401(k) balance',
        'Must repay within 5 years',
        'If you leave job, must repay immediately',
        'Miss out on tax-free growth in 401(k)'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Navigation Bar */}
      <nav className="bg-slate-800/50 border-b border-slate-700 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => setCurrentPage('home')}
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer"
            >
              <TrendingUp className="w-8 h-8 text-blue-400" />
              <span className="text-xl font-bold">StreetFi</span>
            </button>
            
            <div className="flex space-x-1 sm:space-x-2">
              {/* Home */}
              <button
                onClick={() => setCurrentPage('home')}
                className={`px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base ${
                  currentPage === 'home'
                    ? 'bg-blue-500 text-white'
                    : 'text-slate-300 hover:bg-slate-700'
                }`}
              >
                <Home className="w-5 h-5 sm:hidden" />
                <span className="hidden sm:inline">Home</span>
              </button>

              {/* Start Here Dropdown */}
              <div className="relative">
                <button
                  onClick={() => {
                    setStartHereDropdownOpen(!startHereDropdownOpen);
                    setCalculatorsDropdownOpen(false);
                    setPlanningDropdownOpen(false);
                    setResourcesDropdownOpen(false);
                  }}
                  className={`px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base flex items-center space-x-1 ${
                    ['quiz', 'about'].includes(currentPage)
                      ? 'bg-blue-500 text-white'
                      : 'text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <Target className="w-5 h-5 sm:hidden" />
                  <span className="hidden sm:inline">Start Here</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${startHereDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {startHereDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-xl overflow-hidden z-50">
                    <button
                      onClick={() => {
                        setCurrentPage('quiz');
                        setStartHereDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'quiz'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <ClipboardCheck className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Risk Assessment Quiz</div>
                        <div className="text-xs text-slate-500">Find your leverage style</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('about');
                        setStartHereDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'about'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <Heart className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Our Mission</div>
                        <div className="text-xs text-slate-500">About StreetFi</div>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              {/* Tools Dropdown */}
              <div className="relative">
                <button
                  onClick={() => {
                    setCalculatorsDropdownOpen(!calculatorsDropdownOpen);
                    setStartHereDropdownOpen(false);
                    setPlanningDropdownOpen(false);
                    setResourcesDropdownOpen(false);
                  }}
                  className={`px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base flex items-center space-x-1 ${
                    ['leverage-trading', 'cost-analysis', 'stock-options', 'stock-tax', '83b-election', 'tax', 'planning', 'mortgage', 'rental', 'cost-seg', '1031-exchange', 'withdrawal-strategy'].includes(currentPage)
                      ? 'bg-blue-500 text-white'
                      : 'text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <BarChart3 className="w-5 h-5 sm:hidden" />
                  <span className="hidden sm:inline">Tools</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${calculatorsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {calculatorsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-80 bg-slate-800 border border-slate-700 rounded-lg shadow-xl overflow-hidden z-50">
                    <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase border-b border-slate-700">Wealth Tools</div>
                    <button
                      onClick={() => {
                        setCurrentPage('planning');
                        setCalculatorsDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'planning'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <Target className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Financial Planning Calculator</div>
                        <div className="text-xs text-slate-500">Goals & projections</div>
                      </div>
                    </button>
                    <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase border-t border-slate-700">Trading Tools</div>
                    <button
                      onClick={() => {
                        setCurrentPage('leverage-trading');
                        setCalculatorsDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'leverage-trading'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <BarChart3 className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Leverage Trading Calculator</div>
                        <div className="text-xs text-slate-500">P&L, sizing, risk & scenarios</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('stock-options');
                        setCalculatorsDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'stock-options'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <TrendingUp className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Stock Options & QSBS</div>
                        <div className="text-xs text-slate-500">$10M exclusion optimizer</div>
                      </div>
                    </button>
                    
                    <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase border-t border-slate-700">Tax Tools</div>
                    <button
                      onClick={() => {
                        setCurrentPage('stock-tax');
                        setCalculatorsDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'stock-tax'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <Percent className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Stock Sale Tax Calculator</div>
                        <div className="text-xs text-slate-500">Capital gains calculator</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('tax');
                        setCalculatorsDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'tax'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <PiggyBank className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Tax Strategies (38)</div>
                        <div className="text-xs text-slate-500">Advanced tax optimization</div>
                      </div>
                    </button>
                    
                    <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase border-t border-slate-700">Real Estate Tools</div>
                    <button
                      onClick={() => {
                        setCurrentPage('mortgage');
                        setCalculatorsDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'mortgage'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <Home className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Mortgage Calculator</div>
                        <div className="text-xs text-slate-500">Affordability & DTI</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('rental');
                        setCalculatorsDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'rental'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <Building2 className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Rental Property ROI</div>
                        <div className="text-xs text-slate-500">Cash flow & returns</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('1031-exchange');
                        setCalculatorsDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === '1031-exchange'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <ArrowLeftRight className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">1031 Exchange</div>
                        <div className="text-xs text-slate-500">Defer capital gains</div>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              {/* Learn Dropdown */}
              <div className="relative">
                <button
                  onClick={() => {
                    setPlanningDropdownOpen(!planningDropdownOpen);
                    setStartHereDropdownOpen(false);
                    setCalculatorsDropdownOpen(false);
                    setResourcesDropdownOpen(false);
                  }}
                  className={`px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base flex items-center space-x-1 ${
                    ['real-estate-leverage', 'investment-leverage', 'crypto-leverage', 'business-leverage', 
                     'real-finance-system', 'wealth-games', 'credit-leverage', 'narrative-economy',
                     'market-psychology', 'reading-fine-print', 'game-theory'].includes(currentPage)
                      ? 'bg-blue-500 text-white'
                      : 'text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <BookOpen className="w-5 h-5 sm:hidden" />
                  <span className="hidden sm:inline">Learn</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${planningDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {planningDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-80 bg-slate-800 border border-slate-700 rounded-lg shadow-xl overflow-hidden z-50">
                    <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase border-b border-slate-700">Methods & Markets</div>
                    <button
                      onClick={() => {
                        setCurrentPage('real-estate-leverage');
                        setPlanningDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'real-estate-leverage'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <Home className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Real Estate Leverage</div>
                        <div className="text-xs text-slate-500">Mortgages & property investing</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('investment-leverage');
                        setPlanningDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'investment-leverage'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <TrendingUp className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Investment Leverage</div>
                        <div className="text-xs text-slate-500">Margin trading & options</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('crypto-leverage');
                        setPlanningDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'crypto-leverage'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <Bitcoin className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Crypto Leverage</div>
                        <div className="text-xs text-slate-500">Perpetual futures & protocols</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('business-leverage');
                        setPlanningDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'business-leverage'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <Globe className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Business Leverage</div>
                        <div className="text-xs text-slate-500">Loans & financial strategy</div>
                      </div>
                    </button>

                    <div className="px-3 py-2 text-xs font-semibold text-amber-400 uppercase border-t border-slate-700 flex items-center space-x-1">
                      <span>🔓</span>
                      <span>The Real Game</span>
                    </div>
                    <button
                      onClick={() => {
                        setCurrentPage('real-finance-system');
                        setPlanningDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'real-finance-system'
                          ? 'bg-amber-500/20 text-amber-400 border-l-2 border-amber-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <Shield className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">How Finance Really Works</div>
                        <div className="text-xs text-slate-500">The system behind the system</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('wealth-games');
                        setPlanningDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'wealth-games'
                          ? 'bg-amber-500/20 text-amber-400 border-l-2 border-amber-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <Target className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Wealth Games & Power</div>
                        <div className="text-xs text-slate-500">How wealth really compounds</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('market-psychology');
                        setPlanningDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'market-psychology'
                          ? 'bg-amber-500/20 text-amber-400 border-l-2 border-amber-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <TrendingUp className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Market Psychology</div>
                        <div className="text-xs text-slate-500">Narratives, emotion & crowds</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('credit-leverage');
                        setPlanningDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'credit-leverage'
                          ? 'bg-amber-500/20 text-amber-400 border-l-2 border-amber-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <DollarSign className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Credit & Leverage</div>
                        <div className="text-xs text-slate-500">How money is really created</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('narrative-economy');
                        setPlanningDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'narrative-economy'
                          ? 'bg-amber-500/20 text-amber-400 border-l-2 border-amber-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <Globe className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Information & Narrative</div>
                        <div className="text-xs text-slate-500">How stories move capital</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('game-theory');
                        setPlanningDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'game-theory'
                          ? 'bg-amber-500/20 text-amber-400 border-l-2 border-amber-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <ShieldAlert className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Game Theory & Incentives</div>
                        <div className="text-xs text-slate-500">Play the player, not the game</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('reading-fine-print');
                        setPlanningDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'reading-fine-print'
                          ? 'bg-amber-500/20 text-amber-400 border-l-2 border-amber-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <FileText className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Reading the Fine Print</div>
                        <div className="text-xs text-slate-500">Spot accounting tricks & red flags</div>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              {/* Resources Dropdown */}
              <div className="relative">
                <button
                  onClick={() => {
                    setResourcesDropdownOpen(!resourcesDropdownOpen);
                    setStartHereDropdownOpen(false);
                    setCalculatorsDropdownOpen(false);
                    setPlanningDropdownOpen(false);
                  }}
                  className={`px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base flex items-center space-x-1 ${
                    ['templates', 'glossary', 'products', 'crypto-protocols', 'disclaimer', 'privacy', 'terms'].includes(currentPage)
                      ? 'bg-blue-500 text-white'
                      : 'text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <BookOpen className="w-5 h-5 sm:hidden" />
                  <span className="hidden sm:inline">Resources</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${resourcesDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {resourcesDropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-xl overflow-hidden z-50">
                    <button
                      onClick={() => {
                        setCurrentPage('templates');
                        setResourcesDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'templates'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <ClipboardCheck className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Financial Templates</div>
                        <div className="text-xs text-slate-500">Pre-built scenarios</div>
                      </div>
                    </button>
                    
                    <div className="border-t border-slate-700 my-1"></div>
                    <div className="px-4 py-2 text-xs font-semibold text-slate-400">References</div>
                    <button
                      onClick={() => {
                        setCurrentPage('glossary');
                        setResourcesDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'glossary'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <Search className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Trading Glossary</div>
                        <div className="text-xs text-slate-500">100+ terms</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('products');
                        setResourcesDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'products'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <BarChart3 className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Leverage Products (31)</div>
                        <div className="text-xs text-slate-500">All leverage methods</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('crypto-protocols');
                        setResourcesDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'crypto-protocols'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <Bitcoin className="w-4 h-4" />
                      <div>
                        <div className="font-semibold">Crypto Protocols</div>
                        <div className="text-xs text-slate-500">Leading DeFi protocols</div>
                      </div>
                    </button>
                    
                    <div className="border-t border-slate-700 my-1"></div>
                    <div className="px-4 py-2 text-xs font-semibold text-slate-400">Legal</div>
                    <button
                      onClick={() => {
                        setCurrentPage('disclaimer');
                        setResourcesDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'disclaimer'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <AlertTriangle className="w-3 h-3" />
                      <div className="text-xs">Disclaimer</div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('privacy');
                        setResourcesDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'privacy'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <Shield className="w-3 h-3" />
                      <div className="text-xs">Privacy Policy</div>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('terms');
                        setResourcesDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center space-x-3 ${
                        currentPage === 'terms'
                          ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <ClipboardCheck className="w-3 h-3" />
                      <div className="text-xs">Terms of Service</div>
                    </button>
                  </div>
                )}
              </div>
              
              {/* Login Button */}
              <div className="ml-4 border-l border-slate-700 pl-4">
                {!isLoggedIn ? (
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg transition-all"
                  >
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">Login</span>
                  </button>
                ) : (
                  <div className="flex items-center space-x-3">
                    <div className="hidden sm:block">
                      <p className="text-xs text-slate-400">Welcome back</p>
                      <p className="text-sm text-white truncate max-w-[150px]">{userEmail}</p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-all"
                    >
                      <User className="w-4 h-4" />
                      <span className="hidden sm:inline">Logout</span>
                    </button>
                  </div>
                )}
              </div>
              
            </div>
          </div>
        </div>
      </nav>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 max-w-md w-full relative">
            {/* Close button */}
            <button
              onClick={() => {
                setShowLoginModal(false);
                setAuthMode('login');
              }}
              className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Modal Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <TrendingUp className="w-8 h-8 text-blue-400" />
                <span className="text-2xl font-bold">StreetFi</span>
              </div>
              <h2 className="text-xl font-semibold mb-2">
                {authMode === 'login' ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p className="text-sm text-slate-400">
                {authMode === 'login' 
                  ? 'Login to save your calculations and settings' 
                  : 'Sign up to unlock personalized features'}
              </p>
            </div>

            {/* Auth Form */}
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const email = formData.get('email');
              const password = formData.get('password');
              
              if (authMode === 'signup') {
                const confirmPassword = formData.get('confirmPassword');
                if (password !== confirmPassword) {
                  setAuthError('Passwords do not match');
                  return;
                }
                handleSignup(email, password);
              } else {
                handleLogin(email, password);
              }
            }}>
              <div className="space-y-4">
                {/* Error Message */}
                {authError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                    <p className="text-red-400 text-sm">{authError}</p>
                  </div>
                )}
                
                {/* Email Input */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full pl-10 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                {/* Password Input */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="password"
                      name="password"
                      required
                      className="w-full pl-10 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                      placeholder="••••••••"
                      minLength={6}
                    />
                  </div>
                </div>

                {/* Confirm Password for Signup */}
                {authMode === 'signup' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="password"
                        name="confirmPassword"
                        required
                        className="w-full pl-10 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                        placeholder="••••••••"
                        minLength={6}
                      />
                    </div>
                  </div>
                )}

                {/* Remember Me / Forgot Password */}
                {authMode === 'login' && (
                  <div className="flex items-center justify-between">
                    <label className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500"
                      />
                      <span className="text-slate-300">Remember me</span>
                    </label>
                    <button
                      type="button"
                      className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={authLoading}
                  className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {authLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>{authMode === 'login' ? 'Signing In...' : 'Creating Account...'}</span>
                    </div>
                  ) : (
                    <span>{authMode === 'login' ? 'Sign In' : 'Create Account'}</span>
                  )}
                </button>

                {/* OAuth Options */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-slate-800 text-slate-400">Or continue with</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    className="flex items-center justify-center space-x-2 py-2 bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span className="text-sm">Google</span>
                  </button>
                  <button
                    type="button"
                    className="flex items-center justify-center space-x-2 py-2 bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                    </svg>
                    <span className="text-sm">GitHub</span>
                  </button>
                </div>

                {/* Toggle Auth Mode */}
                <p className="text-center text-sm text-slate-400">
                  {authMode === 'login' ? (
                    <>
                      Don't have an account?{' '}
                      <button
                        type="button"
                        onClick={() => setAuthMode('signup')}
                        className="text-blue-400 hover:text-blue-300 transition-colors font-semibold"
                      >
                        Sign up
                      </button>
                    </>
                  ) : (
                    <>
                      Already have an account?{' '}
                      <button
                        type="button"
                        onClick={() => setAuthMode('login')}
                        className="text-blue-400 hover:text-blue-300 transition-colors font-semibold"
                      >
                        Sign in
                      </button>
                    </>
                  )}
                </p>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Main Content Area */}
      <main className="pb-12">{/* HOME PAGE with all methods - I'll add the complete home page here */}
        {currentPage === 'home' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Hero Section */}
            <div className="text-center mb-20">
              <div className="inline-block mb-4 px-4 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-full">
                <span className="text-sm text-blue-400 font-semibold">🎯 Wall Street Tools • FREE • No Gatekeepers</span>
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
                <span className="text-slate-100">Professional-Grade</span>
                <br />
                <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Financial Analysis
                </span>
                <br />
                <span className="text-slate-100">For Everyone</span>
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-4">
                Get FREE access to tools that wealth advisors charge <span className="text-blue-400 font-bold">$10,000/year</span> for. 
                Monte Carlo simulations, DeFi protocol analysis, tax optimization, and fundamental analysis—all in one place.
              </p>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
                We bridge traditional finance and crypto, making Wall Street analytics accessible without the gatekeepers.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <button
                  onClick={() => setCurrentPage('leverage-trading')}
                  className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg shadow-blue-500/25 flex items-center space-x-2"
                >
                  <BarChart3 className="w-5 h-5" />
                  <span>Try Calculator</span>
                </button>
                <button
                  onClick={() => setCurrentPage('planning')}
                  className="px-8 py-4 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
                >
                  Financial Planning
                </button>
              </div>
              <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-400">
                <div className="flex items-center space-x-2">
                  <span className="text-green-400">✓</span>
                  <span>Monte Carlo Simulations</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-green-400">✓</span>
                  <span>DeFi + TradFi Analysis</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-green-400">✓</span>
                  <span>No Signup Required</span>
                </div>
              </div>
            </div>

            {/* Popular Tools Section */}
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">🧮 Professional Calculators</h2>
                <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                  Wall Street-grade tools that would cost $10K+/year from advisors. Free, no signup required.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Leverage Trading Calculator */}
                <button
                  onClick={() => setCurrentPage('leverage-trading')}
                  className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-6 hover:border-blue-500/50 transition-all text-left group"
                >
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg group-hover:scale-110 transition-transform">
                      <LineChart className="w-6 h-6" />
                    </div>
                    <h3 className="ml-3 text-xl font-bold text-white">Leverage Trading</h3>
                  </div>
                  <p className="text-slate-300 text-sm mb-4">
                    Calculate P&L, position sizing, risk analysis, and trading scenarios with multiple leverage levels.
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-400 text-sm font-semibold group-hover:text-blue-300">
                      Launch Calculator →
                    </span>
                    <span className="text-xs text-slate-500 bg-slate-800/50 px-2 py-1 rounded">Most Popular</span>
                  </div>
                </button>

                {/* Mortgage Calculator */}
                <button
                  onClick={() => setCurrentPage('mortgage')}
                  className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-xl p-6 hover:border-emerald-500/50 transition-all text-left group"
                >
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg group-hover:scale-110 transition-transform">
                      <Home className="w-6 h-6" />
                    </div>
                    <h3 className="ml-3 text-xl font-bold text-white">Mortgage Affordability</h3>
                  </div>
                  <p className="text-slate-300 text-sm mb-4">
                    Discover what you can afford, analyze DTI ratios, and understand PMI requirements for your home purchase.
                  </p>
                  <span className="text-emerald-400 text-sm font-semibold group-hover:text-emerald-300">
                    Launch Calculator →
                  </span>
                </button>

                {/* Stock Tax Calculator */}
                <button
                  onClick={() => setCurrentPage('stock-tax')}
                  className="bg-gradient-to-br from-orange-500/10 to-amber-500/10 border border-orange-500/20 rounded-xl p-6 hover:border-orange-500/50 transition-all text-left group"
                >
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg group-hover:scale-110 transition-transform">
                      <Percent className="w-6 h-6" />
                    </div>
                    <h3 className="ml-3 text-xl font-bold text-white">Stock Sale Tax</h3>
                  </div>
                  <p className="text-slate-300 text-sm mb-4">
                    Calculate capital gains taxes on stock sales with 2025 tax rates and optimize your selling strategy.
                  </p>
                  <span className="text-orange-400 text-sm font-semibold group-hover:text-orange-300">
                    Launch Calculator →
                  </span>
                </button>

                {/* Financial Planning */}
                <button
                  onClick={() => setCurrentPage('planning')}
                  className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-6 hover:border-cyan-500/50 transition-all text-left group"
                >
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg group-hover:scale-110 transition-transform">
                      <Target className="w-6 h-6" />
                    </div>
                    <h3 className="ml-3 text-xl font-bold text-white">Financial Planning + Monte Carlo</h3>
                  </div>
                  <p className="text-slate-300 text-sm mb-4">
                    Create comprehensive plans with income, expenses, and goals. Includes FREE Monte Carlo retirement simulator ($200/year value).
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-cyan-400 text-sm font-semibold group-hover:text-cyan-300">
                      Launch Calculator →
                    </span>
                    <span className="text-xs text-slate-500 bg-slate-800/50 px-2 py-1 rounded">Monte Carlo</span>
                  </div>
                </button>

                {/* Rental Property ROI */}
                <button
                  onClick={() => setCurrentPage('rental')}
                  className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border border-purple-500/20 rounded-xl p-6 hover:border-purple-500/50 transition-all text-left group"
                >
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg group-hover:scale-110 transition-transform">
                      <Building2 className="w-6 h-6" />
                    </div>
                    <h3 className="ml-3 text-xl font-bold text-white">Rental Property ROI</h3>
                  </div>
                  <p className="text-slate-300 text-sm mb-4">
                    Analyze rental property investments with cash flow, cap rate, and cash-on-cash return calculations.
                  </p>
                  <span className="text-purple-400 text-sm font-semibold group-hover:text-purple-300">
                    Launch Calculator →
                  </span>
                </button>

                {/* 1031 Exchange */}
                <button
                  onClick={() => setCurrentPage('1031-exchange')}
                  className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/20 rounded-xl p-6 hover:border-emerald-500/50 transition-all text-left group"
                >
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg group-hover:scale-110 transition-transform">
                      <ArrowLeftRight className="w-6 h-6" />
                    </div>
                    <h3 className="ml-3 text-xl font-bold text-white">1031 Exchange</h3>
                  </div>
                  <p className="text-slate-300 text-sm mb-4">
                    Defer capital gains taxes through like-kind property exchanges. Calculate tax savings and timeline requirements.
                  </p>
                  <span className="text-emerald-400 text-sm font-semibold group-hover:text-emerald-300">
                    Launch Calculator →
                  </span>
                </button>
              </div>
            </div>

            {/* How It Works Section */}
            <div className="mb-20 bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-2xl p-8 sm:p-12">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">⚡ How It Works</h2>
                <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                  Get professional-grade analysis in 3 simple steps
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full text-2xl font-bold mb-4">
                    1
                  </div>
                  <h3 className="text-xl font-bold mb-3">Choose Your Calculator</h3>
                  <p className="text-slate-400">
                    Select from 7 professional calculators designed for trading, real estate, and financial planning.
                  </p>
                </div>

                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full text-2xl font-bold mb-4">
                    2
                  </div>
                  <h3 className="text-xl font-bold mb-3">Input Your Numbers</h3>
                  <p className="text-slate-400">
                    Enter your specific values using intuitive sliders and inputs. All calculations happen instantly.
                  </p>
                </div>

                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500 to-orange-600 rounded-full text-2xl font-bold mb-4">
                    3
                  </div>
                  <h3 className="text-xl font-bold mb-3">Get Instant Analysis</h3>
                  <p className="text-slate-400">
                    Receive detailed breakdowns, risk assessments, and actionable insights to make better decisions.
                  </p>
                </div>
              </div>

              <div className="text-center mt-12">
                <button
                  onClick={() => setCurrentPage('leverage-trading')}
                  className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg inline-flex items-center space-x-2"
                >
                  <span>Try It Now</span>
                  <span>→</span>
                </button>
              </div>
            </div>

            {/* Why StreetFi Section */}
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">💎 Why StreetFi?</h2>
                <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                  Democratizing financial tools that advisors gatekeep behind $10K/year fees
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                  <div className="text-3xl mb-3">💰</div>
                  <h3 className="text-lg font-bold mb-2">FREE $10K Tools</h3>
                  <p className="text-sm text-slate-400">
                    Monte Carlo simulators that advisors charge $10,000/year for. We make them free for everyone.
                  </p>
                </div>

                <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                  <div className="text-3xl mb-3">🌉</div>
                  <h3 className="text-lg font-bold mb-2">TradFi + DeFi</h3>
                  <p className="text-sm text-slate-400">
                    Only platform bridging traditional finance and crypto. Learn accounting red flags AND leveraged yield farming.
                  </p>
                </div>

                <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                  <div className="text-3xl mb-3">📊</div>
                  <h3 className="text-lg font-bold mb-2">Comprehensive Depth</h3>
                  <p className="text-sm text-slate-400">
                    150+ terms, 11 modules, 8 calculators. From fundamental analysis to DeFi protocols—all in one place.
                  </p>
                </div>

                <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                  <div className="text-3xl mb-3">🔓</div>
                  <h3 className="text-lg font-bold mb-2">No Gatekeepers</h3>
                  <p className="text-sm text-slate-400">
                    Knowledge shouldn't be paywalled. Access Wall Street-level analysis without paying advisors or subscriptions.
                  </p>
                </div>
              </div>

              <div className="mt-8 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-6 text-center">
                <p className="text-slate-300 mb-3">
                  <span className="font-bold text-white">📈 What advisors charge for:</span> Monte Carlo retirement simulations ($200-500/year), 
                  Portfolio analysis ($10K/year), Tax optimization ($2-5K/year), DeFi education (priceless)
                </p>
                <p className="text-lg font-bold text-blue-400">
                  StreetFi: $0. Forever.
                </p>
              </div>
            </div>

            {/* Use Cases Section */}
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">👥 Perfect For</h2>
                <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                  From traditional investors to DeFi degens—we bridge both worlds
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-blue-500/5 to-purple-500/5 border border-blue-500/20 rounded-xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="text-3xl">📈</div>
                    <div>
                      <h3 className="text-lg font-bold mb-2">Crypto Traders</h3>
                      <p className="text-sm text-slate-400 mb-3">
                        Master leverage trading, analyze DeFi protocols, understand yield farming risks, and optimize perpetual positions.
                      </p>
                      <button
                        onClick={() => setCurrentPage('leverage-trading')}
                        className="text-sm text-blue-400 hover:text-blue-300 font-semibold"
                      >
                        Explore Trading Tools →
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-500/5 to-pink-500/5 border border-purple-500/20 rounded-xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="text-3xl">🌉</div>
                    <div>
                      <h3 className="text-lg font-bold mb-2">TradFi → DeFi Learners</h3>
                      <p className="text-sm text-slate-400 mb-3">
                        Bridge traditional finance knowledge to crypto. Learn accounting red flags AND protocol risk analysis.
                      </p>
                      <button
                        onClick={() => setCurrentPage('crypto-leverage')}
                        className="text-sm text-purple-400 hover:text-purple-300 font-semibold"
                      >
                        Start Learning →
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-cyan-500/5 to-blue-500/5 border border-cyan-500/20 rounded-xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="text-3xl">🎯</div>
                    <div>
                      <h3 className="text-lg font-bold mb-2">Retirement Planners</h3>
                      <p className="text-sm text-slate-400 mb-3">
                        Run Monte Carlo simulations to see your retirement odds. Get analysis that advisors charge $10K/year for.
                      </p>
                      <button
                        onClick={() => setCurrentPage('planning')}
                        className="text-sm text-cyan-400 hover:text-cyan-300 font-semibold"
                      >
                        Run Monte Carlo →
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-emerald-500/5 to-teal-500/5 border border-emerald-500/20 rounded-xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="text-3xl">🏠</div>
                    <div>
                      <h3 className="text-lg font-bold mb-2">Homebuyers</h3>
                      <p className="text-sm text-slate-400 mb-3">
                        Determine mortgage affordability, calculate monthly payments, understand DTI ratios, and plan your home purchase.
                      </p>
                      <button
                        onClick={() => setCurrentPage('mortgage')}
                        className="text-sm text-emerald-400 hover:text-emerald-300 font-semibold"
                      >
                        Check Affordability →
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-500/5 to-amber-500/5 border border-orange-500/20 rounded-xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="text-3xl">💼</div>
                    <div>
                      <h3 className="text-lg font-bold mb-2">Stock Investors</h3>
                      <p className="text-sm text-slate-400 mb-3">
                        Optimize tax strategy on stock sales, calculate capital gains, and understand the impact of holding periods.
                      </p>
                      <button
                        onClick={() => setCurrentPage('stock-tax')}
                        className="text-sm text-orange-400 hover:text-orange-300 font-semibold"
                      >
                        Calculate Taxes →
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-500/5 to-pink-500/5 border border-purple-500/20 rounded-xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="text-3xl">🚀</div>
                    <div>
                      <h3 className="text-lg font-bold mb-2">Startup Employees & Founders</h3>
                      <p className="text-sm text-slate-400 mb-3">
                        Multi-year projections with QSBS optimization to exclude up to $10M in capital gains on stock options.
                      </p>
                      <button
                        onClick={() => setCurrentPage('stock-options')}
                        className="text-sm text-purple-400 hover:text-purple-300 font-semibold"
                      >
                        Optimize QSBS →
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-indigo-500/5 to-violet-500/5 border border-indigo-500/20 rounded-xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="text-3xl">🏘️</div>
                    <div>
                      <h3 className="text-lg font-bold mb-2">Real Estate Investors</h3>
                      <p className="text-sm text-slate-400 mb-3">
                        Analyze rental property ROI, calculate cash flow, cap rates, and evaluate investment opportunities.
                      </p>
                      <button
                        onClick={() => setCurrentPage('rental')}
                        className="text-sm text-indigo-400 hover:text-indigo-300 font-semibold"
                      >
                        Analyze Properties →
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Compound Interest Calculator */}
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                  <span className="text-slate-100">The Power of </span>
                  <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                    Compound Interest
                  </span>
                </h2>
                <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                  Einstein called it the 8th wonder of the world. Watch your money grow exponentially over time.
                </p>
              </div>

              <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-2xl p-8">
                {/* Slider Control */}
                <div className="mb-8">
                  <div className="flex justify-between items-baseline mb-4">
                    <label className="text-sm font-semibold text-slate-400">Initial Investment</label>
                    <div className="text-3xl font-bold text-green-400">
                      ${compoundAmount.toLocaleString()}
                    </div>
                  </div>
                  <input
                    type="range"
                    min="10000"
                    max="100000"
                    step="1000"
                    value={compoundAmount}
                    onChange={(e) => setCompoundAmount(parseInt(e.target.value))}
                    className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #10b981 0%, #10b981 ${((compoundAmount - 10000) / 90000) * 100}%, #475569 ${((compoundAmount - 10000) / 90000) * 100}%, #475569 100%)`,
                      WebkitAppearance: 'none',
                      outline: 'none'
                    }}
                  />
                  <div className="flex justify-between mt-2">
                    <span className="text-xs text-slate-500">$10,000</span>
                    <span className="text-xs text-slate-500">$100,000</span>
                  </div>
                </div>

                {/* Compound Growth Visualization */}
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Chart */}
                  <div>
                    <div className="mb-4">
                      <p className="text-sm text-slate-400 mb-2">Growth Over 30 Years (7% Annual Return)</p>
                    </div>
                    <div className="h-64 relative">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={(() => {
                            const data = [];
                            for (let year = 0; year <= 30; year += 5) {
                              const value = compoundAmount * Math.pow(1.07, year);
                              data.push({
                                year: year,
                                value: Math.round(value),
                                label: year === 0 ? 'Today' : `Year ${year}`
                              });
                            }
                            return data;
                          })()}
                        >
                          <defs>
                            <linearGradient id="compoundGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis 
                            dataKey="label" 
                            stroke="#94a3b8"
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis 
                            stroke="#94a3b8"
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                          />
                          <Tooltip
                            contentStyle={{ 
                              backgroundColor: '#1e293b', 
                              border: '1px solid #334155',
                              borderRadius: '8px'
                            }}
                            labelStyle={{ color: '#cbd5e1' }}
                            formatter={(value) => [`$${value.toLocaleString()}`, 'Value']}
                          />
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke="#10b981"
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#compoundGradient)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Key Metrics */}
                  <div>
                    <div className="space-y-4">
                      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                        <p className="text-sm text-slate-400 mb-1">After 10 Years</p>
                        <p className="text-2xl font-bold text-blue-400">
                          ${Math.round(compoundAmount * Math.pow(1.07, 10)).toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {((Math.pow(1.07, 10) - 1) * 100).toFixed(0)}% total return
                        </p>
                      </div>
                      
                      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                        <p className="text-sm text-slate-400 mb-1">After 20 Years</p>
                        <p className="text-2xl font-bold text-purple-400">
                          ${Math.round(compoundAmount * Math.pow(1.07, 20)).toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {((Math.pow(1.07, 20) - 1) * 100).toFixed(0)}% total return
                        </p>
                      </div>
                      
                      <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg p-4 border border-green-500/30">
                        <p className="text-sm text-slate-400 mb-1">After 30 Years</p>
                        <p className="text-3xl font-bold text-green-400">
                          ${Math.round(compoundAmount * Math.pow(1.07, 30)).toLocaleString()}
                        </p>
                        <p className="text-sm text-green-400 mt-1">
                          <strong>{((Math.pow(1.07, 30) - 1) * 100).toFixed(0)}%</strong> total return
                        </p>
                        <p className="text-xs text-slate-400 mt-2">
                          That's {Math.round(Math.pow(1.07, 30))}x your initial investment!
                        </p>
                      </div>

                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mt-4">
                        <p className="text-xs text-yellow-400">
                          💡 <strong>Key Insight:</strong> Starting with just ${compoundAmount.toLocaleString()} and adding $100/month 
                          would give you ${Math.round(compoundAmount * Math.pow(1.07, 30) + (100 * 12 * ((Math.pow(1.07, 30) - 1) / 0.07))).toLocaleString()} in 30 years!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom Message */}
                <div className="mt-8 text-center">
                  <p className="text-sm text-slate-400">
                    The difference between starting today and waiting 5 years? 
                    <span className="text-red-400 font-semibold"> ${Math.round(compoundAmount * (Math.pow(1.07, 30) - Math.pow(1.07, 25))).toLocaleString()} less</span> at retirement.
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    *Assuming 7% annual return (conservative market estimate)
                  </p>
                </div>
              </div>
            </div>

            {/* Education CTA Section */}
            <div className="mb-20 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-8 sm:p-12">
              <div className="text-center">
                <div className="text-5xl mb-4">📚</div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">Learn Everything. For Free.</h2>
                <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-4">
                  Comprehensive education across traditional finance AND crypto. 150+ terms, 11 modules, real-world examples.
                </p>
                <p className="text-md text-slate-400 max-w-xl mx-auto mb-8">
                  From accounting red flags to DeFi protocol risk analysis—we cover what business schools and YouTube crypto bros both miss.
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <button
                    onClick={() => setCurrentPage('real-estate-leverage')}
                    className="bg-slate-800/50 hover:bg-slate-800 border border-slate-600 rounded-lg p-4 transition-all text-center"
                  >
                    <Home className="w-6 h-6 mx-auto mb-2 text-emerald-400" />
                    <div className="text-sm font-semibold">Real Estate</div>
                  </button>
                  <button
                    onClick={() => setCurrentPage('investment-leverage')}
                    className="bg-slate-800/50 hover:bg-slate-800 border border-slate-600 rounded-lg p-4 transition-all text-center"
                  >
                    <TrendingUp className="w-6 h-6 mx-auto mb-2 text-blue-400" />
                    <div className="text-sm font-semibold">Investing</div>
                  </button>
                  <button
                    onClick={() => setCurrentPage('crypto-leverage')}
                    className="bg-slate-800/50 hover:bg-slate-800 border border-slate-600 rounded-lg p-4 transition-all text-center"
                  >
                    <Bitcoin className="w-6 h-6 mx-auto mb-2 text-orange-400" />
                    <div className="text-sm font-semibold">Crypto & DeFi</div>
                  </button>
                  <button
                    onClick={() => setCurrentPage('business-leverage')}
                    className="bg-slate-800/50 hover:bg-slate-800 border border-slate-600 rounded-lg p-4 transition-all text-center"
                  >
                    <Briefcase className="w-6 h-6 mx-auto mb-2 text-purple-400" />
                    <div className="text-sm font-semibold">Business</div>
                  </button>
                </div>

                <button
                  onClick={() => setCurrentPage('modules')}
                  className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg inline-flex items-center space-x-2"
                >
                  <BookOpen className="w-5 h-5" />
                  <span>Explore All Modules</span>
                </button>
              </div>
            </div>

            {/* Final CTA */}
            <div className="text-center bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Get Started?</h2>
              <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
                Join thousands of traders, investors, and homebuyers using professional-grade calculators to make better financial decisions.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => setCurrentPage('quiz')}
                  className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg shadow-blue-500/25"
                >
                  Take Risk Quiz
                </button>
                <button
                  onClick={() => setCurrentPage('about')}
                  className="px-8 py-4 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
                >
                  Learn About Us
                </button>
              </div>
              <p className="text-sm text-slate-400 mt-6">
                No signup required • 100% free • Your data stays private
              </p>
            </div>
          </div>
        )}

        {/* LEVERAGE TRADING CALCULATOR - MERGED */}
        {currentPage === 'leverage-trading' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                  <BarChart3 className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold">Leverage Trading Calculator</h2>
                  <p className="text-slate-300 mt-2">Complete trading toolkit: P&L, position sizing, risk analysis & scenarios</p>
                </div>
              </div>
              <div className="mt-6 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-300">
                  💡 <strong>All-in-One Tool:</strong> Everything you need to plan, analyze, and execute leveraged trades. 
                  No more jumping between calculators—all your trading math in one place.
                </p>
              </div>
            </div>

            {/* How to Use Section */}
            <div className="mb-8 bg-gradient-to-r from-blue-500/5 to-purple-500/5 border border-blue-500/20 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center text-blue-400">
                <BookOpen className="w-5 h-5 mr-2" />
                How to Use This Calculator
              </h3>
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <LineChart className="w-4 h-4 mr-1 text-blue-400" />
                    Trade Setup
                  </h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• Enter capital & leverage</li>
                    <li>• Select Long/Short position</li>
                    <li>• Input entry & exit prices</li>
                    <li>• Add trading fees</li>
                    <li>• See P&L and liquidation</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <Target className="w-4 h-4 mr-1 text-purple-400" />
                    Position Sizing
                  </h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• Set total account size</li>
                    <li>• Choose risk % (1-2% rec.)</li>
                    <li>• Enter stop loss distance</li>
                    <li>• Get optimal position size</li>
                    <li>• Calculate required margin</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <Shield className="w-4 h-4 mr-1 text-orange-400" />
                    Risk Analysis
                  </h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• Monitor open positions</li>
                    <li>• Track real-time P&L</li>
                    <li>• See distance to liquidation</li>
                    <li>• Calculate break-even price</li>
                    <li>• Assess risk level</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <TrendingUp className="w-4 h-4 mr-1 text-green-400" />
                    Scenarios
                  </h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• Compare different setups</li>
                    <li>• Model price movements</li>
                    <li>• Test leverage levels</li>
                    <li>• Visualize outcomes</li>
                    <li>• Plan before trading</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 grid md:grid-cols-2 gap-3">
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                  <p className="text-xs text-blue-300">
                    💡 <strong>Quick Start:</strong> New to leverage? Click the "Quick Scenarios" buttons to see pre-loaded examples of conservative, moderate, and aggressive trades.
                  </p>
                </div>
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                  <p className="text-xs text-purple-300">
                    ⚠️ <strong>Risk Warning:</strong> Always use the Position Sizing tab to calculate proper risk per trade. Never risk more than 1-2% of your account on a single trade.
                  </p>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="mb-8 bg-slate-800/50 border border-slate-700 rounded-xl p-2 flex flex-wrap gap-2">
              <button
                onClick={() => setLeverageCalcTab('trade')}
                className={`flex-1 min-w-[140px] px-4 py-3 rounded-lg font-semibold transition-all ${
                  leverageCalcTab === 'trade'
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <LineChart className="w-4 h-4" />
                  <span>Trade Setup</span>
                </div>
              </button>
              <button
                onClick={() => setLeverageCalcTab('sizing')}
                className={`flex-1 min-w-[140px] px-4 py-3 rounded-lg font-semibold transition-all ${
                  leverageCalcTab === 'sizing'
                    ? 'bg-purple-500 text-white shadow-lg'
                    : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Target className="w-4 h-4" />
                  <span>Position Sizing</span>
                </div>
              </button>
              <button
                onClick={() => setLeverageCalcTab('risk')}
                className={`flex-1 min-w-[140px] px-4 py-3 rounded-lg font-semibold transition-all ${
                  leverageCalcTab === 'risk'
                    ? 'bg-orange-500 text-white shadow-lg'
                    : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Shield className="w-4 h-4" />
                  <span>Risk Analysis</span>
                </div>
              </button>
              <button
                onClick={() => setLeverageCalcTab('scenarios')}
                className={`flex-1 min-w-[140px] px-4 py-3 rounded-lg font-semibold transition-all ${
                  leverageCalcTab === 'scenarios'
                    ? 'bg-green-500 text-white shadow-lg'
                    : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <TrendingUp className="w-4 h-4" />
                  <span>Scenarios</span>
                </div>
              </button>
            </div>

            {/* TAB 1: TRADE SETUP */}
            {leverageCalcTab === 'trade' && (
              <div className="space-y-8">
                <div className="bg-gradient-to-r from-blue-500/5 to-cyan-500/5 border border-blue-500/20 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center text-blue-400">
                    <span className="mr-2">📊</span> Calculate Trade P&L & Liquidation
                  </h3>
                  <p className="text-slate-300 text-sm mb-4">
                    Enter your trade parameters to calculate profit/loss, liquidation price, and risk level.
                  </p>
                </div>

                {/* Calculator Inputs */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 space-y-4">
                    <h4 className="font-bold text-white text-lg mb-4">Trade Parameters</h4>
                    
                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-2">
                        Capital ($)
                      </label>
                      <input
                        type="number"
                        value={calcInputs.capital}
                        onChange={(e) => setCalcInputs({...calcInputs, capital: parseFloat(e.target.value) || 0})}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-2">
                        Leverage (x)
                      </label>
                      <input
                        type="number"
                        value={calcInputs.leverage}
                        onChange={(e) => setCalcInputs({...calcInputs, leverage: parseFloat(e.target.value) || 1})}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-2">
                        Entry Price ($)
                      </label>
                      <input
                        type="number"
                        value={calcInputs.entryPrice}
                        onChange={(e) => setCalcInputs({...calcInputs, entryPrice: parseFloat(e.target.value) || 0})}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-2">
                        Exit/Target Price ($)
                      </label>
                      <input
                        type="number"
                        value={calcInputs.exitPrice}
                        onChange={(e) => setCalcInputs({...calcInputs, exitPrice: parseFloat(e.target.value) || 0})}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-2">
                        Position Type
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setCalcInputs({...calcInputs, position: 'long'})}
                          className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                            calcInputs.position === 'long'
                              ? 'bg-green-500 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          Long (Buy)
                        </button>
                        <button
                          onClick={() => setCalcInputs({...calcInputs, position: 'short'})}
                          className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                            calcInputs.position === 'short'
                              ? 'bg-red-500 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          Short (Sell)
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Results */}
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 space-y-4">
                    <h4 className="font-bold text-white text-lg mb-4">Results</h4>
                    
                    {leverageCalcTab === 'trade' && (() => {
                      const results = calculateResults();
                      return (
                        <>
                          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                            <div className="text-sm text-slate-400 mb-1">Position Size</div>
                            <div className="text-2xl font-bold text-white">
                              ${results.positionSize.toLocaleString()}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {calcInputs.capital.toLocaleString()} × {calcInputs.leverage}x
                            </div>
                          </div>

                          <div className={`border rounded-lg p-4 ${
                            results.netProfitLoss >= 0 
                              ? 'bg-green-500/10 border-green-500/30' 
                              : 'bg-red-500/10 border-red-500/30'
                          }`}>
                            <div className="text-sm text-slate-400 mb-1">Profit/Loss</div>
                            <div className={`text-2xl font-bold ${
                              results.netProfitLoss >= 0 ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {results.netProfitLoss >= 0 ? '+' : ''}${results.netProfitLoss.toFixed(2)}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              ROI: {results.roi >= 0 ? '+' : ''}{results.roi.toFixed(2)}%
                            </div>
                          </div>

                          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                            <div className="text-sm text-slate-400 mb-1">Liquidation Price</div>
                            <div className="text-2xl font-bold text-red-400">
                              ${results.liquidationPrice.toFixed(2)}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {Math.abs(((results.liquidationPrice - calcInputs.entryPrice) / calcInputs.entryPrice) * 100).toFixed(2)}% from entry
                            </div>
                          </div>

                          <div className={`border rounded-lg p-4 ${
                            results.riskLevel === 'low' ? 'bg-green-500/10 border-green-500/30' :
                            results.riskLevel === 'medium' ? 'bg-yellow-500/10 border-yellow-500/30' :
                            'bg-red-500/10 border-red-500/30'
                          }`}>
                            <div className="text-sm text-slate-400 mb-1">Risk Level</div>
                            <div className={`text-lg font-bold ${
                              results.riskLevel === 'low' ? 'text-green-400' :
                              results.riskLevel === 'medium' ? 'text-yellow-400' :
                              'text-red-400'
                            }`}>
                              {results.riskLevel.toUpperCase()}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {results.riskLevel === 'low' && '≤5x leverage - Safer range'}
                              {results.riskLevel === 'medium' && '6-10x leverage - Moderate risk'}
                              {results.riskLevel === 'high' && '>10x leverage - High risk'}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2: POSITION SIZING */}
            {leverageCalcTab === 'sizing' && (
              <div className="space-y-8">
                <div className="bg-gradient-to-r from-purple-500/5 to-pink-500/5 border border-purple-500/20 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center text-purple-400">
                    <span className="mr-2">🎯</span> Calculate Optimal Position Size
                  </h3>
                  <p className="text-slate-300 text-sm mb-4">
                    Determine the right position size based on your risk tolerance and account size.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Kelly Criterion Method */}
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                    <h4 className="font-bold text-purple-400 text-lg mb-4">Kelly Criterion Method</h4>
                    <p className="text-sm text-slate-300 mb-4">
                      Mathematically optimal position sizing based on win rate and win/loss ratio.
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-2">
                          Win Rate (%)
                        </label>
                        <input
                          type="number"
                          defaultValue="55"
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-2">
                          Win/Loss Ratio
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          defaultValue="1.5"
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                        />
                      </div>

                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mt-4">
                        <div className="text-sm text-slate-400 mb-1">Recommended Position Size</div>
                        <div className="text-2xl font-bold text-purple-400">15.0%</div>
                        <div className="text-xs text-slate-500 mt-2">
                          Of total capital per trade
                        </div>
                      </div>

                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mt-4">
                        <p className="text-xs text-yellow-300">
                          ⚠️ <strong>Note:</strong> Full Kelly can be aggressive. Many traders use "Half Kelly" (7.5% in this case) for safety.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Fixed % Risk Method */}
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                    <h4 className="font-bold text-blue-400 text-lg mb-4">Fixed % Risk Method</h4>
                    <p className="text-sm text-slate-300 mb-4">
                      Risk a fixed percentage of your account on each trade.
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-2">
                          Account Size ($)
                        </label>
                        <input
                          type="number"
                          defaultValue="10000"
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-2">
                          Risk Per Trade (%)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          defaultValue="2"
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-2">
                          Stop Loss Distance (%)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          defaultValue="5"
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                        />
                      </div>

                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mt-4">
                        <div className="text-sm text-slate-400 mb-1">Position Size</div>
                        <div className="text-2xl font-bold text-blue-400">$4,000</div>
                        <div className="text-xs text-slate-500 mt-2">
                          Risk: $200 (2% of $10,000)
                        </div>
                      </div>

                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mt-4">
                        <p className="text-xs text-green-300">
                          ✅ <strong>Conservative:</strong> 1-2% risk per trade is recommended for beginners.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Reference */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="font-bold text-white text-lg mb-4">Position Sizing Guidelines</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <div className="font-semibold text-green-400 mb-2">Conservative</div>
                      <ul className="text-sm text-slate-300 space-y-1">
                        <li>• 1-2% risk per trade</li>
                        <li>• 2-3x max leverage</li>
                        <li>• Beginners start here</li>
                      </ul>
                    </div>
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                      <div className="font-semibold text-yellow-400 mb-2">Moderate</div>
                      <ul className="text-sm text-slate-300 space-y-1">
                        <li>• 2-3% risk per trade</li>
                        <li>• 3-5x max leverage</li>
                        <li>• Experienced traders</li>
                      </ul>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <div className="font-semibold text-red-400 mb-2">Aggressive</div>
                      <ul className="text-sm text-slate-300 space-y-1">
                        <li>• 3-5% risk per trade</li>
                        <li>• 5-10x max leverage</li>
                        <li>• High risk tolerance</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 3: RISK ANALYSIS */}
            {leverageCalcTab === 'risk' && (
              <div className="space-y-8">
                <div className="bg-gradient-to-r from-orange-500/5 to-red-500/5 border border-orange-500/20 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center text-orange-400">
                    <span className="mr-2">🛡️</span> Analyze Portfolio Risk
                  </h3>
                  <p className="text-slate-300 text-sm mb-4">
                    Calculate risk-to-reward ratios and analyze your total portfolio exposure.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* R:R Ratio Calculator */}
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                    <h4 className="font-bold text-orange-400 text-lg mb-4">Risk:Reward Ratio</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-2">
                          Entry Price ($)
                        </label>
                        <input
                          type="number"
                          defaultValue="100"
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-2">
                          Stop Loss Price ($)
                        </label>
                        <input
                          type="number"
                          defaultValue="95"
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-2">
                          Take Profit Price ($)
                        </label>
                        <input
                          type="number"
                          defaultValue="115"
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                        />
                      </div>

                      <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mt-4">
                        <div className="text-sm text-slate-400 mb-1">Risk:Reward Ratio</div>
                        <div className="text-3xl font-bold text-orange-400">1:3</div>
                        <div className="text-xs text-slate-500 mt-2">
                          Risk $5 to make $15
                        </div>
                      </div>

                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                        <p className="text-xs text-green-300">
                          ✅ <strong>Good Trade:</strong> Minimum 1:2 R:R is recommended. This trade has 1:3.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Portfolio Risk Analysis */}
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                    <h4 className="font-bold text-red-400 text-lg mb-4">Portfolio Risk Analysis</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-2">
                          Total Portfolio Value ($)
                        </label>
                        <input
                          type="number"
                          defaultValue="50000"
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-red-500 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-2">
                          Number of Open Positions
                        </label>
                        <input
                          type="number"
                          defaultValue="3"
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-red-500 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-2">
                          Total Exposure ($)
                        </label>
                        <input
                          type="number"
                          defaultValue="75000"
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-red-500 focus:outline-none"
                        />
                      </div>

                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mt-4">
                        <div className="text-sm text-slate-400 mb-1">Effective Leverage</div>
                        <div className="text-3xl font-bold text-red-400">1.5x</div>
                        <div className="text-xs text-slate-500 mt-2">
                          $75K exposure / $50K portfolio
                        </div>
                      </div>

                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                        <p className="text-xs text-yellow-300">
                          ⚠️ <strong>Moderate Risk:</strong> Keep total portfolio leverage under 3x for safety.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Risk Levels Reference */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="font-bold text-white text-lg mb-4">Portfolio Risk Levels</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div>
                        <div className="font-semibold text-green-400">Low Risk</div>
                        <div className="text-xs text-slate-400">Total portfolio leverage &lt; 2x</div>
                      </div>
                      <div className="text-sm text-slate-300">✅ Safe</div>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <div>
                        <div className="font-semibold text-yellow-400">Medium Risk</div>
                        <div className="text-xs text-slate-400">Total portfolio leverage 2-5x</div>
                      </div>
                      <div className="text-sm text-slate-300">⚠️ Caution</div>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <div>
                        <div className="font-semibold text-red-400">High Risk</div>
                        <div className="text-xs text-slate-400">Total portfolio leverage &gt; 5x</div>
                      </div>
                      <div className="text-sm text-slate-300">🚨 Danger</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 4: SCENARIOS */}
            {leverageCalcTab === 'scenarios' && (
              <div className="space-y-8">
                <div className="bg-gradient-to-r from-green-500/5 to-emerald-500/5 border border-green-500/20 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center text-green-400">
                    <span className="mr-2">🎲</span> What-If Scenarios
                  </h3>
                  <p className="text-slate-300 text-sm mb-4">
                    Model different price movements and see how they impact your P&L.
                  </p>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="font-bold text-white text-lg mb-6">Price Movement Scenarios</h4>
                  
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-2">
                        Position Size ($)
                      </label>
                      <input
                        type="number"
                        defaultValue="10000"
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-2">
                        Leverage (x)
                      </label>
                      <input
                        type="number"
                        defaultValue="5"
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* Scenario Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left p-3 text-slate-400 font-semibold">Price Move</th>
                          <th className="text-right p-3 text-slate-400 font-semibold">Position P&L</th>
                          <th className="text-right p-3 text-slate-400 font-semibold">Your P&L</th>
                          <th className="text-right p-3 text-slate-400 font-semibold">ROI</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { move: '+20%', positionPL: '+$10,000', yourPL: '+$10,000', roi: '+100%', color: 'text-green-400' },
                          { move: '+10%', positionPL: '+$5,000', yourPL: '+$5,000', roi: '+50%', color: 'text-green-400' },
                          { move: '+5%', positionPL: '+$2,500', yourPL: '+$2,500', roi: '+25%', color: 'text-green-400' },
                          { move: '0%', positionPL: '$0', yourPL: '$0', roi: '0%', color: 'text-slate-400' },
                          { move: '-5%', positionPL: '-$2,500', yourPL: '-$2,500', roi: '-25%', color: 'text-red-400' },
                          { move: '-10%', positionPL: '-$5,000', yourPL: '-$5,000', roi: '-50%', color: 'text-red-400' },
                          { move: '-20%', positionPL: '-$10,000', yourPL: '-$10,000', roi: '-100%', color: 'text-red-500' },
                        ].map((scenario, i) => (
                          <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                            <td className="p-3 text-white font-semibold">{scenario.move}</td>
                            <td className={`p-3 text-right ${scenario.color}`}>{scenario.positionPL}</td>
                            <td className={`p-3 text-right font-semibold ${scenario.color}`}>{scenario.yourPL}</td>
                            <td className={`p-3 text-right font-semibold ${scenario.color}`}>{scenario.roi}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-6 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <p className="text-sm text-red-300">
                      ⚠️ <strong>Liquidation Warning:</strong> At 5x leverage, a -20% price move results in total loss. 
                      Your actual liquidation may occur slightly before this due to maintenance margin requirements.
                    </p>
                  </div>
                </div>

                {/* Quick Scenario Buttons */}
                <div className="grid md:grid-cols-3 gap-4">
                  <button className="bg-gradient-to-br from-green-500/10 to-green-600/10 border border-green-500/30 rounded-xl p-6 hover:border-green-500/50 transition-all text-left">
                    <div className="text-lg font-bold text-green-400 mb-2">Bull Market</div>
                    <div className="text-sm text-slate-300 mb-3">Strong uptrend scenario</div>
                    <div className="text-xs text-slate-400">+15% move, 3x leverage</div>
                  </button>
                  
                  <button className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 border border-yellow-500/30 rounded-xl p-6 hover:border-yellow-500/50 transition-all text-left">
                    <div className="text-lg font-bold text-yellow-400 mb-2">Sideways</div>
                    <div className="text-sm text-slate-300 mb-3">Range-bound market</div>
                    <div className="text-xs text-slate-400">±5% chop, funding costs</div>
                  </button>
                  
                  <button className="bg-gradient-to-br from-red-500/10 to-red-600/10 border border-red-500/30 rounded-xl p-6 hover:border-red-500/50 transition-all text-left">
                    <div className="text-lg font-bold text-red-400 mb-2">Bear Market</div>
                    <div className="text-sm text-slate-300 mb-3">Strong downtrend</div>
                    <div className="text-xs text-slate-400">-15% move, 3x leverage</div>
                  </button>
                </div>
              </div>
            )}

            {/* Bottom CTA */}
            <div className="mt-12 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Master Leverage Trading</h3>
              <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Use all 4 tools together: plan your trade, size your position correctly, analyze your risk, and model scenarios before executing.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => setCurrentPage('glossary')}
                  className="px-6 py-3 bg-indigo-500 text-white font-semibold rounded-lg hover:bg-indigo-600 transition-all"
                >
                  Learn Key Terms →
                </button>
                <button
                  onClick={() => setCurrentPage('leverage-trading')}
                  className="px-6 py-3 bg-slate-700 border border-slate-600 text-white font-semibold rounded-lg hover:bg-slate-600 transition-all"
                >
                  Leverage Calculator →
                </button>
              </div>
            </div>
          </section>
        )}

        {/* CALCULATOR PAGE */}
        {/* TRADE CALCULATOR PAGE */}
        {currentPage === 'trade-calculator' && (
          <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg">
                  <LineChart className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold">Trade Calculator</h2>
                  <p className="text-slate-300 mt-2">Model your trades: calculate P&L, liquidation price, and risk level</p>
                </div>
              </div>
              <div className="mt-6 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-300">
                  💡 <strong>Pro Tip:</strong> Always calculate your liquidation price and max loss before entering a leveraged trade. 
                  Need help sizing your position? Try the <button onClick={() => setCurrentPage('position-sizing')} className="text-blue-400 hover:text-blue-300 underline font-semibold">Position Sizing Calculator</button>
                </p>
              </div>
            </div>

            {/* How to Use Section */}
            <div className="mb-8 bg-gradient-to-r from-cyan-500/5 to-blue-500/5 border border-cyan-500/20 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center text-cyan-400">
                <span className="mr-2">📖</span> How to Use This Calculator
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-white mb-2">Step 1: Enter Your Trade Details</h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• <strong>Capital:</strong> Amount you're investing ($)</li>
                    <li>• <strong>Leverage:</strong> Multiplier (2x, 10x, 50x, etc.)</li>
                    <li>• <strong>Entry Price:</strong> Price you buy/sell at</li>
                    <li>• <strong>Position:</strong> Long (betting up) or Short (betting down)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">Step 2: Review Results</h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• <strong>Position Size:</strong> Total exposure (capital × leverage)</li>
                    <li>• <strong>Liquidation Price:</strong> Price where you lose everything</li>
                    <li>• <strong>P&L at Different Prices:</strong> Profit/loss scenarios</li>
                    <li>• <strong>Risk Level:</strong> Visual indicator of danger</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-xs text-blue-300">
                  💡 <strong>Quick Tip:</strong> Use the "Quick Scenarios" buttons below to see pre-configured examples, then adjust the values to match your actual trade.
                </p>
              </div>
            </div>

            {/* Quick Scenario Buttons */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Quick Scenarios</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <button
                  onClick={() => loadScenario('conservative')}
                  className="px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 hover:bg-green-500/20 transition-all text-sm"
                >
                  🛡️ Conservative
                  <div className="text-xs text-slate-400 mt-1">2x leverage</div>
                </button>
                <button
                  onClick={() => loadScenario('moderate')}
                  className="px-4 py-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-400 hover:bg-yellow-500/20 transition-all text-sm"
                >
                  ⚖️ Moderate
                  <div className="text-xs text-slate-400 mt-1">10x leverage</div>
                </button>
                <button
                  onClick={() => loadScenario('aggressive')}
                  className="px-4 py-3 bg-orange-500/10 border border-orange-500/30 rounded-lg text-orange-400 hover:bg-orange-500/20 transition-all text-sm"
                >
                  🚀 Aggressive
                  <div className="text-xs text-slate-400 mt-1">50x leverage</div>
                </button>
                <button
                  onClick={() => loadScenario('loss')}
                  className="px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/20 transition-all text-sm"
                >
                  💀 Loss Scenario
                  <div className="text-xs text-slate-400 mt-1">-5% move</div>
                </button>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Input Side */}
              <div className="space-y-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-semibold mb-6">Position Details</h3>
                  
                  {/* Position Type */}
                  <div className="mb-6">
                    <label className="block text-sm text-slate-400 mb-3">Position Type</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => updateCalcInput('position', 'long')}
                        className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                          calcInputs.position === 'long'
                            ? 'bg-green-500 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        📈 Long (Buy)
                      </button>
                      <button
                        onClick={() => updateCalcInput('position', 'short')}
                        className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                          calcInputs.position === 'short'
                            ? 'bg-red-500 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        📉 Short (Sell)
                      </button>
                    </div>
                  </div>

                  {/* Capital Input */}
                  <div className="mb-6">
                    <label className="block text-sm text-slate-400 mb-2">
                      Your Capital: ${calcInputs.capital.toLocaleString()}
                    </label>
                    <input
                      type="range"
                      min="100"
                      max="100000"
                      step="100"
                      value={calcInputs.capital}
                      onChange={(e) => updateCalcInput('capital', e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>$100</span>
                      <span>$100,000</span>
                    </div>
                  </div>

                  {/* Leverage Input */}
                  <div className="mb-6">
                    <label className="block text-sm text-slate-400 mb-2">
                      Leverage: {calcInputs.leverage}x
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="100"
                      step="1"
                      value={calcInputs.leverage}
                      onChange={(e) => updateCalcInput('leverage', e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>1x (No leverage)</span>
                      <span>100x</span>
                    </div>
                  </div>

                  {/* Entry Price */}
                  <div className="mb-6">
                    <label className="block text-sm text-slate-400 mb-2">Entry Price</label>
                    <input
                      type="number"
                      value={calcInputs.entryPrice}
                      onChange={(e) => updateCalcInput('entryPrice', e.target.value)}
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none"
                      placeholder="100"
                    />
                  </div>

                  {/* Exit Price */}
                  <div className="mb-6">
                    <label className="block text-sm text-slate-400 mb-2">Exit Price (Target)</label>
                    <input
                      type="number"
                      value={calcInputs.exitPrice}
                      onChange={(e) => updateCalcInput('exitPrice', e.target.value)}
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none"
                      placeholder="110"
                    />
                  </div>

                  {/* Trading Fee */}
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">
                      Trading Fee: {calcInputs.feePercent}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={calcInputs.feePercent}
                      onChange={(e) => updateCalcInput('feePercent', e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>0%</span>
                      <span>1%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Results Side */}
              <div className="space-y-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-semibold mb-6">Trade Results</h3>
                  
                  {/* Position Size */}
                  <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-400">Position Size</span>
                      <span className="text-2xl font-bold text-white">
                        ${parseFloat(results.positionSize).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 text-right">
                      {calcInputs.capital.toLocaleString()} × {calcInputs.leverage}x leverage
                    </div>
                  </div>

                  {/* Price Change */}
                  <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Price Movement</span>
                      <span className={`text-xl font-bold ${
                        parseFloat(results.priceChangePercent) >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {parseFloat(results.priceChangePercent) >= 0 ? '+' : ''}{results.priceChangePercent}%
                      </span>
                    </div>
                  </div>

                  {/* Profit/Loss - Featured */}
                  <div className={`bg-gradient-to-br rounded-lg p-6 mb-4 ${
                    parseFloat(results.profitLoss) >= 0 
                      ? 'from-green-500/20 to-emerald-500/20 border border-green-500/30' 
                      : 'from-red-500/20 to-red-600/20 border border-red-500/30'
                  }`}>
                    <div className="text-sm text-slate-300 mb-2">Net Profit/Loss</div>
                    <div className={`text-4xl font-bold mb-2 ${
                      parseFloat(results.profitLoss) >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {parseFloat(results.profitLoss) >= 0 ? '+' : ''}${results.profitLoss}
                    </div>
                    <div className="text-xs text-slate-400">
                      After ${results.fees} in trading fees
                    </div>
                  </div>

                  {/* ROI */}
                  <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Return on Investment</span>
                      <span className={`text-2xl font-bold ${
                        parseFloat(results.roi) >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {parseFloat(results.roi) >= 0 ? '+' : ''}{results.roi}%
                      </span>
                    </div>
                  </div>

                  {/* Liquidation Price */}
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-400">Liquidation Price</span>
                      <span className="text-xl font-bold text-red-400">
                        ${results.liquidationPrice}
                      </span>
                    </div>
                    <div className="text-xs text-red-400">
                      ⚠️ Position liquidates if price hits this level
                    </div>
                  </div>

                  {/* Risk Level */}
                  <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-slate-400">Risk Level</span>
                      <span className={`text-xl font-bold uppercase ${results.riskColor}`}>
                        {results.riskLevel}
                      </span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all ${
                          results.riskLevel === 'low' ? 'bg-green-400 w-1/4' :
                          results.riskLevel === 'medium' ? 'bg-yellow-400 w-2/4' :
                          results.riskLevel === 'high' ? 'bg-orange-400 w-3/4' :
                          'bg-red-400 w-full'
                        }`}
                      />
                    </div>
                    <div className="text-xs text-slate-500 mt-2">
                      {results.riskLevel === 'low' && '✓ Safe leverage for most traders'}
                      {results.riskLevel === 'medium' && '⚠️ Moderate risk - use caution'}
                      {results.riskLevel === 'high' && '⚠️ High risk - experienced traders only'}
                      {results.riskLevel === 'extreme' && '🚨 Extreme risk - very dangerous!'}
                    </div>
                  </div>
                  
                  {/* Save Calculation Button */}
                  <button
                    onClick={() => handleSaveCalculation('leverage-trading', calcInputs, results)}
                    className="w-full mt-4 px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 rounded-lg font-semibold transition-all shadow-lg shadow-emerald-500/25 flex items-center justify-center space-x-2"
                  >
                    <ClipboardCheck className="w-4 h-4" />
                    <span>{isLoggedIn ? 'Save This Calculation' : 'Login to Save'}</span>
                  </button>
                </div>

                {/* Warning Box */}
                {calcInputs.leverage > 10 && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-red-300">
                        <strong>High Leverage Warning:</strong> At {calcInputs.leverage}x leverage, 
                        a small price movement can liquidate your position. Most traders lose money 
                        with leverage this high.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Educational Tips Section */}
            <div className="mt-12">
              <h3 className="text-2xl font-bold mb-6">Understanding Your Trade Results</h3>
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-xl p-6">
                  <h4 className="font-semibold text-blue-400 mb-3 flex items-center">
                    <LineChart className="w-5 h-5 mr-2" />
                    Leverage Multiplier
                  </h4>
                  <p className="text-sm text-slate-300">
                    Leverage multiplies both gains AND losses. A 10% price move with 10x leverage = 100% gain or loss 
                    on your capital. Always know your liquidation price before entering.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-xl p-6">
                  <h4 className="font-semibold text-red-400 mb-3 flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    Liquidation Risk
                  </h4>
                  <p className="text-sm text-slate-300">
                    When price hits your liquidation price, you lose 100% of your capital instantly. Set stop losses 
                    well before liquidation to protect yourself from extreme market moves.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-6">
                  <h4 className="font-semibold text-green-400 mb-3 flex items-center">
                    <Target className="w-5 h-5 mr-2" />
                    Risk Management
                  </h4>
                  <p className="text-sm text-slate-300">
                    Professional traders never risk more than 1-2% of their account per trade. Calculate proper position 
                    size using the <button onClick={() => setCurrentPage('position-sizing')} className="text-green-400 hover:text-green-300 underline font-semibold">Position Sizing Calculator</button>.
                  </p>
                </div>
              </div>

              {/* Cost Analysis CTA */}
              <div className="bg-gradient-to-r from-orange-500/10 to-yellow-500/10 border border-orange-500/30 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <DollarSign className="w-8 h-8 text-orange-400 flex-shrink-0" />
                  <div>
                    <h4 className="text-xl font-semibold text-orange-400 mb-2">Ready to Calculate Your Trade?</h4>
                    <p className="text-slate-300 mb-4">
                      Use the Leverage Trading Calculator to model your full trade including P&L, liquidation prices, and risk scenarios.
                    </p>
                    <button
                      onClick={() => setCurrentPage('leverage-trading')}
                      className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-all"
                    >
                      Leverage Trading Calculator →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* POSITION SIZING CALCULATOR PAGE */}
        {currentPage === 'position-sizing' && (
          <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Hero */}
            <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                  <Target className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold">Position Sizing Calculator</h2>
                  <p className="text-slate-300 mt-2">Calculate the perfect position size based on your risk tolerance</p>
                </div>
              </div>
              <div className="mt-6 bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <p className="text-sm text-green-300">
                  💡 <strong>Golden Rule:</strong> Never risk more than 1-2% of your account on a single trade. 
                  This calculator helps you determine exactly how much to invest based on your stop loss.
                </p>
              </div>
            </div>

            {/* How to Use Section */}
            <div className="mb-8 bg-gradient-to-r from-emerald-500/5 to-green-500/5 border border-emerald-500/20 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center text-emerald-400">
                <span className="mr-2">📖</span> How to Use This Calculator
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-white mb-2">Step 1: Set Your Risk Parameters</h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• <strong>Account Size:</strong> Your total trading capital</li>
                    <li>• <strong>Risk Per Trade:</strong> % of account you're willing to lose (1-2% recommended)</li>
                    <li>• <strong>Entry Price:</strong> Price you plan to enter the trade</li>
                    <li>• <strong>Stop Loss:</strong> Price where you'll exit if trade goes against you</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">Step 2: Get Your Position Size</h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• <strong>Dollar Risk:</strong> Maximum $ amount you'll lose</li>
                    <li>• <strong>Position Size:</strong> Exact amount to invest</li>
                    <li>• <strong>Number of Units:</strong> Quantity to buy/sell</li>
                    <li>• <strong>Max Leverage:</strong> Highest leverage for your risk level</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <p className="text-xs text-green-300">
                  💡 <strong>Professional Tip:</strong> Start with 1% risk per trade. Only increase to 2% once you're consistently profitable. Never go above 2% per trade.
                </p>
              </div>
            </div>

            {/* Main Calculator */}
            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              {/* Inputs */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-6">Your Parameters</h3>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">
                      Account Size: ${positionSizingInputs.accountSize.toLocaleString()}
                    </label>
                    <input
                      type="range"
                      min="1000"
                      max="100000"
                      step="1000"
                      value={positionSizingInputs.accountSize}
                      onChange={(e) => setPositionSizingInputs({...positionSizingInputs, accountSize: parseInt(e.target.value)})}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>$1K</span>
                      <span>$100K</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-slate-400 mb-2">
                      Risk Per Trade: {positionSizingInputs.riskPercent}%
                    </label>
                    <input
                      type="range"
                      min="0.5"
                      max="10"
                      step="0.5"
                      value={positionSizingInputs.riskPercent}
                      onChange={(e) => setPositionSizingInputs({...positionSizingInputs, riskPercent: parseFloat(e.target.value)})}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>0.5% (Conservative)</span>
                      <span className="text-red-400">10% (Aggressive)</span>
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      💡 Pro traders typically risk 1-2% per trade
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-slate-400 mb-2">
                      Stop Loss Distance: {positionSizingInputs.stopLossPercent}%
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="20"
                      step="0.5"
                      value={positionSizingInputs.stopLossPercent}
                      onChange={(e) => setPositionSizingInputs({...positionSizingInputs, stopLossPercent: parseFloat(e.target.value)})}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>1% (Tight)</span>
                      <span>20% (Wide)</span>
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      Distance from entry to your stop loss
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-slate-400 mb-2">
                      Leverage: {positionSizingInputs.leverage}x
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="20"
                      step="1"
                      value={positionSizingInputs.leverage}
                      onChange={(e) => setPositionSizingInputs({...positionSizingInputs, leverage: parseInt(e.target.value)})}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>1x (No leverage)</span>
                      <span>20x</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-6">Recommended Position</h3>
                
                {(() => {
                  const psResults = calculatePositionSizing();
                  return (
                    <div className="space-y-6">
                      <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg p-6">
                        <div className="text-sm text-green-400 mb-2">Max Position Size</div>
                        <div className="text-4xl font-bold text-white mb-2">
                          ${parseFloat(psResults.maxPositionSize).toLocaleString()}
                        </div>
                        <div className="text-sm text-slate-400">
                          ({psResults.contracts} contracts @ $100 each)
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="bg-slate-900/50 rounded-lg p-4">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-slate-400">Capital Required</span>
                            <span className="text-white font-semibold text-xl">${psResults.capitalRequired}</span>
                          </div>
                          <div className="text-xs text-slate-500">
                            Amount of your account used for this trade
                          </div>
                        </div>

                        <div className="bg-slate-900/50 rounded-lg p-4">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-slate-400">Dollar Risk</span>
                            <span className="text-red-400 font-semibold text-xl">${psResults.dollarRisk}</span>
                          </div>
                          <div className="text-xs text-slate-500">
                            Maximum loss if stop loss hits
                          </div>
                        </div>

                        <div className="bg-slate-900/50 rounded-lg p-4">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-slate-400">Liquidation Buffer</span>
                            <span className="text-yellow-400 font-semibold text-xl">{psResults.liquidationBuffer}%</span>
                          </div>
                          <div className="text-xs text-slate-500">
                            Safety margin before liquidation
                          </div>
                        </div>
                      </div>

                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                        <div className="text-sm font-semibold text-blue-400 mb-2">💡 What This Means:</div>
                        <p className="text-sm text-slate-300">
                          You can open a <strong>${psResults.maxPositionSize}</strong> position using <strong>${psResults.capitalRequired}</strong> of your capital. 
                          If your stop loss hits, you'll lose exactly <strong>${psResults.dollarRisk}</strong>, which is {positionSizingInputs.riskPercent}% 
                          of your ${positionSizingInputs.accountSize.toLocaleString()} account.
                        </p>
                      </div>

                      {positionSizingInputs.riskPercent > 3 && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                          <div className="flex items-start space-x-2">
                            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-semibold text-red-400 mb-1">⚠️ High Risk Warning</p>
                              <p className="text-sm text-red-300">
                                Risking more than 2-3% per trade is aggressive. A few losing trades in a row 
                                can seriously damage your account. Consider reducing your risk percentage.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Examples Section */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6">Position Sizing Examples</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-green-500/20 rounded-lg">
                      <Shield className="w-5 h-5 text-green-400" />
                    </div>
                    <h4 className="font-semibold text-green-400">Conservative</h4>
                  </div>
                  <div className="space-y-2 text-sm text-slate-300">
                    <div className="flex justify-between">
                      <span>Account:</span>
                      <span className="font-semibold">$10,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Risk:</span>
                      <span className="font-semibold">1%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Stop Loss:</span>
                      <span className="font-semibold">5%</span>
                    </div>
                    <div className="flex justify-between border-t border-slate-700 pt-2 mt-2">
                      <span>Position Size:</span>
                      <span className="font-semibold text-white">$2,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Max Loss:</span>
                      <span className="font-semibold text-red-400">$100</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <Target className="w-5 h-5 text-blue-400" />
                    </div>
                    <h4 className="font-semibold text-blue-400">Moderate</h4>
                  </div>
                  <div className="space-y-2 text-sm text-slate-300">
                    <div className="flex justify-between">
                      <span>Account:</span>
                      <span className="font-semibold">$10,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Risk:</span>
                      <span className="font-semibold">2%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Stop Loss:</span>
                      <span className="font-semibold">5%</span>
                    </div>
                    <div className="flex justify-between border-t border-slate-700 pt-2 mt-2">
                      <span>Position Size:</span>
                      <span className="font-semibold text-white">$4,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Max Loss:</span>
                      <span className="font-semibold text-red-400">$200</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-orange-500/20 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-orange-400" />
                    </div>
                    <h4 className="font-semibold text-orange-400">Aggressive</h4>
                  </div>
                  <div className="space-y-2 text-sm text-slate-300">
                    <div className="flex justify-between">
                      <span>Account:</span>
                      <span className="font-semibold">$10,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Risk:</span>
                      <span className="font-semibold">5%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Stop Loss:</span>
                      <span className="font-semibold">5%</span>
                    </div>
                    <div className="flex justify-between border-t border-slate-700 pt-2 mt-2">
                      <span>Position Size:</span>
                      <span className="font-semibold text-white">$10,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Max Loss:</span>
                      <span className="font-semibold text-red-400">$500</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Educational Tips */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/20 rounded-xl p-6">
                <h4 className="font-semibold text-blue-400 mb-3 flex items-center">
                  <BookOpen className="w-5 h-5 mr-2" />
                  Why Position Sizing Matters
                </h4>
                <p className="text-sm text-slate-300 mb-3">
                  Even the best traders have losing streaks. Proper position sizing ensures you can survive 
                  20, 30, or even 50 losing trades in a row before your account is seriously damaged.
                </p>
                <p className="text-sm text-slate-300">
                  <strong>Example:</strong> With 1% risk per trade, you'd need 100 consecutive losses to blow your account. 
                  With 10% risk? Just 10 losses and you're done.
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-6">
                <h4 className="font-semibold text-green-400 mb-3 flex items-center">
                  <Target className="w-5 h-5 mr-2" />
                  The 1% Rule
                </h4>
                <p className="text-sm text-slate-300 mb-3">
                  Professional traders often follow the "1% rule" - never risk more than 1% of your account 
                  on any single trade. This might seem conservative, but it's what keeps pros in the game for years.
                </p>
                <p className="text-sm text-slate-300">
                  <strong>Pro Tip:</strong> Start with 1% risk. Once you're consistently profitable, you can consider 
                  moving to 2%. Never go above 3% per trade.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* QUIZ PAGE */}
        {currentPage === 'quiz' && (
          <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {!quizComplete ? (
              <div>
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-3xl font-bold">Risk Assessment Quiz</h2>
                    <span className="text-slate-400">
                      {quizStep + 1} / {quizQuestions.length}
                    </span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((quizStep + 1) / quizQuestions.length) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
                  <h3 className="text-2xl font-semibold mb-6">
                    {quizQuestions[quizStep].question}
                  </h3>
                  
                  <div className="space-y-3">
                    {quizQuestions[quizStep].options.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleQuizAnswer(option.value)}
                        className="w-full p-4 bg-slate-700 hover:bg-slate-600 border border-slate-600 hover:border-blue-500 rounded-xl transition-all text-left flex items-center space-x-4"
                      >
                        <span className="text-3xl">{option.icon}</span>
                        <span className="text-white">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="mb-8 text-center">
                  <h2 className="text-3xl font-bold mb-2">Your Results</h2>
                  <p className="text-slate-300">Based on your answers, here's our assessment</p>
                </div>

                {(() => {
                  const recommendations = getRecommendations();
                  return (
                    <div className="space-y-6">
                      {/* Risk Level Card */}
                      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 text-center">
                        <div className={`text-6xl font-bold mb-4 ${recommendations.color}`}>
                          {recommendations.level}
                        </div>
                        <div className="text-xl text-slate-300 mb-4">
                          Risk Profile
                        </div>
                        <div className="max-w-2xl mx-auto text-slate-400">
                          {recommendations.description}
                        </div>
                      </div>

                      {/* Recommended Leverage */}
                      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                        <h3 className="text-xl font-semibold mb-4">💪 Recommended Leverage Range</h3>
                        <div className="text-3xl font-bold text-blue-400 mb-2">
                          {recommendations.recommendedLeverage}
                        </div>
                        <p className="text-sm text-slate-400">
                          Start at the lower end and only increase with experience
                        </p>
                      </div>

                      {/* Recommended Methods */}
                      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                        <h3 className="text-xl font-semibold mb-4">✅ Best Leverage Methods For You</h3>
                        <div className="space-y-3">
                          {recommendations.recommendedMethods.map((method, i) => (
                            <div key={i} className="flex items-center space-x-3 p-3 bg-slate-700/50 rounded-lg">
                              <span className="text-green-400 text-xl">•</span>
                              <span className="text-white">{method}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Warnings */}
                      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
                        <h3 className="text-xl font-semibold mb-4 text-red-400">⚠️ Important Warnings</h3>
                        <ul className="space-y-2">
                          {recommendations.warnings.map((warning, i) => (
                            <li key={i} className="flex items-start space-x-2">
                              <span className="text-red-400 mt-1">•</span>
                              <span className="text-slate-300">{warning}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-4 justify-center">
                        <button
                          onClick={() => setCurrentPage('leverage-trading')}
                          className="px-6 py-3 bg-blue-500 rounded-lg font-semibold hover:bg-blue-600 transition-all"
                        >
                          Try Calculator
                        </button>
                        <button
                          onClick={() => setCurrentPage('planning')}
                          className="px-6 py-3 bg-purple-500 rounded-lg font-semibold hover:bg-purple-600 transition-all"
                        >
                          Try Planning
                        </button>
                        <button
                          onClick={resetQuiz}
                          className="px-6 py-3 bg-slate-700 rounded-lg font-semibold hover:bg-slate-600 transition-all"
                        >
                          Retake Quiz
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </section>
        )}

        {/* REAL ESTATE LEVERAGE PAGE */}
        {currentPage === 'real-estate-leverage' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg">
                  <Home className="w-8 h-8" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold">Real Estate Leverage 101</h1>
                  <p className="text-slate-300 mt-2">How mortgages and real estate leverage build wealth for millions</p>
                </div>
              </div>
              <div className="mt-6 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <p className="text-sm text-emerald-300">
                  💡 <strong>Key Insight:</strong> A mortgage is the most common form of leverage in America. With just $50K down, 
                  you control a $250K home (5x leverage). As the home appreciates, you earn returns on the full value—not just your down payment.
                </p>
              </div>
            </div>

            {/* What Is It Section */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">🏠 What Is Real Estate Leverage?</h2>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
                <p className="text-slate-300 text-lg mb-6">
                  Real estate leverage means using borrowed money (debt) to buy property. Instead of paying 100% cash, you make a down payment 
                  (typically 5-20%) and borrow the rest from a lender. This allows you to control a valuable asset with less capital.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-6">
                    <h3 className="font-bold text-emerald-400 mb-3 text-lg">💰 The Power of Leverage</h3>
                    <p className="text-slate-300 text-sm mb-4">
                      <strong>Example:</strong> You buy a $300K home with $60K down (20%). The home appreciates 5% annually.
                    </p>
                    <ul className="text-sm text-slate-300 space-y-2">
                      <li>• <strong>Year 1 appreciation:</strong> $15,000 (5% of $300K)</li>
                      <li>• <strong>Your return:</strong> 25% ($15K gain on $60K invested)</li>
                      <li>• <strong>Without leverage:</strong> Only 5% return on $300K cash</li>
                    </ul>
                    <p className="text-emerald-400 font-semibold mt-4">Leverage amplified your returns 5x!</p>
                  </div>

                  <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-6">
                    <h3 className="font-bold text-white mb-3 text-lg">📊 Common Leverage Ratios</h3>
                    <ul className="text-sm text-slate-300 space-y-3">
                      <li>• <strong>20% Down (5x leverage):</strong> Most common for conventional mortgages</li>
                      <li>• <strong>10% Down (10x leverage):</strong> Available for some buyers</li>
                      <li>• <strong>3.5% Down (28x leverage!):</strong> FHA loans for first-time buyers</li>
                      <li>• <strong>0% Down (Infinite leverage):</strong> VA loans for veterans</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Types of Real Estate Leverage */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">🔑 Types of Real Estate Leverage</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-emerald-400 mb-3">1. Primary Residence Mortgages</h3>
                  <p className="text-slate-300 text-sm mb-4">
                    The most common type. Borrow money to buy the home you live in.
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Down Payment:</span>
                      <span className="text-white">3.5% - 20%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Interest Rate:</span>
                      <span className="text-white">~6-7% (2025)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Loan Terms:</span>
                      <span className="text-white">15 or 30 years</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-blue-400 mb-3">2. Investment Property Loans</h3>
                  <p className="text-slate-300 text-sm mb-4">
                    Buy rental properties to generate income while building equity.
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Down Payment:</span>
                      <span className="text-white">20% - 25%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Interest Rate:</span>
                      <span className="text-white">~6-7% (2025)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Strategy:</span>
                      <span className="text-white">Cash flow + appreciation</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-purple-400 mb-3">3. HELOCs (Home Equity Lines of Credit)</h3>
                  <p className="text-slate-300 text-sm mb-4">
                    Borrow against the equity in your existing home. Works like a credit card secured by your house.
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Max Borrowing:</span>
                      <span className="text-white">Up to 85% of equity</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Interest Rate:</span>
                      <span className="text-white">Variable (~8-10%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Use Cases:</span>
                      <span className="text-white">Renovations, investments</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-orange-400 mb-3">4. Cash-Out Refinance</h3>
                  <p className="text-slate-300 text-sm mb-4">
                    Replace your existing mortgage with a larger one, taking the difference in cash.
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Max Cash Out:</span>
                      <span className="text-white">Up to 80% of home value</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Best When:</span>
                      <span className="text-white">Rates are lower</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Risk Level:</span>
                      <span className="text-yellow-400">Medium</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Real Examples Section */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">📈 Real-World Examples</h2>
              
              {/* Example 1: First-Time Buyer */}
              <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 border border-emerald-500/20 rounded-xl p-8 mb-6">
                <h3 className="text-2xl font-bold text-emerald-400 mb-4">Example 1: First-Time Homebuyer (Sarah, 28)</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-white mb-3">The Numbers:</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li>• <strong>Home Price:</strong> $350,000</li>
                      <li>• <strong>Down Payment:</strong> $17,500 (5% - FHA loan)</li>
                      <li>• <strong>Mortgage:</strong> $332,500 at 6.5% for 30 years</li>
                      <li>• <strong>Monthly Payment:</strong> $2,102 (+ insurance, taxes)</li>
                      <li>• <strong>Leverage Ratio:</strong> 20x</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-3">After 5 Years (assuming 4% annual appreciation):</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li>• <strong>Home Value:</strong> $425,650 (+$75,650)</li>
                      <li>• <strong>Loan Balance:</strong> $307,200 (paid down $25,300)</li>
                      <li>• <strong>Equity Built:</strong> $118,450</li>
                      <li>• <strong>Total Invested:</strong> $17,500 (down) + ~$126,120 (payments) = $143,620</li>
                      <li>• <strong>Return on Initial Investment:</strong> 677% on the $17,500 down!</li>
                    </ul>
                    <p className="mt-4 text-emerald-400 font-semibold">Without leverage, she would have needed $350K in cash upfront.</p>
                  </div>
                </div>
              </div>

              {/* Example 2: Rental Property */}
              <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/20 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-blue-400 mb-4">Example 2: Rental Property Investor (Mike, 35)</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-white mb-3">The Numbers:</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li>• <strong>Property Price:</strong> $200,000 (duplex)</li>
                      <li>• <strong>Down Payment:</strong> $50,000 (25%)</li>
                      <li>• <strong>Mortgage:</strong> $150,000 at 7.5% for 30 years</li>
                      <li>• <strong>Monthly Payment:</strong> $1,049</li>
                      <li>• <strong>Rental Income:</strong> $1,800/month (both units)</li>
                      <li>• <strong>Monthly Cash Flow:</strong> +$751 (after mortgage, taxes, insurance)</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-3">Annual Returns:</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li>• <strong>Cash Flow:</strong> $9,012/year</li>
                      <li>• <strong>Principal Paydown:</strong> ~$2,100/year (increases over time)</li>
                      <li>• <strong>Appreciation (3%):</strong> $6,000/year</li>
                      <li>• <strong>Total Annual Gain:</strong> $17,112</li>
                      <li>• <strong>ROI on $50K invested:</strong> 34.2% per year!</li>
                    </ul>
                    <p className="mt-4 text-blue-400 font-semibold">Leverage turns a 3% appreciation into a 34% return.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Risks & Warnings */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">⚠️ Risks & What Can Go Wrong</h2>
              <div className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-8 mb-6">
                <div className="flex items-start space-x-4">
                  <AlertTriangle className="w-8 h-8 text-red-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-red-400 mb-3">Critical Warnings</h3>
                    <ul className="space-y-3 text-slate-300">
                      <li>• <strong>Market Crashes:</strong> Home values can drop 30-50% in recessions (2008 housing crisis)</li>
                      <li>• <strong>Negative Equity:</strong> You could owe more than the home is worth</li>
                      <li>• <strong>Foreclosure Risk:</strong> Miss payments → lose the home AND your down payment</li>
                      <li>• <strong>Hidden Costs:</strong> Repairs, maintenance, property taxes, insurance add 1-2% of home value annually</li>
                      <li>• <strong>Illiquidity:</strong> Can't sell quickly if you need cash urgently</li>
                      <li>• <strong>Interest Burden:</strong> Over 30 years, you pay nearly 2x the home price in interest</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="font-bold text-yellow-400 mb-3">🟡 Medium Risk</h4>
                  <ul className="text-sm text-slate-300 space-y-2">
                    <li>• 20% down payment</li>
                    <li>• Strong income (3x housing costs)</li>
                    <li>• 6-month emergency fund</li>
                    <li>• Stable job/industry</li>
                  </ul>
                </div>
                
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="font-bold text-orange-400 mb-3">🟠 Higher Risk</h4>
                  <ul className="text-sm text-slate-300 space-y-2">
                    <li>• Low down payment (3-5%)</li>
                    <li>• High debt-to-income ratio</li>
                    <li>• Limited savings</li>
                    <li>• Adjustable-rate mortgage (ARM)</li>
                  </ul>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="font-bold text-red-400 mb-3">🔴 Very High Risk</h4>
                  <ul className="text-sm text-slate-300 space-y-2">
                    <li>• 0% down payment</li>
                    <li>• Maxing out budget</li>
                    <li>• No emergency fund</li>
                    <li>• Multiple leveraged properties</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Tools & Templates */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">🛠️ Tools & Resources</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <button
                  onClick={() => setCurrentPage('templates')}
                  className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-6 hover:border-emerald-500/50 transition-all text-left group"
                >
                  <div className="flex items-center mb-4">
                    <Home className="w-8 h-8 text-emerald-400 mr-3" />
                    <h3 className="text-xl font-bold text-white">First-Time Homebuyer Template</h3>
                  </div>
                  <p className="text-slate-300 text-sm mb-4">
                    Pre-built financial plan for buying your first home. Includes down payment savings goals, 
                    closing costs, and monthly budget.
                  </p>
                  <span className="text-emerald-400 font-semibold group-hover:text-emerald-300">
                    Use Template →
                  </span>
                </button>

                <button
                  onClick={() => setCurrentPage('planning')}
                  className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6 hover:border-blue-500/50 transition-all text-left group"
                >
                  <div className="flex items-center mb-4">
                    <BarChart3 className="w-8 h-8 text-blue-400 mr-3" />
                    <h3 className="text-xl font-bold text-white">Financial Planning Calculator</h3>
                  </div>
                  <p className="text-slate-300 text-sm mb-4">
                    Calculate how much home you can afford, project your net worth growth with real estate, 
                    and plan for all goals.
                  </p>
                  <span className="text-blue-400 font-semibold group-hover:text-blue-300">
                    Start Planning →
                  </span>
                </button>
              </div>
            </div>

            {/* Key Terms */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">📚 Key Terms to Know</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-emerald-400 mb-2">LTV (Loan-to-Value Ratio)</h4>
                  <p className="text-sm text-slate-300">Percentage of home value you're borrowing. 80% LTV = 20% down payment.</p>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-emerald-400 mb-2">PMI (Private Mortgage Insurance)</h4>
                  <p className="text-sm text-slate-300">Required insurance when you put down less than 20%. Adds $50-200/month.</p>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-emerald-400 mb-2">DTI (Debt-to-Income Ratio)</h4>
                  <p className="text-sm text-slate-300">Total monthly debt payments ÷ monthly income. Lenders want &lt;43%.</p>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-emerald-400 mb-2">Amortization</h4>
                  <p className="text-sm text-slate-300">How your payment is split between principal and interest over time.</p>
                </div>
              </div>
              <button
                onClick={() => setCurrentPage('glossary')}
                className="mt-6 px-6 py-3 bg-indigo-500 text-white font-semibold rounded-lg hover:bg-indigo-600 transition-all"
              >
                See Full Glossary (150+ Terms) →
              </button>
            </div>

            {/* Final CTA */}
            <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-xl p-8 text-center">
              <h3 className="text-3xl font-bold mb-4">Ready to Buy Your First Home?</h3>
              <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Use our First-Time Homebuyer template to plan your down payment, budget for closing costs, and understand 
                how much home you can afford.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => setCurrentPage('templates')}
                  className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg font-semibold hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg"
                >
                  Use Homebuyer Template
                </button>
                <button
                  onClick={() => setCurrentPage('home')}
                  className="px-8 py-4 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
                >
                  Back to Home
                </button>
              </div>
            </div>
          </section>
        )}

        {/* INVESTMENT LEVERAGE PAGE */}
        {currentPage === 'investment-leverage' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg">
                  <TrendingUp className="w-8 h-8" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold">Investment Leverage Guide</h1>
                  <p className="text-slate-300 mt-2">Margin, options, futures, and leveraged ETFs explained</p>
                </div>
              </div>
              <div className="mt-6 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-300">
                  💡 <strong>Key Insight:</strong> Investment leverage lets you control more assets with less capital. 
                  A $10K margin account can control $20K in stocks (2x leverage). Options can provide 10-50x leverage. 
                  But with great power comes great risk—you can lose more than you invest.
                </p>
              </div>
            </div>

            {/* What Is It Section */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">📊 What Is Investment Leverage?</h2>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
                <p className="text-slate-300 text-lg mb-6">
                  Investment leverage means borrowing money or using financial instruments to increase your exposure to assets 
                  beyond what you could buy with cash alone. This amplifies both gains AND losses.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6">
                    <h3 className="font-bold text-blue-400 mb-3 text-lg">📈 How Leverage Amplifies Gains</h3>
                    <p className="text-slate-300 text-sm mb-4">
                      <strong>Example:</strong> You have $10K and want to invest in a stock trading at $100/share.
                    </p>
                    <ul className="text-sm text-slate-300 space-y-2">
                      <li>• <strong>No Leverage:</strong> Buy 100 shares ($10K)</li>
                      <li>• <strong>With 2x Leverage:</strong> Borrow $10K, buy 200 shares ($20K)</li>
                      <li>• <strong>Stock rises 20% to $120:</strong></li>
                      <li className="ml-4">- No leverage: +$2K profit (20% return)</li>
                      <li className="ml-4">- 2x leverage: +$4K profit (40% return)</li>
                    </ul>
                    <p className="text-blue-400 font-semibold mt-4">Leverage doubled your returns!</p>
                  </div>

                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
                    <h3 className="font-bold text-red-400 mb-3 text-lg">📉 How Leverage Amplifies Losses</h3>
                    <p className="text-slate-300 text-sm mb-4">
                      <strong>Same scenario, but stock drops 20% to $80:</strong>
                    </p>
                    <ul className="text-sm text-slate-300 space-y-2">
                      <li>• <strong>No Leverage:</strong> -$2K loss (20% loss)</li>
                      <li>• <strong>With 2x Leverage:</strong> -$4K loss (40% loss)</li>
                      <li>• <strong>You still owe:</strong> The $10K you borrowed + interest</li>
                      <li>• <strong>Net position:</strong> $6K left from $10K invested</li>
                    </ul>
                    <p className="text-red-400 font-semibold mt-4">Leverage doubled your losses too!</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Types of Investment Leverage */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">🔑 Types of Investment Leverage</h2>
              <div className="space-y-6">
                {/* Margin Trading */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-blue-500/20 rounded-lg mr-4">
                      <TrendingUp className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-blue-400">1. Margin Trading</h3>
                      <p className="text-sm text-slate-400">Borrow money from your broker to buy stocks</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Leverage Range</p>
                      <p className="text-white font-semibold">2x - 4x</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Interest Rate</p>
                      <p className="text-white font-semibold">~8-12% per year</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Risk Level</p>
                      <p className="text-yellow-400 font-semibold">Medium-High</p>
                    </div>
                  </div>
                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-green-400 mb-2">✅ Pros:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Easy to access through most brokers</li>
                        <li>• Flexible - borrow what you need</li>
                        <li>• Can close position anytime</li>
                      </ul>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-red-400 mb-2">❌ Cons:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Margin calls if account drops</li>
                        <li>• Interest charges eat into profits</li>
                        <li>• Can lose more than you invest</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Options */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-purple-500/20 rounded-lg mr-4">
                      <Target className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-purple-400">2. Options Trading</h3>
                      <p className="text-sm text-slate-400">Contracts giving the right to buy/sell at a set price</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Leverage Range</p>
                      <p className="text-white font-semibold">10x - 50x</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Max Loss</p>
                      <p className="text-white font-semibold">Premium paid (buying)</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Risk Level</p>
                      <p className="text-red-400 font-semibold">High</p>
                    </div>
                  </div>
                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-green-400 mb-2">✅ Pros:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Defined max loss (when buying)</li>
                        <li>• Massive leverage potential</li>
                        <li>• Can profit in any market direction</li>
                      </ul>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-red-400 mb-2">❌ Cons:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Time decay erodes value daily</li>
                        <li>• Complex - steep learning curve</li>
                        <li>• 75% of options expire worthless</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Futures */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-orange-500/20 rounded-lg mr-4">
                      <LineChart className="w-6 h-6 text-orange-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-orange-400">3. Futures Contracts</h3>
                      <p className="text-sm text-slate-400">Agreements to buy/sell assets at a future date</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Leverage Range</p>
                      <p className="text-white font-semibold">10x - 20x</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Initial Margin</p>
                      <p className="text-white font-semibold">5-10% of contract</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Risk Level</p>
                      <p className="text-red-400 font-semibold">Very High</p>
                    </div>
                  </div>
                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-green-400 mb-2">✅ Pros:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• High leverage with small capital</li>
                        <li>• Liquid markets, easy to exit</li>
                        <li>• Tax advantages (60/40 rule)</li>
                      </ul>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-red-400 mb-2">❌ Cons:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Unlimited loss potential</li>
                        <li>• Requires $25K+ typically</li>
                        <li>• Fast-moving, high stress</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Leveraged ETFs */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-green-500/20 rounded-lg mr-4">
                      <BarChart3 className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-green-400">4. Leveraged ETFs</h3>
                      <p className="text-sm text-slate-400">ETFs that use leverage to amplify returns (2x, 3x)</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Leverage Range</p>
                      <p className="text-white font-semibold">2x or 3x</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Expense Ratio</p>
                      <p className="text-white font-semibold">~1% per year</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Risk Level</p>
                      <p className="text-orange-400 font-semibold">Medium-High</p>
                    </div>
                  </div>
                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-green-400 mb-2">✅ Pros:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Easy - trade like regular stocks</li>
                        <li>• No margin calls or interest</li>
                        <li>• Can use in retirement accounts</li>
                      </ul>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-red-400 mb-2">❌ Cons:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Decay over time in sideways markets</li>
                        <li>• NOT for long-term holding</li>
                        <li>• Can lose 100% in extreme moves</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Real Examples Section */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">📈 Real-World Examples</h2>
              
              {/* Example 1: Margin Trading Win */}
              <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 border border-green-500/20 rounded-xl p-8 mb-6">
                <h3 className="text-2xl font-bold text-green-400 mb-4">Example 1: Margin Trading Success (2x Leverage)</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-white mb-3">The Trade:</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li>• <strong>Your Cash:</strong> $10,000</li>
                      <li>• <strong>Borrowed:</strong> $10,000 (2x leverage)</li>
                      <li>• <strong>Total Position:</strong> $20,000 in AAPL @ $150/share</li>
                      <li>• <strong>Stock rises:</strong> $150 → $165 (+10%)</li>
                      <li>• <strong>Interest paid:</strong> $50 (holding 1 month @ 6% annual)</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-3">The Results:</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li>• <strong>Position Value:</strong> $22,000</li>
                      <li>• <strong>Gross Profit:</strong> $2,000</li>
                      <li>• <strong>Minus Interest:</strong> -$50</li>
                      <li>• <strong>Net Profit:</strong> $1,950</li>
                      <li>• <strong>Return on YOUR $10K:</strong> 19.5%</li>
                    </ul>
                    <p className="mt-4 text-green-400 font-semibold">Without leverage: Only 10% return ($1,000 profit)</p>
                  </div>
                </div>
              </div>

              {/* Example 2: Options Loss */}
              <div className="bg-gradient-to-br from-red-500/10 to-red-600/10 border border-red-500/20 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-red-400 mb-4">Example 2: Options Trading Loss (20x Leverage)</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-white mb-3">The Trade:</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li>• <strong>Your Cash:</strong> $1,000</li>
                      <li>• <strong>Strategy:</strong> Buy 10 call options on TSLA</li>
                      <li>• <strong>Cost:</strong> $100 per contract = $1,000 total</li>
                      <li>• <strong>Exposure:</strong> Controls $20,000 of TSLA stock</li>
                      <li>• <strong>Expiration:</strong> 30 days</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-3">What Happened:</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li>• <strong>Stock barely moved:</strong> +2% (not enough)</li>
                      <li>• <strong>Options expired:</strong> Out of the money</li>
                      <li>• <strong>Final Value:</strong> $0</li>
                      <li>• <strong>Total Loss:</strong> $1,000 (100% of investment)</li>
                    </ul>
                    <p className="mt-4 text-red-400 font-semibold">The stock went UP but you still lost everything due to time decay!</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Risks & Warnings */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">⚠️ Critical Risks</h2>
              <div className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-8">
                <div className="flex items-start space-x-4 mb-6">
                  <AlertTriangle className="w-8 h-8 text-red-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-red-400 mb-3">Why 90% of Leveraged Traders Lose Money</h3>
                    <ul className="space-y-3 text-slate-300">
                      <li>• <strong>Over-Leveraging:</strong> Using 10x+ leverage = one bad trade wipes you out</li>
                      <li>• <strong>Margin Calls:</strong> Broker forces you to sell at the worst possible time</li>
                      <li>• <strong>Time Decay:</strong> Options lose value every day, even if you're right about direction</li>
                      <li>• <strong>Volatility:</strong> Small price swings = large gains/losses with leverage</li>
                      <li>• <strong>Fees Add Up:</strong> Interest, commissions, and spreads eat profits</li>
                      <li>• <strong>Emotional Trading:</strong> Leverage amplifies fear and greed</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-slate-900/50 border border-red-500/30 rounded-lg p-6">
                  <h4 className="font-bold text-red-400 mb-3">Real Horror Stories:</h4>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li>• Trader lost $50K on a $2K investment using 25x options leverage</li>
                    <li>• Margin call forced sale at bottom, missing the recovery</li>
                    <li>• 3x leveraged ETF holder lost 80% over 2 years despite index being flat</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Tools & Calculators */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">🛠️ Tools & Calculators</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <button
                  onClick={() => setCurrentPage('trade-calculator')}
                  className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6 hover:border-blue-500/50 transition-all text-left group"
                >
                  <LineChart className="w-8 h-8 text-blue-400 mb-3" />
                  <h3 className="text-lg font-bold text-white mb-2">Trade Calculator</h3>
                  <p className="text-slate-300 text-sm mb-3">
                    Model P&L, calculate liquidation prices, simulate trades
                  </p>
                  <span className="text-blue-400 font-semibold group-hover:text-blue-300">
                    Calculate Now →
                  </span>
                </button>

                <button
                  onClick={() => setCurrentPage('position-sizing')}
                  className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border border-purple-500/30 rounded-xl p-6 hover:border-purple-500/50 transition-all text-left group"
                >
                  <Target className="w-8 h-8 text-purple-400 mb-3" />
                  <h3 className="text-lg font-bold text-white mb-2">Position Sizing</h3>
                  <p className="text-slate-300 text-sm mb-3">
                    Calculate safe position sizes based on your risk tolerance
                  </p>
                  <span className="text-purple-400 font-semibold group-hover:text-purple-300">
                    Size Positions →
                  </span>
                </button>

                <button
                  onClick={() => setCurrentPage('risk-management')}
                  className="bg-gradient-to-br from-orange-500/10 to-orange-600/10 border border-orange-500/30 rounded-xl p-6 hover:border-orange-500/50 transition-all text-left group"
                >
                  <Shield className="w-8 h-8 text-orange-400 mb-3" />
                  <h3 className="text-lg font-bold text-white mb-2">Risk Management</h3>
                  <p className="text-slate-300 text-sm mb-3">
                    R:R ratios, portfolio risk, drawdown calculators
                  </p>
                  <span className="text-orange-400 font-semibold group-hover:text-orange-300">
                    Manage Risk →
                  </span>
                </button>
              </div>
            </div>

            {/* Best Practices */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">✅ Best Practices for Beginners</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
                  <h3 className="font-bold text-green-400 mb-4 text-lg">DO:</h3>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li>✓ Start with 2-3x leverage maximum</li>
                    <li>✓ Use stop losses on EVERY trade</li>
                    <li>✓ Risk only 1-2% of capital per trade</li>
                    <li>✓ Practice with paper trading first</li>
                    <li>✓ Keep detailed records for taxes</li>
                    <li>✓ Learn one strategy deeply before adding more</li>
                  </ul>
                </div>

                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
                  <h3 className="font-bold text-red-400 mb-4 text-lg">DON'T:</h3>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li>✗ Use more than 5x leverage as a beginner</li>
                    <li>✗ Trade with money you can't afford to lose</li>
                    <li>✗ Hold leveraged ETFs long-term</li>
                    <li>✗ Sell options without understanding risks</li>
                    <li>✗ Average down on losing positions</li>
                    <li>✗ Trade based on emotions or FOMO</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Final CTA */}
            <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-xl p-8 text-center">
              <h3 className="text-3xl font-bold mb-4">Ready to Start Trading with Leverage?</h3>
              <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Use our calculators to model trades, size positions safely, and manage risk properly before risking real money.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => setCurrentPage('trade-calculator')}
                  className="px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all shadow-lg"
                >
                  Try Trade Calculator
                </button>
                <button
                  onClick={() => setCurrentPage('home')}
                  className="px-8 py-4 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
                >
                  Back to Home
                </button>
              </div>
            </div>
          </section>
        )}

        {/* CRYPTO LEVERAGE PAGE */}
        {currentPage === 'crypto-leverage' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg">
                  <Bitcoin className="w-8 h-8" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold">Crypto Leverage & DeFi Guide</h1>
                  <p className="text-slate-300 mt-2">Perpetual futures, lending protocols, and decentralized leverage</p>
                </div>
              </div>
              <div className="mt-6 bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                <p className="text-sm text-orange-300">
                  ⚠️ <strong>Critical Warning:</strong> Crypto leverage is the Wild West. Platforms offer 100x+ leverage, 
                  liquidations happen in seconds, and you can lose your entire account in one bad trade. This is NOT for beginners.
                </p>
              </div>
            </div>

            {/* What Is It Section */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">🪙 What Is Crypto Leverage?</h2>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
                <p className="text-slate-300 text-lg mb-6">
                  Crypto leverage lets you control large positions in cryptocurrencies with small amounts of capital. Unlike traditional 
                  markets, crypto exchanges offer extreme leverage (up to 125x) and trade 24/7/365—which means you can get liquidated 
                  at 3am on a Sunday.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-6">
                    <h3 className="font-bold text-orange-400 mb-3 text-lg">⚡ The Speed & Scale</h3>
                    <p className="text-slate-300 text-sm mb-4">
                      <strong>Example:</strong> Bitcoin perpetual futures with 10x leverage
                    </p>
                    <ul className="text-sm text-slate-300 space-y-2">
                      <li>• <strong>Your collateral:</strong> $1,000</li>
                      <li>• <strong>Position size:</strong> $10,000 (10 BTC if BTC = $1000)</li>
                      <li>• <strong>BTC moves 5%:</strong> Your position moves $500</li>
                      <li>• <strong>Your P&L:</strong> +/- 50% of your $1,000</li>
                      <li>• <strong>Liquidation:</strong> ~8-9% move against you</li>
                    </ul>
                    <p className="text-orange-400 font-semibold mt-4">One volatile hour can make or break you!</p>
                  </div>

                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
                    <h3 className="font-bold text-red-400 mb-3 text-lg">💀 The Danger Zone</h3>
                    <p className="text-slate-300 text-sm mb-4">
                      <strong>Why crypto leverage is more dangerous:</strong>
                    </p>
                    <ul className="text-sm text-slate-300 space-y-2">
                      <li>• <strong>24/7 markets:</strong> No sleep, no breaks</li>
                      <li>• <strong>Extreme volatility:</strong> 10-20% swings daily</li>
                      <li>• <strong>Cascade liquidations:</strong> Your liquidation triggers others</li>
                      <li>• <strong>Exchange risk:</strong> Platform hacks, freezes, exits</li>
                      <li>• <strong>No regulation:</strong> No FDIC, no protections</li>
                    </ul>
                    <p className="text-red-400 font-semibold mt-4">$2B+ liquidated in a single day (May 2021)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Types of Crypto Leverage */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">🔑 Types of Crypto Leverage</h2>
              <div className="space-y-6">
                {/* Perpetual Futures */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-orange-500/20 rounded-lg mr-4">
                      <TrendingUp className="w-6 h-6 text-orange-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-orange-400">1. Perpetual Futures (Perps)</h3>
                      <p className="text-sm text-slate-400">Never-expiring futures contracts, most popular</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Max Leverage</p>
                      <p className="text-white font-semibold">Up to 125x</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Funding Rate</p>
                      <p className="text-white font-semibold">±0.01% every 8hrs</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Risk Level</p>
                      <p className="text-red-400 font-semibold">Extreme</p>
                    </div>
                  </div>
                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-green-400 mb-2">✅ Pros:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• No expiration date</li>
                        <li>• Massive leverage available</li>
                        <li>• Liquid markets 24/7</li>
                        <li>• Can short easily</li>
                      </ul>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-red-400 mb-2">❌ Cons:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Instant liquidation possible</li>
                        <li>• Funding costs add up</li>
                        <li>• Counterparty risk (exchange)</li>
                        <li>• Addictive nature</li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-4 bg-slate-900/50 rounded-lg p-4">
                    <p className="text-xs text-slate-400 mb-2">Popular Platforms:</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-1 rounded">Hyperliquid</span>
                      <span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-1 rounded">Lighter</span>
                      <span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-1 rounded">Coinbase</span>
                      <span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-1 rounded">Binance</span>
                      <span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-1 rounded">Bybit</span>
                    </div>
                  </div>
                </div>

                {/* Margin Trading */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-blue-500/20 rounded-lg mr-4">
                      <DollarSign className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-blue-400">2. Spot Margin Trading</h3>
                      <p className="text-sm text-slate-400">Borrow crypto or stablecoins to trade spot</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Max Leverage</p>
                      <p className="text-white font-semibold">3x - 10x</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Interest Rate</p>
                      <p className="text-white font-semibold">~10-30% APR</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Risk Level</p>
                      <p className="text-yellow-400 font-semibold">High</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-slate-300 mb-2">
                      <strong>How it works:</strong> Borrow USDT to buy more BTC, or borrow BTC to short it. 
                      You own the actual crypto (unlike futures), but pay interest rates.
                    </p>
                  </div>
                </div>

                {/* DeFi Lending */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-purple-500/20 rounded-lg mr-4">
                      <Globe className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-purple-400">3. DeFi Lending Protocols</h3>
                      <p className="text-sm text-slate-400">Decentralized borrowing without intermediaries</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Max LTV</p>
                      <p className="text-white font-semibold">50-75%</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Interest Rate</p>
                      <p className="text-white font-semibold">Variable, ~5-15%</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Risk Level</p>
                      <p className="text-orange-400 font-semibold">High</p>
                    </div>
                  </div>
                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-green-400 mb-2">✅ Pros:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• No KYC required</li>
                        <li>• Permissionless access</li>
                        <li>• Transparent on-chain</li>
                      </ul>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-red-400 mb-2">❌ Cons:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Smart contract risk</li>
                        <li>• Oracle manipulation</li>
                        <li>• Gas fees to liquidate</li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-4 bg-slate-900/50 rounded-lg p-4">
                    <p className="text-xs text-slate-400 mb-2">Popular Protocols:</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded">Aave</span>
                      <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded">Compound</span>
                      <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded">MakerDAO</span>
                    </div>
                  </div>
                </div>

                {/* Leveraged Yield Farming */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-green-500/20 rounded-lg mr-4">
                      <PiggyBank className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-green-400">4. Leveraged Yield Farming</h3>
                      <p className="text-sm text-slate-400">Borrow to multiply LP yields (extremely risky)</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Max Leverage</p>
                      <p className="text-white font-semibold">2x - 5x</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Potential APY</p>
                      <p className="text-white font-semibold">100-500%+</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Risk Level</p>
                      <p className="text-red-400 font-semibold">Extreme</p>
                    </div>
                  </div>
                  <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <p className="text-sm text-red-300">
                      ⚠️ <strong>Not Recommended:</strong> Combines leverage risk + impermanent loss + smart contract risk + 
                      protocol risk. Many have lost 100% trying to chase high yields.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Real Examples */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">📈 Real-World Examples</h2>
              
              {/* Example 1: Perps Win */}
              <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 border border-green-500/20 rounded-xl p-8 mb-6">
                <h3 className="text-2xl font-bold text-green-400 mb-4">Example 1: Profitable Perp Trade (10x Leverage)</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-white mb-3">The Setup:</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li>• <strong>Collateral:</strong> $1,000 USDT</li>
                      <li>• <strong>Leverage:</strong> 10x</li>
                      <li>• <strong>Position Size:</strong> $10,000 (long BTC)</li>
                      <li>• <strong>Entry:</strong> BTC at $40,000</li>
                      <li>• <strong>Exit:</strong> BTC at $42,000 (+5%)</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-3">The Results:</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li>• <strong>Position Gain:</strong> $500 (5% of $10K)</li>
                      <li>• <strong>Funding Costs:</strong> -$10 (held 1 day)</li>
                      <li>• <strong>Trading Fees:</strong> -$20 (0.1% each side)</li>
                      <li>• <strong>Net Profit:</strong> $470</li>
                      <li>• <strong>ROI:</strong> 47% on $1,000 collateral</li>
                    </ul>
                    <p className="mt-4 text-green-400 font-semibold">10x leverage turned 5% move into 47% profit!</p>
                  </div>
                </div>
              </div>

              {/* Example 2: Liquidation */}
              <div className="bg-gradient-to-br from-red-500/10 to-red-600/10 border border-red-500/20 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-red-400 mb-4">Example 2: Liquidation Horror Story (50x Leverage)</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-white mb-3">The Setup:</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li>• <strong>Collateral:</strong> $5,000</li>
                      <li>• <strong>Leverage:</strong> 50x (!)</li>
                      <li>• <strong>Position Size:</strong> $250,000 (long ETH)</li>
                      <li>• <strong>Entry:</strong> ETH at $2,000</li>
                      <li>• <strong>Liquidation Price:</strong> $1,960 (-2%)</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-3">What Happened:</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li>• <strong>Flash crash:</strong> ETH dumped to $1,940</li>
                      <li>• <strong>Liquidation triggered:</strong> Position closed</li>
                      <li>• <strong>Time elapsed:</strong> 47 minutes from entry</li>
                      <li>• <strong>Account value:</strong> $0 (entire $5K gone)</li>
                      <li>• <strong>ETH price 1 hour later:</strong> $2,100</li>
                    </ul>
                    <p className="mt-4 text-red-400 font-semibold">Would have been +$12.5K profit if not liquidated!</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Risks & Warnings */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">⚠️ Why Crypto Leverage Is Different</h2>
              <div className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-8">
                <div className="flex items-start space-x-4 mb-6">
                  <AlertTriangle className="w-8 h-8 text-red-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-red-400 mb-3">Unique Crypto Risks</h3>
                    <ul className="space-y-3 text-slate-300">
                      <li>• <strong>24/7/365 Trading:</strong> No circuit breakers, no breaks, liquidations while you sleep</li>
                      <li>• <strong>Flash Crashes:</strong> 20% dumps in minutes (BTC wick to $8K in 2020)</li>
                      <li>• <strong>Exchange Risk:</strong> FTX, Mt.Gox, Celsius—billions lost to platform failures</li>
                      <li>• <strong>Funding Costs:</strong> Can pay 0.3%/day to hold positions (100%+/year!)</li>
                      <li>• <strong>Cascade Liquidations:</strong> Your liquidation triggers others, creating death spirals</li>
                      <li>• <strong>No Regulation:</strong> No FDIC, no SEC protections, no recourse</li>
                      <li>• <strong>Addiction Factor:</strong> Gambling-like dopamine hits, many ruined lives</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-slate-900/50 border border-red-500/30 rounded-lg p-6">
                  <h4 className="font-bold text-red-400 mb-3">Stat That Should Scare You:</h4>
                  <p className="text-slate-300 text-lg">
                    <strong>$10 Billion+</strong> liquidated across crypto markets in a single quarter. That's real money from real people—gone in seconds.
                  </p>
                </div>
              </div>
            </div>

            {/* Best Practices */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">✅ Survival Rules</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
                  <h3 className="font-bold text-green-400 mb-4 text-lg">If You MUST Use Crypto Leverage:</h3>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li>✓ Never use more than 3-5x leverage</li>
                    <li>✓ Always set stop losses (don't trust yourself)</li>
                    <li>✓ Risk MAX 1% of portfolio per trade</li>
                    <li>✓ Use cross-margin, not isolated (more buffer)</li>
                    <li>✓ Watch funding rates (can kill profitability)</li>
                    <li>✓ Have alerts for liquidation prices</li>
                    <li>✓ Practice on testnet first (months, not days)</li>
                  </ul>
                </div>

                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
                  <h3 className="font-bold text-red-400 mb-4 text-lg">Never, Ever:</h3>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li>✗ Use 10x+ leverage (death wish)</li>
                    <li>✗ Trade with rent/bill money</li>
                    <li>✗ Hold high-leverage overnight</li>
                    <li>✗ Trade on emotion or FOMO</li>
                    <li>✗ Use unregulated exchanges</li>
                    <li>✗ Ignore liquidation warnings</li>
                    <li>✗ Try to "get even" after losses</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Tools */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">🛠️ Education & Tools</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <button
                  onClick={() => setCurrentPage('trade-calculator')}
                  className="bg-gradient-to-br from-orange-500/10 to-amber-500/10 border border-orange-500/30 rounded-xl p-6 hover:border-orange-500/50 transition-all text-left group"
                >
                  <LineChart className="w-8 h-8 text-orange-400 mb-3" />
                  <h3 className="text-lg font-bold text-white mb-2">Perps Calculator</h3>
                  <p className="text-slate-300 text-sm mb-3">
                    Calculate liquidation prices, funding costs, P&L for perpetual futures
                  </p>
                  <span className="text-orange-400 font-semibold group-hover:text-orange-300">
                    Calculate Risk →
                  </span>
                </button>

                <button
                  onClick={() => setCurrentPage('glossary')}
                  className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border border-purple-500/30 rounded-xl p-6 hover:border-purple-500/50 transition-all text-left group"
                >
                  <BookOpen className="w-8 h-8 text-purple-400 mb-3" />
                  <h3 className="text-lg font-bold text-white mb-2">Crypto Glossary</h3>
                  <p className="text-slate-300 text-sm mb-3">
                    Learn terms: Perps, Funding Rate, LTV, Liquidation, and more
                  </p>
                  <span className="text-purple-400 font-semibold group-hover:text-purple-300">
                    Learn Terms →
                  </span>
                </button>
              </div>
            </div>

            {/* Final CTA */}
            <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/20 rounded-xl p-8 text-center">
              <h3 className="text-3xl font-bold mb-4">Think Twice Before Using Crypto Leverage</h3>
              <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Most people lose money with crypto leverage. If you're going to do it anyway, at least use our calculators 
                to understand your liquidation risk and position sizing.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => setCurrentPage('trade-calculator')}
                  className="px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg font-semibold hover:from-orange-600 hover:to-amber-600 transition-all shadow-lg"
                >
                  Calculate Liquidation Risk
                </button>
                <button
                  onClick={() => setCurrentPage('home')}
                  className="px-8 py-4 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
                >
                  Back to Home
                </button>
              </div>
            </div>
          </section>
        )}

        {/* BUSINESS LEVERAGE PAGE */}
        {currentPage === 'business-leverage' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg">
                  <Building2 className="w-8 h-8" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold">Business Leverage Guide</h1>
                  <p className="text-slate-300 mt-2">How entrepreneurs use debt to scale companies and build wealth</p>
                </div>
              </div>
              <div className="mt-6 bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <p className="text-sm text-purple-300">
                  💡 <strong>Key Insight:</strong> Nearly every successful business uses leverage. A $50K SBA loan can help 
                  you buy $200K in inventory or equipment. Smart business leverage = buying growth, not just buying stuff.
                </p>
              </div>
            </div>

            {/* What Is It Section */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">🏢 What Is Business Leverage?</h2>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
                <p className="text-slate-300 text-lg mb-6">
                  Business leverage means using borrowed capital to grow your company faster than you could with savings alone. 
                  Unlike consumer debt, business debt is used to generate revenue and profits that exceed the cost of borrowing.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-6">
                    <h3 className="font-bold text-purple-400 mb-3 text-lg">💰 Good Debt vs Bad Debt</h3>
                    <div className="space-y-3 text-sm text-slate-300">
                      <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                        <p className="font-semibold text-green-400 mb-2">✅ Good Business Debt:</p>
                        <ul className="space-y-1">
                          <li>• Generates revenue (inventory, equipment)</li>
                          <li>• ROI &gt; Interest rate</li>
                          <li>• Grows business value</li>
                          <li>• Tax deductible interest</li>
                        </ul>
                      </div>
                      <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                        <p className="font-semibold text-red-400 mb-2">❌ Bad Business Debt:</p>
                        <ul className="space-y-1">
                          <li>• Covers operating losses</li>
                          <li>• Buys depreciating assets</li>
                          <li>• No clear path to ROI</li>
                          <li>• High-interest (credit cards)</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-6">
                    <h3 className="font-bold text-white mb-3 text-lg">📊 ROI Example</h3>
                    <p className="text-slate-300 text-sm mb-4">
                      <strong>Restaurant buying a pizza oven:</strong>
                    </p>
                    <ul className="text-sm text-slate-300 space-y-2">
                      <li>• <strong>Oven cost:</strong> $30,000 (via loan)</li>
                      <li>• <strong>Loan terms:</strong> 6% APR, 5 years</li>
                      <li>• <strong>Monthly payment:</strong> $580</li>
                      <li>• <strong>Additional pizzas sold:</strong> 100/month</li>
                      <li>• <strong>Profit per pizza:</strong> $8</li>
                      <li>• <strong>Monthly profit:</strong> $800</li>
                      <li>• <strong>Net after loan:</strong> +$220/month</li>
                    </ul>
                    <p className="text-purple-400 font-semibold mt-4">Oven pays for itself + generates profit!</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Types of Business Leverage */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">🔑 Types of Business Leverage</h2>
              <div className="space-y-6">
                {/* SBA Loans */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-blue-500/20 rounded-lg mr-4">
                      <Building2 className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-blue-400">1. SBA Loans (Small Business Administration)</h3>
                      <p className="text-sm text-slate-400">Government-backed loans with favorable terms</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Loan Amount</p>
                      <p className="text-white font-semibold">$50K - $5M</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Interest Rate</p>
                      <p className="text-white font-semibold">~10-12% (2025)</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Repayment</p>
                      <p className="text-white font-semibold">5-25 years</p>
                    </div>
                  </div>
                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-green-400 mb-2">✅ Best For:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Starting or acquiring a business</li>
                        <li>• Real estate purchases</li>
                        <li>• Major equipment</li>
                        <li>• Working capital</li>
                      </ul>
                    </div>
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-yellow-400 mb-2">⚠️ Requirements:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Good personal credit (680+)</li>
                        <li>• Down payment (10-20%)</li>
                        <li>• Business plan</li>
                        <li>• Personal guarantee</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Business Line of Credit */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-green-500/20 rounded-lg mr-4">
                      <DollarSign className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-green-400">2. Business Line of Credit</h3>
                      <p className="text-sm text-slate-400">Revolving credit like a business credit card</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Credit Limit</p>
                      <p className="text-white font-semibold">$10K - $500K</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Interest Rate</p>
                      <p className="text-white font-semibold">~8-25%</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Draw Period</p>
                      <p className="text-white font-semibold">Ongoing</p>
                    </div>
                  </div>
                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-green-400 mb-2">✅ Pros:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Only pay interest on what you use</li>
                        <li>• Flexible access to cash</li>
                        <li>• Reusable as you pay down</li>
                        <li>• Great for cash flow gaps</li>
                      </ul>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-red-400 mb-2">❌ Cons:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Variable rates can spike</li>
                        <li>• Requires good credit</li>
                        <li>• May have draw fees</li>
                        <li>• Can be revoked</li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-4 bg-slate-900/50 rounded-lg p-4">
                    <p className="text-xs text-slate-400 mb-2">Common Uses:</p>
                    <p className="text-sm text-slate-300">Inventory purchases, seasonal cash flow, payroll gaps, unexpected expenses</p>
                  </div>
                </div>

                {/* Equipment Financing */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-orange-500/20 rounded-lg mr-4">
                      <Target className="w-6 h-6 text-orange-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-orange-400">3. Equipment Financing</h3>
                      <p className="text-sm text-slate-400">Loans secured by the equipment itself</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Financing Amount</p>
                      <p className="text-white font-semibold">100% of cost</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Interest Rate</p>
                      <p className="text-white font-semibold">~6-15%</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Repayment</p>
                      <p className="text-white font-semibold">2-7 years</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-slate-300 mb-2">
                      <strong>How it works:</strong> Equipment serves as collateral. If you default, lender takes the equipment. 
                      Great for machinery, vehicles, computers, restaurant equipment, medical devices.
                    </p>
                  </div>
                </div>

                {/* Invoice Factoring */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-purple-500/20 rounded-lg mr-4">
                      <BarChart3 className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-purple-400">4. Invoice Factoring / Invoice Financing</h3>
                      <p className="text-sm text-slate-400">Sell unpaid invoices for immediate cash</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Advance Rate</p>
                      <p className="text-white font-semibold">70-90% of invoice</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Factor Fee</p>
                      <p className="text-white font-semibold">1-5% per month</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Speed</p>
                      <p className="text-white font-semibold">24-48 hours</p>
                    </div>
                  </div>
                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-green-400 mb-2">✅ Best For:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• B2B businesses with Net 30/60 terms</li>
                        <li>• Cash flow emergencies</li>
                        <li>• Fast-growing companies</li>
                      </ul>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-red-400 mb-2">❌ Downsides:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Expensive (can be 15-60% APR)</li>
                        <li>• Customer knows you factored</li>
                        <li>• Can damage relationships</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Merchant Cash Advance */}
                <div className="bg-slate-800/50 border border-red-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-red-500/20 rounded-lg mr-4">
                      <AlertTriangle className="w-6 h-6 text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-red-400">5. Merchant Cash Advance (⚠️ AVOID)</h3>
                      <p className="text-sm text-slate-400">Predatory lender of last resort</p>
                    </div>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <p className="text-sm text-red-300 mb-3">
                      <strong>Why to avoid:</strong> MCAs are NOT loans—they're purchases of your future revenue. 
                      APRs often exceed 100-400%! They take a % of daily credit card sales, strangling cash flow.
                    </p>
                    <p className="text-xs text-slate-400">
                      Example: Borrow $10K, pay back $15K within 6 months = ~100% APR. Many businesses get trapped 
                      in an MCA debt spiral.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Startup Leverage / Equity Financing */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">🚀 Startup Leverage: Equity Financing</h2>
              <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-xl p-6 mb-6">
                <p className="text-slate-300 mb-4">
                  <strong>Unlike traditional debt,</strong> startups often leverage <strong>equity financing</strong>—selling ownership 
                  stakes to raise capital. Instead of borrowing money to repay, you give investors a % of your company in exchange for cash.
                </p>
                <div className="bg-indigo-500/20 border border-indigo-500/40 rounded-lg p-4">
                  <p className="text-sm text-indigo-200">
                    💡 <strong>Key Difference:</strong> Debt = you owe money back. Equity = investors own a piece of your future success (or failure).
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Friends & Family Round */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-pink-500/20 rounded-lg mr-4">
                      <Heart className="w-6 h-6 text-pink-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-pink-400">1. Friends & Family Round</h3>
                      <p className="text-sm text-slate-400">Early-stage capital from personal network</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Typical Amount</p>
                      <p className="text-white font-semibold">$10K - $100K</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Equity Given</p>
                      <p className="text-white font-semibold">5-15%</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Valuation</p>
                      <p className="text-white font-semibold">$500K - $2M</p>
                    </div>
                  </div>
                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-green-400 mb-2">✅ Pros:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Easy to close (less paperwork)</li>
                        <li>• Flexible terms</li>
                        <li>• No professional investor scrutiny</li>
                        <li>• Supportive investors</li>
                      </ul>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-red-400 mb-2">❌ Cons:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Can strain relationships if failed</li>
                        <li>• Limited capital available</li>
                        <li>• May lack business expertise</li>
                        <li>• Awkward equity conversations</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Angel Investors */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-yellow-500/20 rounded-lg mr-4">
                      <Target className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-yellow-400">2. Angel Investors</h3>
                      <p className="text-sm text-slate-400">Wealthy individuals investing their own money</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Typical Investment</p>
                      <p className="text-white font-semibold">$25K - $500K</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Equity Stake</p>
                      <p className="text-white font-semibold">10-25%</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Stage</p>
                      <p className="text-white font-semibold">Seed / Pre-seed</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-slate-300 mb-3">
                      <strong>Common structures:</strong> Convertible notes, SAFE agreements, or direct equity rounds.
                    </p>
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <p className="text-xs text-slate-400 mb-2">What angels look for:</p>
                      <p className="text-xs text-slate-300">
                        Strong team, big market opportunity, early traction (users/revenue), clear path to profitability, 
                        10x+ return potential
                      </p>
                    </div>
                  </div>
                </div>

                {/* Venture Capital */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-indigo-500/20 rounded-lg mr-4">
                      <TrendingUp className="w-6 h-6 text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-indigo-400">3. Venture Capital (VC)</h3>
                      <p className="text-sm text-slate-400">Professional firms investing institutional money</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Investment Size</p>
                      <p className="text-white font-semibold">$500K - $100M+</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Equity Stake</p>
                      <p className="text-white font-semibold">15-30% per round</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Stage</p>
                      <p className="text-white font-semibold">Seed → Series A/B/C</p>
                    </div>
                  </div>
                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-green-400 mb-2">✅ Pros:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Large capital for fast growth</li>
                        <li>• Expert guidance & networks</li>
                        <li>• Brand credibility (top-tier VCs)</li>
                        <li>• Follow-on funding in later rounds</li>
                      </ul>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                      <p className="text-xs font-semibold text-red-400 mb-2">❌ Cons:</p>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Significant dilution (give up control)</li>
                        <li>• Pressure for hypergrowth & exits</li>
                        <li>• Board seats & voting rights</li>
                        <li>• Long, rigorous due diligence</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* SAFE Notes & Convertible Notes */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-cyan-500/20 rounded-lg mr-4">
                      <FileText className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-cyan-400">4. SAFE Notes & Convertible Notes</h3>
                      <p className="text-sm text-slate-400">Delay valuation until a future funding round</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
                      <h4 className="font-bold text-white mb-2 text-sm">SAFE (Simple Agreement for Future Equity)</h4>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• <strong>No interest, no maturity date</strong></li>
                        <li>• Converts to equity in next priced round</li>
                        <li>• Includes valuation cap & discount</li>
                        <li>• Fastest, simplest structure</li>
                      </ul>
                    </div>
                    <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
                      <h4 className="font-bold text-white mb-2 text-sm">Convertible Note</h4>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• <strong>Debt that converts to equity</strong></li>
                        <li>• Has interest rate (4-8%) & maturity (18-24 mo)</li>
                        <li>• Valuation cap & conversion discount</li>
                        <li>• More investor protection</li>
                      </ul>
                    </div>
                  </div>
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                    <p className="text-xs text-cyan-200 mb-2">
                      <strong>Example:</strong> Investor gives $100K via SAFE with $5M cap + 20% discount.
                    </p>
                    <p className="text-xs text-slate-300">
                      When you raise Series A at $10M valuation, their $100K converts as if company was worth $5M (the cap) 
                      OR $8M (20% discount), whichever is better for them. They get 2x more equity than Series A investors!
                    </p>
                  </div>
                </div>

                {/* Equity Crowdfunding */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-emerald-500/20 rounded-lg mr-4">
                      <Globe className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-emerald-400">5. Equity Crowdfunding</h3>
                      <p className="text-sm text-slate-400">Raise from many small investors online</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Typical Raise</p>
                      <p className="text-white font-semibold">$50K - $5M</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Platform Fee</p>
                      <p className="text-white font-semibold">5-7%</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Min Investment</p>
                      <p className="text-white font-semibold">$100 - $1,000</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-xs text-slate-400 mb-2">Popular platforms:</p>
                    <p className="text-sm text-slate-300 mb-3">
                      Wefunder, Republic, StartEngine, SeedInvest
                    </p>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                        <p className="text-xs font-semibold text-green-400 mb-1">✅ Good for:</p>
                        <ul className="text-xs text-slate-300 space-y-1">
                          <li>• Consumer brands with fans</li>
                          <li>• Marketing buzz & validation</li>
                          <li>• Building community</li>
                        </ul>
                      </div>
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3">
                        <p className="text-xs font-semibold text-yellow-400 mb-1">⚠️ Challenges:</p>
                        <ul className="text-xs text-slate-300 space-y-1">
                          <li>• Managing hundreds of investors</li>
                          <li>• Complex cap table</li>
                          <li>• Ongoing reporting requirements</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Equity vs Debt Comparison */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">⚖️ Equity vs Debt: When to Use Each</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {/* When to Use Equity */}
                <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-indigo-400 mb-4">Use Equity When:</h3>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start">
                      <span className="text-indigo-400 mr-2">✓</span>
                      <span><strong>High-risk, high-reward business:</strong> Tech startups, biotech, new markets</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-indigo-400 mr-2">✓</span>
                      <span><strong>No revenue yet:</strong> Pre-revenue or burning cash to grow fast</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-indigo-400 mr-2">✓</span>
                      <span><strong>Need large capital:</strong> Building software, scaling quickly, R&D heavy</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-indigo-400 mr-2">✓</span>
                      <span><strong>Can't afford monthly payments:</strong> Unpredictable cash flow</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-indigo-400 mr-2">✓</span>
                      <span><strong>Want investor expertise:</strong> Network, advice, credibility matter</span>
                    </li>
                  </ul>
                  <div className="mt-4 bg-indigo-500/20 border border-indigo-500/40 rounded-lg p-3">
                    <p className="text-xs text-indigo-200">
                      <strong>Trade-off:</strong> Give up ownership & control in exchange for zero repayment obligation
                    </p>
                  </div>
                </div>

                {/* When to Use Debt */}
                <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-green-400 mb-4">Use Debt When:</h3>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start">
                      <span className="text-green-400 mr-2">✓</span>
                      <span><strong>Profitable or near-profitable:</strong> Stable cash flow to cover payments</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-2">✓</span>
                      <span><strong>Buying tangible assets:</strong> Equipment, inventory, real estate</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-2">✓</span>
                      <span><strong>Want to keep ownership:</strong> Don't want to dilute equity</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-2">✓</span>
                      <span><strong>Traditional business model:</strong> Restaurant, construction, retail, services</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-2">✓</span>
                      <span><strong>Tax advantages:</strong> Interest is tax-deductible</span>
                    </li>
                  </ul>
                  <div className="mt-4 bg-green-500/20 border border-green-500/40 rounded-lg p-3">
                    <p className="text-xs text-green-200">
                      <strong>Trade-off:</strong> Must repay regardless of success, but you keep 100% ownership
                    </p>
                  </div>
                </div>
              </div>

              {/* Hybrid Approach */}
              <div className="mt-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-6">
                <h3 className="text-xl font-bold text-purple-400 mb-3">💡 The Hybrid Approach</h3>
                <p className="text-slate-300 text-sm mb-3">
                  Many successful companies use <strong>both equity AND debt</strong> at different stages:
                </p>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div className="bg-slate-800/50 border border-slate-600 rounded-lg p-4">
                    <p className="font-semibold text-purple-400 mb-2">Stage 1: Pre-Revenue</p>
                    <p className="text-slate-300 text-xs">Raise equity (angel/VC) to build product & find product-market fit</p>
                  </div>
                  <div className="bg-slate-800/50 border border-slate-600 rounded-lg p-4">
                    <p className="font-semibold text-purple-400 mb-2">Stage 2: Early Revenue</p>
                    <p className="text-slate-300 text-xs">Use debt (line of credit) for inventory/working capital as revenue grows</p>
                  </div>
                  <div className="bg-slate-800/50 border border-slate-600 rounded-lg p-4">
                    <p className="font-semibold text-purple-400 mb-2">Stage 3: Scaling</p>
                    <p className="text-slate-300 text-xs">Raise more equity for expansion + debt for assets/equipment</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Equity Dilution Calculator Example */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">📊 Understanding Equity Dilution</h2>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
                <p className="text-slate-300 mb-6">
                  Every time you raise equity, your ownership % decreases. Here's how dilution works across multiple rounds:
                </p>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-600">
                        <th className="text-left py-3 px-4 text-slate-400 font-semibold">Round</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-semibold">Valuation</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-semibold">Amount Raised</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-semibold">Equity Sold</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-semibold">Founder Ownership</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-300">
                      <tr className="border-b border-slate-700">
                        <td className="py-3 px-4 font-semibold">Start</td>
                        <td className="py-3 px-4">—</td>
                        <td className="py-3 px-4">$0</td>
                        <td className="py-3 px-4">0%</td>
                        <td className="py-3 px-4 text-green-400 font-bold">100%</td>
                      </tr>
                      <tr className="border-b border-slate-700">
                        <td className="py-3 px-4 font-semibold">Friends & Family</td>
                        <td className="py-3 px-4">$1M</td>
                        <td className="py-3 px-4">$100K</td>
                        <td className="py-3 px-4">10%</td>
                        <td className="py-3 px-4 text-green-400 font-bold">90%</td>
                      </tr>
                      <tr className="border-b border-slate-700">
                        <td className="py-3 px-4 font-semibold">Seed Round</td>
                        <td className="py-3 px-4">$5M</td>
                        <td className="py-3 px-4">$1M</td>
                        <td className="py-3 px-4">20%</td>
                        <td className="py-3 px-4 text-yellow-400 font-bold">72%</td>
                      </tr>
                      <tr className="border-b border-slate-700">
                        <td className="py-3 px-4 font-semibold">Series A</td>
                        <td className="py-3 px-4">$20M</td>
                        <td className="py-3 px-4">$5M</td>
                        <td className="py-3 px-4">25%</td>
                        <td className="py-3 px-4 text-yellow-400 font-bold">54%</td>
                      </tr>
                      <tr className="border-b border-slate-700">
                        <td className="py-3 px-4 font-semibold">Series B</td>
                        <td className="py-3 px-4">$60M</td>
                        <td className="py-3 px-4">$15M</td>
                        <td className="py-3 px-4">25%</td>
                        <td className="py-3 px-4 text-orange-400 font-bold">40.5%</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-semibold text-purple-400">Exit at $200M</td>
                        <td className="py-3 px-4 text-purple-400">$200M</td>
                        <td className="py-3 px-4">—</td>
                        <td className="py-3 px-4">—</td>
                        <td className="py-3 px-4 text-purple-400 font-bold">40.5% = $81M</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 grid md:grid-cols-2 gap-4">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">✅ The Win:</p>
                    <p className="text-xs text-slate-300">
                      Founder went from 100% of $0 → 40.5% of $200M = <strong>$81 million</strong>. Dilution was worth it!
                    </p>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-blue-400 mb-2">💡 Key Insight:</p>
                    <p className="text-xs text-slate-300">
                      Better to own 40% of something huge than 100% of something small. Equity is about growing the pie.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Real Examples */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">📈 Real-World Examples</h2>
              
              {/* Example 1: Equipment Success */}
              <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 border border-green-500/20 rounded-xl p-8 mb-6">
                <h3 className="text-2xl font-bold text-green-400 mb-4">Example 1: Equipment Financing Success (Construction Company)</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-white mb-3">The Setup:</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li>• <strong>Business:</strong> Small construction company</li>
                      <li>• <strong>Need:</strong> Excavator to take larger jobs</li>
                      <li>• <strong>Excavator Cost:</strong> $120,000</li>
                      <li>• <strong>Loan Terms:</strong> 7% APR, 5 years</li>
                      <li>• <strong>Monthly Payment:</strong> $2,376</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-3">The Results (Year 1):</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li>• <strong>New contracts won:</strong> $300K/year</li>
                      <li>• <strong>Profit margin:</strong> 25%</li>
                      <li>• <strong>Annual profit:</strong> $75,000</li>
                      <li>• <strong>Loan payments:</strong> -$28,512</li>
                      <li>• <strong>Net gain:</strong> +$46,488</li>
                    </ul>
                    <p className="mt-4 text-green-400 font-semibold">Equipment paid for itself in 1.6 years + continues generating profit!</p>
                  </div>
                </div>
              </div>

              {/* Example 2: Line of Credit Saves Business */}
              <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/20 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-blue-400 mb-4">Example 2: Line of Credit Bridges Cash Flow (Wholesale Business)</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-white mb-3">The Problem:</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li>• <strong>Business:</strong> Wholesale distributor</li>
                      <li>• <strong>Issue:</strong> Customers pay Net 60, supplier wants payment in 30 days</li>
                      <li>• <strong>Gap:</strong> 30-day cash crunch</li>
                      <li>• <strong>Order Size:</strong> $50K inventory needed</li>
                      <li>• <strong>Cash on Hand:</strong> $20K</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-3">The Solution:</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li>• <strong>Line of Credit:</strong> $100K available @ 12% APR</li>
                      <li>• <strong>Drew:</strong> $30K for 45 days</li>
                      <li>• <strong>Interest paid:</strong> $444 (1% monthly rate)</li>
                      <li>• <strong>Profit on order:</strong> $12,000</li>
                      <li>• <strong>Net profit:</strong> $11,556</li>
                    </ul>
                    <p className="mt-4 text-blue-400 font-semibold">Avoided losing $12K deal for just $444 in interest!</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Risks & Warnings */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">⚠️ Business Leverage Risks</h2>
              <div className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-8">
                <div className="flex items-start space-x-4 mb-6">
                  <AlertTriangle className="w-8 h-8 text-red-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-red-400 mb-3">Why Business Loans Go Wrong</h3>
                    <ul className="space-y-3 text-slate-300">
                      <li>• <strong>Personal Guarantee:</strong> You're on the hook personally if business fails</li>
                      <li>• <strong>Over-Leveraging:</strong> Too much debt kills cash flow and flexibility</li>
                      <li>• <strong>Using for Wrong Reasons:</strong> Covering losses instead of generating revenue</li>
                      <li>• <strong>Underestimating Time to ROI:</strong> Takes longer than expected to profit</li>
                      <li>• <strong>Market Changes:</strong> Business conditions shift, making debt unsustainable</li>
                      <li>• <strong>Fixed Payments:</strong> Must pay even in slow months</li>
                    </ul>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-slate-900/50 border border-red-500/30 rounded-lg p-4">
                    <h4 className="font-bold text-red-400 mb-2 text-sm">Debt-to-Income Ratio</h4>
                    <p className="text-xs text-slate-300">
                      Keep total debt payments under 35-40% of monthly revenue. Higher = danger zone.
                    </p>
                  </div>
                  <div className="bg-slate-900/50 border border-red-500/30 rounded-lg p-4">
                    <h4 className="font-bold text-red-400 mb-2 text-sm">Cash Reserve Rule</h4>
                    <p className="text-xs text-slate-300">
                      Have 3-6 months of expenses saved BEFORE taking on debt.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tools & Templates */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">🛠️ Tools & Resources</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <button
                  onClick={() => setCurrentPage('templates')}
                  className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-6 hover:border-purple-500/50 transition-all text-left group"
                >
                  <Building2 className="w-8 h-8 text-purple-400 mb-3" />
                  <h3 className="text-lg font-bold text-white mb-2">Small Business Owner Template</h3>
                  <p className="text-slate-300 text-sm mb-3">
                    Financial planning template for entrepreneurs. Track business expenses, expansion goals, cash flow.
                  </p>
                  <span className="text-purple-400 font-semibold group-hover:text-purple-300">
                    Use Template →
                  </span>
                </button>

                <button
                  onClick={() => setCurrentPage('planning')}
                  className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6 hover:border-blue-500/50 transition-all text-left group"
                >
                  <BarChart3 className="w-8 h-8 text-blue-400 mb-3" />
                  <h3 className="text-lg font-bold text-white mb-2">Financial Planning Calculator</h3>
                  <p className="text-slate-300 text-sm mb-3">
                    Model business growth scenarios, calculate ROI on loans, plan for expansion.
                  </p>
                  <span className="text-blue-400 font-semibold group-hover:text-blue-300">
                    Start Planning →
                  </span>
                </button>
              </div>
            </div>

            {/* Best Practices */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">✅ Best Practices for Business Leverage</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
                  <h3 className="font-bold text-green-400 mb-4 text-lg">DO:</h3>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li>✓ Borrow for revenue-generating assets</li>
                    <li>✓ Have a clear ROI projection</li>
                    <li>✓ Shop multiple lenders for rates</li>
                    <li>✓ Keep 3-6 months cash reserve</li>
                    <li>✓ Track loan covenants carefully</li>
                    <li>✓ Pay down high-interest debt first</li>
                    <li>✓ Use QBI deduction for tax savings</li>
                  </ul>
                </div>

                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
                  <h3 className="font-bold text-red-400 mb-4 text-lg">DON'T:</h3>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li>✗ Borrow to cover operating losses</li>
                    <li>✗ Use merchant cash advances</li>
                    <li>✗ Max out all available credit</li>
                    <li>✗ Skip reading loan agreements</li>
                    <li>✗ Ignore debt-to-income ratios</li>
                    <li>✗ Co-mingle personal & business debt</li>
                    <li>✗ Take on debt without a plan</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Final CTA */}
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-8 text-center">
              <h3 className="text-3xl font-bold mb-4">Ready to Scale Your Business?</h3>
              <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Use our Small Business Owner template to plan your expansion, calculate loan affordability, 
                and manage cash flow as you grow.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => setCurrentPage('templates')}
                  className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg"
                >
                  Use Business Template
                </button>
                <button
                  onClick={() => setCurrentPage('home')}
                  className="px-8 py-4 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
                >
                  Back to Home
                </button>
              </div>
            </div>
          </section>
        )}

        {/* STREET FINANCE: THE REAL FINANCIAL SYSTEM */}
        {currentPage === 'real-finance-system' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero Section with Disclaimer */}
            <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg">
                  <Shield className="w-8 h-8" />
                </div>
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl">🔓</span>
                    <h2 className="text-3xl sm:text-4xl font-bold">Street Finance</h2>
                  </div>
                  <h3 className="text-xl sm:text-2xl text-amber-400 font-semibold mb-2">The Real Financial System</h3>
                  <p className="text-slate-300">Who Controls What & Why It Matters</p>
                </div>
              </div>
              
              {/* Disclaimer */}
              <div className="mt-6 bg-amber-500/10 border-l-4 border-amber-500 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-amber-400 mb-2">⚠️ Educational Purpose Only</h4>
                    <p className="text-xs text-amber-200/90">
                      This content explains how financial systems and power structures operate in practice. 
                      It is provided for educational purposes to help you understand market dynamics, not as 
                      advice to engage in any questionable or illegal activities. Always consult with licensed 
                      professionals for financial and legal advice. StreetFi does not endorse tax evasion, 
                      market manipulation, or any illegal activities.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-r from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-lg p-4">
                <p className="text-sm text-slate-300">
                  <span className="font-bold text-white">"The difference between studying finance and understanding it."</span>
                  <br />
                  <span className="text-slate-400 mt-2 block">
                    Most people study finance. Smart money understands how it really works. 
                    This module teaches what separates people who make money from people who just study it.
                  </span>
                </p>
              </div>
            </div>

            {/* What School Teaches vs Reality */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📚</span>
                What School Teaches vs. What's Real
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Traditional View */}
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <GraduationCap className="w-5 h-5 text-blue-400" />
                    <h4 className="text-lg font-bold text-blue-400">Traditional View</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Markets are efficient and rational</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Price = intrinsic value</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Markets are democratic - everyone has equal access</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Central banks manage the economy through interest rates</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Regulations protect retail investors</span>
                    </li>
                  </ul>
                </div>

                {/* Reality */}
                <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Eye className="w-5 h-5 text-amber-400" />
                    <h4 className="text-lg font-bold text-amber-400">How It Actually Works</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start space-x-2">
                      <span className="text-amber-400 flex-shrink-0">•</span>
                      <span>Markets are shaped by <strong className="text-white">liquidity, power, and information asymmetry</strong></span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-amber-400 flex-shrink-0">•</span>
                      <span>Price = <strong className="text-white">narrative + emotion + liquidity</strong></span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-amber-400 flex-shrink-0">•</span>
                      <span>Markets are controlled by <strong className="text-white">institutions with privileged access</strong></span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-amber-400 flex-shrink-0">•</span>
                      <span>Central banks control everything through <strong className="text-white">liquidity, not rates</strong></span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-amber-400 flex-shrink-0">•</span>
                      <span>Regulations are written by (and for) <strong className="text-white">those who can afford lobbyists</strong></span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Core Concepts */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">🎯</span>
                Core Concepts: The System Behind the System
              </h3>

              <div className="space-y-6">
                {/* Concept 1 */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-amber-400 mb-3 flex items-center">
                    <span className="mr-2">1.</span>
                    Central Banks Control Everything Through Liquidity
                  </h4>
                  <p className="text-slate-300 mb-4">
                    The Federal Reserve doesn't just set interest rates—they control the entire flow of money through the financial system. 
                    When the Fed injects liquidity (QE/money printing), asset prices rise regardless of fundamentals. When they drain it (QT), everything falls.
                  </p>
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                    <p className="text-sm text-amber-200">
                      <strong className="text-amber-400">Reality Check:</strong> The 2020 COVID crash recovery wasn't about the economy recovering—
                      it was about the Fed injecting $4 trillion in liquidity. Stocks went up because money had nowhere else to go.
                    </p>
                  </div>
                </div>

                {/* Concept 2 */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-amber-400 mb-3 flex items-center">
                    <span className="mr-2">2.</span>
                    The Shadow Banking System (The Real Plumbing)
                  </h4>
                  <p className="text-slate-300 mb-4">
                    Most money doesn't flow through traditional banks—it flows through the "shadow banking" system: repo markets, 
                    money market funds, hedge funds, and private credit. This $65+ trillion system operates with less regulation 
                    but more influence than retail banking.
                  </p>
                  <div className="grid md:grid-cols-3 gap-3 text-sm">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3">
                      <div className="font-bold text-blue-400 mb-1">Repo Markets</div>
                      <div className="text-slate-400">Where banks lend to each other overnight</div>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded p-3">
                      <div className="font-bold text-purple-400 mb-1">Eurodollar System</div>
                      <div className="text-slate-400">$13T of USD outside US banks</div>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                      <div className="font-bold text-red-400 mb-1">Private Credit</div>
                      <div className="text-slate-400">$1.7T lending outside banks</div>
                    </div>
                  </div>
                </div>

                {/* Concept 3 */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-amber-400 mb-3 flex items-center">
                    <span className="mr-2">3.</span>
                    Who Actually Moves Markets (Hint: Not Retail)
                  </h4>
                  <p className="text-slate-300 mb-4">
                    Retail investors (you) control less than 10% of market volume. The real players: algorithmic trading firms, 
                    sovereign wealth funds, pension funds, and prime brokers who see order flow before you do.
                  </p>
                  <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-lg p-4">
                    <p className="text-sm text-slate-300">
                      <strong className="text-red-400">Example:</strong> When you buy a stock on Robinhood, your order is sold to 
                      Citadel Securities (payment for order flow). They see your trade <em>before</em> it hits the market, 
                      allowing them to profit on the spread. This is legal.
                    </p>
                  </div>
                </div>

                {/* Concept 4 */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-amber-400 mb-3 flex items-center">
                    <span className="mr-2">4.</span>
                    Liquidity Cycles Matter More Than GDP
                  </h4>
                  <p className="text-slate-300 mb-4">
                    Economic growth is nice, but liquidity is what moves asset prices. When there's abundant liquidity 
                    (low rates + QE), bad companies get funded and stocks soar. When liquidity tightens, even good companies fall.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <div className="font-bold text-green-400 mb-2">High Liquidity Era (2009-2021)</div>
                      <div className="text-sm text-slate-300">
                        • Ultra-low rates<br />
                        • QE (money printing)<br />
                        • Result: Everything goes up<br />
                        • "TINA" - There Is No Alternative
                      </div>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <div className="font-bold text-red-400 mb-2">Low Liquidity Era (2022-2023)</div>
                      <div className="text-sm text-slate-300">
                        • Rising rates<br />
                        • QT (balance sheet reduction)<br />
                        • Result: Asset repricing<br />
                        • Cash suddenly competitive
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Case Studies */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📖</span>
                Case Studies: When Theory Met Reality
              </h3>

              <div className="space-y-6">
                {/* 2008 Crisis */}
                <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-red-400 mb-2">2008 Financial Crisis</h4>
                      <p className="text-sm text-slate-400">The Shadow Banking Collapse</p>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">CRISIS</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">What Happened:</span> The repo market (shadow banking) froze. 
                      Banks stopped trusting each other overnight. $65 trillion in credit disappeared.
                    </div>
                    <div>
                      <span className="font-bold text-white">Why It Matters:</span> The crisis wasn't about mortgages—
                      it was about the interconnected web of leverage in shadow banking. When one piece failed, everything froze.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Fix:</span> Fed injected $4.5 trillion in liquidity (QE). 
                      They didn't fix the system; they flooded it with money to restart the engine.
                    </div>
                  </div>

                  <div className="bg-red-500/10 border-l-4 border-red-500 rounded p-3">
                    <p className="text-xs text-red-200">
                      <strong>Key Lesson:</strong> Credit is the system. When credit freezes, everything stops—regardless of economic fundamentals.
                    </p>
                  </div>
                </div>

                {/* 2020 COVID Response */}
                <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-green-400 mb-2">2020 COVID Response</h4>
                      <p className="text-sm text-slate-400">The Liquidity Bazooka</p>
                    </div>
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">INTERVENTION</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">What Happened:</span> Markets crashed 35% in March 2020. 
                      Fed responded with unlimited QE, buying $120B/month in assets. Congress added $5 trillion in fiscal stimulus.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Result:</span> Fastest recovery in history. S&P 500 hit all-time highs 
                      within 5 months while unemployment was 10%+. Why? Liquidity.
                    </div>
                    <div>
                      <span className="font-bold text-white">What It Proved:</span> Markets don't price fundamentals—they price liquidity. 
                      When money is abundant, assets rise. Simple as that.
                    </div>
                  </div>

                  <div className="bg-green-500/10 border-l-4 border-green-500 rounded p-3">
                    <p className="text-xs text-green-200">
                      <strong>Key Lesson:</strong> "Don't fight the Fed" isn't a meme—it's the golden rule. Follow the liquidity, not the headlines.
                    </p>
                  </div>
                </div>

                {/* 2022-2023 Tightening */}
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-blue-400 mb-2">2022-2023 Tightening Cycle</h4>
                      <p className="text-sm text-slate-400">When Liquidity Dries Up</p>
                    </div>
                    <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded-full">REPRICING</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">What Happened:</span> Fed raised rates from 0% to 5.5% in 18 months—
                      the fastest tightening cycle in history. Simultaneously ran QT (draining liquidity).
                    </div>
                    <div>
                      <span className="font-bold text-white">The Casualties:</span> Tech stocks down 40-80%. Crypto crashed. 
                      Unprofitable growth companies destroyed. SVB collapsed. $3 trillion wiped out.
                    </div>
                    <div>
                      <span className="font-bold text-white">What It Showed:</span> When liquidity disappears, valuations mean nothing. 
                      Assets get repriced based on what capital is available, not what they're "worth."
                    </div>
                  </div>

                  <div className="bg-blue-500/10 border-l-4 border-blue-500 rounded p-3">
                    <p className="text-xs text-blue-200">
                      <strong>Key Lesson:</strong> High liquidity hides all sins. Low liquidity exposes all weaknesses. Know which regime you're in.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Resources Section */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📚</span>
                Recommended Resources
              </h3>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Books */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <BookOpen className="w-5 h-5 text-emerald-400" />
                    <h4 className="font-bold text-emerald-400">Books</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">The Lords of Easy Money</div>
                      <div className="text-xs text-slate-400">Chris Leonard</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">When Genius Failed</div>
                      <div className="text-xs text-slate-400">Roger Lowenstein (LTCM)</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">The Big Short</div>
                      <div className="text-xs text-slate-400">Michael Lewis</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">The Alchemy of Finance</div>
                      <div className="text-xs text-slate-400">George Soros</div>
                    </li>
                  </ul>
                </div>

                {/* Documentaries */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <FileText className="w-5 h-5 text-blue-400" />
                    <h4 className="font-bold text-blue-400">Documentaries</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Inside Job (2010)</div>
                      <div className="text-xs text-slate-400">2008 crisis breakdown</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Panic: The Untold Story</div>
                      <div className="text-xs text-slate-400">HBO (Fed response 2008)</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Money & Power</div>
                      <div className="text-xs text-slate-400">Wall Street history</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">Enron: Smartest Guys</div>
                      <div className="text-xs text-slate-400">Corporate fraud case study</div>
                    </li>
                  </ul>
                </div>

                {/* Research & Analysis */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <TrendingUp className="w-5 h-5 text-purple-400" />
                    <h4 className="font-bold text-purple-400">Follow the Data</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Federal Reserve Data</div>
                      <div className="text-xs text-slate-400">FRED (St. Louis Fed)</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">BIS Quarterly Review</div>
                      <div className="text-xs text-slate-400">Global credit data</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Shadow Banking Monitor</div>
                      <div className="text-xs text-slate-400">FSB Reports</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">Repo Market Data</div>
                      <div className="text-xs text-slate-400">NY Fed</div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Practical Application */}
            <div className="mb-12 bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center text-amber-400">
                <span className="mr-3">💡</span>
                How This Affects Your Trading & Investing
              </h3>

              <div className="space-y-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">1. Follow Liquidity, Not Headlines</h4>
                  <p className="text-sm text-slate-300">
                    When the Fed is expanding its balance sheet (QE), buy risk assets. When they're contracting (QT), 
                    reduce exposure. Liquidity drives everything.
                  </p>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">2. Don't Fight the Fed</h4>
                  <p className="text-slate-300 text-sm">
                    This isn't just a saying—it's the most important rule in markets. When the Fed wants asset prices higher 
                    (loose policy), they go higher. When they want them lower (tight policy), they go lower. Trade with them, not against them.
                  </p>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">3. Think Like an Allocator, Not a Stock Picker</h4>
                  <p className="text-sm text-slate-300">
                    Retail investors pick stocks. Sophisticated investors allocate capital based on where liquidity is flowing. 
                    Ask: "Where is money moving?" not "What's undervalued?"
                  </p>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">4. Understand the Regime You're In</h4>
                  <p className="text-sm text-slate-300">
                    High liquidity = buy growth, risk-on, leverage up. Low liquidity = preserve capital, quality over growth, 
                    reduce leverage. The rules change based on the environment.
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom Navigation */}
            <div className="flex flex-wrap justify-center gap-4 pt-8 border-t border-slate-700">
              <button
                onClick={() => setCurrentPage('glossary')}
                className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
              >
                📚 Trading Glossary
              </button>
              <button
                onClick={() => setCurrentPage('tax')}
                className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
              >
                💰 Tax Strategies
              </button>
              <button
                onClick={() => setCurrentPage('home')}
                className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-600 transition-all"
              >
                Back to Home
              </button>
            </div>
          </section>
        )}

        {/* STREET FINANCE MODULE 2: WEALTH GAMES & POWER STRUCTURES */}
        {currentPage === 'wealth-games' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero Section with Disclaimer */}
            <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg">
                  <Target className="w-8 h-8" />
                </div>
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl">🔓</span>
                    <h2 className="text-3xl sm:text-4xl font-bold">Street Finance</h2>
                  </div>
                  <h3 className="text-xl sm:text-2xl text-emerald-400 font-semibold mb-2">Wealth Games & Power Structures</h3>
                  <p className="text-slate-300">How The Wealthy Actually Build & Protect Wealth</p>
                </div>
              </div>
              
              {/* Disclaimer */}
              <div className="mt-6 bg-amber-500/10 border-l-4 border-amber-500 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-amber-400 mb-2">⚠️ Educational Purpose Only</h4>
                    <p className="text-xs text-amber-200/90">
                      This content explains wealth-building strategies used by high-net-worth individuals and institutions. 
                      It is provided for educational purposes only. Always consult licensed tax, legal, and financial professionals 
                      before implementing any strategies. StreetFi does not provide financial, tax, or legal advice and does 
                      not endorse tax evasion or illegal activities.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-r from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-lg p-4">
                <p className="text-sm text-slate-300">
                  <span className="font-bold text-white">"Work hard, save, diversify" vs. "Leverage, entities, tax code arbitrage"</span>
                  <br />
                  <span className="text-slate-400 mt-2 block">
                    The wealthy don't just earn more—they play a different game entirely. This module reveals the structural 
                    advantages that separate those who build generational wealth from those who trade time for money.
                  </span>
                </p>
              </div>
            </div>

            {/* What School Teaches vs Reality */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📚</span>
                What School Teaches vs. The Wealth Game
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Traditional Advice */}
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <GraduationCap className="w-5 h-5 text-blue-400" />
                    <h4 className="text-lg font-bold text-blue-400">Traditional Advice</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Work hard, get promoted, save 10-15%</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Max out your 401(k), invest in index funds</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Avoid debt - pay off your mortgage early</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Diversify across 60/40 stocks/bonds</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Pay your taxes, be a good citizen</span>
                    </li>
                  </ul>
                </div>

                {/* The Wealth Game */}
                <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Target className="w-5 h-5 text-emerald-400" />
                    <h4 className="text-lg font-bold text-emerald-400">How Wealth Actually Compounds</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start space-x-2">
                      <span className="text-emerald-400 flex-shrink-0">•</span>
                      <span><strong className="text-white">Own assets that appreciate</strong> (business, real estate, equity)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-emerald-400 flex-shrink-0">•</span>
                      <span><strong className="text-white">Use entities</strong> (LLCs, trusts) to shield wealth from taxes</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-emerald-400 flex-shrink-0">•</span>
                      <span><strong className="text-white">Leverage debt intelligently</strong> - borrow against assets at low rates</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-emerald-400 flex-shrink-0">•</span>
                      <span><strong className="text-white">Concentrate wealth</strong> in high-conviction bets, diversify later</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-emerald-400 flex-shrink-0">•</span>
                      <span><strong className="text-white">Tax arbitrage legally</strong> - depreciation, 1031, carried interest</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-sm text-red-200">
                  <strong className="text-red-400">The Truth:</strong> Wages are designed to keep you comfortable, not wealthy. 
                  Real wealth comes from <strong>owning assets</strong> (equity, real estate, businesses) that appreciate faster than 
                  inflation and using the tax code's <strong>legal loopholes</strong> to compound returns.
                </p>
              </div>
            </div>

            {/* Core Wealth Strategies */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">🎯</span>
                Core Wealth Strategies: The Playbook
              </h3>

              <div className="space-y-6">
                {/* Strategy 1: Entity Structuring */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-emerald-400 mb-3 flex items-center">
                    <span className="mr-2">1.</span>
                    Entity Structuring: The Wealth Shield
                  </h4>
                  <p className="text-slate-300 mb-4">
                    The wealthy don't hold assets personally—they use legal entities (LLCs, S-Corps, trusts, foundations) to 
                    separate ownership, reduce liability, and optimize taxes. This isn't tax evasion; it's using the system as designed.
                  </p>
                  
                  <div className="grid md:grid-cols-3 gap-4 mb-4">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <h5 className="font-bold text-blue-400 mb-2 text-sm">LLC (Limited Liability Co.)</h5>
                      <p className="text-xs text-slate-400 mb-2">Shields personal assets from business liabilities</p>
                      <p className="text-xs text-slate-300"><strong>Use:</strong> Real estate, side businesses</p>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                      <h5 className="font-bold text-purple-400 mb-2 text-sm">Revocable Living Trust</h5>
                      <p className="text-xs text-slate-400 mb-2">Avoids probate, maintains control during life</p>
                      <p className="text-xs text-slate-300"><strong>Use:</strong> Estate planning, privacy</p>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <h5 className="font-bold text-green-400 mb-2 text-sm">Private Foundation</h5>
                      <p className="text-xs text-slate-400 mb-2">Tax-deductible giving with family control</p>
                      <p className="text-xs text-slate-300"><strong>Use:</strong> Ultra high net worth ($10M+)</p>
                    </div>
                  </div>

                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                    <p className="text-sm text-emerald-200">
                      <strong className="text-emerald-400">Real Example:</strong> A $500K real estate portfolio held personally 
                      = you're personally liable for lawsuits. Same portfolio in an LLC = liability limited to the entity's assets. 
                      Plus you can deduct entity expenses and depreciate the property.
                    </p>
                  </div>
                </div>

                {/* Strategy 2: Leverage Game */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-emerald-400 mb-3 flex items-center">
                    <span className="mr-2">2.</span>
                    The Leverage Game: Borrow Against Appreciating Assets
                  </h4>
                  <p className="text-slate-300 mb-4">
                    The wealthy rarely sell assets (triggering capital gains taxes). Instead, they <strong>borrow against them</strong> 
                    at low interest rates, using the loans as tax-free income while assets continue appreciating.
                  </p>

                  <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <h5 className="font-bold text-blue-400 mb-3">The Buy-Borrow-Die Strategy</h5>
                    <div className="space-y-2 text-sm text-slate-300">
                      <div className="flex items-start space-x-2">
                        <span className="text-blue-400 font-bold">1. BUY:</span>
                        <span>Acquire appreciating assets (stocks, real estate, businesses)</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-purple-400 font-bold">2. BORROW:</span>
                        <span>Take loans against assets (1-3% interest, tax-free)</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-pink-400 font-bold">3. DIE:</span>
                        <span>Assets pass to heirs with stepped-up basis (no capital gains tax)</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                      <div className="text-sm font-bold text-red-400 mb-1">❌ Wage Earner Path</div>
                      <div className="text-xs text-slate-300">
                        • Earn $200K salary<br />
                        • Pay 35% taxes = $130K after-tax<br />
                        • Spend $100K, save $30K<br />
                        • Invest savings, pay taxes on gains
                      </div>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                      <div className="text-sm font-bold text-green-400 mb-1">✅ Wealth Builder Path</div>
                      <div className="text-xs text-slate-300">
                        • Own $10M in stocks<br />
                        • Borrow $500K at 3% ($15K interest)<br />
                        • $485K tax-free to spend<br />
                        • Assets appreciate 8% = $800K gain (unrealized)
                      </div>
                    </div>
                  </div>
                </div>

                {/* Strategy 3: Tax Arbitrage */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-emerald-400 mb-3 flex items-center">
                    <span className="mr-2">3.</span>
                    Tax Code Arbitrage: Legal Loopholes
                  </h4>
                  <p className="text-slate-300 mb-4">
                    The U.S. tax code has ~10,000 pages of rules—many designed to incentivize specific behaviors. 
                    The wealthy use these <strong>legal strategies</strong> to reduce effective tax rates dramatically.
                  </p>

                  <div className="space-y-3">
                    <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="font-bold text-white">Real Estate Depreciation</h5>
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">LEGAL</span>
                      </div>
                      <p className="text-sm text-slate-300 mb-2">
                        Deduct ~3.6% of property value annually (27.5 years residential) even if property appreciates.
                      </p>
                      <div className="text-xs text-slate-400">
                        <strong>Example:</strong> $1M property → $36K annual deduction → saves $12K in taxes (33% bracket)
                      </div>
                    </div>

                    <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="font-bold text-white">1031 Exchange (Like-Kind Exchange)</h5>
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">LEGAL</span>
                      </div>
                      <p className="text-sm text-slate-300 mb-2">
                        Sell investment property, buy another within 180 days → defer ALL capital gains taxes indefinitely.
                      </p>
                      <div className="text-xs text-slate-400">
                        <strong>Example:</strong> Sell $2M property (bought for $500K) → $1.5M gain → $300K tax... OR 1031 into new property, pay $0 now
                      </div>
                    </div>

                    <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="font-bold text-white">Qualified Small Business Stock (QSBS)</h5>
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">LEGAL</span>
                      </div>
                      <p className="text-sm text-slate-300 mb-2">
                        Hold startup stock 5+ years → up to $10M in gains are 100% tax-free (Section 1202).
                      </p>
                      <div className="text-xs text-slate-400">
                        <strong>Example:</strong> Early employee with $50K → $10M → normally $2.4M in taxes → with QSBS: $0
                      </div>
                    </div>

                    <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="font-bold text-white">Carried Interest (Private Equity)</h5>
                        <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded">CONTROVERSIAL</span>
                      </div>
                      <p className="text-sm text-slate-300 mb-2">
                        PE fund managers pay 20% capital gains rate on "performance fees" instead of 37% ordinary income.
                      </p>
                      <div className="text-xs text-slate-400">
                        <strong>Example:</strong> $10M performance fee → normally $3.7M tax → with carried interest: $2M → saves $1.7M
                      </div>
                    </div>
                  </div>
                </div>

                {/* Strategy 4: Asset vs Income */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-emerald-400 mb-3 flex items-center">
                    <span className="mr-2">4.</span>
                    Own Assets, Not Income: The Fundamental Shift
                  </h4>
                  <p className="text-slate-300 mb-4">
                    Wages = highest tax rate (up to 37%). Capital gains = 20% max. Unrealized gains = 0%. 
                    The game is simple: <strong>convert labor income into equity ownership as fast as possible.</strong>
                  </p>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg p-4">
                      <h5 className="font-bold text-orange-400 mb-3">Wealth from Income (Slow)</h5>
                      <div className="space-y-2 text-sm text-slate-300">
                        <div>• Doctor earns $400K/year</div>
                        <div>• Pays $140K in taxes (35%)</div>
                        <div>• Lives on $200K, saves $60K</div>
                        <div>• After 20 years: ~$2M net worth</div>
                        <div className="text-xs text-red-300 mt-2">⏱️ Time-for-money treadmill</div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-4">
                      <h5 className="font-bold text-green-400 mb-3">Wealth from Assets (Fast)</h5>
                      <div className="space-y-2 text-sm text-slate-300">
                        <div>• Founder owns 10% of startup</div>
                        <div>• Company exits for $50M</div>
                        <div>• Founder's share: $5M</div>
                        <div>• After QSBS: Keep ~$4.5M tax-free</div>
                        <div className="text-xs text-green-300 mt-2">🚀 Leverage & equity ownership</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 bg-emerald-500/10 border-l-4 border-emerald-500 rounded p-3">
                    <p className="text-xs text-emerald-200">
                      <strong>Key Insight:</strong> Every hour you trade for money is a dead end. Every hour you invest in 
                      building equity (startup, business, real estate, portfolio) compounds forever.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Case Studies */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📖</span>
                Case Studies: How The Game Is Played
              </h3>

              <div className="space-y-6">
                {/* Trump Real Estate */}
                <div className="bg-gradient-to-br from-orange-500/10 to-amber-500/10 border border-orange-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-orange-400 mb-2">Trump's Real Estate Empire</h4>
                      <p className="text-sm text-slate-400">Depreciation + Leverage = Tax-Free Cash Flow</p>
                    </div>
                    <span className="px-3 py-1 bg-orange-500/20 text-orange-400 text-xs font-bold rounded-full">REAL ESTATE</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">The Strategy:</span> Buy commercial real estate with debt (leverage), 
                      depreciate the buildings aggressively, offset rental income with depreciation to pay minimal taxes, refinance 
                      to pull out equity tax-free, repeat.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Numbers:</span> Trump famously paid $750 in federal income taxes 
                      in 2016-2017 despite living lavishly. How? Massive depreciation deductions from his real estate holdings 
                      legally offset his income.
                    </div>
                    <div>
                      <span className="font-bold text-white">Why It Works:</span> Real estate depreciation is one of the most powerful 
                      legal tax shelters in the U.S. code. You can show a "loss" on paper (depreciation) while actually cash flowing positive.
                    </div>
                  </div>

                  <div className="bg-orange-500/10 border-l-4 border-orange-500 rounded p-3">
                    <p className="text-xs text-orange-200">
                      <strong>Key Lesson:</strong> Real estate isn't just about appreciation—it's about using the tax code to 
                      generate tax-free income through depreciation and refinancing.
                    </p>
                  </div>
                </div>

                {/* Elon Musk Borrowing */}
                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-purple-400 mb-2">Elon Musk's $0 Salary, Billions in Spending</h4>
                      <p className="text-sm text-slate-400">Buy, Borrow, Die in Action</p>
                    </div>
                    <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-xs font-bold rounded-full">LEVERAGE</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">The Setup:</span> Musk owns ~13% of Tesla (worth $80B+ at peak). 
                      He takes $0 salary, avoiding income taxes entirely.
                    </div>
                    <div>
                      <span className="font-bold text-white">How He Lives:</span> Borrows against his Tesla shares at 1-3% interest 
                      through securities-backed lines of credit (SBLOC). Loans are not taxable income.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Math:</span> Borrow $500M at 2% ($10M interest) vs. selling shares 
                      and paying 20% capital gains ($100M). He pays 1/10th the cost and keeps his equity stake growing.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Endgame:</span> His shares continue appreciating. When he dies, 
                      heirs get stepped-up basis (no capital gains). Generational wealth transferred tax-efficiently.
                    </div>
                  </div>

                  <div className="bg-purple-500/10 border-l-4 border-purple-500 rounded p-3">
                    <p className="text-xs text-purple-200">
                      <strong>Key Lesson:</strong> Never sell appreciating assets if you can borrow against them. Interest is 
                      cheaper than taxes, and you keep the upside.
                    </p>
                  </div>
                </div>

                {/* Private Equity Tax Advantages */}
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-blue-400 mb-2">Private Equity's Carried Interest Loophole</h4>
                      <p className="text-sm text-slate-400">Labor Income Taxed as Capital Gains</p>
                    </div>
                    <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded-full">TAX CODE</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">How It Works:</span> PE fund managers take 20% of profits (carried interest) 
                      as compensation. Instead of being taxed as income (37%), it's taxed as long-term capital gains (20%).
                    </div>
                    <div>
                      <span className="font-bold text-white">The Numbers:</span> A PE manager making $50M/year in carried interest 
                      pays $10M in taxes (20%) instead of $18.5M (37%). Saves $8.5M annually.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Controversy:</span> Critics call it a loophole—managers aren't 
                      investing their own capital, they're managing other people's money. But it's 100% legal.
                    </div>
                    <div>
                      <span className="font-bold text-white">Why It Persists:</span> Private equity has massive lobbying power. 
                      They've successfully defended this provision for decades despite reform attempts.
                    </div>
                  </div>

                  <div className="bg-blue-500/10 border-l-4 border-blue-500 rounded p-3">
                    <p className="text-xs text-blue-200">
                      <strong>Key Lesson:</strong> Tax rates aren't fixed—they're negotiated by those with political capital. 
                      The rules favor those who can afford to shape them.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Political Capital Section */}
            <div className="mb-12 bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center text-red-400">
                <span className="mr-3">🏛️</span>
                The Role of Political Capital
              </h3>

              <p className="text-slate-300 mb-6">
                The wealthy don't just play within the rules—they <strong>influence the rules.</strong> Through lobbying, 
                campaign donations, and policy advocacy, they shape tax codes, regulations, and opportunities in their favor.
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-3">How It Works</h4>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li>• <strong>Lobbying:</strong> $3.7B spent annually to influence legislation</li>
                    <li>• <strong>Campaign Finance:</strong> Billionaires fund candidates who protect their interests</li>
                    <li>• <strong>Regulatory Capture:</strong> Industry insiders write the rules they'll follow</li>
                    <li>• <strong>Tax Code Complexity:</strong> Benefits those who can afford expert advisors</li>
                  </ul>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-3">Recent Examples</h4>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li>• <strong>2017 Tax Cuts:</strong> Corporate rate 35% → 21%, estate tax doubled</li>
                    <li>• <strong>Carried Interest:</strong> Defended successfully for 15+ years</li>
                    <li>• <strong>Opportunity Zones:</strong> Real estate tax breaks in designated areas</li>
                    <li>• <strong>SALT Cap Repeal:</strong> Ongoing lobbying by high-net-worth individuals</li>
                  </ul>
                </div>
              </div>

              <div className="bg-red-500/10 border-l-4 border-red-500 rounded p-4">
                <p className="text-sm text-red-200">
                  <strong className="text-red-400">Reality Check:</strong> You don't need to lobby Congress, but understanding 
                  that the game is tilted helps you play smarter within it. Focus on what you CAN control: entity structuring, 
                  tax-efficient investing, and building equity ownership.
                </p>
              </div>
            </div>

            {/* Practical Application */}
            <div className="mb-12 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center text-emerald-400">
                <span className="mr-3">💡</span>
                How to Apply This (Even Without Billions)
              </h3>

              <div className="space-y-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">1. Start Building Equity Ownership Early</h4>
                  <p className="text-sm text-slate-300">
                    Join startups with equity compensation, invest in index funds, buy rental property, or start a side business. 
                    Every dollar in equity ownership compounds. Every dollar in wages dies when spent.
                  </p>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">2. Use Tax-Advantaged Accounts Aggressively</h4>
                  <p className="text-sm text-slate-300">
                    Max 401(k), Roth IRA, HSA, 529. These aren't sexy, but they're legal tax shelters for ordinary people. 
                    A $6K Roth IRA contribution → $100K tax-free in 30 years beats paying taxes on gains.
                  </p>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">3. Consider Entity Structuring for Side Income</h4>
                  <p className="text-sm text-slate-300">
                    If you have side income ($25K+), an LLC or S-Corp can save thousands in self-employment taxes and enable 
                    deductions unavailable to W-2 employees. Consult a CPA—this pays for itself.
                  </p>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">4. Learn the 1031 Exchange for Real Estate</h4>
                  <p className="text-sm text-slate-300">
                    If you invest in rental property, the 1031 exchange lets you defer capital gains indefinitely. 
                    Trade up properties over decades, building wealth while paying $0 in capital gains taxes.
                  </p>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">5. Think Long-Term Capital Gains, Not Short-Term</h4>
                  <p className="text-sm text-slate-300">
                    Hold investments 1+ year → 20% max tax rate vs. 37% short-term. That 17% difference compounds massively 
                    over a lifetime. Patience is literally profitable.
                  </p>
                </div>
              </div>
            </div>

            {/* Resources */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📚</span>
                Recommended Resources
              </h3>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Books */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <BookOpen className="w-5 h-5 text-emerald-400" />
                    <h4 className="font-bold text-emerald-400">Books</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Tax-Free Wealth</div>
                      <div className="text-xs text-slate-400">Tom Wheelwright</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Rich Dad Poor Dad</div>
                      <div className="text-xs text-slate-400">Robert Kiyosaki</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">The Millionaire Next Door</div>
                      <div className="text-xs text-slate-400">Thomas Stanley</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">Trusts & Estates Guide</div>
                      <div className="text-xs text-slate-400">Various legal publishers</div>
                    </li>
                  </ul>
                </div>

                {/* Professional Services */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Target className="w-5 h-5 text-blue-400" />
                    <h4 className="font-bold text-blue-400">Professional Help</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">CPA (Tax Strategist)</div>
                      <div className="text-xs text-slate-400">Essential for entity structuring</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Estate Planning Attorney</div>
                      <div className="text-xs text-slate-400">Trusts, wills, asset protection</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Fee-Only Financial Planner</div>
                      <div className="text-xs text-slate-400">Unbiased advice (CFP®)</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">Real Estate CPA</div>
                      <div className="text-xs text-slate-400">For depreciation & 1031s</div>
                    </li>
                  </ul>
                </div>

                {/* Online Resources */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Globe className="w-5 h-5 text-purple-400" />
                    <h4 className="font-bold text-purple-400">Online Learning</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">IRS.gov Tax Publications</div>
                      <div className="text-xs text-slate-400">Official tax code guidance</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Bigger Pockets (Real Estate)</div>
                      <div className="text-xs text-slate-400">Forum & education</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">White Coat Investor</div>
                      <div className="text-xs text-slate-400">Tax strategies for high earners</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">ChooseFI Podcast</div>
                      <div className="text-xs text-slate-400">Financial independence strategies</div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Bottom Navigation */}
            <div className="flex flex-wrap justify-center gap-4 pt-8 border-t border-slate-700">
              <button
                onClick={() => setCurrentPage('real-finance-system')}
                className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
              >
                ← Module 1: Real Financial System
              </button>
              <button
                onClick={() => setCurrentPage('tax')}
                className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
              >
                💰 View 38 Tax Strategies
              </button>
              <button
                onClick={() => setCurrentPage('home')}
                className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg font-semibold hover:from-emerald-600 hover:to-teal-600 transition-all"
              >
                Back to Home
              </button>
            </div>
          </section>
        )}

        {/* STREET FINANCE MODULE 3: MARKET PSYCHOLOGY DEEP DIVE */}
        {currentPage === 'market-psychology' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero Section with Disclaimer */}
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg">
                  <TrendingUp className="w-8 h-8" />
                </div>
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl">🔓</span>
                    <h2 className="text-3xl sm:text-4xl font-bold">Street Finance</h2>
                  </div>
                  <h3 className="text-xl sm:text-2xl text-purple-400 font-semibold mb-2">Market Psychology Deep Dive</h3>
                  <p className="text-slate-300">Why Markets Are Emotion Machines, Not Calculators</p>
                </div>
              </div>
              
              {/* Disclaimer */}
              <div className="mt-6 bg-amber-500/10 border-l-4 border-amber-500 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-amber-400 mb-2">⚠️ Educational Purpose Only</h4>
                    <p className="text-xs text-amber-200/90">
                      This content explains market psychology, behavioral patterns, and crowd dynamics for educational purposes. 
                      Understanding how markets move is not the same as being able to predict them. All investing involves risk. 
                      Past performance does not guarantee future results. Always do your own research and consult licensed professionals.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-r from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-lg p-4">
                <p className="text-sm text-slate-300">
                  <span className="font-bold text-white">"Price = intrinsic value" vs. "Price = narrative + emotion + liquidity"</span>
                  <br />
                  <span className="text-slate-400 mt-2 block">
                    Markets don't price assets rationally—they price them based on stories, fear, greed, and herd behavior. 
                    Understanding this psychology is how you avoid getting swept up in manias and crushed in crashes.
                  </span>
                </p>
              </div>
            </div>

            {/* What School Teaches vs Reality */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📚</span>
                What School Teaches vs. How Markets Actually Work
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Traditional View */}
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <GraduationCap className="w-5 h-5 text-blue-400" />
                    <h4 className="text-lg font-bold text-blue-400">Efficient Market Hypothesis</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Markets are rational and efficient</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Price always equals intrinsic value</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>All information is instantly reflected in prices</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Investors make logical, data-driven decisions</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Bubbles and crashes are rare anomalies</span>
                    </li>
                  </ul>
                </div>

                {/* Reality */}
                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Eye className="w-5 h-5 text-purple-400" />
                    <h4 className="text-lg font-bold text-purple-400">Market Psychology Reality</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start space-x-2">
                      <span className="text-purple-400 flex-shrink-0">•</span>
                      <span>Markets are <strong className="text-white">emotional herd behavior</strong> machines</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-purple-400 flex-shrink-0">•</span>
                      <span>Price = <strong className="text-white">narrative + fear + greed + liquidity</strong></span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-purple-400 flex-shrink-0">•</span>
                      <span>Information is <strong className="text-white">interpreted through emotional bias</strong></span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-purple-400 flex-shrink-0">•</span>
                      <span>Investors are <strong className="text-white">predictably irrational</strong> (FOMO, panic)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-purple-400 flex-shrink-0">•</span>
                      <span>Bubbles and crashes are <strong className="text-white">recurring patterns</strong></span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-sm text-red-200">
                  <strong className="text-red-400">The Truth:</strong> Markets are not calculators—they're crowds. And crowds panic, 
                  stampede, follow leaders, and alternate between euphoria and despair. Understanding this psychology is the difference 
                  between buying at the top (pain) and buying at the bottom (profit).
                </p>
              </div>
            </div>

            {/* Core Concepts */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">🧠</span>
                Core Concepts: The Psychology Playbook
              </h3>

              <div className="space-y-6">
                {/* Concept 1: Reflexivity */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-purple-400 mb-3 flex items-center">
                    <span className="mr-2">1.</span>
                    Reflexivity: Markets Create Their Own Reality
                  </h4>
                  <p className="text-slate-300 mb-4">
                    George Soros's theory: Markets don't just <em>reflect</em> reality—they <strong>shape it</strong>. 
                    When investors believe something will happen, their actions often make it happen (self-fulfilling prophecy). 
                    This creates feedback loops that drive prices far from "fair value."
                  </p>
                  
                  <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <h5 className="font-bold text-blue-400 mb-3">The Reflexivity Loop</h5>
                    <div className="space-y-2 text-sm text-slate-300">
                      <div className="flex items-start space-x-2">
                        <span className="text-blue-400 font-bold">1.</span>
                        <span>Stock price rises → Company seems successful</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-purple-400 font-bold">2.</span>
                        <span>Media writes positive stories → More buyers</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-pink-400 font-bold">3.</span>
                        <span>Company uses high stock price to raise cheap capital</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-red-400 font-bold">4.</span>
                        <span>Company actually becomes more successful (temporarily)</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-orange-400 font-bold">5.</span>
                        <span>Loop continues until unsustainable → crash</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-500/10 border-l-4 border-purple-500 rounded p-3">
                    <p className="text-xs text-purple-200">
                      <strong>Key Insight:</strong> Price movements aren't just measuring value—they're <strong>creating</strong> value 
                      (and destroying it). The narrative follows the price, not the other way around.
                    </p>
                  </div>
                </div>

                {/* Concept 2: Narrative > Fundamentals */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-purple-400 mb-3 flex items-center">
                    <span className="mr-2">2.</span>
                    Markets Are Story Machines, Not Valuation Machines
                  </h4>
                  <p className="text-slate-300 mb-4">
                    Humans don't buy spreadsheets—they buy <strong>stories</strong>. The most powerful force in markets isn't data; 
                    it's the narrative that makes sense of the data. Whoever controls the narrative controls the price.
                  </p>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <h5 className="font-bold text-red-400 mb-2">Weak Narrative (Ignored)</h5>
                      <div className="text-sm text-slate-300 space-y-2">
                        <div>• Company has solid fundamentals</div>
                        <div>• Growing revenue, decent margins</div>
                        <div>• No compelling story</div>
                        <div className="text-red-300 font-semibold mt-2">→ Stock flat or declines</div>
                      </div>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <h5 className="font-bold text-green-400 mb-2">Strong Narrative (Rockets)</h5>
                      <div className="text-sm text-slate-300 space-y-2">
                        <div>• Company has compelling story</div>
                        <div>• "AI will change everything"</div>
                        <div>• Mediocre fundamentals don't matter</div>
                        <div className="text-green-300 font-semibold mt-2">→ Stock soars 10x</div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-slate-300 mb-4">
                    <div className="bg-slate-900/50 border border-slate-600 rounded p-3">
                      <strong className="text-white">2020-2021 Tech Narrative:</strong> "Zero rates forever + digital transformation + 
                      stay-at-home = tech stocks to the moon" → Zoom went from $60 to $580
                    </div>
                    <div className="bg-slate-900/50 border border-slate-600 rounded p-3">
                      <strong className="text-white">2022 Narrative Shift:</strong> "Inflation + rate hikes + recession fears = 
                      growth stocks are overvalued" → Zoom crashed to $80
                    </div>
                  </div>

                  <div className="bg-purple-500/10 border-l-4 border-purple-500 rounded p-3">
                    <p className="text-xs text-purple-200">
                      <strong>Key Insight:</strong> Facts don't matter until they fit a narrative. The same company can be "visionary" 
                      one year and "overvalued" the next—not because it changed, but because the <strong>story</strong> changed.
                    </p>
                  </div>
                </div>

                {/* Concept 3: FOMO & Panic */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-purple-400 mb-3 flex items-center">
                    <span className="mr-2">3.</span>
                    FOMO and Panic: The Two Market Emotions
                  </h4>
                  <p className="text-slate-300 mb-4">
                    Markets swing between two extremes: <strong>Fear of Missing Out</strong> (greed) and <strong>Panic</strong> (fear). 
                    Most retail investors buy during FOMO (tops) and sell during panic (bottoms)—the exact opposite of "buy low, sell high."
                  </p>

                  <div className="bg-gradient-to-r from-green-500/10 to-red-500/10 border border-slate-600 rounded-lg p-4 mb-4">
                    <h5 className="font-bold text-white mb-3 text-center">The Emotion Cycle</h5>
                    <div className="grid md:grid-cols-5 gap-2 text-xs text-center">
                      <div className="bg-green-500/20 border border-green-500/50 rounded p-2">
                        <div className="font-bold text-green-400">1. Optimism</div>
                        <div className="text-slate-400 mt-1">"This is going up!"</div>
                      </div>
                      <div className="bg-blue-500/20 border border-blue-500/50 rounded p-2">
                        <div className="font-bold text-blue-400">2. Excitement</div>
                        <div className="text-slate-400 mt-1">"Everyone's buying!"</div>
                      </div>
                      <div className="bg-purple-500/20 border border-purple-500/50 rounded p-2">
                        <div className="font-bold text-purple-400">3. Euphoria</div>
                        <div className="text-slate-400 mt-1">"I'm a genius!" 🚨 TOP</div>
                      </div>
                      <div className="bg-orange-500/20 border border-orange-500/50 rounded p-2">
                        <div className="font-bold text-orange-400">4. Anxiety</div>
                        <div className="text-slate-400 mt-1">"Wait, why falling?"</div>
                      </div>
                      <div className="bg-red-500/20 border border-red-500/50 rounded p-2">
                        <div className="font-bold text-red-400">5. Panic</div>
                        <div className="text-slate-400 mt-1">"SELL EVERYTHING!" 🚨 BOTTOM</div>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <h5 className="font-bold text-green-400 mb-2">FOMO Phase (Tops)</h5>
                      <ul className="space-y-1 text-sm text-slate-300">
                        <li>• "Everyone's making money but me"</li>
                        <li>• "This time is different"</li>
                        <li>• "I'll just hold forever"</li>
                        <li>• Media: "New paradigm!"</li>
                        <li className="text-red-300 font-semibold pt-2">→ Retail buys at peak</li>
                      </ul>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <h5 className="font-bold text-red-400 mb-2">Panic Phase (Bottoms)</h5>
                      <ul className="space-y-1 text-sm text-slate-300">
                        <li>• "It's going to zero!"</li>
                        <li>• "The system is broken"</li>
                        <li>• "I need to preserve capital"</li>
                        <li>• Media: "Crisis! Recession!"</li>
                        <li className="text-green-300 font-semibold pt-2">→ Retail sells at bottom</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Concept 4: Institutional Front-Running */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-purple-400 mb-3 flex items-center">
                    <span className="mr-2">4.</span>
                    How Institutions Front-Run Retail Sentiment
                  </h4>
                  <p className="text-slate-300 mb-4">
                    Sophisticated investors don't follow the crowd—they <strong>anticipate</strong> it. They buy when retail is fearful 
                    (capitulation), and sell when retail is euphoric (distribution). They trade <em>against</em> emotion, not with it.
                  </p>

                  <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-4 mb-4">
                    <h5 className="font-bold text-white mb-3">The Smart Money Playbook</h5>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-start space-x-3">
                        <div className="bg-blue-500/20 rounded px-2 py-1 text-blue-400 font-bold text-xs flex-shrink-0">Phase 1</div>
                        <div className="text-slate-300">
                          <strong className="text-white">Accumulation (Bottom):</strong> Retail panic-selling, media doom. 
                          Institutions quietly buy cheap → "Blood in the streets"
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="bg-green-500/20 rounded px-2 py-1 text-green-400 font-bold text-xs flex-shrink-0">Phase 2</div>
                        <div className="text-slate-300">
                          <strong className="text-white">Markup (Rise):</strong> Price rises slowly. Retail skeptical. 
                          Institutions continue buying → "Dead cat bounce?"
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="bg-purple-500/20 rounded px-2 py-1 text-purple-400 font-bold text-xs flex-shrink-0">Phase 3</div>
                        <div className="text-slate-300">
                          <strong className="text-white">Distribution (Top):</strong> Retail FOMO buying, media euphoria. 
                          Institutions quietly sell to retail → "New paradigm!"
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="bg-red-500/20 rounded px-2 py-1 text-red-400 font-bold text-xs flex-shrink-0">Phase 4</div>
                        <div className="text-slate-300">
                          <strong className="text-white">Markdown (Fall):</strong> Price crashes. Retail holds bags. 
                          Institutions wait for next bottom → Cycle repeats
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-500/10 border-l-4 border-purple-500 rounded p-3">
                    <p className="text-xs text-purple-200">
                      <strong>Key Insight:</strong> When your barber, Uber driver, and relatives are all talking about a stock/crypto, 
                      that's the <strong>distribution phase</strong>. Smart money is selling to the crowd.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Case Studies */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📖</span>
                Case Studies: Psychology in Action
              </h3>

              <div className="space-y-6">
                {/* 2008 Housing Bubble */}
                <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-orange-400 mb-2">2008 Housing Bubble & Crash</h4>
                      <p className="text-sm text-slate-400">The Big Short: When Narrative Collapsed</p>
                    </div>
                    <span className="px-3 py-1 bg-orange-500/20 text-orange-400 text-xs font-bold rounded-full">BUBBLE</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">The Narrative (2002-2007):</span> "Housing prices never go down" + 
                      "Everyone deserves a home" + "Financial innovation = no risk." Result: Subprime mortgages to anyone with a pulse.
                    </div>
                    <div>
                      <span className="font-bold text-white">The FOMO Phase:</span> People buying houses they couldn't afford. 
                      Flippers everywhere. Mortgage brokers lying on applications. "If you don't buy now, you'll be priced out forever!"
                    </div>
                    <div>
                      <span className="font-bold text-white">The Panic Phase:</span> Defaults spike → Banks realize mortgages are worthless → 
                      Credit freezes → Everything crashes. "Housing will never recover!"
                    </div>
                    <div>
                      <span className="font-bold text-white">Who Profited:</span> Michael Burry, John Paulson, others who bet 
                      <em>against</em> the crowd. They saw the narrative was fiction and positioned accordingly.
                    </div>
                  </div>

                  <div className="bg-orange-500/10 border-l-4 border-orange-500 rounded p-3">
                    <p className="text-xs text-orange-200">
                      <strong>Key Lesson:</strong> When everyone believes something "can't go down," that's when you should be most skeptical. 
                      Narratives collapse violently when reality intrudes.
                    </p>
                  </div>
                </div>

                {/* GameStop / Meme Stocks */}
                <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-green-400 mb-2">GameStop & Meme Stock Mania (2021)</h4>
                      <p className="text-sm text-slate-400">Reddit vs. Wall Street: Narrative > Fundamentals</p>
                    </div>
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">MANIA</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">The Setup:</span> GameStop = dying brick-and-mortar retailer. 
                      Hedge funds massively short (betting it goes to zero). Fundamentals said they were right.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Narrative Shift:</span> Reddit's WallStreetBets creates new story: 
                      "Screw Wall Street, let's squeeze the shorts!" Millions buy. Stock goes from $4 → $483 in weeks.
                    </div>
                    <div>
                      <span className="font-bold text-white">Peak FOMO:</span> Everyone's buying GME. Media coverage everywhere. 
                      "Diamond hands!" "To the moon!" 🚀 Fundamentals irrelevant—it's about the <em>movement</em>.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Crash:</span> Euphoria fades. Late buyers hold bags. Stock crashes 
                      back to $40. Early buyers (who sold) made millions. Late buyers (who "held the line") lost everything.
                    </div>
                  </div>

                  <div className="bg-green-500/10 border-l-4 border-green-500 rounded p-3">
                    <p className="text-xs text-green-200">
                      <strong>Key Lesson:</strong> Powerful narratives can override fundamentals for weeks/months, but not forever. 
                      Know when you're trading a story vs. an asset. And never invest more than you can lose in a movement.
                    </p>
                  </div>
                </div>

                {/* Crypto Cycles */}
                <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-blue-400 mb-2">Crypto Boom-Bust Cycles (2017, 2021)</h4>
                      <p className="text-sm text-slate-400">The Fastest Emotion Machine Ever Built</p>
                    </div>
                    <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded-full">CYCLES</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">2017 Cycle:</span> Bitcoin $1K → $20K in one year. "Everyone's getting rich!" 
                      ICOs raising hundreds of millions with whitepapers. Your coworkers quit jobs to day-trade crypto. Then: crash to $3K.
                    </div>
                    <div>
                      <span className="font-bold text-white">2021 Cycle:</span> Bitcoin $10K → $69K. NFTs selling for millions. 
                      Celebrities shilling dog coins. "Crypto is the future!" "Number go up!" Then: crash to $16K. 70% wipeout.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Pattern:</span> Every cycle, same story. New narrative → FOMO → 
                      euphoria ("this time is different!") → crash → panic ("it's going to zero!") → capitulation → repeat.
                    </div>
                    <div>
                      <span className="font-bold text-white">Who Wins:</span> Those who buy during panic (when everyone says "crypto is dead") 
                      and sell during euphoria (when taxi drivers give crypto advice). Not those who HODL through -90% drawdowns.
                    </div>
                  </div>

                  <div className="bg-blue-500/10 border-l-4 border-blue-500 rounded p-3">
                    <p className="text-xs text-blue-200">
                      <strong>Key Lesson:</strong> Crypto is pure psychology—no cash flows, no intrinsic value, just narrative. 
                      This makes it the perfect laboratory for studying market emotion. Same patterns as stocks, just 10x faster.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Practical Application */}
            <div className="mb-12 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center text-purple-400">
                <span className="mr-3">💡</span>
                How to Use Market Psychology (Without Getting Wrecked)
              </h3>

              <div className="space-y-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">1. Recognize Which Phase You're In</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Are people fearful (accumulation) or euphoric (distribution)? Check sentiment indicators: Fear & Greed Index, 
                    social media chatter, media headlines. When everyone's bullish, be cautious. When everyone's bearish, look for opportunities.
                  </p>
                  <div className="text-xs text-purple-300">
                    🎯 <strong>Action:</strong> If your group chat is full of rocket emojis → Distribution phase. Time to sell or reduce exposure.
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">2. Trade Against Your Own Emotions</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    When you feel intense FOMO and want to buy → that's probably the top. When you feel panic and want to sell everything → 
                    that's probably the bottom. Your emotions are a <strong>contrarian indicator</strong>.
                  </p>
                  <div className="text-xs text-purple-300">
                    🎯 <strong>Action:</strong> Write down how you feel about a trade. If you feel euphoric confidence → reduce position size.
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">3. Question Every Narrative</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    When you hear "this time is different" or "X will change everything forever," ask: Is this a real paradigm shift, 
                    or just a story to justify high prices? Most narratives are post-hoc rationalizations for price moves.
                  </p>
                  <div className="text-xs text-purple-300">
                    🎯 <strong>Action:</strong> If the narrative requires everything to go right → it's probably wrong. Reality is messy.
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">4. Use Position Sizing to Manage Psychology</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Your emotions scale with your position size. A 5% position going -20%? Annoying. A 50% position going -20%? Panic-inducing. 
                    Keep positions small enough that you can think rationally even if they go to zero.
                  </p>
                  <div className="text-xs text-purple-300">
                    🎯 <strong>Action:</strong> Never put more than 10% in a single "high conviction" bet. Even if you're "sure."
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">5. Build a "Capitulation Watchlist"</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Make a list of quality assets you'd love to own at 50-70% discounts. When markets crash and everyone panics, 
                    you'll have a shopping list ready instead of freezing or panic-selling.
                  </p>
                  <div className="text-xs text-purple-300">
                    🎯 <strong>Action:</strong> List 10 stocks/assets you'd buy if they crashed. Update quarterly. Buy when blood is in streets.
                  </div>
                </div>
              </div>
            </div>

            {/* Resources */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📚</span>
                Recommended Resources
              </h3>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Books */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <BookOpen className="w-5 h-5 text-purple-400" />
                    <h4 className="font-bold text-purple-400">Books</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">The Alchemy of Finance</div>
                      <div className="text-xs text-slate-400">George Soros (Reflexivity)</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Thinking, Fast and Slow</div>
                      <div className="text-xs text-slate-400">Daniel Kahneman</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Extraordinary Popular Delusions</div>
                      <div className="text-xs text-slate-400">Charles Mackay (Crowd madness)</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">Irrational Exuberance</div>
                      <div className="text-xs text-slate-400">Robert Shiller</div>
                    </li>
                  </ul>
                </div>

                {/* Documentaries */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <FileText className="w-5 h-5 text-blue-400" />
                    <h4 className="font-bold text-blue-400">Watch</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">The Big Short (2015)</div>
                      <div className="text-xs text-slate-400">2008 housing bubble psychology</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">GameStop: The Movie</div>
                      <div className="text-xs text-slate-400">Reddit vs Wall Street</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Bitcoin: The End of Money</div>
                      <div className="text-xs text-slate-400">Crypto mania psychology</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">Panic: The Untold Story</div>
                      <div className="text-xs text-slate-400">2008 crisis decision-making</div>
                    </li>
                  </ul>
                </div>

                {/* Tools & Indicators */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    <h4 className="font-bold text-green-400">Sentiment Tools</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">CNN Fear & Greed Index</div>
                      <div className="text-xs text-slate-400">Market sentiment gauge</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">AAII Investor Sentiment</div>
                      <div className="text-xs text-slate-400">Retail bullish/bearish %</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">VIX (Volatility Index)</div>
                      <div className="text-xs text-slate-400">"Fear gauge" for markets</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">Google Trends</div>
                      <div className="text-xs text-slate-400">Search volume = FOMO tracker</div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Bottom Navigation */}
            <div className="flex flex-wrap justify-center gap-4 pt-8 border-t border-slate-700">
              <button
                onClick={() => setCurrentPage('real-finance-system')}
                className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
              >
                ← Module 1: Real Financial System
              </button>
              <button
                onClick={() => setCurrentPage('wealth-games')}
                className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
              >
                ← Module 2: Wealth Games
              </button>
              <button
                onClick={() => setCurrentPage('home')}
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all"
              >
                Back to Home
              </button>
            </div>
          </section>
        )}

        {/* STREET FINANCE MODULE 4: CREDIT & LEVERAGE MECHANICS */}
        {currentPage === 'credit-leverage' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero Section with Disclaimer */}
            <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg">
                  <DollarSign className="w-8 h-8" />
                </div>
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl">🔓</span>
                    <h2 className="text-3xl sm:text-4xl font-bold">Street Finance</h2>
                  </div>
                  <h3 className="text-xl sm:text-2xl text-red-400 font-semibold mb-2">Credit & Leverage Mechanics</h3>
                  <p className="text-slate-300">How Money Is Created & Destroyed in the Modern System</p>
                </div>
              </div>
              
              {/* Disclaimer */}
              <div className="mt-6 bg-amber-500/10 border-l-4 border-amber-500 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-amber-400 mb-2">⚠️ Educational Purpose Only</h4>
                    <p className="text-xs text-amber-200/90">
                      This content explains the mechanics of credit, leverage, and financial system plumbing for educational purposes. 
                      Leverage amplifies both gains and losses. Using excessive leverage can result in total loss of capital. 
                      This information is not investment advice. Always consult licensed professionals and never use leverage you don't understand.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-r from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-lg p-4">
                <p className="text-sm text-slate-300">
                  <span className="font-bold text-white">"Debt is bad, avoid it" vs. "Credit is the system—whoever controls it, controls everything"</span>
                  <br />
                  <span className="text-slate-400 mt-2 block">
                    The financial system doesn't run on "money"—it runs on credit. Banks don't lend out deposits; they create new money 
                    with every loan. Understanding this changes everything about how you see the economy.
                  </span>
                </p>
              </div>
            </div>

            {/* What School Teaches vs Reality */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📚</span>
                What School Teaches vs. How Credit Really Works
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Traditional View */}
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <GraduationCap className="w-5 h-5 text-blue-400" />
                    <h4 className="text-lg font-bold text-blue-400">Traditional Banking Model</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Banks take deposits, then lend them out</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Money supply is fixed by central bank</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Debt is separate from "real" money</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Leverage is a tool for professionals</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Financial system is stable and regulated</span>
                    </li>
                  </ul>
                </div>

                {/* Reality */}
                <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <DollarSign className="w-5 h-5 text-red-400" />
                    <h4 className="text-lg font-bold text-red-400">Credit System Reality</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start space-x-2">
                      <span className="text-red-400 flex-shrink-0">•</span>
                      <span>Banks <strong className="text-white">create new money</strong> with every loan (not lending deposits)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-400 flex-shrink-0">•</span>
                      <span>Money supply <strong className="text-white">expands/contracts</strong> with credit creation/destruction</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-400 flex-shrink-0">•</span>
                      <span>Debt <strong className="text-white">IS</strong> money—97% of "money" is credit, not cash</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-400 flex-shrink-0">•</span>
                      <span>Leverage is <strong className="text-white">everywhere</strong>—derivatives = $600T+ notional</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-400 flex-shrink-0">•</span>
                      <span>System is <strong className="text-white">interconnected and fragile</strong>—contagion spreads fast</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-sm text-red-200">
                  <strong className="text-red-400">The Truth:</strong> The financial system runs on credit, not savings. When credit expands, 
                  the economy booms. When credit contracts, everything freezes—no matter how much "cash" exists. Credit is the oil that 
                  makes the engine run.
                </p>
              </div>
            </div>

            {/* Core Concepts */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">⚙️</span>
                Core Mechanics: How The System Actually Works
              </h3>

              <div className="space-y-6">
                {/* Concept 1: Banks Create Money */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-red-400 mb-3 flex items-center">
                    <span className="mr-2">1.</span>
                    Credit = Money Creation (Not Lending Deposits)
                  </h4>
                  <p className="text-slate-300 mb-4">
                    When you take out a loan, the bank doesn't give you someone else's deposits—it creates <strong>new money</strong> 
                    with a keystroke. This is how 97% of money in the economy is created: through private bank lending, not central banks 
                    printing cash.
                  </p>
                  
                  <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <h5 className="font-bold text-blue-400 mb-3">How It Works (Step by Step)</h5>
                    <div className="space-y-2 text-sm text-slate-300">
                      <div className="flex items-start space-x-2">
                        <span className="text-blue-400 font-bold">1.</span>
                        <span>You apply for $100K mortgage → Bank approves</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-purple-400 font-bold">2.</span>
                        <span>Bank types "$100,000" into your account (new money created)</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-pink-400 font-bold">3.</span>
                        <span>Bank records $100K <em>asset</em> (your loan) and $100K <em>liability</em> (your deposit)</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-red-400 font-bold">4.</span>
                        <span>You spend money → it flows through economy → money supply increased</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-orange-400 font-bold">5.</span>
                        <span>When you repay loan → money is destroyed (leaves money supply)</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-red-500/10 border-l-4 border-red-500 rounded p-3">
                    <p className="text-xs text-red-200">
                      <strong>Key Insight:</strong> Banks aren't intermediaries between savers and borrowers—they're <strong>money creators</strong>. 
                      The only limits are capital requirements and demand for loans. This is why recessions hurt: when no one borrows, no new money enters the system.
                    </p>
                  </div>
                </div>

                {/* Concept 2: Fractional Reserve Myth */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-red-400 mb-3 flex items-center">
                    <span className="mr-2">2.</span>
                    The Fractional Reserve Banking Myth
                  </h4>
                  <p className="text-slate-300 mb-4">
                    You've probably heard banks "keep 10% reserves and lend out 90%." That's outdated. Modern banks don't work that way. 
                    They create loans <em>first</em>, then find reserves <em>after</em> if needed. Reserves are a regulatory requirement, 
                    not a lending constraint.
                  </p>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <h5 className="font-bold text-blue-400 mb-2">Old Story (Fractional Reserve)</h5>
                      <div className="text-sm text-slate-300 space-y-2">
                        <div>• Bank gets $1,000 deposit</div>
                        <div>• Keeps $100 (10% reserve)</div>
                        <div>• Lends out $900</div>
                        <div>• That creates "money multiplier"</div>
                        <div className="text-xs text-blue-300 mt-2">❌ Not how it actually works</div>
                      </div>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <h5 className="font-bold text-red-400 mb-2">Reality (Credit Creation)</h5>
                      <div className="text-sm text-slate-300 space-y-2">
                        <div>• Customer wants $900 loan</div>
                        <div>• Bank creates $900 with keystroke</div>
                        <div>• Bank finds reserves later if needed</div>
                        <div>• No deposit required to make loan</div>
                        <div className="text-xs text-red-300 mt-2">✅ How banks actually operate</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-900/50 border border-slate-600 rounded p-3 text-sm text-slate-300">
                    <strong className="text-white">Post-2008 Reality:</strong> Many countries (including the U.S. as of 2020) have <strong>zero 
                    reserve requirements</strong>. Banks can create unlimited credit, limited only by capital ratios and demand.
                  </div>
                </div>

                {/* Concept 3: Derivatives & Synthetic Liquidity */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-red-400 mb-3 flex items-center">
                    <span className="mr-2">3.</span>
                    Derivatives: Leverage on Top of Leverage
                  </h4>
                  <p className="text-slate-300 mb-4">
                    Derivatives are bets on top of assets. They create "synthetic" exposure without owning the underlying asset. 
                    The notional value of derivatives is <strong>$600+ trillion</strong>—10x global GDP. When they unwind, contagion spreads instantly.
                  </p>

                  <div className="grid md:grid-cols-3 gap-3 mb-4 text-sm">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3">
                      <div className="font-bold text-blue-400 mb-1">Swaps</div>
                      <div className="text-slate-400 mb-2">Exchange cash flows between parties</div>
                      <div className="text-xs text-slate-300">Example: Interest rate swaps ($400T market)</div>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded p-3">
                      <div className="font-bold text-purple-400 mb-1">Options</div>
                      <div className="text-slate-400 mb-2">Right to buy/sell at set price</div>
                      <div className="text-xs text-slate-300">Example: Put options = insurance against crash</div>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                      <div className="font-bold text-red-400 mb-1">CDOs/CLOs</div>
                      <div className="text-slate-400 mb-2">Bundled loans sold as securities</div>
                      <div className="text-xs text-slate-300">Example: 2008 subprime CDOs</div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-lg p-4">
                    <p className="text-sm text-slate-300 mb-2">
                      <strong className="text-red-400">The Problem:</strong> Derivatives create interconnected webs of counterparty risk. 
                      If Bank A fails, it can't pay Bank B, which can't pay Bank C → cascade failure.
                    </p>
                    <p className="text-xs text-red-200">
                      This is what happened in 2008: Lehman Brothers' derivatives book ($35T notional) froze the entire system when they collapsed.
                    </p>
                  </div>
                </div>

                {/* Concept 4: Collateral Hierarchy */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-red-400 mb-3 flex items-center">
                    <span className="mr-2">4.</span>
                    Collateral Hierarchy: What Backs the System
                  </h4>
                  <p className="text-slate-300 mb-4">
                    Credit requires collateral—something of value backing the loan. But not all collateral is equal. U.S. Treasuries sit at the 
                    top (highest quality), everything else cascades down. When collateral quality deteriorates, the whole system wobbles.
                  </p>

                  <div className="space-y-2">
                    <div className="bg-green-500/10 border-l-4 border-green-500 rounded p-3">
                      <div className="flex items-center justify-between mb-1">
                        <h5 className="font-bold text-green-400 text-sm">Tier 1: U.S. Treasuries</h5>
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Risk-Free</span>
                      </div>
                      <p className="text-xs text-slate-300">Backing of U.S. government. Accepts globally. The base layer of the system.</p>
                    </div>

                    <div className="bg-blue-500/10 border-l-4 border-blue-500 rounded p-3">
                      <div className="flex items-center justify-between mb-1">
                        <h5 className="font-bold text-blue-400 text-sm">Tier 2: Investment Grade Bonds</h5>
                        <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">Low Risk</span>
                      </div>
                      <p className="text-xs text-slate-300">AAA to BBB rated. Corporate bonds, municipal bonds. Slight haircut on value.</p>
                    </div>

                    <div className="bg-yellow-500/10 border-l-4 border-yellow-500 rounded p-3">
                      <div className="flex items-center justify-between mb-1">
                        <h5 className="font-bold text-yellow-400 text-sm">Tier 3: Stocks & Real Estate</h5>
                        <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">Medium Risk</span>
                      </div>
                      <p className="text-xs text-slate-300">Volatile assets. Margin requirements apply. Larger haircuts (50-70% loan-to-value).</p>
                    </div>

                    <div className="bg-red-500/10 border-l-4 border-red-500 rounded p-3">
                      <div className="flex items-center justify-between mb-1">
                        <h5 className="font-bold text-red-400 text-sm">Tier 4: Junk Bonds & Speculative Assets</h5>
                        <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">High Risk</span>
                      </div>
                      <p className="text-xs text-slate-300">Below investment grade. Crypto, meme stocks. Massive haircuts or rejected entirely.</p>
                    </div>
                  </div>

                  <div className="mt-4 bg-red-500/10 border-l-4 border-red-500 rounded p-3">
                    <p className="text-xs text-red-200">
                      <strong>Crisis Dynamic:</strong> In crashes, lower-tier collateral becomes worthless → lenders demand Tier 1 (Treasuries) → 
                      "flight to quality" → everything else crashes harder.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Case Studies */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📖</span>
                Case Studies: When Leverage Explodes
              </h3>

              <div className="space-y-6">
                {/* LTCM 1998 */}
                <div className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 border border-orange-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-orange-400 mb-2">Long-Term Capital Management (1998)</h4>
                      <p className="text-sm text-slate-400">When Genius Failed: Nobel Prize Winners Nearly Blow Up Wall Street</p>
                    </div>
                    <span className="px-3 py-1 bg-orange-500/20 text-orange-400 text-xs font-bold rounded-full">MELTDOWN</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">The Setup:</span> Hedge fund run by Nobel Prize-winning economists (Myron Scholes, Robert Merton). 
                      Strategy: Arbitrage small pricing differences with massive leverage. Leverage ratio: <strong>25:1 to 100:1</strong>.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Collapse:</span> Russia defaults on debt (1998) → Market correlations break down → 
                      LTCM's models fail → Lost $4.6 billion in 4 months. With $125B in assets and $1+ trillion in derivatives, collapse threatens entire system.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Bailout:</span> Fed orchestrates $3.6B rescue by 14 banks to prevent systemic meltdown. 
                      If LTCM failed, counterparty contagion would have crashed Wall Street.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Lesson:</span> "Genius" + leverage = disaster when models break. Small probability events 
                      (tail risk) happen more than models predict. Interconnectedness = fragility.
                    </div>
                  </div>

                  <div className="bg-orange-500/10 border-l-4 border-orange-500 rounded p-3">
                    <p className="text-xs text-orange-200">
                      <strong>Key Lesson:</strong> High leverage works... until it doesn't. When models fail, leverage amplifies losses exponentially. 
                      No amount of intelligence protects you from black swans.
                    </p>
                  </div>
                </div>

                {/* Archegos 2021 */}
                <div className="bg-gradient-to-br from-red-500/10 to-pink-500/10 border border-red-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-red-400 mb-2">Archegos Capital Collapse (2021)</h4>
                      <p className="text-sm text-slate-400">$20B Evaporated in Days - Hidden Leverage Destroys Family Office</p>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">IMPLOSION</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">The Setup:</span> Bill Hwang's family office (Archegos) used total return swaps to build 
                      <strong>$50B+ in stock exposure</strong> with only $10B in assets → effective leverage of 5:1 to 10:1. Holdings: ViacomCBS, Discovery, Chinese tech.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Trigger:</span> ViacomCBS announces stock offering → Stock drops 9% → Margin calls → 
                      Archegos can't meet them → Prime brokers liquidate $20B in holdings in 48 hours → Stocks crash 50%+.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Damage:</span> Archegos: $20B+ loss (total wipeout). Prime brokers: $10B+ losses 
                      (Credit Suisse lost $5.5B, went bankrupt later). Retail investors holding same stocks: crushed.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Scandal:</span> Archegos used swaps to hide positions from regulators. 
                      Multiple banks gave them leverage on <em>the same stocks</em>, not knowing others were doing the same.
                    </div>
                  </div>

                  <div className="bg-red-500/10 border-l-4 border-red-500 rounded p-3">
                    <p className="text-xs text-red-200">
                      <strong>Key Lesson:</strong> Hidden leverage is everywhere. Swaps and derivatives let investors build massive positions invisibly. 
                      When it unwinds, the cascade is violent and instant.
                    </p>
                  </div>
                </div>

                {/* Evergrande 2021 */}
                <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-purple-400 mb-2">Evergrande Crisis (2021-Present)</h4>
                      <p className="text-sm text-slate-400">China's Lehman Moment: $300B in Debt, Property Market Collapse</p>
                    </div>
                    <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-xs font-bold rounded-full">ONGOING</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">The Setup:</span> Evergrande = China's largest property developer. $300B+ in liabilities. 
                      Built empire on debt during China's real estate boom. Strategy: Presell apartments, use cash to build more → massive leverage.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Crisis:</span> Chinese government implements "three red lines" policy (debt caps) → 
                      Evergrande can't refinance → Defaults on bonds → Can't finish apartment projects → Protests from homebuyers.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Contagion:</span> Evergrande collapse spreads to other developers (Country Garden, others) → 
                      70% of Chinese household wealth is in real estate → Property values crash 20-40% → Banks exposed.
                    </div>
                    <div>
                      <span className="font-bold text-white">Global Impact:</span> Chinese economy slows → Commodity demand drops → Global supply chain disruption → 
                      Foreign investors (BlackRock, HSBC) lose billions in Evergrande bonds.
                    </div>
                  </div>

                  <div className="bg-purple-500/10 border-l-4 border-purple-500 rounded p-3">
                    <p className="text-xs text-purple-200">
                      <strong>Key Lesson:</strong> Leverage works great when asset prices rise. When they fall, debt doesn't disappear—it becomes 
                      crushing. Real estate leverage + economic slowdown = crisis.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* The Three Types of Leverage */}
            <div className="mb-12 bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">⚖️</span>
                The Three Types of Leverage
              </h3>

              <p className="text-slate-300 mb-6">
                Not all leverage is created equal. Understanding the three types helps you identify risk and opportunity.
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
                  <h4 className="font-bold text-green-400 mb-3 text-lg">1. Good Leverage</h4>
                  <div className="text-sm text-slate-300 space-y-2 mb-4">
                    <div><strong className="text-white">Definition:</strong> Borrowing at low rates to invest in appreciating assets</div>
                    <div><strong className="text-white">Examples:</strong></div>
                    <ul className="space-y-1 ml-4">
                      <li>• 3% mortgage on house appreciating 5%/year</li>
                      <li>• Business loan at 6% generating 20% ROI</li>
                      <li>• Margin on diversified portfolio of stocks</li>
                    </ul>
                  </div>
                  <div className="text-xs bg-green-500/20 text-green-300 p-2 rounded">
                    ✅ <strong>Result:</strong> Amplified returns, manageable risk, positive carry
                  </div>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
                  <h4 className="font-bold text-yellow-400 mb-3 text-lg">2. Neutral Leverage</h4>
                  <div className="text-sm text-slate-300 space-y-2 mb-4">
                    <div><strong className="text-white">Definition:</strong> Borrowing for necessities or strategic timing</div>
                    <div><strong className="text-white">Examples:</strong></div>
                    <ul className="space-y-1 ml-4">
                      <li>• Student loans for high-ROI degree</li>
                      <li>• 0% APR car loan (opportunity cost play)</li>
                      <li>• Credit card paid off monthly (no interest)</li>
                    </ul>
                  </div>
                  <div className="text-xs bg-yellow-500/20 text-yellow-300 p-2 rounded">
                    ⚖️ <strong>Result:</strong> Neither wealth-building nor wealth-destroying, depends on execution
                  </div>
                </div>

                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
                  <h4 className="font-bold text-red-400 mb-3 text-lg">3. Bad Leverage</h4>
                  <div className="text-sm text-slate-300 space-y-2 mb-4">
                    <div><strong className="text-white">Definition:</strong> High-interest debt on depreciating assets or consumption</div>
                    <div><strong className="text-white">Examples:</strong></div>
                    <ul className="space-y-1 ml-4">
                      <li>• Credit card debt at 20%+ APR</li>
                      <li>• Payday loans (400%+ APR)</li>
                      <li>• High leverage on speculative assets</li>
                    </ul>
                  </div>
                  <div className="text-xs bg-red-500/20 text-red-300 p-2 rounded">
                    ❌ <strong>Result:</strong> Wealth destruction, debt spiral, potential ruin
                  </div>
                </div>
              </div>
            </div>

            {/* Practical Application */}
            <div className="mb-12 bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center text-red-400">
                <span className="mr-3">💡</span>
                How to Use Leverage Without Getting Destroyed
              </h3>

              <div className="space-y-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">1. Only Leverage Assets That Appreciate</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Real estate, businesses, stocks—assets that grow in value over time can support debt. Never leverage depreciating assets 
                    (cars, consumer goods) or pure speculation (altcoins, meme stocks).
                  </p>
                  <div className="text-xs text-red-300">
                    🎯 <strong>Rule:</strong> If it doesn't generate cash flow or appreciate, don't leverage it.
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">2. Keep Leverage Ratio Conservative</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Real estate: 75% loan-to-value max. Margin trading: 1.5x to 2x max (not 10x). Business loans: Ensure cash flow covers 
                    2x the debt payment. Leave room for things to go wrong.
                  </p>
                  <div className="text-xs text-red-300">
                    🎯 <strong>Rule:</strong> If a 20% drop would wipe you out, you're over-leveraged.
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">3. Understand Your Collateral's Volatility</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Real estate: Stable, slow-moving (safe to leverage). Stocks: Volatile, can drop 50% (dangerous to over-leverage). 
                    Crypto: Extremely volatile (leverage = suicide). Match leverage to asset stability.
                  </p>
                  <div className="text-xs text-red-300">
                    🎯 <strong>Rule:</strong> Higher volatility = lower leverage. Crypto at 2x is like stocks at 10x.
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">4. Always Have an Exit Plan</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Know your liquidation price. Know where you'll cut losses. Know how you'll meet margin calls if they come. 
                    Never assume "it'll come back"—that's how people lose everything.
                  </p>
                  <div className="text-xs text-red-300">
                    🎯 <strong>Rule:</strong> Before entering leveraged position, write down exact exit conditions.
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">5. Never Leverage Money You Can't Afford to Lose</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Leverage on retirement savings? Terrible idea. Leverage on emergency fund? Insane. Leverage on investment capital you can 
                    lose without affecting lifestyle? Acceptable risk if done conservatively.
                  </p>
                  <div className="text-xs text-red-300">
                    🎯 <strong>Rule:</strong> If losing it would change your life, don't leverage it.
                  </div>
                </div>
              </div>
            </div>

            {/* Resources */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📚</span>
                Recommended Resources
              </h3>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Books */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <BookOpen className="w-5 h-5 text-red-400" />
                    <h4 className="font-bold text-red-400">Books</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">When Genius Failed</div>
                      <div className="text-xs text-slate-400">Roger Lowenstein (LTCM)</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">The Ascent of Money</div>
                      <div className="text-xs text-slate-400">Niall Ferguson</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Debt: The First 5,000 Years</div>
                      <div className="text-xs text-slate-400">David Graeber</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">Margin of Safety</div>
                      <div className="text-xs text-slate-400">Seth Klarman</div>
                    </li>
                  </ul>
                </div>

                {/* Documentaries */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <FileText className="w-5 h-5 text-orange-400" />
                    <h4 className="font-bold text-orange-400">Watch</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Inside Job (2010)</div>
                      <div className="text-xs text-slate-400">Derivatives crisis 2008</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Margin Call (2011)</div>
                      <div className="text-xs text-slate-400">Fictional but realistic</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">The China Hustle</div>
                      <div className="text-xs text-slate-400">Chinese leverage fraud</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">97% Owned (YouTube)</div>
                      <div className="text-xs text-slate-400">Money creation explained</div>
                    </li>
                  </ul>
                </div>

                {/* Academic Papers */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Search className="w-5 h-5 text-blue-400" />
                    <h4 className="font-bold text-blue-400">Research</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Bank of England Paper</div>
                      <div className="text-xs text-slate-400">"Money creation in modern economy"</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">BIS Quarterly Reports</div>
                      <div className="text-xs text-slate-400">Global derivatives data</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Fed Credit Market Data</div>
                      <div className="text-xs text-slate-400">FRED database</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">Shadow Banking Reports</div>
                      <div className="text-xs text-slate-400">FSB annual monitoring</div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Bottom Navigation */}
            <div className="flex flex-wrap justify-center gap-4 pt-8 border-t border-slate-700">
              <button
                onClick={() => setCurrentPage('real-finance-system')}
                className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
              >
                ← Module 1: Real Financial System
              </button>
              <button
                onClick={() => setCurrentPage('wealth-games')}
                className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
              >
                ← Module 2: Wealth Games
              </button>
              <button
                onClick={() => setCurrentPage('market-psychology')}
                className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
              >
                ← Module 3: Market Psychology
              </button>
              <button
                onClick={() => setCurrentPage('home')}
                className="px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg font-semibold hover:from-red-600 hover:to-orange-600 transition-all"
              >
                Back to Home
              </button>
            </div>
          </section>
        )}

        {/* STREET FINANCE MODULE 5: INFORMATION & NARRATIVE ECONOMY */}
        {currentPage === 'narrative-economy' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero Section with Disclaimer */}
            <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg">
                  <Globe className="w-8 h-8" />
                </div>
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl">🔓</span>
                    <h2 className="text-3xl sm:text-4xl font-bold">Street Finance</h2>
                  </div>
                  <h3 className="text-xl sm:text-2xl text-cyan-400 font-semibold mb-2">Information & Narrative Economy</h3>
                  <p className="text-slate-300">How Stories, Media & Attention Control Capital Flows</p>
                </div>
              </div>
              
              {/* Disclaimer */}
              <div className="mt-6 bg-amber-500/10 border-l-4 border-amber-500 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-amber-400 mb-2">⚠️ Educational Purpose Only</h4>
                    <p className="text-xs text-amber-200/90">
                      This content analyzes how information, media, and narratives influence markets for educational purposes. 
                      Understanding these dynamics does not guarantee investment success. Be skeptical of all sources, including this one. 
                      Always conduct independent research and consult licensed professionals before making investment decisions.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-r from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-lg p-4">
                <p className="text-sm text-slate-300">
                  <span className="font-bold text-white">"Just read the news and research" vs. "The news IS the market—whoever controls the narrative controls the price"</span>
                  <br />
                  <span className="text-slate-400 mt-2 block">
                    In the modern economy, attention is capital. A compelling story on social media can move billions in days. 
                    Understanding the attention-to-capital pipeline is how you avoid buying someone else's exit.
                  </span>
                </p>
              </div>
            </div>

            {/* What School Teaches vs Reality */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📚</span>
                What School Teaches vs. The Information Game
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Traditional View */}
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <GraduationCap className="w-5 h-5 text-blue-400" />
                    <h4 className="text-lg font-bold text-blue-400">Traditional Information Model</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>News reports facts objectively</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Analysts give unbiased research</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Social media is just people sharing opinions</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Markets respond to fundamentals, not stories</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Information spreads evenly to everyone</span>
                    </li>
                  </ul>
                </div>

                {/* Reality */}
                <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Globe className="w-5 h-5 text-cyan-400" />
                    <h4 className="text-lg font-bold text-cyan-400">Narrative Economy Reality</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start space-x-2">
                      <span className="text-cyan-400 flex-shrink-0">•</span>
                      <span>News <strong className="text-white">shapes narratives</strong> to drive engagement (clicks = revenue)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-cyan-400 flex-shrink-0">•</span>
                      <span>Analysts are <strong className="text-white">paid by investment banks</strong> to promote deals</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-cyan-400 flex-shrink-0">•</span>
                      <span>Social media is a <strong className="text-white">coordinated influence machine</strong> (bots, paid shills)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-cyan-400 flex-shrink-0">•</span>
                      <span>Markets respond to <strong className="text-white">attention and narrative</strong>, then rationalize later</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-cyan-400 flex-shrink-0">•</span>
                      <span>Information is <strong className="text-white">asymmetric</strong>—insiders know first, retail last</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-sm text-red-200">
                  <strong className="text-red-400">The Truth:</strong> We live in a narrative economy where stories drive capital faster than 
                  fundamentals ever could. The "attention to capital" pipeline is real: viral tweet → retail FOMO → billions flow in → insiders exit. 
                  Understanding this game is defensive, not cynical.
                </p>
              </div>
            </div>

            {/* Core Concepts */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📡</span>
                Core Mechanics: How Information Moves Markets
              </h3>

              <div className="space-y-6">
                {/* Concept 1: Attention → Capital Pipeline */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-cyan-400 mb-3 flex items-center">
                    <span className="mr-2">1.</span>
                    The Attention → Capital Pipeline
                  </h4>
                  <p className="text-slate-300 mb-4">
                    In the modern economy, <strong>attention precedes capital</strong>. A stock/crypto/trend that goes viral on social media 
                    attracts billions within days. The pipeline is predictable: obscure → niche community → influencers → mainstream → retail flood → crash.
                  </p>
                  
                  <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-lg p-4 mb-4">
                    <h5 className="font-bold text-cyan-400 mb-3">The 5-Stage Pipeline</h5>
                    <div className="space-y-3 text-sm text-slate-300">
                      <div className="bg-slate-900/50 border border-slate-600 rounded p-3">
                        <div className="flex items-start space-x-2">
                          <span className="text-cyan-400 font-bold">Stage 1:</span>
                          <div>
                            <strong className="text-white">Discovery</strong> - Early adopters find opportunity (insiders, smart money)
                            <div className="text-xs text-slate-400 mt-1">Example: DeFi protocols in 2019, AI stocks pre-ChatGPT</div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-slate-900/50 border border-slate-600 rounded p-3">
                        <div className="flex items-start space-x-2">
                          <span className="text-blue-400 font-bold">Stage 2:</span>
                          <div>
                            <strong className="text-white">Niche Hype</strong> - Crypto Twitter, finance subreddits discuss
                            <div className="text-xs text-slate-400 mt-1">Example: r/wallstreetbets discussing GME in late 2020</div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-slate-900/50 border border-slate-600 rounded p-3">
                        <div className="flex items-start space-x-2">
                          <span className="text-purple-400 font-bold">Stage 3:</span>
                          <div>
                            <strong className="text-white">Influencer Amplification</strong> - YouTubers, podcasters, "fin-fluencers" promote
                            <div className="text-xs text-slate-400 mt-1">Example: Elon tweets about Dogecoin, price 10x</div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-slate-900/50 border border-slate-600 rounded p-3">
                        <div className="flex items-start space-x-2">
                          <span className="text-pink-400 font-bold">Stage 4:</span>
                          <div>
                            <strong className="text-white">Mainstream Mania</strong> - CNBC covers it, your relatives ask about it
                            <div className="text-xs text-slate-400 mt-1">Example: Bitcoin $60K headlines everywhere (2021)</div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-slate-900/50 border border-slate-600 rounded p-3">
                        <div className="flex items-start space-x-2">
                          <span className="text-red-400 font-bold">Stage 5:</span>
                          <div>
                            <strong className="text-white">Retail Capitulation</strong> - Peak FOMO, insiders exit, crash begins
                            <div className="text-xs text-slate-400 mt-1">Example: Super Bowl crypto ads = top signal (Feb 2022)</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-cyan-500/10 border-l-4 border-cyan-500 rounded p-3">
                    <p className="text-xs text-cyan-200">
                      <strong>Key Insight:</strong> By the time mainstream media covers a trend, smart money is already exiting. 
                      The attention pipeline is a <strong>wealth transfer mechanism</strong> from late arrivals (retail) to early movers (insiders).
                    </p>
                  </div>
                </div>

                {/* Concept 2: Financial Media as Marketing */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-cyan-400 mb-3 flex items-center">
                    <span className="mr-2">2.</span>
                    Financial Media: News or Marketing?
                  </h4>
                  <p className="text-slate-300 mb-4">
                    Financial media doesn't exist to inform you—it exists to <strong>sell advertising</strong> and drive clicks. 
                    Sensational headlines ("Stock Crashes!" "Bull Market Forever!") generate engagement. Nuanced analysis doesn't.
                  </p>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
                      <h5 className="font-bold text-white mb-3 text-sm">How Financial News Works</h5>
                      <ul className="space-y-2 text-xs text-slate-300">
                        <li className="flex items-start space-x-2">
                          <span className="text-cyan-400">•</span>
                          <span>Revenue = ads + subscriptions → need constant engagement</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <span className="text-cyan-400">•</span>
                          <span>Extreme views get clicks: "Crash coming!" or "Stocks to the moon!"</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <span className="text-cyan-400">•</span>
                          <span>Journalists need sources → cozy relationships with companies/analysts</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <span className="text-cyan-400">•</span>
                          <span>"Breaking news" is often leaked by insiders with agendas</span>
                        </li>
                      </ul>
                    </div>
                    <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
                      <h5 className="font-bold text-white mb-3 text-sm">The Conflicts of Interest</h5>
                      <ul className="space-y-2 text-xs text-slate-300">
                        <li className="flex items-start space-x-2">
                          <span className="text-red-400">•</span>
                          <span>Parent companies own stakes in companies they cover</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <span className="text-red-400">•</span>
                          <span>Advertisers are financial firms that want positive coverage</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <span className="text-red-400">•</span>
                          <span>TV guests are fund managers promoting their own positions</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <span className="text-red-400">•</span>
                          <span>"Sponsored content" disguised as news articles</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg p-4">
                    <p className="text-sm text-slate-300 mb-2">
                      <strong className="text-orange-400">Example:</strong> When investment banks underwrite an IPO, they need to create demand. 
                      Sell-side analysts (employed by those banks) issue "buy" ratings. Financial media covers the "hot new stock." 
                      All coordinated to create narrative → drive retail buying → bank profits on IPO.
                    </p>
                    <p className="text-xs text-orange-200">
                      This isn't conspiracy—it's how the industry works. Everyone knows it, but retail doesn't.
                    </p>
                  </div>
                </div>

                {/* Concept 3: Social Media Manipulation */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-cyan-400 mb-3 flex items-center">
                    <span className="mr-2">3.</span>
                    Social Media: Organic or Orchestrated?
                  </h4>
                  <p className="text-slate-300 mb-4">
                    What looks like organic community enthusiasm is often <strong>coordinated campaigns</strong>. Bots, paid influencers, 
                    astroturfing—all designed to create the illusion of grassroots momentum. When thousands of accounts suddenly tweet about 
                    the same stock, it's not coincidence.
                  </p>

                  <div className="space-y-3">
                    <div className="bg-slate-900/50 border border-slate-600 rounded p-3">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="font-bold text-white text-sm">Crypto Influencer Model</h5>
                        <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">COMMON</span>
                      </div>
                      <p className="text-xs text-slate-300 mb-2">
                        1. Project pays influencer $50K-$500K<br />
                        2. Influencer tweets "bullish" takes, shows "their bag"<br />
                        3. Followers FOMO buy<br />
                        4. Influencer exits at peak (never discloses)<br />
                        5. Token crashes, followers hold bags
                      </p>
                      <p className="text-xs text-red-200">
                        Often labeled as "#ad" or "#sponsored" in fine print, buried in tweet thread.
                      </p>
                    </div>

                    <div className="bg-slate-900/50 border border-slate-600 rounded p-3">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="font-bold text-white text-sm">Pump Groups & Discord Channels</h5>
                        <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">COORDINATED</span>
                      </div>
                      <p className="text-xs text-slate-300 mb-2">
                        Private groups (Discord, Telegram) coordinate:<br />
                        1. Leaders accumulate position quietly<br />
                        2. Group members instructed to buy & tweet at same time<br />
                        3. Coordinated volume creates momentum → price spikes<br />
                        4. Leaders dump on latecomers<br />
                        5. Group moves to next target
                      </p>
                      <p className="text-xs text-red-200">
                        Technically illegal (market manipulation), but enforcement is limited in crypto.
                      </p>
                    </div>

                    <div className="bg-slate-900/50 border border-slate-600 rounded p-3">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="font-bold text-white text-sm">Bot Armies & Sentiment Manipulation</h5>
                        <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded">AUTOMATED</span>
                      </div>
                      <p className="text-xs text-slate-300 mb-2">
                        Thousands of bot accounts programmed to:<br />
                        • Amplify positive sentiment (upvotes, retweets)<br />
                        • Suppress negative sentiment (downvotes, attacks)<br />
                        • Create illusion of consensus ("everyone loves this!")<br />
                        • Trigger FOMO in retail investors
                      </p>
                      <p className="text-xs text-orange-200">
                        Research shows 15-20% of crypto Twitter accounts are bots. Stock market has similar issues.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Concept 4: Narrative Arbitrage */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-cyan-400 mb-3 flex items-center">
                    <span className="mr-2">4.</span>
                    Narrative Arbitrage: Reality vs. Belief
                  </h4>
                  <p className="text-slate-300 mb-4">
                    Smart money doesn't just invest in assets—they invest in <strong>the gap between narrative and reality</strong>. 
                    Sometimes you buy reality when the narrative is pessimistic (value investing). Sometimes you buy belief when reality doesn't matter yet (growth investing).
                  </p>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <h5 className="font-bold text-green-400 mb-3">Buy Reality, Sell Belief</h5>
                      <div className="text-sm text-slate-300 space-y-2 mb-3">
                        <div><strong className="text-white">When:</strong> Negative narrative, solid fundamentals</div>
                        <div><strong className="text-white">Example:</strong> Tobacco stocks in 2000s</div>
                        <ul className="text-xs space-y-1 ml-4 mt-2">
                          <li>• Narrative: "Smoking is dying, lawsuits, regulation"</li>
                          <li>• Reality: Massive cash flows, dividends, international growth</li>
                          <li>• Result: Tobacco stocks massively outperformed S&P 500</li>
                        </ul>
                      </div>
                      <div className="text-xs bg-green-500/20 text-green-300 p-2 rounded">
                        ✅ Strategy: Be contrarian when fundamentals are strong but narrative is weak
                      </div>
                    </div>

                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                      <h5 className="font-bold text-purple-400 mb-3">Buy Belief, Sell Reality</h5>
                      <div className="text-sm text-slate-300 space-y-2 mb-3">
                        <div><strong className="text-white">When:</strong> Powerful narrative, weak fundamentals</div>
                        <div><strong className="text-white">Example:</strong> Tesla 2019-2021</div>
                        <ul className="text-xs space-y-1 ml-4 mt-2">
                          <li>• Narrative: "EVs will change world, Tesla is Apple of cars"</li>
                          <li>• Reality: Barely profitable, competition coming, high valuation</li>
                          <li>• Result: Stock went 20x on narrative (before coming back to reality)</li>
                        </ul>
                      </div>
                      <div className="text-xs bg-purple-500/20 text-purple-300 p-2 rounded">
                        ⚡ Strategy: Ride momentum but have clear exit plan—narratives eventually collapse
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 bg-cyan-500/10 border-l-4 border-cyan-500 rounded p-3">
                    <p className="text-xs text-cyan-200">
                      <strong>Advanced Play:</strong> The best investors identify which regime they're in. 
                      2009-2021 = buy belief (narratives ruled). 2022-2023 = buy reality (fundamentals matter again). 
                      Know the meta-game.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Case Studies */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📖</span>
                Case Studies: When Narratives Moved Billions
              </h3>

              <div className="space-y-6">
                {/* Theranos */}
                <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-red-400 mb-2">Theranos: The $9B Narrative with No Reality</h4>
                      <p className="text-sm text-slate-400">How Media Hype Created Billions from Nothing</p>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">FRAUD</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">The Narrative:</span> Elizabeth Holmes = "next Steve Jobs." Theranos claims revolutionary 
                      blood testing with one drop of blood. Media goes wild: Fortune covers, Forbes 400, TED talk. Narrative: "disrupting healthcare."
                    </div>
                    <div>
                      <span className="font-bold text-white">The Reality:</span> Technology didn't work. Blood tests were fake, using standard machines. 
                      Company threatened whistleblowers. Investors (Walgreens, Rupert Murdoch, Betsy DeVos) never verified claims.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Collapse:</span> Wall Street Journal investigation (2015) exposes fraud. 
                      Valuation crashes from $9B to $0. Holmes convicted of fraud (2022).
                    </div>
                    <div>
                      <span className="font-bold text-white">The Lesson:</span> Media narrative created $9B valuation despite zero working technology. 
                      Investors fell for story, not science. Due diligence was replaced by FOMO.
                    </div>
                  </div>

                  <div className="bg-red-500/10 border-l-4 border-red-500 rounded p-3">
                    <p className="text-xs text-red-200">
                      <strong>Key Lesson:</strong> A compelling narrative + media amplification can create billions in "value" even when 
                      the underlying product is fake. Verify, don't trust. Narratives without fundamentals always collapse.
                    </p>
                  </div>
                </div>

                {/* WeWork */}
                <div className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 border border-orange-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-orange-400 mb-2">WeWork: "Tech Company" or Real Estate Landlord?</h4>
                      <p className="text-sm text-slate-400">$47B Valuation Built on Narrative Manipulation</p>
                    </div>
                    <span className="px-3 py-1 bg-orange-500/20 text-orange-400 text-xs font-bold rounded-full">HYPE</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">The Narrative:</span> WeWork isn't a real estate company—it's a "technology platform" 
                      that's "elevating consciousness" (actual quotes from Adam Neumann). SoftBank values it at $47B pre-IPO. Media calls it the future of work.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Reality:</span> WeWork leases office space long-term, subleases short-term. 
                      That's... a landlord. Losing $2B/year. No technology moat. CEO cashing out hundreds of millions while company burns cash.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Collapse:</span> IPO filing (2019) exposes financials. Public investors see losses. 
                      Valuation crashes from $47B → $8B. CEO forced out. Company barely survives (now worth $400M).
                    </div>
                    <div>
                      <span className="font-bold text-white">The Trick:</span> Call yourself a "tech company" → get tech multiple (20-30x revenue) 
                      instead of real estate multiple (1-2x revenue). Narrative engineering = valuation arbitrage.
                    </div>
                  </div>

                  <div className="bg-orange-500/10 border-l-4 border-orange-500 rounded p-3">
                    <p className="text-xs text-orange-200">
                      <strong>Key Lesson:</strong> Labels matter. "Tech company" gets 10x valuation of "real estate company" for same business. 
                      Private market bought the narrative. Public market demanded reality. Always ask: what business is this actually?
                    </p>
                  </div>
                </div>

                {/* NFT Boom */}
                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-purple-400 mb-2">NFT Mania (2021): JPEG for $69M</h4>
                      <p className="text-sm text-slate-400">Peak Attention Economy: Digital Art Sells for Millions</p>
                    </div>
                    <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-xs font-bold rounded-full">MANIA</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">The Narrative:</span> NFTs are "digital art ownership" and "the future of everything." 
                      Beeple sells NFT for $69M at Christie's. Bored Apes at $300K each. Celebrities, athletes, brands rush in. Peak attention.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Mechanics:</span> Wash trading (buying from yourself to fake demand), celebrity 
                      endorsements (often paid/undisclosed), artificial scarcity, FOMO marketing, Discord communities creating hype.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Crash:</span> 2022-2023, most NFTs lose 90-99% of value. Bored Apes floor price 
                      crashes from $400K → $30K. Celebrities stop posting. Projects abandoned. Billions evaporated.
                    </div>
                    <div>
                      <span className="font-bold text-white">What Happened:</span> Classic attention → capital → exit. Early NFT buyers profited. 
                      Celebrities/influencers got paid for promotion. Late retail buyers (FOMO phase) lost everything.
                    </div>
                  </div>

                  <div className="bg-purple-500/10 border-l-4 border-purple-500 rounded p-3">
                    <p className="text-xs text-purple-200">
                      <strong>Key Lesson:</strong> The NFT boom was a perfect example of the attention economy. No fundamentals, pure narrative. 
                      When mainstream media covered it (Super Bowl ads, celebrity endorsements), that was the exit signal for insiders.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Practical Application */}
            <div className="mb-12 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center text-cyan-400">
                <span className="mr-3">💡</span>
                How to Navigate the Information War
              </h3>

              <div className="space-y-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">1. Identify Which Stage of the Pipeline You're In</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Are you discovering (Stage 1-2) or arriving late (Stage 4-5)? If mainstream media is covering it and your relatives are asking 
                    about it, you're at Stage 5 (distribution phase). Smart money is exiting.
                  </p>
                  <div className="text-xs text-cyan-300">
                    🎯 <strong>Rule:</strong> The more attention something has, the more dangerous it is. Inverse correlation.
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">2. Trace Incentives: Who Profits from This Narrative?</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Every piece of information has a source with incentives. Ask: Who benefits if I believe this? Investment bank pushing IPO? 
                    Influencer paid to promote? Founder trying to pump token before exit?
                  </p>
                  <div className="text-xs text-cyan-300">
                    🎯 <strong>Rule:</strong> Assume everyone has an agenda. Follow the money, not the message.
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">3. Distinguish Signal from Noise</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Signal = verifiable facts, primary sources, data. Noise = opinions, predictions, narratives, hype. 90% of financial content 
                    is noise designed to generate engagement. Focus on the 10% that's signal.
                  </p>
                  <div className="text-xs text-cyan-300">
                    🎯 <strong>Rule:</strong> Primary sources (10-Ks, earnings calls) > analyst reports > financial media > social media
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">4. Use Narrative as a Contrarian Indicator</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    When narrative is overwhelmingly positive ("everyone must own this!"), that's often the top. When narrative is overwhelmingly 
                    negative ("this is dead"), that's often the bottom. Extreme consensus is usually wrong.
                  </p>
                  <div className="text-xs text-cyan-300">
                    🎯 <strong>Rule:</strong> Peak narrative optimism = sell. Peak narrative pessimism = buy (if fundamentals support).
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">5. Build Your Own Information Edge</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Stop consuming what everyone else consumes (CNBC, Twitter algorithm). Curate your own sources: primary documents, 
                    specialized newsletters, contrarian thinkers. The best information isn't free or widely available.
                  </p>
                  <div className="text-xs text-cyan-300">
                    🎯 <strong>Rule:</strong> If everyone's reading it, it's already priced in. Find information others aren't seeing.
                  </div>
                </div>
              </div>
            </div>

            {/* Resources */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📚</span>
                Recommended Resources
              </h3>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Books */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <BookOpen className="w-5 h-5 text-cyan-400" />
                    <h4 className="font-bold text-cyan-400">Books</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Bad Blood</div>
                      <div className="text-xs text-slate-400">John Carreyrou (Theranos)</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">The Cult of We</div>
                      <div className="text-xs text-slate-400">WeWork scandal</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Influence</div>
                      <div className="text-xs text-slate-400">Robert Cialdini (Persuasion)</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">Trust Me, I'm Lying</div>
                      <div className="text-xs text-slate-400">Ryan Holiday (Media manipulation)</div>
                    </li>
                  </ul>
                </div>

                {/* Documentaries */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <FileText className="w-5 h-5 text-blue-400" />
                    <h4 className="font-bold text-blue-400">Watch</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">The Inventor (HBO)</div>
                      <div className="text-xs text-slate-400">Theranos documentary</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">WeWork: Or the Making of a $47B Unicorn</div>
                      <div className="text-xs text-slate-400">Hulu documentary</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">The Social Dilemma</div>
                      <div className="text-xs text-slate-400">How platforms manipulate</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">Line Goes Up</div>
                      <div className="text-xs text-slate-400">NFT/crypto critique (YouTube)</div>
                    </li>
                  </ul>
                </div>

                {/* Critical Sources */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Search className="w-5 h-5 text-purple-400" />
                    <h4 className="font-bold text-purple-400">Better Sources</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">SEC Filings (EDGAR)</div>
                      <div className="text-xs text-slate-400">Primary source data</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Company Earnings Calls</div>
                      <div className="text-xs text-slate-400">Hear it directly from executives</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Hindenburg Research</div>
                      <div className="text-xs text-slate-400">Fraud investigation reports</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">Financial Investigative Journalism</div>
                      <div className="text-xs text-slate-400">WSJ, FT, Bloomberg (not headlines)</div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Bottom Navigation */}
            <div className="flex flex-wrap justify-center gap-4 pt-8 border-t border-slate-700">
              <button
                onClick={() => setCurrentPage('real-finance-system')}
                className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
              >
                ← Module 1
              </button>
              <button
                onClick={() => setCurrentPage('wealth-games')}
                className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
              >
                ← Module 2
              </button>
              <button
                onClick={() => setCurrentPage('market-psychology')}
                className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
              >
                ← Module 3
              </button>
              <button
                onClick={() => setCurrentPage('credit-leverage')}
                className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
              >
                ← Module 4
              </button>
              <button
                onClick={() => setCurrentPage('home')}
                className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg font-semibold hover:from-cyan-600 hover:to-blue-600 transition-all"
              >
                Back to Home
              </button>
            </div>
          </section>
        )}

        {/* STREET FINANCE MODULE 6: GAME THEORY & INCENTIVES - FINAL MODULE */}
        {currentPage === 'game-theory' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero Section with Disclaimer */}
            <div className="bg-gradient-to-r from-indigo-500/10 to-violet-500/10 border border-indigo-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                  <ShieldAlert className="w-8 h-8" />
                </div>
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl">🔓</span>
                    <h2 className="text-3xl sm:text-4xl font-bold">Street Finance</h2>
                  </div>
                  <h3 className="text-xl sm:text-2xl text-indigo-400 font-semibold mb-2">Game Theory & Incentives</h3>
                  <p className="text-slate-300">Strategic Thinking: Play the Player, Not the Game</p>
                </div>
              </div>
              
              {/* Disclaimer */}
              <div className="mt-6 bg-amber-500/10 border-l-4 border-amber-500 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-amber-400 mb-2">⚠️ Educational Purpose Only</h4>
                    <p className="text-xs text-amber-200/90">
                      This content teaches strategic thinking and incentive analysis for educational purposes. Understanding how others 
                      make decisions based on incentives does not guarantee investment success. Markets are unpredictable and influenced by 
                      countless factors. Always conduct independent research and consult licensed professionals before making financial decisions.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-r from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-lg p-4">
                <p className="text-sm text-slate-300">
                  <span className="font-bold text-white">"Just analyze the fundamentals" vs. "Follow the incentives—they explain everything"</span>
                  <br />
                  <span className="text-slate-400 mt-2 block">
                    The best investors don't just analyze companies—they analyze the <strong>players</strong>: What do CEOs want? What do 
                    analysts need? What do central banks fear? Understanding incentives lets you predict behavior before it happens.
                  </span>
                </p>
              </div>

              <div className="mt-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-3 text-center">
                <p className="text-sm font-bold text-indigo-400">
                  🎓 FINAL MODULE: This completes the Street Finance series. You now have the complete framework.
                </p>
              </div>
            </div>

            {/* What School Teaches vs Reality */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📚</span>
                What School Teaches vs. Strategic Thinking
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Traditional View */}
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <GraduationCap className="w-5 h-5 text-blue-400" />
                    <h4 className="text-lg font-bold text-blue-400">Traditional Analysis</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Analyze fundamentals: revenue, earnings, growth</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Assume rational actors making optimal decisions</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Trust official guidance and public statements</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Markets are zero-sum games of skill</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-blue-400 flex-shrink-0">•</span>
                      <span>Focus on "what" will happen</span>
                    </li>
                  </ul>
                </div>

                {/* Reality */}
                <div className="bg-gradient-to-br from-indigo-500/10 to-violet-500/10 border border-indigo-500/30 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <ShieldAlert className="w-5 h-5 text-indigo-400" />
                    <h4 className="text-lg font-bold text-indigo-400">Strategic Reality</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start space-x-2">
                      <span className="text-indigo-400 flex-shrink-0">•</span>
                      <span>Analyze <strong className="text-white">incentives</strong>: What do players want? What do they fear?</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-indigo-400 flex-shrink-0">•</span>
                      <span>Assume <strong className="text-white">self-interested actors</strong> optimizing for their goals, not "the market"</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-indigo-400 flex-shrink-0">•</span>
                      <span><strong className="text-white">Question everything</strong>—statements reflect incentives, not truth</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-indigo-400 flex-shrink-0">•</span>
                      <span>Markets are <strong className="text-white">games of positioning</strong>—timing and opponents matter</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-indigo-400 flex-shrink-0">•</span>
                      <span>Focus on <strong className="text-white">"who" and "why"</strong> before "what"</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-sm text-red-200">
                  <strong className="text-red-400">The Truth:</strong> Show me the incentives and I'll show you the outcome. CEOs want stock prices up 
                  (their comp depends on it). Analysts want investment banking fees (from companies they cover). Central bankers want political survival 
                  (avoid crises on their watch). Everyone optimizes for their incentives, not "the truth."
                </p>
              </div>
            </div>

            {/* Core Concepts */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">♟️</span>
                Core Strategic Principles
              </h3>

              <div className="space-y-6">
                {/* Concept 1: Follow the Incentives */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-indigo-400 mb-3 flex items-center">
                    <span className="mr-2">1.</span>
                    Follow the Incentives: They Explain Everything
                  </h4>
                  <p className="text-slate-300 mb-4">
                    Charlie Munger: <em>"Show me the incentives and I'll show you the outcome."</em> People don't do what's "right"—they do 
                    what they're <strong>incentivized</strong> to do. To predict behavior, map the incentives.
                  </p>
                  
                  <div className="space-y-3">
                    <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
                      <h5 className="font-bold text-white mb-3">CEO Incentives</h5>
                      <div className="grid md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <div className="text-slate-400 mb-2"><strong className="text-white">What they're paid for:</strong></div>
                          <ul className="space-y-1 text-xs text-slate-300">
                            <li>• Stock price appreciation (equity comp)</li>
                            <li>• Hitting earnings targets (bonuses)</li>
                            <li>• Revenue growth (not always profit)</li>
                            <li>• Staying employed (avoiding board removal)</li>
                          </ul>
                        </div>
                        <div>
                          <div className="text-slate-400 mb-2"><strong className="text-white">What they'll do:</strong></div>
                          <ul className="space-y-1 text-xs text-slate-300">
                            <li>• Buy back stock to boost price</li>
                            <li>• Use accounting tricks to hit numbers</li>
                            <li>• Prioritize growth over profitability</li>
                            <li>• Hide problems, spin bad news</li>
                          </ul>
                        </div>
                      </div>
                      <div className="mt-3 text-xs bg-red-500/10 text-red-200 p-2 rounded">
                        <strong>Example:</strong> Boeing 737 MAX. Engineers flagged safety issues. Management pushed forward anyway (deadline pressure, 
                        competition with Airbus). Incentive = hit timeline. Result = crashes, $20B+ in losses.
                      </div>
                    </div>

                    <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
                      <h5 className="font-bold text-white mb-3">Analyst Incentives</h5>
                      <div className="grid md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <div className="text-slate-400 mb-2"><strong className="text-white">What they're paid for:</strong></div>
                          <ul className="space-y-1 text-xs text-slate-300">
                            <li>• Investment banking fees (IPOs, M&A)</li>
                            <li>• Maintaining access to company management</li>
                            <li>• Not making waves with employer's clients</li>
                            <li>• Being right directionally (not precisely)</li>
                          </ul>
                        </div>
                        <div>
                          <div className="text-slate-400 mb-2"><strong className="text-white">What they'll do:</strong></div>
                          <ul className="space-y-1 text-xs text-slate-300">
                            <li>• Issue "buy" ratings overwhelmingly (90%+ buys)</li>
                            <li>• Avoid "sell" ratings on bank's clients</li>
                            <li>• Be optimistic to maintain relationships</li>
                            <li>• Move target prices with stock price (not lead)</li>
                          </ul>
                        </div>
                      </div>
                      <div className="mt-3 text-xs bg-orange-500/10 text-orange-200 p-2 rounded">
                        <strong>Reality:</strong> Only 5-10% of analyst ratings are "sell." Not because 90% of companies are good investments, 
                        but because negative ratings kill relationships and lose banking fees.
                      </div>
                    </div>

                    <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
                      <h5 className="font-bold text-white mb-3">Central Bank Incentives</h5>
                      <div className="grid md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <div className="text-slate-400 mb-2"><strong className="text-white">What they fear most:</strong></div>
                          <ul className="space-y-1 text-xs text-slate-300">
                            <li>• Crisis on their watch (career-ending)</li>
                            <li>• Political backlash (unemployment spikes)</li>
                            <li>• Inflation blamed on them personally</li>
                            <li>• Being wrong publicly</li>
                          </ul>
                        </div>
                        <div>
                          <div className="text-slate-400 mb-2"><strong className="text-white">What they'll do:</strong></div>
                          <ul className="space-y-1 text-xs text-slate-300">
                            <li>• Err on side of easing (print money) vs tightening</li>
                            <li>• React to crises (bail out), not prevent them</li>
                            <li>• Use vague language ("transitory") to avoid commitment</li>
                            <li>• Change course when politically necessary</li>
                          </ul>
                        </div>
                      </div>
                      <div className="mt-3 text-xs bg-green-500/10 text-green-200 p-2 rounded">
                        <strong>Outcome:</strong> Fed has incentive to keep markets happy (wealth effect, political stability). 
                        Hence "Fed put"—they'll bail out markets before letting them crash too hard.
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 bg-indigo-500/10 border-l-4 border-indigo-500 rounded p-3">
                    <p className="text-xs text-indigo-200">
                      <strong>Strategic Application:</strong> Before trusting any statement, ask: What is this person incentivized to say? 
                      CEO saying "everything is fine" means nothing if they're incentivized to keep stock price up. Analyst downgrade only happens 
                      when disaster is already obvious.
                    </p>
                  </div>
                </div>

                {/* Concept 2: Information Asymmetry */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-indigo-400 mb-3 flex items-center">
                    <span className="mr-2">2.</span>
                    Information Asymmetry: The Edges in Markets
                  </h4>
                  <p className="text-slate-300 mb-4">
                    Markets aren't level playing fields—they're <strong>hierarchies of information</strong>. Insiders know first, institutions second, 
                    retail last. The game isn't finding hidden value; it's understanding who knows what, when.
                  </p>

                  <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                    <h5 className="font-bold text-red-400 mb-3">The Information Hierarchy</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start space-x-2">
                        <span className="text-red-400 font-bold">Tier 1:</span>
                        <div className="text-slate-300">
                          <strong className="text-white">Company Insiders</strong> - Know everything first (financials, pipelines, problems)
                          <div className="text-xs text-slate-400 mt-1">They're legally restricted from trading, but information still "leaks"</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-orange-400 font-bold">Tier 2:</span>
                        <div className="text-slate-300">
                          <strong className="text-white">Institutional Investors</strong> - Direct access to management, private calls, networks
                          <div className="text-xs text-slate-400 mt-1">Hedge funds, PE firms get information edge through relationships</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-yellow-400 font-bold">Tier 3:</span>
                        <div className="text-slate-300">
                          <strong className="text-white">Professional Analysts</strong> - Industry contacts, channel checks, modeling
                          <div className="text-xs text-slate-400 mt-1">Better than retail, but still behind institutions</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-blue-400 font-bold">Tier 4:</span>
                        <div className="text-slate-300">
                          <strong className="text-white">Informed Retail</strong> - Read filings, follow news, understand metrics
                          <div className="text-xs text-slate-400 mt-1">Better than average, but information is already priced in</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-slate-400 font-bold">Tier 5:</span>
                        <div className="text-slate-300">
                          <strong className="text-white">Average Retail</strong> - Gets info from headlines, social media, tips
                          <div className="text-xs text-slate-400 mt-1">By the time they know, the move is over</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 border border-slate-600 rounded p-3 text-sm">
                      <div className="font-bold text-white mb-2">Where Retail Has Edge:</div>
                      <ul className="space-y-1 text-xs text-slate-300">
                        <li>• Patience (no quarterly reporting pressure)</li>
                        <li>• Flexibility (can invest in anything, any size)</li>
                        <li>• Long time horizon (compound for decades)</li>
                        <li>• Niche knowledge (your industry, local area)</li>
                      </ul>
                    </div>
                    <div className="bg-slate-900/50 border border-slate-600 rounded p-3 text-sm">
                      <div className="font-bold text-white mb-2">Where Retail Has No Edge:</div>
                      <ul className="space-y-1 text-xs text-slate-300">
                        <li>• Short-term trading (competing with algos)</li>
                        <li>• Information speed (you're always last)</li>
                        <li>• Relationship access (no CEO calls for you)</li>
                        <li>• Resource advantage (can't hire 50 analysts)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Concept 3: Prisoner's Dilemma */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-indigo-400 mb-3 flex items-center">
                    <span className="mr-2">3.</span>
                    Prisoner's Dilemma: Why Coordination Fails
                  </h4>
                  <p className="text-slate-300 mb-4">
                    Classic game theory: Two prisoners. If both stay silent, light sentences. If one defects, he goes free and the other gets max sentence. 
                    If both defect, both get medium sentences. <strong>Rational choice = both defect</strong>, even though cooperation would be better.
                  </p>

                  <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-4 mb-4">
                    <h5 className="font-bold text-white mb-3">Market Applications</h5>
                    <div className="space-y-3 text-sm">
                      <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                        <div className="font-semibold text-red-400 mb-1">Bank Runs</div>
                        <div className="text-xs text-slate-300">
                          If everyone keeps deposits at bank → bank is fine. If you think others will withdraw → rational to withdraw first. 
                          Everyone thinks this → bank run. Result: Bank fails even if solvent.
                        </div>
                      </div>
                      <div className="bg-orange-500/10 border border-orange-500/30 rounded p-3">
                        <div className="font-semibold text-orange-400 mb-1">Market Crashes</div>
                        <div className="text-xs text-slate-300">
                          If everyone holds → prices stabilize. If you think others will sell → rational to sell first. 
                          Result: Flash crashes, panic selling.
                        </div>
                      </div>
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3">
                        <div className="font-semibold text-yellow-400 mb-1">Price Fixing Cartels</div>
                        <div className="text-xs text-slate-300">
                          OPEC agrees to cut oil production → higher prices for all. But each country incentivized to cheat (produce more, earn more). 
                          Result: Cartels eventually collapse.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-indigo-500/10 border-l-4 border-indigo-500 rounded p-3">
                    <p className="text-xs text-indigo-200">
                      <strong>Strategic Insight:</strong> Never assume coordination will hold. In a crisis, it's every player for themselves. 
                      The first to exit gets the best price. The last to exit gets wrecked. Game theory says: defect before others do.
                    </p>
                  </div>
                </div>

                {/* Concept 4: Meta-Game */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-indigo-400 mb-3 flex items-center">
                    <span className="mr-2">4.</span>
                    The Meta-Game: Levels of Thinking
                  </h4>
                  <p className="text-slate-300 mb-4">
                    Beginners think Level 1: "What's the fundamental value?" Intermediates think Level 2: "What do others think the value is?" 
                    Experts think Level 3: "What do others think others think?" The meta-game is about <strong>thinking one level deeper</strong>.
                  </p>

                  <div className="space-y-3">
                    <div className="bg-blue-500/10 border-l-4 border-blue-500 rounded p-3">
                      <div className="font-bold text-blue-400 mb-1 text-sm">Level 0: Fundamentals Only</div>
                      <div className="text-xs text-slate-300">
                        "Tesla is overvalued based on P/E ratio" → Loses money as stock 10x on narrative
                      </div>
                    </div>
                    <div className="bg-cyan-500/10 border-l-4 border-cyan-500 rounded p-3">
                      <div className="font-bold text-cyan-400 mb-1 text-sm">Level 1: What Others Think</div>
                      <div className="text-xs text-slate-300">
                        "Tesla is overvalued, but others believe the EV story" → Rides momentum, exits before crash
                      </div>
                    </div>
                    <div className="bg-purple-500/10 border-l-4 border-purple-500 rounded p-3">
                      <div className="font-bold text-purple-400 mb-1 text-sm">Level 2: What Others Think Others Think</div>
                      <div className="text-xs text-slate-300">
                        "Tesla overvalued, but I think institutions think retail will keep buying" → Trades the flows
                      </div>
                    </div>
                    <div className="bg-pink-500/10 border-l-4 border-pink-500 rounded p-3">
                      <div className="font-bold text-pink-400 mb-1 text-sm">Level 3: Regime Analysis (Meta-Meta)</div>
                      <div className="text-xs text-slate-300">
                        "In zero-rate environment, narratives dominate (growth stocks soar). In high-rate environment, fundamentals matter (value outperforms)" 
                        → Adjusts strategy to regime
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg p-4">
                    <p className="text-sm text-slate-300">
                      <strong className="text-purple-400">Advanced Play:</strong> The best investors don't just think about what's true—they think about 
                      <em>what the market will eventually believe is true</em>. That's 2-3 levels deeper than fundamentals.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Case Studies */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📖</span>
                Case Studies: Incentives in Action
              </h3>

              <div className="space-y-6">
                {/* Wells Fargo Scandal */}
                <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-red-400 mb-2">Wells Fargo Fake Accounts (2016)</h4>
                      <p className="text-sm text-slate-400">When Incentives Create Fraud</p>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">SCANDAL</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">The Incentive Structure:</span> Wells Fargo employees paid based on number of accounts opened. 
                      Aggressive quotas ("Eight is great"—sell 8 products per customer). Bonuses for hitting targets. Termination for missing them.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Predictable Outcome:</span> Employees created <strong>3.5 million fake accounts</strong> 
                      without customer knowledge. Forged signatures. Moved money between accounts. All to hit metrics.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Fallout:</span> $3B+ in fines. CEO forced out. Thousands of employees fired 
                      (including whistleblowers). Stock price crashed 20%. Congressional hearings.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Lesson:</span> People optimize for metrics, not values. If you incentivize accounts opened 
                      (not customer satisfaction), you get fake accounts. Incentives > stated mission, every time.
                    </div>
                  </div>

                  <div className="bg-red-500/10 border-l-4 border-red-500 rounded p-3">
                    <p className="text-xs text-red-200">
                      <strong>Key Lesson:</strong> "Show me the incentives and I'll show you the outcome." Wells Fargo's fraud wasn't a surprise—
                      it was the inevitable result of their incentive structure. This applies everywhere: CEOs, analysts, politicians. Follow the incentives.
                    </p>
                  </div>
                </div>

                {/* Hedge Fund Shorting */}
                <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-purple-400 mb-2">Bill Ackman vs. Herbalife (2012-2018)</h4>
                      <p className="text-sm text-slate-400">Incentives Conflict: Short vs. Long</p>
                    </div>
                    <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-xs font-bold rounded-full">BATTLE</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">Bill Ackman's Position:</span> Short $1B of Herbalife. Publicly declares it a "pyramid scheme." 
                      Holds press conference, releases 342-slide presentation. His incentive: Stock goes to zero, he makes $1B+.
                    </div>
                    <div>
                      <span className="font-bold text-white">Carl Icahn's Counter:</span> Takes large long position. Publicly attacks Ackman. 
                      His incentive: Stock goes up, he makes hundreds of millions. Also: humiliate rival.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Battle:</span> 6-year war fought in media, regulatory filings, TV interviews. 
                      Both sides weaponize "research" to support their position. Herbalife lobbies FTC. Ackman funds "whistleblowers."
                    </div>
                    <div>
                      <span className="font-bold text-white">The Outcome:</span> Herbalife survives (stock doubled). Ackman closes short at huge loss. 
                      Icahn profits. Neither was "right" about fundamentals—it was pure incentive warfare.
                    </div>
                  </div>

                  <div className="bg-purple-500/10 border-l-4 border-purple-500 rounded p-3">
                    <p className="text-xs text-purple-200">
                      <strong>Key Lesson:</strong> When someone has a massive financial position, their public statements are strategic, not truth. 
                      Ackman wasn't doing research—he was fighting for his short. Question every statement: What's the incentive?
                    </p>
                  </div>
                </div>

                {/* COVID Policy Response */}
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-blue-400 mb-2">COVID-19 Fed Response (2020)</h4>
                      <p className="text-sm text-slate-400">Central Bank Incentives in Crisis</p>
                    </div>
                    <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded-full">CRISIS</span>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-300 mb-4">
                    <div>
                      <span className="font-bold text-white">The Situation:</span> March 2020, markets crash 35%. Credit markets freeze. 
                      Unemployment spikes to 15%. Fed Chair Powell faces: Do nothing (depression) or print money (inflation risk).
                    </div>
                    <div>
                      <span className="font-bold text-white">The Incentives:</span> Powell's worst outcome = depression on his watch (career over, massive political backlash). 
                      Inflation is future problem. Market crash is <em>today's</em> problem. Clear choice: print.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Action:</span> Fed launches unlimited QE ($120B/month), cuts rates to zero, buys corporate bonds, 
                      backstops entire market. "Whatever it takes." Most aggressive intervention in history.
                    </div>
                    <div>
                      <span className="font-bold text-white">The Outcome:</span> Markets recover in record time. Massive inflation 2021-2022 (exactly as warned). 
                      But Powell avoided depression, saved his career. Inflation blame shared (supply chains, Russia, "transitory").
                    </div>
                  </div>

                  <div className="bg-blue-500/10 border-l-4 border-blue-500 rounded p-3">
                    <p className="text-xs text-blue-200">
                      <strong>Key Lesson:</strong> Central banks have political survival incentive. They'll choose "print money" over "let markets crash" 
                      every time because crashes happen now, inflation happens later. This is the "Fed put"—it exists because of incentives, not altruism.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Practical Application */}
            <div className="mb-12 bg-gradient-to-br from-indigo-500/10 to-violet-500/10 border border-indigo-500/30 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center text-indigo-400">
                <span className="mr-3">💡</span>
                How to Think Strategically (The Framework)
              </h3>

              <div className="space-y-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">1. Map the Players & Their Incentives</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Before analyzing any situation, identify: Who are the players? What do they want? What do they fear? What are they compensated for? 
                    This tells you 80% of what they'll do.
                  </p>
                  <div className="text-xs text-indigo-300">
                    🎯 <strong>Exercise:</strong> For any company, map: CEO (wants stock up), CFO (wants earnings hit), Board (wants no scandals), 
                    Analysts (want banking fees). Now predict what happens when revenue misses.
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">2. Think One Level Deeper Than the Crowd</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Don't ask "Is this a good investment?" Ask "What will others think about this in 6 months?" Then ask "What will others think 
                    others will think?" Meta-game thinking = your edge.
                  </p>
                  <div className="text-xs text-indigo-300">
                    🎯 <strong>Rule:</strong> If you're thinking about fundamentals while everyone else thinks about narratives, you'll lose. 
                    Match your strategy to the regime you're in.
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">3. Identify Information Advantages (or Disadvantages)</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Be honest: Are you early (informational edge) or late (you're the exit liquidity)? If everyone's talking about it, you're late. 
                    Find edges where you have information others don't.
                  </p>
                  <div className="text-xs text-indigo-300">
                    🎯 <strong>Edge Examples:</strong> Your industry (insider knowledge), local real estate (ground truth), long time horizon 
                    (patience vs quarterly pressure), niche expertise (deep understanding).
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">4. Assume Coordination Will Fail</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    In crises, prisoner's dilemma rules. Don't be the last to act. When bank runs start, withdraw early. When market panics, sell before 
                    it's "obvious." When cartels show cracks, assume they'll collapse.
                  </p>
                  <div className="text-xs text-indigo-300">
                    🎯 <strong>Rule:</strong> Trust stated coordination (OPEC, price fixing) until incentives change. Then defect immediately.
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">5. Play the Long Game Where You Have Edge</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Your edge as retail: Time horizon. Institutions report quarterly. You can wait decades. Use it. Avoid competing where you have no edge: 
                    short-term trading, information speed, access.
                  </p>
                  <div className="text-xs text-indigo-300">
                    🎯 <strong>Strategy:</strong> Buy quality assets (when others panic), hold long enough for fundamentals to win (decades), 
                    let compounding do the work. This is where retail beats institutions.
                  </div>
                </div>
              </div>
            </div>

            {/* Resources */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <span className="mr-3">📚</span>
                Recommended Resources
              </h3>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Books */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <BookOpen className="w-5 h-5 text-indigo-400" />
                    <h4 className="font-bold text-indigo-400">Books</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Thinking Strategically</div>
                      <div className="text-xs text-slate-400">Dixit & Nalebuff (Game Theory)</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Poor Charlie's Almanack</div>
                      <div className="text-xs text-slate-400">Charlie Munger (Incentives)</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">The Art of Strategy</div>
                      <div className="text-xs text-slate-400">Applied game theory</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">Antifragile</div>
                      <div className="text-xs text-slate-400">Nassim Taleb</div>
                    </li>
                  </ul>
                </div>

                {/* Academic */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <FileText className="w-5 h-5 text-violet-400" />
                    <h4 className="font-bold text-violet-400">Game Theory Basics</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Prisoner's Dilemma</div>
                      <div className="text-xs text-slate-400">Classic coordination failure</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Nash Equilibrium</div>
                      <div className="text-xs text-slate-400">No player can improve unilaterally</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Principal-Agent Problem</div>
                      <div className="text-xs text-slate-400">Misaligned incentives</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">Information Asymmetry</div>
                      <div className="text-xs text-slate-400">Adverse selection, moral hazard</div>
                    </li>
                  </ul>
                </div>

                {/* Practical */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Target className="w-5 h-5 text-blue-400" />
                    <h4 className="font-bold text-blue-400">Apply It</h4>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Map Incentives Exercise</div>
                      <div className="text-xs text-slate-400">Pick 5 companies, map all players' incentives</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Predict Based on Incentives</div>
                      <div className="text-xs text-slate-400">Before earnings: What will CEO say/do?</div>
                    </li>
                    <li className="pb-2 border-b border-slate-700">
                      <div className="font-semibold text-white">Level 2+ Thinking</div>
                      <div className="text-xs text-slate-400">For any investment thesis, add "What will others think?"</div>
                    </li>
                    <li>
                      <div className="font-semibold text-white">Track Your Edge</div>
                      <div className="text-xs text-slate-400">Where do YOU have information advantage?</div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Bottom Navigation */}
            <div className="flex flex-wrap justify-center gap-4 pt-8 border-t border-slate-700">
              <button
                onClick={() => setCurrentPage('real-finance-system')}
                className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-700 transition-all"
              >
                Module 1
              </button>
              <button
                onClick={() => setCurrentPage('wealth-games')}
                className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-700 transition-all"
              >
                Module 2
              </button>
              <button
                onClick={() => setCurrentPage('market-psychology')}
                className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-700 transition-all"
              >
                Module 3
              </button>
              <button
                onClick={() => setCurrentPage('credit-leverage')}
                className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-700 transition-all"
              >
                Module 4
              </button>
              <button
                onClick={() => setCurrentPage('narrative-economy')}
                className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-700 transition-all"
              >
                Module 5
              </button>
              <button
                onClick={() => setCurrentPage('home')}
                className="px-8 py-3 bg-gradient-to-r from-indigo-500 to-violet-500 rounded-lg font-bold hover:from-indigo-600 hover:to-violet-600 transition-all"
              >
                🏠 Back to Home
              </button>
            </div>
          </section>
        )}

        {/* STREET FINANCE MODULE 7: READING THE FINE PRINT - FINANCIAL STATEMENTS */}
        {currentPage === 'glossary' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
                  <BookOpen className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold">Trading Glossary</h2>
                  <p className="text-slate-300 mt-2">Master the language of leverage trading with comprehensive definitions</p>
                </div>
              </div>
              <div className="mt-6 bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
                <p className="text-sm text-indigo-300">
                  💡 <strong>Quick Reference:</strong> Search through {glossaryTerms.length} essential terms or filter by category. 
                  Understanding these concepts is crucial for successful leverage trading.
                </p>
              </div>
            </div>

            {/* Search & Filter Section */}
            <div className="mb-8">
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                {/* Search Bar */}
                <div className="md:col-span-2">
                  <label className="block text-sm text-slate-400 mb-2 font-semibold">Search Terms</label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search by term or definition..."
                      value={glossarySearch}
                      onChange={(e) => setGlossarySearch(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg focus:border-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                </div>

                {/* Results Counter */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-indigo-400">{filteredTerms.length}</div>
                    <div className="text-sm text-slate-400">Terms Found</div>
                  </div>
                </div>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-sm text-slate-400 mb-3 font-semibold">Filter by Category</label>
                <div className="flex flex-wrap gap-2">
                  {glossaryCategories.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => setGlossaryCategory(cat.value)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        glossaryCategory === cat.value
                          ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20'
                          : 'bg-slate-800/50 border border-slate-700 text-slate-300 hover:bg-slate-700 hover:border-indigo-500/50'
                      }`}
                    >
                      {cat.label}
                      <span className={`ml-2 text-xs ${
                        glossaryCategory === cat.value ? 'text-indigo-200' : 'text-slate-500'
                      }`}>
                        ({cat.count})
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Terms Grid */}
            <div>
              {filteredTerms.length > 0 ? (
                <>
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold">
                      {glossaryCategory === 'all' ? 'All Terms' : glossaryCategories.find(c => c.value === glossaryCategory)?.label}
                      <span className="text-slate-500 ml-2 text-base font-normal">
                        ({filteredTerms.length} {filteredTerms.length === 1 ? 'term' : 'terms'})
                      </span>
                    </h3>
                  </div>
                  <div className="grid gap-4">
                    {filteredTerms.map((term, index) => (
                      <div
                        key={index}
                        className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-indigo-500/50 hover:shadow-lg hover:shadow-indigo-500/10 transition-all group"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-2 h-2 rounded-full bg-indigo-400 group-hover:animate-pulse" />
                            <h3 className="text-xl font-bold text-white group-hover:text-indigo-400 transition-colors">
                              {term.term}
                            </h3>
                          </div>
                          <span className="text-xs px-3 py-1 bg-indigo-500/20 text-indigo-400 rounded-full border border-indigo-500/30 font-medium">
                            {term.category}
                          </span>
                        </div>
                        <p className="text-slate-300 leading-relaxed">{term.definition}</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-12 text-center">
                  <div className="mb-4">
                    <Search className="w-16 h-16 text-slate-600 mx-auto" />
                  </div>
                  <p className="text-slate-400 text-lg mb-2">No terms found matching "{glossarySearch}"</p>
                  <p className="text-slate-500 text-sm mb-6">Try a different search term or clear your filters</p>
                  <button
                    onClick={() => {
                      setGlossarySearch('');
                      setGlossaryCategory('all');
                    }}
                    className="px-6 py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-all font-semibold"
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>

            {/* Quick Navigation CTAs */}
            <div className="mt-12 grid md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <LineChart className="w-6 h-6 text-blue-400" />
                  <h4 className="font-semibold text-blue-400">Practice Trading</h4>
                </div>
                <p className="text-sm text-slate-300 mb-4">
                  Use our calculators to model trades and understand the impact of leverage.
                </p>
                <button
                  onClick={() => setCurrentPage('trade-calculator')}
                  className="w-full px-4 py-2 bg-blue-500 rounded-lg font-semibold hover:bg-blue-600 transition-all text-sm"
                >
                  Try Calculator →
                </button>
              </div>

              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Shield className="w-6 h-6 text-purple-400" />
                  <h4 className="font-semibold text-purple-400">Test Your Knowledge</h4>
                </div>
                <p className="text-sm text-slate-300 mb-4">
                  Take our risk assessment quiz to evaluate your readiness for leverage trading.
                </p>
                <button
                  onClick={() => setCurrentPage('quiz')}
                  className="w-full px-4 py-2 bg-purple-500 rounded-lg font-semibold hover:bg-purple-600 transition-all text-sm"
                >
                  Take Quiz →
                </button>
              </div>

              <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <BarChart3 className="w-6 h-6 text-emerald-400" />
                  <h4 className="font-semibold text-emerald-400">Plan Your Future</h4>
                </div>
                <p className="text-sm text-slate-300 mb-4">
                  Create a comprehensive financial plan with our interactive planning tool.
                </p>
                <button
                  onClick={() => setCurrentPage('planning')}
                  className="w-full px-4 py-2 bg-emerald-500 rounded-lg font-semibold hover:bg-emerald-600 transition-all text-sm"
                >
                  Start Planning →
                </button>
              </div>
            </div>

            {/* Learning Resources */}
            <div className="mt-8 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-xl p-6">
              <h4 className="font-semibold text-indigo-400 mb-3 flex items-center">
                <GraduationCap className="w-5 h-5 mr-2" />
                Keep Learning
              </h4>
              <p className="text-sm text-slate-300 mb-4">
                Understanding these terms is just the beginning. Combine your knowledge with our calculators, 
                risk management tools, and financial planning features to make informed decisions.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setCurrentPage('position-sizing')}
                  className="text-sm px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg hover:border-indigo-500/50 transition-all"
                >
                  Position Sizing →
                </button>
                <button
                  onClick={() => setCurrentPage('risk-management')}
                  className="text-sm px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg hover:border-indigo-500/50 transition-all"
                >
                  Risk Management →
                </button>
                <button
                  onClick={() => setCurrentPage('tax')}
                  className="text-sm px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg hover:border-indigo-500/50 transition-all"
                >
                  Tax Strategies →
                </button>
              </div>
            </div>
          </section>
        )}

        {/* RISK MANAGEMENT TOOLS PAGE */}
        {currentPage === 'risk-management' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Hero */}
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg">
                  <Shield className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold">Risk Management Tools</h2>
                  <p className="text-slate-300 mt-2">Calculate risk-reward ratios, track portfolio exposure, and monitor liquidation risk</p>
                </div>
              </div>
              <div className="mt-6 bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <p className="text-sm text-purple-300">
                  💡 <strong>Pro Tip:</strong> Professional traders focus on risk management first, profits second. 
                  Protect your downside and the upside takes care of itself.
                </p>
              </div>
            </div>

            {/* How to Use Section */}
            <div className="mb-8 bg-gradient-to-r from-pink-500/5 to-purple-500/5 border border-pink-500/20 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center text-pink-400">
                <span className="mr-2">📖</span> How to Use These Tools
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <h4 className="font-semibold text-white mb-2">Tool 1: Risk-Reward Calculator</h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• Enter entry price, stop loss, and take profit</li>
                    <li>• Get your R:R ratio (should be 2:1 minimum)</li>
                    <li>• Calculate win rate needed to be profitable</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">Tool 2: Portfolio Risk</h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• Add all your open positions</li>
                    <li>• See total exposure across portfolio</li>
                    <li>• Monitor if you're over-leveraged</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">Tool 3: Liquidation Distance</h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• Enter position details and leverage</li>
                    <li>• See how far price can move before liquidation</li>
                    <li>• Get danger level assessment</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                <p className="text-xs text-purple-300">
                  💡 <strong>Best Practice:</strong> Use all three tools together. Check R:R before entering, track portfolio exposure, and always know your liquidation distance.
                </p>
              </div>
            </div>

            {/* Tool 1: Risk-Reward Calculator */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <Percent className="w-6 h-6 text-blue-400" />
                <h3 className="text-2xl font-bold">Risk-Reward Calculator</h3>
              </div>
              <p className="text-slate-300 mb-6">
                Calculate risk-reward ratios and the win rate needed to be profitable. Know if a trade is worth taking.
              </p>

              <div className="grid lg:grid-cols-2 gap-8">
                {/* Inputs */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="font-semibold mb-4">Trade Setup</h4>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Entry Price</label>
                      <input
                        type="number"
                        value={riskRewardInputs.entryPrice}
                        onChange={(e) => setRiskRewardInputs({...riskRewardInputs, entryPrice: parseFloat(e.target.value) || 0})}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Stop Loss</label>
                      <input
                        type="number"
                        value={riskRewardInputs.stopLoss}
                        onChange={(e) => setRiskRewardInputs({...riskRewardInputs, stopLoss: parseFloat(e.target.value) || 0})}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">TP1</label>
                        <input
                          type="number"
                          value={riskRewardInputs.takeProfit1}
                          onChange={(e) => setRiskRewardInputs({...riskRewardInputs, takeProfit1: parseFloat(e.target.value) || 0})}
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">TP2</label>
                        <input
                          type="number"
                          value={riskRewardInputs.takeProfit2}
                          onChange={(e) => setRiskRewardInputs({...riskRewardInputs, takeProfit2: parseFloat(e.target.value) || 0})}
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">TP3</label>
                        <input
                          type="number"
                          value={riskRewardInputs.takeProfit3}
                          onChange={(e) => setRiskRewardInputs({...riskRewardInputs, takeProfit3: parseFloat(e.target.value) || 0})}
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Position Size</label>
                      <input
                        type="number"
                        value={riskRewardInputs.positionSize}
                        onChange={(e) => setRiskRewardInputs({...riskRewardInputs, positionSize: parseFloat(e.target.value) || 0})}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Results */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="font-semibold mb-4">Analysis</h4>
                  
                  {(() => {
                    const rrResults = calculateRiskReward();
                    return (
                      <div className="space-y-6">
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                          <div className="text-sm text-red-400 mb-1">Your Risk</div>
                          <div className="text-2xl font-bold text-white">
                            ${rrResults.dollarRisk} ({rrResults.riskPercent}%)
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-slate-400">Target 1</span>
                              <div className="text-right">
                                <div className="text-green-400 font-semibold">+${rrResults.reward1}</div>
                                <div className="text-xs text-slate-500">R:R = {rrResults.rr1}:1</div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="text-xs text-slate-400">Need {rrResults.winRate1}% win rate</div>
                              <div className={`text-xs px-2 py-0.5 rounded ${
                                parseFloat(rrResults.winRate1) < 50 ? 'bg-green-500/20 text-green-400' :
                                parseFloat(rrResults.winRate1) < 60 ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                {parseFloat(rrResults.winRate1) < 50 ? 'Good' : parseFloat(rrResults.winRate1) < 60 ? 'OK' : 'Hard'}
                              </div>
                            </div>
                          </div>

                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-slate-400">Target 2</span>
                              <div className="text-right">
                                <div className="text-green-400 font-semibold">+${rrResults.reward2}</div>
                                <div className="text-xs text-slate-500">R:R = {rrResults.rr2}:1</div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="text-xs text-slate-400">Need {rrResults.winRate2}% win rate</div>
                              <div className={`text-xs px-2 py-0.5 rounded ${
                                parseFloat(rrResults.winRate2) < 50 ? 'bg-green-500/20 text-green-400' :
                                parseFloat(rrResults.winRate2) < 60 ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                {parseFloat(rrResults.winRate2) < 50 ? 'Good' : parseFloat(rrResults.winRate2) < 60 ? 'OK' : 'Hard'}
                              </div>
                            </div>
                          </div>

                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-slate-400">Target 3</span>
                              <div className="text-right">
                                <div className="text-green-400 font-semibold">+${rrResults.reward3}</div>
                                <div className="text-xs text-slate-500">R:R = {rrResults.rr3}:1</div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="text-xs text-slate-400">Need {rrResults.winRate3}% win rate</div>
                              <div className={`text-xs px-2 py-0.5 rounded ${
                                parseFloat(rrResults.winRate3) < 50 ? 'bg-green-500/20 text-green-400' :
                                parseFloat(rrResults.winRate3) < 60 ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                {parseFloat(rrResults.winRate3) < 50 ? 'Good' : parseFloat(rrResults.winRate3) < 60 ? 'OK' : 'Hard'}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                          <div className="text-sm font-semibold text-blue-400 mb-2">💡 Pro Tip:</div>
                          <p className="text-sm text-slate-300">
                            Look for trades with at least 2:1 risk-reward ratio. This means you only need to win 
                            34% of the time to be profitable!
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Tool 3: Portfolio Risk Calculator */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <BarChart3 className="w-6 h-6 text-purple-400" />
                <h3 className="text-2xl font-bold">Portfolio Risk Calculator</h3>
              </div>
              <p className="text-slate-300 mb-6">
                Track multiple leveraged positions and see your total portfolio exposure and risk.
              </p>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                {(() => {
                  const portfolioResults = calculatePortfolioRisk();
                  return (
                    <div className="space-y-6">
                      {/* Portfolio Summary */}
                      <div className="grid md:grid-cols-4 gap-4">
                        <div className="bg-slate-900/50 rounded-lg p-4">
                          <div className="text-sm text-slate-400 mb-1">Total Capital</div>
                          <div className="text-2xl font-bold text-white">${portfolioResults.totalCapital}</div>
                        </div>
                        <div className="bg-slate-900/50 rounded-lg p-4">
                          <div className="text-sm text-slate-400 mb-1">Total Exposure</div>
                          <div className="text-2xl font-bold text-orange-400">${portfolioResults.totalExposure}</div>
                        </div>
                        <div className="bg-slate-900/50 rounded-lg p-4">
                          <div className="text-sm text-slate-400 mb-1">Avg Leverage</div>
                          <div className="text-2xl font-bold text-yellow-400">{portfolioResults.avgLeverage}x</div>
                        </div>
                        <div className="bg-slate-900/50 rounded-lg p-4">
                          <div className="text-sm text-slate-400 mb-1">Portfolio P&L</div>
                          <div className={`text-2xl font-bold ${parseFloat(portfolioResults.totalPnL) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {parseFloat(portfolioResults.totalPnL) >= 0 ? '+' : ''}${portfolioResults.totalPnL}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            ({parseFloat(portfolioResults.portfolioReturn) >= 0 ? '+' : ''}{portfolioResults.portfolioReturn}%)
                          </div>
                        </div>
                      </div>

                      {/* Positions List */}
                      <div>
                        <h4 className="font-semibold mb-3">Current Positions</h4>
                        <div className="space-y-2">
                          {portfolioPositions.map((pos) => {
                            const priceChange = ((pos.currentPrice - pos.entryPrice) / pos.entryPrice) * 100;
                            const positionPnL = pos.size * pos.leverage * (priceChange / 100);
                            return (
                              <div key={pos.id} className="bg-slate-900/50 rounded-lg p-4">
                                <div className="flex justify-between items-center">
                                  <div>
                                    <div className="font-semibold text-white">{pos.asset}</div>
                                    <div className="text-sm text-slate-400">
                                      ${pos.size.toLocaleString()} @ {pos.leverage}x leverage
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className={`font-semibold ${priceChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                      {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
                                    </div>
                                    <div className={`text-sm ${positionPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                      {positionPnL >= 0 ? '+' : ''}${positionPnL.toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {parseFloat(portfolioResults.avgLeverage) > 10 && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <div className="font-semibold text-red-400 mb-1">⚠️ High Portfolio Leverage Warning</div>
                              <p className="text-sm text-red-300">
                                Your average leverage is {portfolioResults.avgLeverage}x. A 10% market move against you 
                                could result in significant losses. Consider reducing some positions.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Tool 4: Liquidation Distance Tracker */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <TrendingDown className="w-6 h-6 text-red-400" />
                <h3 className="text-2xl font-bold">Liquidation Distance Tracker</h3>
              </div>
              <p className="text-slate-300 mb-6">
                See exactly how close you are to liquidation and how much price can move against you.
              </p>

              <div className="grid lg:grid-cols-2 gap-8">
                {/* Inputs */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="font-semibold mb-4">Position Details</h4>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-3">Position Type</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setLiquidationInputs({...liquidationInputs, position: 'long'})}
                          className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                            liquidationInputs.position === 'long'
                              ? 'bg-green-500 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          📈 Long
                        </button>
                        <button
                          onClick={() => setLiquidationInputs({...liquidationInputs, position: 'short'})}
                          className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                            liquidationInputs.position === 'short'
                              ? 'bg-red-500 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          📉 Short
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Entry Price</label>
                      <input
                        type="number"
                        value={liquidationInputs.entryPrice}
                        onChange={(e) => setLiquidationInputs({...liquidationInputs, entryPrice: parseFloat(e.target.value) || 0})}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Current Price</label>
                      <input
                        type="number"
                        value={liquidationInputs.currentPrice}
                        onChange={(e) => setLiquidationInputs({...liquidationInputs, currentPrice: parseFloat(e.target.value) || 0})}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Leverage: {liquidationInputs.leverage}x
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="100"
                        value={liquidationInputs.leverage}
                        onChange={(e) => setLiquidationInputs({...liquidationInputs, leverage: parseInt(e.target.value)})}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>1x</span>
                        <span>100x</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Maintenance Margin: {liquidationInputs.maintenanceMargin}%
                      </label>
                      <input
                        type="range"
                        min="20"
                        max="80"
                        step="5"
                        value={liquidationInputs.maintenanceMargin}
                        onChange={(e) => setLiquidationInputs({...liquidationInputs, maintenanceMargin: parseInt(e.target.value)})}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>20%</span>
                        <span>80%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Results */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="font-semibold mb-4">Liquidation Analysis</h4>
                  
                  {(() => {
                    const liqResults = calculateLiquidationDistance();
                    return (
                      <div className="space-y-6">
                        <div className={`bg-gradient-to-br rounded-lg p-6 ${
                          liqResults.safetyLevel === 'safe' ? 'from-green-500/20 to-emerald-500/20 border border-green-500/30' :
                          liqResults.safetyLevel === 'warning' ? 'from-yellow-500/20 to-orange-500/20 border border-yellow-500/30' :
                          'from-red-500/20 to-red-600/20 border border-red-500/30'
                        }`}>
                          <div className="text-sm text-slate-300 mb-2">Liquidation Price</div>
                          <div className="text-4xl font-bold text-white mb-2">
                            ${liqResults.liquidationPrice}
                          </div>
                          <div className={`text-sm font-semibold uppercase ${liqResults.safetyColor}`}>
                            {liqResults.safetyLevel === 'safe' ? '✅ Safe Zone' :
                             liqResults.safetyLevel === 'warning' ? '⚠️ Warning Zone' :
                             '🚨 Danger Zone'}
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-slate-400">Distance to Liquidation</span>
                              <span className={`font-semibold ${liqResults.safetyColor}`}>
                                ${liqResults.distanceToLiquidation}
                              </span>
                            </div>
                            <div className="w-full bg-slate-700 rounded-full h-3">
                              <div
                                className={`h-3 rounded-full ${
                                  liqResults.safetyLevel === 'safe' ? 'bg-green-400' :
                                  liqResults.safetyLevel === 'warning' ? 'bg-yellow-400' :
                                  'bg-red-400'
                                }`}
                                style={{ width: `${Math.min(100, parseFloat(liqResults.distancePercent) * 2)}%` }}
                              />
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {liqResults.distancePercent}% price distance
                            </div>
                          </div>

                          <div className="bg-slate-900/50 rounded-lg p-4">
                            <div className="text-sm text-slate-400 mb-2">Price Must Move</div>
                            <div className="text-2xl font-bold text-white">
                              {liqResults.priceChangeToLiquidation}%
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {liquidationInputs.position === 'long' ? 'downward' : 'upward'} to liquidate
                            </div>
                          </div>
                        </div>

                        {liqResults.safetyLevel === 'danger' && (
                          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                              <div>
                                <div className="font-semibold text-red-400 mb-1">🚨 Critical Warning</div>
                                <p className="text-sm text-red-300">
                                  You are dangerously close to liquidation! Consider:
                                </p>
                                <ul className="text-sm text-red-300 mt-2 space-y-1">
                                  <li>• Closing part of your position immediately</li>
                                  <li>• Adding more margin if possible</li>
                                  <li>• Moving your stop loss closer</li>
                                  <li>• Never letting it reach liquidation</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}

                        {liqResults.safetyLevel === 'warning' && (
                          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                              <div>
                                <div className="font-semibold text-yellow-400 mb-1">⚠️ Caution</div>
                                <p className="text-sm text-yellow-300">
                                  You're in the warning zone. Monitor this position closely and consider 
                                  reducing risk if price moves further against you.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Bottom Tips */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-6">
                <h4 className="font-semibold text-green-400 mb-3">💰 Position Sizing Rule</h4>
                <p className="text-sm text-slate-300">
                  Never risk more than 1-2% of your account on a single trade. This ensures you can 
                  survive 50+ losing trades in a row before blowing up your account.
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/20 rounded-xl p-6">
                <h4 className="font-semibold text-blue-400 mb-3">📊 Risk-Reward Rule</h4>
                <p className="text-sm text-slate-300">
                  Always aim for at least 2:1 risk-reward ratio. This means if you risk $100, you should 
                  be targeting $200+ profit. With 2:1 R:R, you only need 34% win rate to break even.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border border-purple-500/20 rounded-xl p-6">
                <h4 className="font-semibold text-purple-400 mb-3">🛡️ Stop Loss Rule</h4>
                <p className="text-sm text-slate-300">
                  ALWAYS use stop losses. Never "hope" a losing trade will turn around. Professional 
                  traders protect their downside first, then worry about profits second.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* COST ANALYSIS CALCULATOR PAGE */}
        {currentPage === 'cost-analysis' && (
          <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero */}
            <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg">
                  <DollarSign className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold">Trading Cost Calculator</h2>
                  <p className="text-slate-300 mt-2">Calculate fees, funding rates, and total cost of leverage across markets</p>
                </div>
              </div>
              <div className="mt-6 bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                <p className="text-sm text-orange-300">
                  💡 <strong>Hidden Costs:</strong> Trading fees, funding rates, and spreads can eat into your profits. 
                  Always factor these into your strategy before entering a trade.
                </p>
              </div>
            </div>

            {/* How to Use Section */}
            <div className="mb-8 bg-gradient-to-r from-red-500/5 to-orange-500/5 border border-red-500/20 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center text-orange-400">
                <span className="mr-2">📖</span> How to Use This Calculator
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-white mb-2">Step 1: Select Your Market</h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• <strong>Crypto:</strong> Includes funding rates (8-hour payments)</li>
                    <li>• <strong>Stocks:</strong> Margin interest + borrowing fees</li>
                    <li>• <strong>Forex:</strong> Rollover rates (swap fees)</li>
                    <li>• Each market has different cost structures</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">Step 2: Enter Trade Details</h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• <strong>Position Size:</strong> Total amount you're trading</li>
                    <li>• <strong>Holding Period:</strong> How long you plan to hold</li>
                    <li>• <strong>Trading Fees:</strong> Maker/taker fees or commissions</li>
                    <li>• <strong>Funding Rate:</strong> Crypto perpetual funding (if applicable)</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 grid md:grid-cols-2 gap-3">
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                  <p className="text-xs text-orange-300">
                    💡 <strong>Crypto Tip:</strong> Negative funding means you GET PAID to hold your position! Check funding rates before entering trades.
                  </p>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-xs text-red-300">
                    ⚠️ <strong>Warning:</strong> Holding leveraged positions long-term (30+ days) can accumulate significant costs. Factor these into your profit targets.
                  </p>
                </div>
              </div>
            </div>

            {/* Main Calculator */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Cost Inputs */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-6">Calculate Your Costs</h3>
                
                {/* Market Selection */}
                <div className="mb-6">
                  <label className="block text-sm text-slate-400 mb-3">Select Market</label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      onClick={() => setCostInputs({...costInputs, market: 'crypto'})}
                      className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                        costInputs.market === 'crypto'
                          ? 'bg-blue-500 text-white'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      Crypto
                    </button>
                    <button
                      onClick={() => setCostInputs({...costInputs, market: 'stocks'})}
                      className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                        costInputs.market === 'stocks'
                          ? 'bg-blue-500 text-white'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      Stocks
                    </button>
                    <button
                      onClick={() => setCostInputs({...costInputs, market: 'forex'})}
                      className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                        costInputs.market === 'forex'
                          ? 'bg-blue-500 text-white'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      Forex
                    </button>
                  </div>
                </div>

                {/* Position Size */}
                <div className="mb-6">
                  <label className="block text-sm text-slate-400 mb-2">
                    Position Size: ${costInputs.positionSize.toLocaleString()}
                  </label>
                  <input
                    type="range"
                    min="1000"
                    max="100000"
                    step="1000"
                    value={costInputs.positionSize}
                    onChange={(e) => setCostInputs({...costInputs, positionSize: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>$1K</span>
                    <span>$100K</span>
                  </div>
                </div>

                {/* Leverage */}
                <div className="mb-6">
                  <label className="block text-sm text-slate-400 mb-2">
                    Leverage: {costInputs.leverage}x
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={costInputs.leverage}
                    onChange={(e) => setCostInputs({...costInputs, leverage: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>1x</span>
                    <span>100x</span>
                  </div>
                </div>

                {/* Holding Days */}
                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    Holding Period: {costInputs.holdingDays} {costInputs.holdingDays === 1 ? 'day' : 'days'}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="365"
                    value={costInputs.holdingDays}
                    onChange={(e) => setCostInputs({...costInputs, holdingDays: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>1 day</span>
                    <span>1 year</span>
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    {costInputs.holdingDays < 7 ? '📈 Day/Swing Trading' : 
                     costInputs.holdingDays < 30 ? '📊 Short-term Hold' : 
                     costInputs.holdingDays < 90 ? '📈 Medium-term' : 
                     '🎯 Long-term Position'}
                  </div>
                </div>
              </div>

              {/* Cost Results */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-6">Cost Breakdown</h3>
                
                <div className="space-y-4">
                  {parseFloat(costResults.tradingFee) > 0 && (
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-slate-400 text-sm">Trading Fees</div>
                          <div className="text-xs text-slate-500 mt-1">Entry + Exit fees</div>
                        </div>
                        <span className="text-white font-semibold text-xl">${costResults.tradingFee}</span>
                      </div>
                    </div>
                  )}
                  {parseFloat(costResults.fundingRate) > 0 && (
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-slate-400 text-sm">Funding/Rollover Costs</div>
                          <div className="text-xs text-slate-500 mt-1">
                            {costInputs.market === 'crypto' ? 'Perpetual funding rate' : 
                             costInputs.market === 'forex' ? 'Swap/rollover fees' : 
                             'Overnight fees'}
                          </div>
                        </div>
                        <span className="text-white font-semibold text-xl">${costResults.fundingRate}</span>
                      </div>
                    </div>
                  )}
                  {parseFloat(costResults.marginInterest) > 0 && (
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-slate-400 text-sm">Margin Interest</div>
                          <div className="text-xs text-slate-500 mt-1">Interest on borrowed capital</div>
                        </div>
                        <span className="text-white font-semibold text-xl">${costResults.marginInterest}</span>
                      </div>
                    </div>
                  )}
                  {parseFloat(costResults.spread) > 0 && (
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-slate-400 text-sm">Spread Cost</div>
                          <div className="text-xs text-slate-500 mt-1">Bid-ask spread</div>
                        </div>
                        <span className="text-white font-semibold text-xl">${costResults.spread}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/30 rounded-lg p-6 mt-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-300 font-semibold">Total Cost</span>
                      <span className="text-3xl font-bold text-red-400">${costResults.totalCost}</span>
                    </div>
                    <div className="text-right text-sm text-slate-400 mt-1">
                      {costResults.costPercent}% of position size
                    </div>
                  </div>

                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mt-4">
                    <div className="text-sm text-blue-300 mb-1">
                      💡 <strong>Break-even Point:</strong>
                    </div>
                    <p className="text-sm text-slate-300">
                      Price must move <strong>{costResults.breakEvenMove}%</strong> in your favor 
                      just to cover your trading costs before you start making profit.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cost Comparison by Market */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6">Cost Comparison Across Markets</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Bitcoin className="w-6 h-6 text-orange-400" />
                    <h4 className="font-semibold text-orange-400">Cryptocurrency</h4>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Trading Fees:</span>
                      <span className="text-white">0.02-0.1%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Funding Rate:</span>
                      <span className="text-white">0.01-0.03%/8h</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Best For:</span>
                      <span className="text-white">Day trading</span>
                    </div>
                    <div className="mt-4 text-xs text-slate-500">
                      ⚡ Lowest fees, 24/7 trading
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <LineChart className="w-6 h-6 text-blue-400" />
                    <h4 className="font-semibold text-blue-400">Stocks/Equities</h4>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Trading Fees:</span>
                      <span className="text-white">$0-5 per trade</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Margin Interest:</span>
                      <span className="text-white">6-10% APR</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Best For:</span>
                      <span className="text-white">Swing/Position</span>
                    </div>
                    <div className="mt-4 text-xs text-slate-500">
                      📊 Lower leverage, regulated
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Globe className="w-6 h-6 text-green-400" />
                    <h4 className="font-semibold text-green-400">Forex</h4>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Spread:</span>
                      <span className="text-white">0.1-3 pips</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Swap Fees:</span>
                      <span className="text-white">Varies by pair</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Best For:</span>
                      <span className="text-white">Day/Scalping</span>
                    </div>
                    <div className="mt-4 text-xs text-slate-500">
                      🌍 High leverage, tight spreads
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Cost Reduction Tips */}
            <div>
              <h3 className="text-2xl font-bold mb-6">How to Reduce Trading Costs</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-6">
                  <h4 className="font-semibold text-green-400 mb-3 flex items-center">
                    <PiggyBank className="w-5 h-5 mr-2" />
                    Lower Your Fees
                  </h4>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li>• Use maker orders instead of taker orders</li>
                    <li>• Increase trading volume to unlock VIP tiers</li>
                    <li>• Use exchange tokens for fee discounts (BNB, FTT, etc.)</li>
                    <li>• Compare fee structures across platforms</li>
                    <li>• Consider fee-free brokers for stocks</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-6">
                  <h4 className="font-semibold text-blue-400 mb-3 flex items-center">
                    <Target className="w-5 h-5 mr-2" />
                    Minimize Funding Costs
                  </h4>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li>• Close positions before funding time (crypto)</li>
                    <li>• Consider spot + options instead of perpetuals</li>
                    <li>• Avoid holding leveraged positions overnight</li>
                    <li>• Use futures instead of CFDs when possible</li>
                    <li>• Monitor funding rates before entering trades</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-6">
                  <h4 className="font-semibold text-orange-400 mb-3 flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    Watch Out For
                  </h4>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li>• Hidden spreads in "commission-free" trading</li>
                    <li>• Inactivity fees after 3-6 months</li>
                    <li>• Withdrawal fees (can be substantial)</li>
                    <li>• Currency conversion fees</li>
                    <li>• Premium data/platform fees</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-6">
                  <h4 className="font-semibold text-purple-400 mb-3 flex items-center">
                    <BookOpen className="w-5 h-5 mr-2" />
                    Smart Cost Management
                  </h4>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li>• Factor costs into your profit targets</li>
                    <li>• Avoid overtrading (death by a thousand fees)</li>
                    <li>• Calculate break-even before every trade</li>
                    <li>• Use limit orders to reduce slippage</li>
                    <li>• Consider position size relative to fees</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* FINANCIAL PLANNING PAGE */}
        {currentPage === 'planning' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg">
                  <BarChart3 className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold">Financial Planning Calculator</h2>
                  <p className="text-slate-300 mt-2">Create a comprehensive financial plan tailored to your goals</p>
                </div>
              </div>
              <div className="mt-6 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <p className="text-sm text-emerald-300">
                  💡 <strong>Professional Planning Tool:</strong> Input your income, expenses, and goals to receive a detailed financial roadmap. 
                  Calculate exactly how much you need to save monthly to achieve your financial objectives.
                </p>
              </div>
            </div>

            {/* How to Use Section */}
            <div className="mb-8 bg-gradient-to-r from-teal-500/5 to-emerald-500/5 border border-teal-500/20 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center text-teal-400">
                <span className="mr-2">📖</span> How to Use This Financial Planner
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <h4 className="font-semibold text-white mb-2">Step 1: Enter Income & Expenses</h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• Set your annual salary and side income</li>
                    <li>• Input all monthly expenses (housing, food, etc.)</li>
                    <li>• Be honest - accuracy is key!</li>
                    <li>• Use sliders or quick buttons</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">Step 2: Define Your Goals</h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• Emergency fund target (3-6 months expenses)</li>
                    <li>• Home down payment amount</li>
                    <li>• Retirement goal (typically $1-3M)</li>
                    <li>• Custom goals (car, education, etc.)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">Step 3: Review Your Plan</h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• See monthly surplus/deficit</li>
                    <li>• Get timeline for each goal</li>
                    <li>• View projections with inflation</li>
                    <li>• Adjust as needed for balance</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 grid md:grid-cols-2 gap-3">
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                  <p className="text-xs text-emerald-300">
                    💡 <strong>Pro Tip:</strong> Not sure where to start? Click "Template Library" in the navigation to load pre-built plans for different life situations (Recent Grad, Family of 4, Early Retirement, etc.)
                  </p>
                </div>
                <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-3">
                  <p className="text-xs text-teal-300">
                    📊 <strong>Best Practice:</strong> Review and update your plan every 3-6 months as your income, expenses, and goals change. Financial planning is an ongoing process!
                  </p>
                </div>
              </div>
            </div>

            {/* Income Section */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <DollarSign className="w-6 h-6 mr-3 text-green-400" />
                Monthly Income Sources
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Annual Salary: ${planningInputs.annualSalary.toLocaleString()}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="300000"
                        step="5000"
                        value={planningInputs.annualSalary}
                        onChange={(e) => updatePlanningInput('annualSalary', e.target.value)}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>$0</span>
                        <span>$300K/year</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Side Income (Annual): ${planningInputs.sideIncome.toLocaleString()}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100000"
                        step="1000"
                        value={planningInputs.sideIncome}
                        onChange={(e) => updatePlanningInput('sideIncome', e.target.value)}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Investment Income (Annual): ${planningInputs.investmentIncome.toLocaleString()}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="50000"
                        step="500"
                        value={planningInputs.investmentIncome}
                        onChange={(e) => updatePlanningInput('investmentIncome', e.target.value)}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-6">
                  <h4 className="font-semibold text-green-400 mb-4">Income Summary</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Total Annual Income:</span>
                      <span className="text-white font-semibold">${planResults.totalAnnualIncome}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Monthly (Gross):</span>
                      <span className="text-white font-semibold">${planResults.monthlyIncome}</span>
                    </div>
                    <div className="flex justify-between border-t border-green-500/30 pt-3">
                      <span className="text-slate-400">After Tax ({planningInputs.taxRate}%):</span>
                      <span className="text-green-400 font-bold text-lg">${planResults.monthlyAfterTaxIncome}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Expenses Section */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <Home className="w-6 h-6 mr-3 text-red-400" />
                Monthly Expenses
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Housing: ${planningInputs.housing}
                      </label>
                      <input
                        type="number"
                        value={planningInputs.housing}
                        onChange={(e) => updatePlanningInput('housing', e.target.value)}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Transportation: ${planningInputs.transportation}
                      </label>
                      <input
                        type="number"
                        value={planningInputs.transportation}
                        onChange={(e) => updatePlanningInput('transportation', e.target.value)}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Food: ${planningInputs.food}
                      </label>
                      <input
                        type="number"
                        value={planningInputs.food}
                        onChange={(e) => updatePlanningInput('food', e.target.value)}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Utilities: ${planningInputs.utilities}
                      </label>
                      <input
                        type="number"
                        value={planningInputs.utilities}
                        onChange={(e) => updatePlanningInput('utilities', e.target.value)}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Insurance: ${planningInputs.insurance}
                      </label>
                      <input
                        type="number"
                        value={planningInputs.insurance}
                        onChange={(e) => updatePlanningInput('insurance', e.target.value)}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Healthcare: ${planningInputs.healthcare}
                      </label>
                      <input
                        type="number"
                        value={planningInputs.healthcare}
                        onChange={(e) => updatePlanningInput('healthcare', e.target.value)}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Discretionary: ${planningInputs.discretionary}
                      </label>
                      <input
                        type="number"
                        value={planningInputs.discretionary}
                        onChange={(e) => updatePlanningInput('discretionary', e.target.value)}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Other Expenses: ${planningInputs.otherExpenses}
                      </label>
                      <input
                        type="number"
                        value={planningInputs.otherExpenses}
                        onChange={(e) => updatePlanningInput('otherExpenses', e.target.value)}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                      <div className="text-sm text-red-400 mb-1">Total Monthly:</div>
                      <div className="text-xl font-bold text-white">${planResults.totalMonthlyExpenses}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Budget Overview */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-6">Budget Overview</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className={`rounded-xl p-6 ${
                  parseFloat(planResults.monthlySurplus) >= 0 
                    ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30'
                    : 'bg-gradient-to-br from-red-500/20 to-red-600/20 border border-red-500/30'
                }`}>
                  <div className="text-sm text-slate-300 mb-2">Monthly Surplus/Deficit</div>
                  <div className={`text-4xl font-bold ${
                    parseFloat(planResults.monthlySurplus) >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {parseFloat(planResults.monthlySurplus) >= 0 ? '+' : ''}${planResults.monthlySurplus}
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    {parseFloat(planResults.monthlySurplus) >= 0 
                      ? '✓ You have money left to save' 
                      : '⚠️ You\'re spending more than you earn'}
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="text-sm text-slate-300 mb-2">Savings Rate</div>
                  <div className="text-4xl font-bold text-blue-400">{planResults.savingsRate}%</div>
                  <div className="text-xs text-slate-400 mt-2">
                    {parseFloat(planResults.savingsRate) < 10 && 'Try to save at least 10%'}
                    {parseFloat(planResults.savingsRate) >= 10 && parseFloat(planResults.savingsRate) < 20 && 'Good start! Aim for 20%'}
                    {parseFloat(planResults.savingsRate) >= 20 && 'Excellent savings rate!'}
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="text-sm text-slate-300 mb-2">Annual Savings Potential</div>
                  <div className="text-4xl font-bold text-purple-400">${planResults.annualSurplus}</div>
                  <div className="text-xs text-slate-400 mt-2">Available for goals</div>
                </div>
              </div>
            </div>

            {/* Interactive Visualizations */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <BarChart3 className="w-6 h-6 mr-3 text-purple-400" />
                Financial Visualizations
              </h3>

              {/* Expense Breakdown Pie Chart */}
              <div className="mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold mb-6 text-center">Monthly Expense Breakdown</h4>
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getExpenseBreakdownData()}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {getExpenseBreakdownData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#1e293b', 
                              border: '1px solid #475569',
                              borderRadius: '8px',
                              color: '#fff'
                            }}
                            formatter={(value) => `$${value}`}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-3">
                      {getExpenseBreakdownData().map((item, index) => (
                        <div key={index} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3">
                          <div className="flex items-center space-x-3">
                            <div 
                              className="w-4 h-4 rounded"
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="text-slate-300">{item.name}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-white font-semibold">${item.value}</div>
                            <div className="text-xs text-slate-500">
                              {((item.value / parseFloat(planResults.totalMonthlyExpenses)) * 100).toFixed(1)}%
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Worth Projection Chart */}
              <div className="mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold mb-6 text-center">Net Worth Projection (Next {Math.min(planningInputs.yearsToRetirement, 30)} Years)</h4>
                  <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={getNetWorthProjectionData()}>
                        <defs>
                          <linearGradient id="colorNetWorth" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorInvestments" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis 
                          dataKey="year" 
                          stroke="#9ca3af"
                          height={60}
                          tick={{ fontSize: 12 }}
                          label={{ value: 'Years from Now', position: 'insideBottom', offset: -10, fill: '#9ca3af' }}
                        />
                        <YAxis 
                          stroke="#9ca3af"
                          width={80}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                          label={{ value: 'Net Worth', angle: -90, position: 'insideLeft', offset: 10, fill: '#9ca3af' }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1e293b', 
                            border: '1px solid #475569',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => `$${value.toLocaleString()}`}
                          labelFormatter={(label) => `Year ${label}`}
                        />
                        <Legend />
                        <Area 
                          type="monotone" 
                          dataKey="netWorth" 
                          stroke="#3b82f6" 
                          fillOpacity={1} 
                          fill="url(#colorNetWorth)" 
                          name="Total Net Worth"
                        />
                        <Area 
                          type="monotone" 
                          dataKey="investments" 
                          stroke="#10b981" 
                          fillOpacity={1} 
                          fill="url(#colorInvestments)" 
                          name="Investment Balance"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-6 grid md:grid-cols-3 gap-4">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
                      <div className="text-sm text-blue-400 mb-1">Projected Net Worth</div>
                      <div className="text-2xl font-bold text-white">
                        ${getNetWorthProjectionData()[getNetWorthProjectionData().length - 1]?.netWorth.toLocaleString()}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        In {Math.min(planningInputs.yearsToRetirement, 30)} years
                      </div>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                      <div className="text-sm text-green-400 mb-1">Total Growth</div>
                      <div className="text-2xl font-bold text-white">
                        ${(getNetWorthProjectionData()[getNetWorthProjectionData().length - 1]?.netWorth - getNetWorthProjectionData()[0]?.netWorth).toLocaleString()}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        Investment gains
                      </div>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 text-center">
                      <div className="text-sm text-purple-400 mb-1">Annual Return</div>
                      <div className="text-2xl font-bold text-white">
                        {planningInputs.investmentReturn}%
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        Assumed rate
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Goal Progression Chart */}
              <div className="mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold mb-6 text-center">Goal Progress Projection (10 Years)</h4>
                  <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsLineChart data={getGoalProjectionData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis 
                          dataKey="year" 
                          stroke="#9ca3af"
                          height={60}
                          tick={{ fontSize: 12 }}
                          label={{ value: 'Years', position: 'insideBottom', offset: -10, fill: '#9ca3af' }}
                        />
                        <YAxis 
                          stroke="#9ca3af"
                          width={80}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                          label={{ value: 'Goal Amount', angle: -90, position: 'insideLeft', offset: 10, fill: '#9ca3af' }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1e293b', 
                            border: '1px solid #475569',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => `$${value.toLocaleString()}`}
                          labelFormatter={(label) => `Year ${label}`}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="Emergency Fund" 
                          stroke="#eab308" 
                          strokeWidth={2}
                          dot={{ fill: '#eab308', r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="Home Fund" 
                          stroke="#3b82f6" 
                          strokeWidth={2}
                          dot={{ fill: '#3b82f6', r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="Retirement" 
                          stroke="#8b5cf6" 
                          strokeWidth={2}
                          dot={{ fill: '#8b5cf6', r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey={planningInputs.customGoalName} 
                          stroke="#10b981" 
                          strokeWidth={2}
                          dot={{ fill: '#10b981', r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </RechartsLineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-6 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-sm text-blue-300">
                      💡 <strong>Chart Assumptions:</strong> This projection assumes you split your monthly surplus (${planResults.monthlySurplus}) 
                      equally among all active goals and earn {planningInputs.investmentReturn}% annual returns on invested funds. 
                      Adjust your inputs above to see how different scenarios affect your goal timelines.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Goals */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <Target className="w-6 h-6 mr-3 text-yellow-400" />
                Financial Goals
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Emergency Fund */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="font-semibold text-yellow-400 mb-4 flex items-center">
                    <Shield className="w-5 h-5 mr-2" />
                    Emergency Fund
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Goal Amount: ${planningInputs.emergencyFundGoal.toLocaleString()}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="50000"
                        step="1000"
                        value={planningInputs.emergencyFundGoal}
                        onChange={(e) => updatePlanningInput('emergencyFundGoal', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Current Saved: ${planningInputs.emergencyFundCurrent.toLocaleString()}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="50000"
                        step="500"
                        value={planningInputs.emergencyFundCurrent}
                        onChange={(e) => updatePlanningInput('emergencyFundCurrent', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4 mt-4">
                      <div className="flex justify-between mb-2">
                        <span className="text-slate-400">Monthly Savings Needed:</span>
                        <span className="text-white font-semibold">${planResults.emergencyMonthlySavings}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Time to Goal:</span>
                        <span className="text-white font-semibold">{planResults.emergencyMonthsNeeded} months</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Home Down Payment */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="font-semibold text-blue-400 mb-4 flex items-center">
                    <Building2 className="w-5 h-5 mr-2" />
                    Home Down Payment
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Goal Amount: ${planningInputs.homeDownPayment.toLocaleString()}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="200000"
                        step="5000"
                        value={planningInputs.homeDownPayment}
                        onChange={(e) => updatePlanningInput('homeDownPayment', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Current Saved: ${planningInputs.homeDownPaymentCurrent.toLocaleString()}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="200000"
                        step="2000"
                        value={planningInputs.homeDownPaymentCurrent}
                        onChange={(e) => updatePlanningInput('homeDownPaymentCurrent', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4 mt-4">
                      <div className="flex justify-between mb-2">
                        <span className="text-slate-400">Monthly Savings Needed:</span>
                        <span className="text-white font-semibold">${planResults.homeMonthlySavings}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Time to Goal:</span>
                        <span className="text-white font-semibold">{planResults.homeMonthsNeeded} months</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Retirement */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="font-semibold text-purple-400 mb-4 flex items-center">
                    <PiggyBank className="w-5 h-5 mr-2" />
                    Retirement
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Retirement Goal: ${planningInputs.retirementGoal.toLocaleString()}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="3000000"
                        step="50000"
                        value={planningInputs.retirementGoal}
                        onChange={(e) => updatePlanningInput('retirementGoal', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Current Saved: ${planningInputs.retirementCurrent.toLocaleString()}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="500000"
                        step="10000"
                        value={planningInputs.retirementCurrent}
                        onChange={(e) => updatePlanningInput('retirementCurrent', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Years to Retirement: {planningInputs.yearsToRetirement}
                      </label>
                      <input
                        type="range"
                        min="5"
                        max="50"
                        step="1"
                        value={planningInputs.yearsToRetirement}
                        onChange={(e) => updatePlanningInput('yearsToRetirement', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4 mt-4">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Monthly Savings Needed:</span>
                        <span className="text-white font-semibold">${planResults.retirementMonthlySavings}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Custom Goal */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="font-semibold text-green-400 mb-4 flex items-center">
                    <Heart className="w-5 h-5 mr-2" />
                    Custom Goal
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Goal Name</label>
                      <input
                        type="text"
                        value={planningInputs.customGoalName}
                        onChange={(e) => updatePlanningTextInput('customGoalName', e.target.value)}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:border-blue-500 focus:outline-none"
                        placeholder="e.g., Vacation, Car, Wedding"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Goal Amount: ${planningInputs.customGoalAmount.toLocaleString()}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100000"
                        step="1000"
                        value={planningInputs.customGoalAmount}
                        onChange={(e) => updatePlanningInput('customGoalAmount', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Current Saved: ${planningInputs.customGoalCurrent.toLocaleString()}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100000"
                        step="500"
                        value={planningInputs.customGoalCurrent}
                        onChange={(e) => updatePlanningInput('customGoalCurrent', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4 mt-4">
                      <div className="flex justify-between mb-2">
                        <span className="text-slate-400">Monthly Savings Needed:</span>
                        <span className="text-white font-semibold">${planResults.customMonthlySavings}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Time to Goal:</span>
                        <span className="text-white font-semibold">{planResults.customMonthsNeeded} months</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Assumptions */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-6">Planning Assumptions</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Tax Rate: {planningInputs.taxRate}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        step="1"
                        value={planningInputs.taxRate}
                        onChange={(e) => updatePlanningInput('taxRate', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="text-xs text-slate-500">
                      Your effective tax rate on income
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Investment Return: {planningInputs.investmentReturn}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="15"
                        step="0.5"
                        value={planningInputs.investmentReturn}
                        onChange={(e) => updatePlanningInput('investmentReturn', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="text-xs text-slate-500">
                      Expected annual return on investments
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Annual Salary Growth: {planningInputs.salaryGrowth}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        step="0.5"
                        value={planningInputs.salaryGrowth}
                        onChange={(e) => updatePlanningInput('salaryGrowth', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="text-xs text-slate-500">
                      Expected yearly raise/promotion
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Total Savings Summary */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-6">Your Financial Plan Summary</h3>
              <div className={`rounded-xl p-8 ${
                planResults.canAfford
                  ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30'
                  : 'bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/30'
              }`}>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-lg font-semibold mb-4">Monthly Savings Required for All Goals</h4>
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between">
                        <span className="text-slate-300">Emergency Fund:</span>
                        <span className="text-white font-semibold">${planResults.emergencyMonthlySavings}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Home Down Payment:</span>
                        <span className="text-white font-semibold">${planResults.homeMonthlySavings}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Retirement:</span>
                        <span className="text-white font-semibold">${planResults.retirementMonthlySavings}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">{planningInputs.customGoalName}:</span>
                        <span className="text-white font-semibold">${planResults.customMonthlySavings}</span>
                      </div>
                      <div className="flex justify-between border-t border-slate-600 pt-3">
                        <span className="text-white font-bold">Total Monthly Savings:</span>
                        <span className="text-2xl font-bold text-white">${planResults.totalMonthlySavingsNeeded}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold mb-4">Can You Afford It?</h4>
                    <div className="bg-slate-900/50 rounded-lg p-6">
                      <div className="flex justify-between mb-4">
                        <span className="text-slate-300">Monthly Surplus:</span>
                        <span className="text-white font-semibold">${planResults.monthlySurplus}</span>
                      </div>
                      <div className="flex justify-between mb-4">
                        <span className="text-slate-300">Required Savings:</span>
                        <span className="text-white font-semibold">${planResults.totalMonthlySavingsNeeded}</span>
                      </div>
                      <div className={`text-center p-4 rounded-lg ${
                        planResults.canAfford
                          ? 'bg-green-500/20 border border-green-500/40'
                          : 'bg-red-500/20 border border-red-500/40'
                      }`}>
                        {planResults.canAfford ? (
                          <div>
                            <div className="text-2xl font-bold text-green-400 mb-2">✓ Achievable!</div>
                            <p className="text-sm text-green-300">
                              You can afford all your goals with your current income and expenses.
                            </p>
                          </div>
                        ) : (
                          <div>
                            <div className="text-2xl font-bold text-red-400 mb-2">⚠️ Adjust Your Plan</div>
                            <p className="text-sm text-red-300">
                              You need ${(parseFloat(planResults.totalMonthlySavingsNeeded) - parseFloat(planResults.monthlySurplus)).toFixed(2)} more per month. 
                              Consider increasing income or reducing expenses.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* SAVINGS RATE VISUALIZATION */}
            <div className="mb-8">
              <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-2 border-emerald-500/30 rounded-2xl p-8">
                <h3 className="text-2xl font-bold mb-6 flex items-center">
                  <TrendingUp className="w-6 h-6 mr-3 text-emerald-400" />
                  Your Savings Rate
                </h3>
                
                {(() => {
                  const savingsRate = parseFloat(planResults.savingsRate);
                  
                  // Determine status based on savings rate
                  let status = '';
                  let statusColor = '';
                  let statusBg = '';
                  let message = '';
                  
                  if (savingsRate < 0) {
                    status = 'Crisis';
                    statusColor = 'text-red-400';
                    statusBg = 'bg-red-500/20 border-red-500/40';
                    message = "You're spending more than you earn. Immediate action needed!";
                  } else if (savingsRate < 5) {
                    status = 'Critical';
                    statusColor = 'text-red-400';
                    statusBg = 'bg-red-500/20 border-red-500/40';
                    message = 'Very low savings rate. You need to save more for financial security.';
                  } else if (savingsRate < 10) {
                    status = 'Struggling';
                    statusColor = 'text-orange-400';
                    statusBg = 'bg-orange-500/20 border-orange-500/40';
                    message = 'Below recommended. Aim for at least 10% to build wealth.';
                  } else if (savingsRate < 15) {
                    status = 'Building';
                    statusColor = 'text-yellow-400';
                    statusBg = 'bg-yellow-500/20 border-yellow-500/40';
                    message = 'Good start! Keep pushing to reach 20% for optimal wealth building.';
                  } else if (savingsRate < 20) {
                    status = 'Strong';
                    statusColor = 'text-green-400';
                    statusBg = 'bg-green-500/20 border-green-500/40';
                    message = 'Excellent savings rate! You\'re building real wealth.';
                  } else {
                    status = 'Exceptional';
                    statusColor = 'text-emerald-400';
                    statusBg = 'bg-emerald-500/20 border-emerald-500/40';
                    message = 'Outstanding! You\'re on the path to financial independence.';
                  }
                  
                  // Calculate gauge metrics
                  const maxRate = 50; // Max gauge at 50%
                  const displayRate = Math.min(savingsRate, maxRate);
                  const gaugePercent = (displayRate / maxRate) * 100;
                  
                  // Determine gauge color
                  let gaugeColor = '';
                  if (savingsRate < 5) {
                    gaugeColor = 'from-red-500 to-red-600';
                  } else if (savingsRate < 10) {
                    gaugeColor = 'from-orange-500 to-orange-600';
                  } else if (savingsRate < 15) {
                    gaugeColor = 'from-yellow-500 to-yellow-600';
                  } else if (savingsRate < 20) {
                    gaugeColor = 'from-green-500 to-green-600';
                  } else {
                    gaugeColor = 'from-emerald-500 to-emerald-600';
                  }

                  return (
                    <>
                      {/* Clean Progress Bar Visualization */}
                      <div className="mb-8">
                        {/* Large Percentage Display */}
                        <div className="text-center mb-8">
                          <div className={`text-7xl font-bold mb-2 ${statusColor}`}>
                            {savingsRate.toFixed(1)}%
                          </div>
                          <div className="text-xl text-slate-300 mb-1">Savings Rate</div>
                          <div className={`text-2xl font-semibold ${statusColor}`}>
                            {status}
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="mb-6">
                          <div className="flex justify-between text-sm text-slate-400 mb-2">
                            <span>0%</span>
                            <span>10%</span>
                            <span>20%</span>
                            <span>30%</span>
                            <span>40%</span>
                            <span>50%+</span>
                          </div>
                          
                          <div className="relative h-8 bg-slate-800 rounded-full overflow-hidden">
                            {/* Background markers */}
                            <div className="absolute inset-0 flex">
                              <div className="w-[20%] border-r border-slate-700"></div>
                              <div className="w-[20%] border-r border-slate-700"></div>
                              <div className="w-[20%] border-r border-slate-700"></div>
                              <div className="w-[20%] border-r border-slate-700"></div>
                              <div className="w-[20%]"></div>
                            </div>
                            
                            {/* Progress fill */}
                            <div 
                              className={`absolute inset-y-0 left-0 rounded-full transition-all duration-1000 ${
                                savingsRate < 5 ? 'bg-red-500' :
                                savingsRate < 10 ? 'bg-orange-500' :
                                savingsRate < 15 ? 'bg-yellow-500' :
                                savingsRate < 20 ? 'bg-green-500' :
                                'bg-emerald-500'
                              }`}
                              style={{ width: `${Math.min(gaugePercent, 100)}%` }}
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Message card */}
                        <div className={`rounded-xl p-6 ${statusBg} border mb-6`}>
                          <p className="text-slate-300 text-center mb-3">{message}</p>
                          <div className="flex justify-center items-center space-x-2">
                            <span className="text-slate-400 text-sm">Monthly Savings:</span>
                            <span className="text-2xl font-bold text-white">${planResults.monthlySurplus}</span>
                          </div>
                        </div>
                      </div>

                      {/* Benchmarks */}
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                          <div className="text-xs text-slate-400 mb-2">Minimum Target</div>
                          <div className="text-2xl font-bold text-yellow-400 mb-1">10%</div>
                          <p className="text-xs text-slate-500">Basic wealth building</p>
                        </div>
                        
                        <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                          <div className="text-xs text-slate-400 mb-2">Recommended</div>
                          <div className="text-2xl font-bold text-green-400 mb-1">20%</div>
                          <p className="text-xs text-slate-500">Strong financial health</p>
                        </div>
                        
                        <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                          <div className="text-xs text-slate-400 mb-2">Your Rate</div>
                          <div className={`text-2xl font-bold ${statusColor} mb-1`}>
                            {savingsRate.toFixed(1)}%
                          </div>
                          <p className="text-xs text-slate-500">
                            {savingsRate >= 20 ? 'Excellent!' : savingsRate >= 10 ? 'Keep going!' : 'Needs work'}
                          </p>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* 50/30/20 BUDGET ANALYSIS - ENHANCED VERSION */}
            {(() => {
              const afterTaxIncome = parseFloat(planResults.monthlyAfterTaxIncome);
              
              // Categorize expenses
              const needs = planningInputs.housing + planningInputs.utilities + 
                            planningInputs.insurance + planningInputs.healthcare + 
                            planningInputs.transportation + planningInputs.food;
              const wants = planningInputs.discretionary + planningInputs.otherExpenses;
              const savings = parseFloat(planResults.monthlySurplus);
              
              // Calculate percentages
              const needsPercent = afterTaxIncome > 0 ? ((needs / afterTaxIncome) * 100) : 0;
              const wantsPercent = afterTaxIncome > 0 ? ((wants / afterTaxIncome) * 100) : 0;
              const savingsPercent = afterTaxIncome > 0 ? ((savings / afterTaxIncome) * 100) : 0;
              
              // Calculate ideal targets (dollar amounts)
              const idealNeeds = afterTaxIncome * 0.5;
              const idealWants = afterTaxIncome * 0.3;
              const idealSavings = afterTaxIncome * 0.2;
              
              // Calculate differences
              const needsDiff = needs - idealNeeds;
              const wantsDiff = wants - idealWants;
              const savingsDiff = savings - idealSavings;
              
              return (
                <div className="mb-8">
                  <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-2 border-purple-500/30 rounded-2xl p-8">
                    <h3 className="text-2xl font-bold mb-2">50/30/20 Budget Rule</h3>
                    <p className="text-slate-400 text-sm mb-6">Compare your spending to the recommended framework</p>
                    
                    {/* Status Cards */}
                    <div className="grid grid-cols-3 gap-4 mb-8">
                      <div className="bg-slate-900/50 rounded-lg p-4 text-center border border-slate-700">
                        <div className="text-sm text-slate-400 mb-1">Needs</div>
                        <div className={`text-3xl font-bold mb-1 ${
                          needsPercent <= 55 ? 'text-green-400' : 
                          needsPercent <= 60 ? 'text-yellow-400' : 
                          'text-red-400'
                        }`}>
                          {needsPercent.toFixed(0)}%
                        </div>
                        <div className="text-xs text-slate-500">Target: 50%</div>
                      </div>
                      
                      <div className="bg-slate-900/50 rounded-lg p-4 text-center border border-slate-700">
                        <div className="text-sm text-slate-400 mb-1">Wants</div>
                        <div className={`text-3xl font-bold mb-1 ${
                          wantsPercent <= 35 ? 'text-green-400' : 
                          wantsPercent <= 40 ? 'text-yellow-400' : 
                          'text-red-400'
                        }`}>
                          {wantsPercent.toFixed(0)}%
                        </div>
                        <div className="text-xs text-slate-500">Target: 30%</div>
                      </div>
                      
                      <div className="bg-slate-900/50 rounded-lg p-4 text-center border border-slate-700">
                        <div className="text-sm text-slate-400 mb-1">Savings</div>
                        <div className={`text-3xl font-bold mb-1 ${
                          savingsPercent >= 15 ? 'text-green-400' : 
                          savingsPercent >= 10 ? 'text-yellow-400' : 
                          'text-red-400'
                        }`}>
                          {savingsPercent.toFixed(0)}%
                        </div>
                        <div className="text-xs text-slate-500">Target: 20%</div>
                      </div>
                    </div>

                    {/* Side-by-Side Comparison */}
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      {/* Your Budget */}
                      <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700">
                        <h4 className="text-lg font-bold text-center mb-4 text-white">Your Budget</h4>
                        <div className="space-y-4">
                          <div>
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-slate-400">Needs</span>
                              <span className="font-semibold text-white">${needs.toFixed(0)}</span>
                            </div>
                            <div className="h-6 bg-slate-800 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-red-500 flex items-center justify-center text-white text-xs font-bold"
                                style={{ width: `${Math.min(needsPercent, 100)}%` }}
                              >
                                {needsPercent >= 8 ? `${needsPercent.toFixed(0)}%` : ''}
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-slate-400">Wants</span>
                              <span className="font-semibold text-white">${wants.toFixed(0)}</span>
                            </div>
                            <div className="h-6 bg-slate-800 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-orange-500 flex items-center justify-center text-white text-xs font-bold"
                                style={{ width: `${Math.min(wantsPercent, 100)}%` }}
                              >
                                {wantsPercent >= 8 ? `${wantsPercent.toFixed(0)}%` : ''}
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-slate-400">Savings</span>
                              <span className="font-semibold text-white">${savings.toFixed(0)}</span>
                            </div>
                            <div className="h-6 bg-slate-800 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-green-500 flex items-center justify-center text-white text-xs font-bold"
                                style={{ width: `${Math.min(savingsPercent, 100)}%` }}
                              >
                                {savingsPercent >= 8 ? `${savingsPercent.toFixed(0)}%` : ''}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Ideal Budget */}
                      <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700">
                        <h4 className="text-lg font-bold text-center mb-4 text-purple-400">Ideal Budget</h4>
                        <div className="space-y-4">
                          <div>
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-slate-400">Needs (50%)</span>
                              <span className="font-semibold text-white">${idealNeeds.toFixed(0)}</span>
                            </div>
                            <div className="h-6 bg-slate-800 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-red-500/70 flex items-center justify-center text-white text-xs font-bold"
                                style={{ width: '50%' }}
                              >
                                50%
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-slate-400">Wants (30%)</span>
                              <span className="font-semibold text-white">${idealWants.toFixed(0)}</span>
                            </div>
                            <div className="h-6 bg-slate-800 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-orange-500/70 flex items-center justify-center text-white text-xs font-bold"
                                style={{ width: '30%' }}
                              >
                                30%
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-slate-400">Savings (20%)</span>
                              <span className="font-semibold text-white">${idealSavings.toFixed(0)}</span>
                            </div>
                            <div className="h-6 bg-slate-800 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-green-500/70 flex items-center justify-center text-white text-xs font-bold"
                                style={{ width: '20%' }}
                              >
                                20%
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Dollar Targets Summary */}
                    <div className="bg-slate-900/50 rounded-lg p-4 mb-6 border border-slate-700">
                      <h4 className="text-sm font-bold text-slate-300 mb-3">💰 Adjustments to Reach 50/30/20</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center">
                          <div className="text-xs text-slate-400 mb-1">Needs</div>
                          <div className={`text-lg font-bold ${
                            Math.abs(needsDiff) < 50 ? 'text-green-400' :
                            Math.abs(needsDiff) < 200 ? 'text-yellow-400' :
                            'text-red-400'
                          }`}>
                            {needsDiff > 0 ? '-' : '+'}${Math.abs(needsDiff).toFixed(0)}
                          </div>
                          <div className="text-xs text-slate-500">
                            {Math.abs(needsDiff) < 50 ? '✓ Close!' : needsDiff > 0 ? 'Reduce' : 'Increase'}
                          </div>
                        </div>
                        
                        <div className="text-center">
                          <div className="text-xs text-slate-400 mb-1">Wants</div>
                          <div className={`text-lg font-bold ${
                            Math.abs(wantsDiff) < 50 ? 'text-green-400' :
                            Math.abs(wantsDiff) < 200 ? 'text-yellow-400' :
                            'text-red-400'
                          }`}>
                            {wantsDiff > 0 ? '-' : '+'}${Math.abs(wantsDiff).toFixed(0)}
                          </div>
                          <div className="text-xs text-slate-500">
                            {Math.abs(wantsDiff) < 50 ? '✓ Close!' : wantsDiff > 0 ? 'Reduce' : 'Increase'}
                          </div>
                        </div>
                        
                        <div className="text-center">
                          <div className="text-xs text-slate-400 mb-1">Savings</div>
                          <div className={`text-lg font-bold ${
                            Math.abs(savingsDiff) < 50 ? 'text-green-400' :
                            Math.abs(savingsDiff) < 200 ? 'text-yellow-400' :
                            'text-red-400'
                          }`}>
                            {savingsDiff > 0 ? '+' : '-'}${Math.abs(savingsDiff).toFixed(0)}
                          </div>
                          <div className="text-xs text-slate-500">
                            {Math.abs(savingsDiff) < 50 ? '✓ Close!' : savingsDiff < 0 ? 'Needs +$' : 'Extra!'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quick Tip */}
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <div className="text-sm text-slate-300">
                        <span className="font-semibold text-blue-400">💡 Quick Tip:</span> The 50/30/20 rule recommends spending 50% on needs, 30% on wants, and saving 20%. Use the adjustments above to fine-tune your budget.
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ACTIONABLE NEXT STEPS - SMART VERSION */}
            {(() => {
              const afterTaxIncome = parseFloat(planResults.monthlyAfterTaxIncome);
              const monthlySurplus = parseFloat(planResults.monthlySurplus);
              const savingsRate = parseFloat(planResults.savingsRate);
              const canAfford = planResults.canAfford;
              
              // Calculate 50/30/20 data
              const needs = planningInputs.housing + planningInputs.utilities + 
                            planningInputs.insurance + planningInputs.healthcare + 
                            planningInputs.transportation + planningInputs.food;
              const wants = planningInputs.discretionary + planningInputs.otherExpenses;
              const savings = monthlySurplus;
              
              const needsPercent = afterTaxIncome > 0 ? ((needs / afterTaxIncome) * 100) : 0;
              const wantsPercent = afterTaxIncome > 0 ? ((wants / afterTaxIncome) * 100) : 0;
              
              const idealNeeds = afterTaxIncome * 0.5;
              const idealWants = afterTaxIncome * 0.3;
              
              const needsDiff = needs - idealNeeds;
              const wantsDiff = wants - idealWants;
              
              // Determine priorities
              const priorities = [];
              
              // Priority 1: Critical issues (spending more than earning)
              if (monthlySurplus < 0) {
                priorities.push({
                  level: 'critical',
                  title: '🚨 URGENT: Stop Overspending',
                  description: `You're spending $${Math.abs(monthlySurplus).toFixed(0)} more than you earn each month. This is unsustainable and must be addressed immediately.`,
                  actions: [
                    `Cut expenses by at least $${Math.abs(monthlySurplus).toFixed(0)}/month`,
                    'Review all discretionary spending and eliminate non-essentials',
                    'Consider ways to increase income (side hustle, raise)',
                    'Avoid using credit cards until budget is balanced'
                  ],
                  icon: AlertTriangle,
                  color: 'red'
                });
              }
              
              // Priority 2: Very low savings rate
              if (savingsRate < 5 && monthlySurplus >= 0) {
                priorities.push({
                  level: 'high',
                  title: '⚠️ Critical: Build Savings Immediately',
                  description: `Your savings rate is only ${savingsRate.toFixed(1)}%. You need at least 10% to build financial security.`,
                  actions: [
                    `Increase savings by $${((afterTaxIncome * 0.1) - savings).toFixed(0)}/month to reach 10%`,
                    'Start with $1,000 emergency fund this month',
                    'Cut wants by 20% to free up cash',
                    'Automate savings on payday (pay yourself first)'
                  ],
                  icon: PiggyBank,
                  color: 'orange'
                });
              }
              
              // Priority 3: High needs (>55%)
              if (needsPercent > 55) {
                priorities.push({
                  level: 'high',
                  title: '⚠️ Reduce Fixed Expenses',
                  description: `Your needs are ${needsPercent.toFixed(0)}% of income (target: 50%). This leaves little room for savings and wants.`,
                  actions: [
                    `Reduce needs by $${Math.abs(needsDiff).toFixed(0)}/month`,
                    `Housing: ${planningInputs.housing > afterTaxIncome * 0.3 ? 'Consider roommate or cheaper place' : 'Look for ways to reduce'}`,
                    'Shop for cheaper insurance (car, health, life)',
                    'Reduce transportation costs (public transit, carpool)'
                  ],
                  icon: Home,
                  color: 'yellow'
                });
              }
              
              // Priority 4: High wants (>40%)
              if (wantsPercent > 40) {
                priorities.push({
                  level: 'medium',
                  title: '💡 Cut Discretionary Spending',
                  description: `Your wants are ${wantsPercent.toFixed(0)}% (target: 30%). You have room to optimize here.`,
                  actions: [
                    `Reduce wants by $${Math.abs(wantsDiff).toFixed(0)}/month`,
                    'Cancel unused subscriptions (streaming, gym, apps)',
                    'Cook at home 5x per week (vs dining out)',
                    'Use the 24-hour rule for impulse purchases'
                  ],
                  icon: DollarSign,
                  color: 'blue'
                });
              }
              
              // Priority 5: Can't afford goals
              if (!canAfford && monthlySurplus >= 0) {
                const shortfall = parseFloat(planResults.totalMonthlySavingsNeeded) - monthlySurplus;
                priorities.push({
                  level: 'medium',
                  title: '🎯 Adjust Goals or Increase Income',
                  description: `You need $${shortfall.toFixed(0)} more per month to afford all your financial goals.`,
                  actions: [
                    'Prioritize goals: Focus on emergency fund first',
                    'Extend timelines for long-term goals',
                    `Find $${shortfall.toFixed(0)}/month: Reduce expenses or increase income`,
                    'Consider side income (freelance, gig economy)'
                  ],
                  icon: Target,
                  color: 'purple'
                });
              }
              
              // Priority 6: Low but positive savings (5-15%)
              if (savingsRate >= 5 && savingsRate < 15) {
                priorities.push({
                  level: 'medium',
                  title: '📈 Boost Your Savings Rate',
                  description: `Your ${savingsRate.toFixed(1)}% savings rate is okay, but aim for 20% for optimal wealth building.`,
                  actions: [
                    `Increase savings by $${((afterTaxIncome * 0.2) - savings).toFixed(0)}/month to reach 20%`,
                    'Allocate 50% of future raises to savings',
                    'Save all windfalls (tax refunds, bonuses)',
                    'Track spending to find hidden savings opportunities'
                  ],
                  icon: TrendingUp,
                  color: 'green'
                });
              }
              
              // Priority 7: Good position - optimize
              if (savingsRate >= 15 && needsPercent <= 55 && wantsPercent <= 40) {
                priorities.push({
                  level: 'low',
                  title: '✅ Optimize & Grow',
                  description: 'Your budget is in great shape! Focus on optimization and wealth building.',
                  actions: [
                    'Max out retirement accounts (401k, IRA)',
                    'Invest surplus in index funds (VTSAX, VOO)',
                    'Build 6-month emergency fund if not complete',
                    'Consider increasing income through career growth'
                  ],
                  icon: BarChart3,
                  color: 'emerald'
                });
              }
              
              // Always include automation step
              priorities.push({
                level: 'always',
                title: '🤖 Automate Everything',
                description: 'Set up automation to make saving effortless and consistent.',
                actions: [
                  `Auto-transfer $${savings.toFixed(0)} to savings on payday`,
                  'Set up automatic bill payments (avoid late fees)',
                  'Use auto-invest for retirement and brokerage accounts',
                  'Review and adjust automation quarterly'
                ],
                icon: Target,
                color: 'cyan'
              });
              
              // Sort priorities: critical > high > medium > low > always
              const levelOrder = { critical: 0, high: 1, medium: 2, low: 3, always: 4 };
              priorities.sort((a, b) => levelOrder[a.level] - levelOrder[b.level]);
              
              // Take top 3-4 priorities
              const topPriorities = priorities.slice(0, Math.min(4, priorities.length));
              
              return (
                <div className="mb-8">
                  <h3 className="text-2xl font-bold mb-2">Your Personalized Action Plan</h3>
                  <p className="text-slate-400 text-sm mb-6">
                    Based on your financial situation, here are your top priorities (in order of urgency)
                  </p>
                  
                  <div className="space-y-4">
                    {topPriorities.map((priority, index) => {
                      const Icon = priority.icon;
                      const colorClasses = {
                        red: 'from-red-500/10 to-red-600/10 border-red-500/40',
                        orange: 'from-orange-500/10 to-orange-600/10 border-orange-500/40',
                        yellow: 'from-yellow-500/10 to-yellow-600/10 border-yellow-500/40',
                        blue: 'from-blue-500/10 to-cyan-500/10 border-blue-500/40',
                        purple: 'from-purple-500/10 to-pink-500/10 border-purple-500/40',
                        green: 'from-green-500/10 to-emerald-500/10 border-green-500/40',
                        emerald: 'from-emerald-500/10 to-teal-500/10 border-emerald-500/40',
                        cyan: 'from-cyan-500/10 to-blue-500/10 border-cyan-500/40'
                      };
                      
                      const textColorClasses = {
                        red: 'text-red-400',
                        orange: 'text-orange-400',
                        yellow: 'text-yellow-400',
                        blue: 'text-blue-400',
                        purple: 'text-purple-400',
                        green: 'text-green-400',
                        emerald: 'text-emerald-400',
                        cyan: 'text-cyan-400'
                      };
                      
                      return (
                        <div 
                          key={index}
                          className={`bg-gradient-to-br ${colorClasses[priority.color]} border rounded-xl p-6`}
                        >
                          <div className="flex items-start space-x-4">
                            <div className={`p-2 bg-slate-900/50 rounded-lg`}>
                              <Icon className={`w-6 h-6 ${textColorClasses[priority.color]}`} />
                            </div>
                            <div className="flex-1">
                              <h4 className={`text-lg font-bold mb-2 ${textColorClasses[priority.color]}`}>
                                {index + 1}. {priority.title}
                              </h4>
                              <p className="text-sm text-slate-300 mb-4">{priority.description}</p>
                              <div className="bg-slate-900/50 rounded-lg p-4">
                                <div className="text-xs font-semibold text-slate-400 mb-2">ACTION STEPS:</div>
                                <ul className="space-y-2">
                                  {priority.actions.map((action, i) => (
                                    <li key={i} className="text-sm text-slate-300 flex items-start">
                                      <span className={`mr-2 ${textColorClasses[priority.color]}`}>•</span>
                                      <span>{action}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Timeline */}
                  <div className="mt-8 bg-slate-900/50 border border-slate-700 rounded-xl p-6">
                    <h4 className="text-lg font-bold mb-4">📅 Recommended Timeline</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <div className="text-sm font-semibold text-red-400 mb-2">Immediate (This Week)</div>
                        <ul className="text-xs text-slate-300 space-y-1">
                          {monthlySurplus < 0 ? (
                            <>
                              <li>• Stop all non-essential spending</li>
                              <li>• Review budget line by line</li>
                              <li>• Contact creditors if needed</li>
                            </>
                          ) : savingsRate < 5 ? (
                            <>
                              <li>• Set up automatic savings transfer</li>
                              <li>• Cut 3 unnecessary subscriptions</li>
                              <li>• Start $1,000 emergency fund</li>
                            </>
                          ) : (
                            <>
                              <li>• Set up automatic savings</li>
                              <li>• Review all subscriptions</li>
                              <li>• Track spending for 1 week</li>
                            </>
                          )}
                        </ul>
                      </div>
                      
                      <div>
                        <div className="text-sm font-semibold text-yellow-400 mb-2">Short-term (This Month)</div>
                        <ul className="text-xs text-slate-300 space-y-1">
                          <li>• Implement budget adjustments</li>
                          <li>• Shop for better insurance rates</li>
                          <li>• Build $1,000 emergency buffer</li>
                          <li>• Optimize recurring expenses</li>
                        </ul>
                      </div>
                      
                      <div>
                        <div className="text-sm font-semibold text-green-400 mb-2">Long-term (Next 3-6 Months)</div>
                        <ul className="text-xs text-slate-300 space-y-1">
                          <li>• Achieve 20% savings rate</li>
                          <li>• Build 3-6 month emergency fund</li>
                          <li>• Reach 50/30/20 budget balance</li>
                          <li>• Start investing surplus</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* MONTE CARLO RETIREMENT SIMULATOR */}
            <div className="mb-8">
              <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg">
                      <TrendingUp className="w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">Monte Carlo Retirement Simulator</h3>
                      <p className="text-slate-300 text-sm mt-1">See probability of reaching your retirement goal</p>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-6">
                  <p className="text-sm text-purple-300">
                    <strong>🎲 What is Monte Carlo?</strong> Instead of assuming constant returns, we run 1,000 simulations with realistic market volatility 
                    to show you the <strong>probability</strong> of reaching your goal. This accounts for market ups and downs, giving you a more realistic picture than simple calculations.
                  </p>
                </div>

                {!showMonteCarlo ? (
                  <div className="text-center">
                    <button
                      onClick={runMonteCarloSimulation}
                      disabled={isSimulating}
                      className={`px-8 py-4 rounded-lg font-semibold transition-all inline-flex items-center space-x-2 ${
                        isSimulating
                          ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                          : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 shadow-lg'
                      }`}
                    >
                      {isSimulating ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Running 1,000 Simulations...</span>
                        </>
                      ) : (
                        <>
                          <TrendingUp className="w-5 h-5" />
                          <span>Run Monte Carlo Simulation</span>
                        </>
                      )}
                    </button>
                    <p className="text-xs text-slate-400 mt-3">
                      Based on your current plan: ${planningInputs.retirementCurrent.toLocaleString()} invested, 
                      ${parseFloat(planResults.monthlySurplus).toLocaleString()}/month savings, {planningInputs.yearsToRetirement} years to retirement
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Success Probability Header */}
                    <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-2xl p-8 text-center">
                      <div className="mb-4">
                        <div className="text-sm text-slate-400 mb-2">Probability of Success</div>
                        <div className={`text-6xl font-bold mb-2 ${
                          parseFloat(monteCarloResults.successRate) >= 90 ? 'text-green-400' :
                          parseFloat(monteCarloResults.successRate) >= 75 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {monteCarloResults.successRate}%
                        </div>
                        <div className="text-sm text-slate-400">
                          {parseFloat(monteCarloResults.successRate) >= 90 && '🎉 Excellent! You\'re very likely to reach your goal'}
                          {parseFloat(monteCarloResults.successRate) >= 75 && parseFloat(monteCarloResults.successRate) < 90 && '✓ Good chance, but room for improvement'}
                          {parseFloat(monteCarloResults.successRate) < 75 && '⚠️ Needs adjustment to improve success odds'}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                        <div className="bg-slate-900/50 rounded-lg p-3">
                          <div className="text-xs text-slate-400">Your Goal</div>
                          <div className="text-lg font-bold text-white">${(planningInputs.retirementGoal / 1000000).toFixed(1)}M</div>
                        </div>
                        <div className="bg-slate-900/50 rounded-lg p-3">
                          <div className="text-xs text-slate-400">Median Outcome</div>
                          <div className="text-lg font-bold text-purple-400">${(monteCarloResults.percentiles.p50 / 1000000).toFixed(1)}M</div>
                        </div>
                      </div>
                    </div>

                    {/* Percentile Distribution */}
                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                      <h4 className="text-xl font-bold mb-4 flex items-center">
                        <BarChart3 className="w-5 h-5 mr-2 text-purple-400" />
                        Outcome Distribution
                      </h4>
                      <p className="text-sm text-slate-300 mb-4">
                        Here's what your retirement balance could be in different scenarios:
                      </p>
                      
                      <div className="space-y-3">
                        <div className="flex items-center justify-between bg-slate-900/50 rounded-lg p-4">
                          <div>
                            <div className="text-sm font-semibold text-red-300">10th Percentile (Worst 10%)</div>
                            <div className="text-xs text-slate-400">If markets perform poorly</div>
                          </div>
                          <div className="text-2xl font-bold text-red-400">
                            ${(monteCarloResults.percentiles.p10 / 1000000).toFixed(2)}M
                          </div>
                        </div>

                        <div className="flex items-center justify-between bg-slate-900/50 rounded-lg p-4">
                          <div>
                            <div className="text-sm font-semibold text-orange-300">25th Percentile</div>
                            <div className="text-xs text-slate-400">Below average scenarios</div>
                          </div>
                          <div className="text-2xl font-bold text-orange-400">
                            ${(monteCarloResults.percentiles.p25 / 1000000).toFixed(2)}M
                          </div>
                        </div>

                        <div className="flex items-center justify-between bg-purple-500/20 border border-purple-500/40 rounded-lg p-4">
                          <div>
                            <div className="text-sm font-semibold text-purple-300">50th Percentile (Median)</div>
                            <div className="text-xs text-slate-400">Most likely outcome</div>
                          </div>
                          <div className="text-2xl font-bold text-purple-400">
                            ${(monteCarloResults.percentiles.p50 / 1000000).toFixed(2)}M
                          </div>
                        </div>

                        <div className="flex items-center justify-between bg-slate-900/50 rounded-lg p-4">
                          <div>
                            <div className="text-sm font-semibold text-blue-300">75th Percentile</div>
                            <div className="text-xs text-slate-400">Above average scenarios</div>
                          </div>
                          <div className="text-2xl font-bold text-blue-400">
                            ${(monteCarloResults.percentiles.p75 / 1000000).toFixed(2)}M
                          </div>
                        </div>

                        <div className="flex items-center justify-between bg-slate-900/50 rounded-lg p-4">
                          <div>
                            <div className="text-sm font-semibold text-green-300">90th Percentile (Best 10%)</div>
                            <div className="text-xs text-slate-400">If markets perform well</div>
                          </div>
                          <div className="text-2xl font-bold text-green-400">
                            ${(monteCarloResults.percentiles.p90 / 1000000).toFixed(2)}M
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                        <p className="text-xs text-blue-300">
                          💡 <strong>Reading this:</strong> There's a 50% chance you'll have at least ${(monteCarloResults.percentiles.p50 / 1000000).toFixed(2)}M, 
                          and a 90% chance you'll have at least ${(monteCarloResults.percentiles.p10 / 1000000).toFixed(2)}M.
                        </p>
                      </div>
                    </div>

                    {/* Risk Analysis */}
                    {monteCarloResults.failures.count > 0 && (
                      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                        <h4 className="text-xl font-bold mb-4 flex items-center text-yellow-400">
                          <AlertCircle className="w-5 h-5 mr-2" />
                          Risk Analysis
                        </h4>
                        
                        <div className="grid md:grid-cols-3 gap-4 mb-4">
                          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                            <div className="text-sm text-slate-400 mb-1">Scenarios Falling Short</div>
                            <div className="text-2xl font-bold text-red-400">{monteCarloResults.failures.count} / 1,000</div>
                            <div className="text-xs text-slate-500 mt-1">{(100 - parseFloat(monteCarloResults.successRate)).toFixed(1)}% chance</div>
                          </div>

                          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                            <div className="text-sm text-slate-400 mb-1">Average Shortfall</div>
                            <div className="text-2xl font-bold text-orange-400">${(monteCarloResults.failures.avgShortfall / 1000).toFixed(0)}K</div>
                            <div className="text-xs text-slate-500 mt-1">When goal is missed</div>
                          </div>

                          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                            <div className="text-sm text-slate-400 mb-1">Worst Case Scenario</div>
                            <div className="text-2xl font-bold text-red-400">${(monteCarloResults.failures.worstCase / 1000000).toFixed(2)}M</div>
                            <div className="text-xs text-slate-500 mt-1">${(monteCarloResults.failures.worstShortfall / 1000).toFixed(0)}K short</div>
                          </div>
                        </div>

                        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                          <p className="text-xs text-yellow-300">
                            ⚠️ In {monteCarloResults.failures.count} simulations, you fell short by an average of ${(monteCarloResults.failures.avgShortfall / 1000).toFixed(0)}K. 
                            This happens when markets underperform. Consider the recommendations below.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Recommendations */}
                    {monteCarloResults.recommendations.needsMoreSavings && (
                      <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-6">
                        <h4 className="text-xl font-bold mb-4 flex items-center text-green-400">
                          <TrendingUp className="w-5 h-5 mr-2" />
                          Recommendations to Reach 95% Confidence
                        </h4>
                        
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="bg-slate-900/50 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                              <div className="text-2xl">💰</div>
                              <div>
                                <div className="font-semibold text-white mb-1">Increase Monthly Savings</div>
                                <div className="text-sm text-slate-300 mb-2">
                                  Save an additional <span className="text-green-400 font-bold">${monteCarloResults.recommendations.additionalMonthlyNeeded.toLocaleString()}/month</span>
                                </div>
                                <div className="text-xs text-slate-400">
                                  New total: ${(parseFloat(planResults.monthlySurplus) + monteCarloResults.recommendations.additionalMonthlyNeeded).toLocaleString()}/month
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="bg-slate-900/50 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                              <div className="text-2xl">📅</div>
                              <div>
                                <div className="font-semibold text-white mb-1">Work Longer</div>
                                <div className="text-sm text-slate-300 mb-2">
                                  Retire <span className="text-blue-400 font-bold">{monteCarloResults.recommendations.additionalYearsNeeded} years later</span>
                                </div>
                                <div className="text-xs text-slate-400">
                                  Or combine: save ${Math.round(monteCarloResults.recommendations.additionalMonthlyNeeded / 2).toLocaleString()}/month + work 1 year longer
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                          <p className="text-xs text-green-300">
                            ✨ <strong>Good news:</strong> Small adjustments now can dramatically improve your odds. Even $200-300/month more can make the difference between stress and security in retirement.
                          </p>
                        </div>
                      </div>
                    )}

                    {!monteCarloResults.recommendations.needsMoreSavings && (
                      <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-6 text-center">
                        <div className="text-4xl mb-3">🎉</div>
                        <h4 className="text-2xl font-bold text-green-400 mb-2">You're On Track!</h4>
                        <p className="text-slate-300 mb-4">
                          Your current plan has a {monteCarloResults.successRate}% chance of reaching your retirement goal. 
                          Keep up the great work!
                        </p>
                        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 max-w-md mx-auto">
                          <p className="text-xs text-green-300">
                            💡 <strong>Stay on track:</strong> Review this plan every 6 months as your income, expenses, and goals change.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Comparison */}
                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                      <h4 className="text-lg font-bold mb-4">📊 Monte Carlo vs Simple Calculation</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm text-slate-400 mb-2">Simple Calculation (Constant Returns)</div>
                          <div className="text-2xl font-bold text-blue-400">${(monteCarloResults.deterministicResult / 1000000).toFixed(2)}M</div>
                          <div className="text-xs text-slate-500 mt-1">Assumes {planningInputs.investmentReturn}% every year</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400 mb-2">Monte Carlo Median Outcome</div>
                          <div className="text-2xl font-bold text-purple-400">${(monteCarloResults.percentiles.p50 / 1000000).toFixed(2)}M</div>
                          <div className="text-xs text-slate-500 mt-1">50% chance of this or better</div>
                        </div>
                      </div>
                      <div className="mt-3 bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                        <p className="text-xs text-purple-300">
                          💡 Simple calculations assume constant returns, but markets don't work that way. Monte Carlo accounts for real-world volatility.
                        </p>
                      </div>
                    </div>

                    {/* Re-run Button */}
                    <div className="text-center">
                      <button
                        onClick={runMonteCarloSimulation}
                        disabled={isSimulating}
                        className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all inline-flex items-center space-x-2"
                      >
                        <TrendingUp className="w-4 h-4" />
                        <span>Re-run Simulation</span>
                      </button>
                      <p className="text-xs text-slate-400 mt-2">
                        Changed your inputs? Run the simulation again to see updated results.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}


        {/* MORTGAGE CALCULATOR PAGE */}
        {currentPage === 'mortgage' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Mortgage Affordability Calculator</h1>
              <p className="text-slate-300 text-lg">
                Calculate how much house you can afford based on your income, debts, and down payment.
              </p>
            </div>

            {/* How to Use Section */}
            <div className="mb-8 bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-3 flex items-center text-blue-400">
                <BookOpen className="w-5 h-5 mr-2" />
                How to Use This Calculator
              </h3>
              <div className="text-sm text-slate-300 space-y-3">
                <div>
                  <h4 className="font-semibold text-white mb-2">Step-by-Step:</h4>
                  <ol className="space-y-1 list-decimal list-inside ml-2">
                    <li>Enter your annual gross income (before taxes)</li>
                    <li>Add up all monthly debt payments (car loans, credit cards, student loans)</li>
                    <li>Input your down payment amount (typically 3-20% of home price)</li>
                    <li>Set the current interest rate (check with lenders for accurate rates)</li>
                    <li>Choose loan term (15 or 30 years)</li>
                  </ol>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                  <p className="text-xs"><strong className="text-blue-400">💡 Understanding Results:</strong></p>
                  <ul className="text-xs mt-1 space-y-1 ml-2">
                    <li>• <strong>28/36 Rule:</strong> Max 28% of income for housing, 36% for total debt</li>
                    <li>• <strong>DTI:</strong> Aim for under 36% for best loan terms</li>
                    <li>• <strong>PMI:</strong> Required if down payment &lt; 20%, adds to monthly cost</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Calculator Grid */}
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Input Section */}
              <div className="space-y-6">
                {/* Income & Debts Card */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <DollarSign className="w-5 h-5 mr-2 text-green-400" />
                    Income & Existing Debts
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Annual Gross Income</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                        <input
                          type="number"
                          value={mortgageInputs.annualIncome}
                          onChange={(e) => setMortgageInputs({...mortgageInputs, annualIncome: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                          placeholder="80000"
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">Before taxes and deductions</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Monthly Debt Payments</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                        <input
                          type="number"
                          value={mortgageInputs.monthlyDebts}
                          onChange={(e) => setMortgageInputs({...mortgageInputs, monthlyDebts: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                          placeholder="500"
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">Car loans, credit cards, student loans, etc.</p>
                    </div>
                  </div>
                </div>

                {/* Loan Details Card */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Home className="w-5 h-5 mr-2 text-blue-400" />
                    Loan Details
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Down Payment</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                        <input
                          type="number"
                          value={mortgageInputs.downPayment}
                          onChange={(e) => setMortgageInputs({...mortgageInputs, downPayment: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                          placeholder="40000"
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">Typical: 3-20% of home price</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Interest Rate</label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.1"
                          value={mortgageInputs.interestRate}
                          onChange={(e) => setMortgageInputs({...mortgageInputs, interestRate: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-4 pr-8 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                          placeholder="6.5"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">Current average: 6.0-7.5%</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Loan Term</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setMortgageInputs({...mortgageInputs, loanTerm: 15})}
                          className={`py-3 rounded-lg font-medium transition-all ${
                            mortgageInputs.loanTerm === 15
                              ? 'bg-blue-500 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          15 Years
                        </button>
                        <button
                          onClick={() => setMortgageInputs({...mortgageInputs, loanTerm: 30})}
                          className={`py-3 rounded-lg font-medium transition-all ${
                            mortgageInputs.loanTerm === 30
                              ? 'bg-blue-500 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          30 Years
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Presets */}
                <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-xl p-6">
                  <h4 className="font-semibold mb-3 text-purple-400">Quick Scenarios</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setMortgageInputs({
                        annualIncome: 50000,
                        monthlyDebts: 300,
                        downPayment: 15000,
                        interestRate: 6.5,
                        loanTerm: 30
                      })}
                      className="px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-all"
                    >
                      First-Time Buyer
                    </button>
                    <button
                      onClick={() => setMortgageInputs({
                        annualIncome: 100000,
                        monthlyDebts: 500,
                        downPayment: 60000,
                        interestRate: 6.0,
                        loanTerm: 30
                      })}
                      className="px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-all"
                    >
                      Move-Up Buyer
                    </button>
                    <button
                      onClick={() => setMortgageInputs({
                        annualIncome: 150000,
                        monthlyDebts: 800,
                        downPayment: 100000,
                        interestRate: 5.75,
                        loanTerm: 15
                      })}
                      className="px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-all"
                    >
                      High Earner
                    </button>
                    <button
                      onClick={() => setMortgageInputs({
                        annualIncome: 35000,
                        monthlyDebts: 200,
                        downPayment: 10000,
                        interestRate: 7.0,
                        loanTerm: 30
                      })}
                      className="px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-all"
                    >
                      Budget Conscious
                    </button>
                  </div>
                </div>
              </div>

              {/* Results Section */}
              <div className="space-y-6">
                {/* Main Affordability Card */}
                <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-2 border-green-500/40 rounded-xl p-8">
                  <div className="text-center mb-6">
                    <div className="inline-block px-4 py-1.5 bg-green-500/20 border border-green-500/30 rounded-full mb-4">
                      <span className="text-sm text-green-400 font-semibold">Maximum Home Price</span>
                    </div>
                    <div className="text-5xl font-bold text-green-400 mb-2">
                      ${parseFloat(mortgageResults.maxHomePrice).toLocaleString()}
                    </div>
                    <p className="text-slate-300 text-sm">Based on 28/36 debt-to-income ratios</p>
                  </div>

                  <div className="space-y-3 bg-slate-900/50 rounded-lg p-4">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-400">Down Payment:</span>
                      <span className="font-semibold">${mortgageInputs.downPayment.toLocaleString()} ({mortgageResults.downPaymentPercent}%)</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-400">Loan Amount:</span>
                      <span className="font-semibold">${parseFloat(mortgageResults.maxLoanAmount).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-400">Loan Term:</span>
                      <span className="font-semibold">{mortgageInputs.loanTerm} years</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-400">Interest Rate:</span>
                      <span className="font-semibold">{mortgageInputs.interestRate}%</span>
                    </div>
                  </div>
                </div>

                {/* Monthly Payment Breakdown */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-lg font-bold mb-4">Monthly Payment Breakdown</h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center pb-3 border-b border-slate-700">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                        <span className="text-slate-300">Principal & Interest</span>
                      </div>
                      <span className="font-semibold">${mortgageResults.monthlyPayment}</span>
                    </div>
                    
                    <div className="flex justify-between items-center pb-3 border-b border-slate-700">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                        <span className="text-slate-300">Property Tax (est.)</span>
                      </div>
                      <span className="font-semibold">${mortgageResults.propertyTax}</span>
                    </div>
                    
                    <div className="flex justify-between items-center pb-3 border-b border-slate-700">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                        <span className="text-slate-300">Home Insurance (est.)</span>
                      </div>
                      <span className="font-semibold">${mortgageResults.insurance}</span>
                    </div>
                    
                    {parseFloat(mortgageResults.pmi) > 0 && (
                      <div className="flex justify-between items-center pb-3 border-b border-slate-700">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                          <span className="text-slate-300">PMI (&lt; 20% down)</span>
                        </div>
                        <span className="font-semibold text-red-400">${mortgageResults.pmi}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-white font-bold">Total Monthly Payment</span>
                      <span className="text-xl font-bold text-green-400">${mortgageResults.totalMonthlyPayment}</span>
                    </div>
                  </div>
                </div>

                {/* DTI Ratio Card */}
                <div className={`border-2 rounded-xl p-6 ${
                  parseFloat(mortgageResults.dti) <= 36
                    ? 'bg-green-500/10 border-green-500/30'
                    : parseFloat(mortgageResults.dti) <= 43
                    ? 'bg-yellow-500/10 border-yellow-500/30'
                    : 'bg-red-500/10 border-red-500/30'
                }`}>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold">Debt-to-Income Ratio</h4>
                    <div className={`text-3xl font-bold ${
                      parseFloat(mortgageResults.dti) <= 36
                        ? 'text-green-400'
                        : parseFloat(mortgageResults.dti) <= 43
                        ? 'text-yellow-400'
                        : 'text-red-400'
                    }`}>
                      {mortgageResults.dti}%
                    </div>
                  </div>
                  
                  {/* DTI Progress Bar */}
                  <div className="relative h-3 bg-slate-700 rounded-full overflow-hidden mb-3">
                    <div 
                      className={`absolute left-0 top-0 h-full transition-all duration-500 ${
                        parseFloat(mortgageResults.dti) <= 36
                          ? 'bg-green-500'
                          : parseFloat(mortgageResults.dti) <= 43
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(parseFloat(mortgageResults.dti), 100)}%` }}
                    ></div>
                  </div>
                  
                  <div className="flex justify-between text-xs text-slate-400 mb-4">
                    <span>0%</span>
                    <span>36%</span>
                    <span>43%</span>
                    <span>50%+</span>
                  </div>
                  
                  <p className="text-sm text-slate-300">
                    {parseFloat(mortgageResults.dti) <= 36
                      ? '✅ Excellent! Your DTI is within recommended limits. Lenders will view this favorably.'
                      : parseFloat(mortgageResults.dti) <= 43
                      ? '⚠️ Acceptable but high. Some lenders may require additional documentation or higher rates.'
                      : '❌ Too high. Most lenders won\'t approve loans above 43% DTI. Consider reducing debts or increasing income.'}
                  </p>
                </div>

                {/* Tips Card */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
                  <h4 className="font-semibold mb-3 flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-blue-400" />
                    Smart Home Buying Tips
                  </h4>
                  <ul className="text-sm text-slate-300 space-y-2">
                    <li className="flex items-start">
                      <span className="text-green-400 mr-2">•</span>
                      <span>Save 20% down payment to avoid PMI and get better rates</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-2">•</span>
                      <span>Keep DTI below 36% for the best loan terms</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-2">•</span>
                      <span>Budget for maintenance: 1-2% of home value annually</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-2">•</span>
                      <span>Get pre-approved before house hunting</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-2">•</span>
                      <span>Compare rates from 3-5 lenders to find the best deal</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Educational Section */}
            <div className="mt-12 grid md:grid-cols-2 gap-6">
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4 text-blue-400">Understanding Mortgage Leverage</h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                  A mortgage is one of the most common and powerful forms of leverage. When you put down $50,000 
                  to buy a $250,000 home, you're using 5x leverage. If the home appreciates 10% to $275,000, your 
                  $50,000 investment grows to $75,000—a 50% return.
                </p>
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <div className="text-xs font-semibold text-slate-400 mb-2">Example:</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <div className="text-slate-400">Home Price:</div>
                      <div className="font-semibold">$250,000</div>
                    </div>
                    <div>
                      <div className="text-slate-400">Down Payment:</div>
                      <div className="font-semibold">$50,000 (20%)</div>
                    </div>
                    <div>
                      <div className="text-slate-400">10% Appreciation:</div>
                      <div className="font-semibold text-green-400">+$25,000</div>
                    </div>
                    <div>
                      <div className="text-slate-400">Your Return:</div>
                      <div className="font-semibold text-green-400">50% ROI</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4 text-yellow-400">Key Terms Explained</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="font-semibold text-white">Front-End Ratio (28%)</div>
                    <p className="text-slate-400">Housing costs shouldn't exceed 28% of gross monthly income</p>
                  </div>
                  <div>
                    <div className="font-semibold text-white">Back-End Ratio (36%)</div>
                    <p className="text-slate-400">Total debt payments shouldn't exceed 36% of gross monthly income</p>
                  </div>
                  <div>
                    <div className="font-semibold text-white">PMI (Private Mortgage Insurance)</div>
                    <p className="text-slate-400">Required if down payment is less than 20%. Protects the lender.</p>
                  </div>
                  <div>
                    <div className="font-semibold text-white">Principal & Interest</div>
                    <p className="text-slate-400">Principal pays down the loan; interest is the cost of borrowing</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}


        {/* RENTAL PROPERTY ROI CALCULATOR PAGE */}
        {currentPage === 'rental' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Rental Property ROI Calculator</h1>
              <p className="text-slate-300 text-lg">
                Analyze rental property investments with cash flow, cap rate, and cash-on-cash return calculations.
              </p>
            </div>

            {/* How to Use Section */}
            <div className="mb-8 bg-purple-500/10 border border-purple-500/30 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-3 flex items-center text-purple-400">
                <BookOpen className="w-5 h-5 mr-2" />
                How to Use This Calculator
              </h3>
              <div className="text-sm text-slate-300 space-y-3">
                <div>
                  <h4 className="font-semibold text-white mb-2">Quick Start:</h4>
                  <ol className="space-y-1 list-decimal list-inside ml-2">
                    <li>Enter property purchase price and down payment (20-25% typical)</li>
                    <li>Set loan details (interest rate and term)</li>
                    <li>Input expected monthly rent</li>
                    <li>Add vacancy rate (5-10% average)</li>
                    <li>Enter annual expenses (taxes, insurance, maintenance, management)</li>
                  </ol>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                    <p className="text-xs font-semibold text-purple-400">Key Metrics:</p>
                    <ul className="text-xs mt-1 space-y-1">
                      <li>• <strong>Cash Flow:</strong> Monthly income after all expenses</li>
                      <li>• <strong>CoC Return:</strong> Annual return on invested capital</li>
                      <li>• <strong>Cap Rate:</strong> Property yield before financing</li>
                    </ul>
                  </div>
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                    <p className="text-xs font-semibold text-purple-400">Success Benchmarks:</p>
                    <ul className="text-xs mt-1 space-y-1">
                      <li>• <strong>Cash Flow:</strong> Positive = good, $200+/month = excellent</li>
                      <li>• <strong>CoC Return:</strong> 8-12% target</li>
                      <li>• <strong>1% Rule:</strong> Rent ≥ 1% of purchase price</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Calculator Grid */}
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Input Section */}
              <div className="space-y-6">
                {/* Property Purchase Card */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Building2 className="w-5 h-5 mr-2 text-purple-400" />
                    Property Purchase
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Purchase Price</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                        <input
                          type="number"
                          value={rentalInputs.purchasePrice}
                          onChange={(e) => setRentalInputs({...rentalInputs, purchasePrice: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                          placeholder="300000"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Down Payment</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                        <input
                          type="number"
                          value={rentalInputs.downPayment}
                          onChange={(e) => setRentalInputs({...rentalInputs, downPayment: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                          placeholder="60000"
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        {((rentalInputs.downPayment / rentalInputs.purchasePrice) * 100).toFixed(1)}% down
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Interest Rate</label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={rentalInputs.interestRate}
                            onChange={(e) => setRentalInputs({...rentalInputs, interestRate: parseFloat(e.target.value) || 0})}
                            className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-4 pr-8 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                            placeholder="6.5"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Loan Term</label>
                        <select
                          value={rentalInputs.loanTerm}
                          onChange={(e) => setRentalInputs({...rentalInputs, loanTerm: parseFloat(e.target.value)})}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                        >
                          <option value={15}>15 years</option>
                          <option value={30}>30 years</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Income Card */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <DollarSign className="w-5 h-5 mr-2 text-green-400" />
                    Rental Income
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Monthly Rent</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                        <input
                          type="number"
                          value={rentalInputs.monthlyRent}
                          onChange={(e) => setRentalInputs({...rentalInputs, monthlyRent: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                          placeholder="2500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Vacancy Rate</label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.5"
                          value={rentalInputs.vacancy}
                          onChange={(e) => setRentalInputs({...rentalInputs, vacancy: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-4 pr-8 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                          placeholder="5"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">Average: 5-10% in most markets</p>
                    </div>
                  </div>
                </div>

                {/* Expenses Card */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <TrendingDown className="w-5 h-5 mr-2 text-red-400" />
                    Annual Expenses
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Property Tax</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                        <input
                          type="number"
                          value={rentalInputs.propertyTax}
                          onChange={(e) => setRentalInputs({...rentalInputs, propertyTax: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                          placeholder="3600"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Insurance</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                        <input
                          type="number"
                          value={rentalInputs.insurance}
                          onChange={(e) => setRentalInputs({...rentalInputs, insurance: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                          placeholder="1200"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Maintenance</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                        <input
                          type="number"
                          value={rentalInputs.maintenance}
                          onChange={(e) => setRentalInputs({...rentalInputs, maintenance: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                          placeholder="2400"
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">Typically 1% of property value</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Property Management</label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.5"
                          value={rentalInputs.propertyMgmt}
                          onChange={(e) => setRentalInputs({...rentalInputs, propertyMgmt: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-4 pr-8 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                          placeholder="10"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">% of rental income (or $0 if self-managed)</p>
                    </div>
                  </div>
                </div>

                {/* Quick Presets */}
                <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-6">
                  <h4 className="font-semibold mb-3 text-green-400">Example Properties</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setRentalInputs({
                        purchasePrice: 150000,
                        downPayment: 30000,
                        interestRate: 7.0,
                        loanTerm: 30,
                        monthlyRent: 1400,
                        vacancy: 5,
                        propertyTax: 1800,
                        insurance: 900,
                        maintenance: 1500,
                        propertyMgmt: 10
                      })}
                      className="px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-all"
                    >
                      Starter Home
                    </button>
                    <button
                      onClick={() => setRentalInputs({
                        purchasePrice: 300000,
                        downPayment: 60000,
                        interestRate: 6.5,
                        loanTerm: 30,
                        monthlyRent: 2500,
                        vacancy: 5,
                        propertyTax: 3600,
                        insurance: 1200,
                        maintenance: 3000,
                        propertyMgmt: 10
                      })}
                      className="px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-all"
                    >
                      Single Family
                    </button>
                    <button
                      onClick={() => setRentalInputs({
                        purchasePrice: 450000,
                        downPayment: 112500,
                        interestRate: 6.25,
                        loanTerm: 30,
                        monthlyRent: 3800,
                        vacancy: 7,
                        propertyTax: 5400,
                        insurance: 1800,
                        maintenance: 4500,
                        propertyMgmt: 10
                      })}
                      className="px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-all"
                    >
                      Multi-Unit
                    </button>
                    <button
                      onClick={() => setRentalInputs({
                        purchasePrice: 800000,
                        downPayment: 200000,
                        interestRate: 6.0,
                        loanTerm: 30,
                        monthlyRent: 6500,
                        vacancy: 5,
                        propertyTax: 9600,
                        insurance: 3000,
                        maintenance: 8000,
                        propertyMgmt: 10
                      })}
                      className="px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-all"
                    >
                      Premium
                    </button>
                  </div>
                </div>
              </div>

              {/* Results Section */}
              <div className="space-y-6">
                {/* Cash Flow Card */}
                <div className={`border-2 rounded-xl p-8 ${
                  parseFloat(rentalResults.monthlyCashFlow) >= 0
                    ? 'bg-green-500/10 border-green-500/30'
                    : 'bg-red-500/10 border-red-500/30'
                }`}>
                  <div className="text-center mb-6">
                    <div className="inline-block px-4 py-1.5 bg-slate-900/50 border border-slate-700 rounded-full mb-4">
                      <span className="text-sm text-slate-300 font-semibold">Monthly Cash Flow</span>
                    </div>
                    <div className={`text-5xl font-bold mb-2 ${
                      parseFloat(rentalResults.monthlyCashFlow) >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      ${rentalResults.monthlyCashFlow}
                    </div>
                    <p className="text-slate-300 text-sm">
                      ${rentalResults.annualCashFlow} annually
                    </p>
                  </div>

                  <div className="space-y-3 bg-slate-900/50 rounded-lg p-4 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Monthly Mortgage:</span>
                      <span className="font-semibold text-red-400">-${rentalResults.monthlyMortgage}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Effective Income:</span>
                      <span className="font-semibold text-green-400">${rentalResults.effectiveIncome}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Total Expenses:</span>
                      <span className="font-semibold text-red-400">-${rentalResults.totalExpenses}</span>
                    </div>
                  </div>
                </div>

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
                    <div className="text-sm text-blue-400 mb-1">Cash-on-Cash Return</div>
                    <div className="text-3xl font-bold text-white mb-1">
                      {rentalResults.cashOnCashReturn}%
                    </div>
                    <p className="text-xs text-slate-400">Annual return on investment</p>
                  </div>

                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-6">
                    <div className="text-sm text-purple-400 mb-1">Cap Rate</div>
                    <div className="text-3xl font-bold text-white mb-1">
                      {rentalResults.capRate}%
                    </div>
                    <p className="text-xs text-slate-400">Property yield rate</p>
                  </div>

                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
                    <div className="text-sm text-green-400 mb-1">Gross Yield</div>
                    <div className="text-3xl font-bold text-white mb-1">
                      {rentalResults.grossYield}%
                    </div>
                    <p className="text-xs text-slate-400">Before expenses</p>
                  </div>

                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-6">
                    <div className="text-sm text-orange-400 mb-1">NOI</div>
                    <div className="text-3xl font-bold text-white mb-1">
                      ${rentalResults.noi}
                    </div>
                    <p className="text-xs text-slate-400">Net Operating Income</p>
                  </div>
                </div>

                {/* Investment Quality Card */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-lg font-bold mb-4">Investment Quality</h3>
                  
                  {/* Cash Flow Assessment */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-400">Cash Flow</span>
                      <span className={`text-sm font-semibold ${
                        parseFloat(rentalResults.monthlyCashFlow) > 200 ? 'text-green-400' :
                        parseFloat(rentalResults.monthlyCashFlow) > 0 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {parseFloat(rentalResults.monthlyCashFlow) > 200 ? 'Excellent' :
                         parseFloat(rentalResults.monthlyCashFlow) > 0 ? 'Good' : 'Negative'}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${
                          parseFloat(rentalResults.monthlyCashFlow) > 200 ? 'bg-green-500' :
                          parseFloat(rentalResults.monthlyCashFlow) > 0 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(Math.max((parseFloat(rentalResults.monthlyCashFlow) / 500) * 100, 0), 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* CoC Return Assessment */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-400">Cash-on-Cash ROI</span>
                      <span className={`text-sm font-semibold ${
                        parseFloat(rentalResults.cashOnCashReturn) > 10 ? 'text-green-400' :
                        parseFloat(rentalResults.cashOnCashReturn) > 5 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {parseFloat(rentalResults.cashOnCashReturn) > 10 ? 'Excellent' :
                         parseFloat(rentalResults.cashOnCashReturn) > 5 ? 'Good' : 'Below Target'}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${
                          parseFloat(rentalResults.cashOnCashReturn) > 10 ? 'bg-green-500' :
                          parseFloat(rentalResults.cashOnCashReturn) > 5 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(parseFloat(rentalResults.cashOnCashReturn) * 5, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Cap Rate Assessment */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-400">Cap Rate</span>
                      <span className={`text-sm font-semibold ${
                        parseFloat(rentalResults.capRate) > 8 ? 'text-green-400' :
                        parseFloat(rentalResults.capRate) > 5 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {parseFloat(rentalResults.capRate) > 8 ? 'Excellent' :
                         parseFloat(rentalResults.capRate) > 5 ? 'Good' : 'Below Target'}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${
                          parseFloat(rentalResults.capRate) > 8 ? 'bg-green-500' :
                          parseFloat(rentalResults.capRate) > 5 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(parseFloat(rentalResults.capRate) * 8, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Investment Tips */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
                  <h4 className="font-semibold mb-3 flex items-center">
                    <Target className="w-5 h-5 mr-2 text-blue-400" />
                    Rental Property Best Practices
                  </h4>
                  <ul className="text-sm text-slate-300 space-y-2">
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>Target 1% rule: Monthly rent should be ≥1% of purchase price</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>Positive cash flow after all expenses is essential</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>Keep 6+ months of reserves for vacancies and repairs</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>Screen tenants thoroughly to reduce turnover</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>Consider property management if you're not local</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Educational Section */}
            <div className="mt-12 grid md:grid-cols-2 gap-6">
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4 text-purple-400">Understanding Key Metrics</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="font-semibold text-white">Cash-on-Cash Return</div>
                    <p className="text-slate-400">Annual cash flow divided by total cash invested. Target: 8-12%</p>
                  </div>
                  <div>
                    <div className="font-semibold text-white">Cap Rate (Capitalization Rate)</div>
                    <p className="text-slate-400">NOI divided by property value. Measures property yield independent of financing</p>
                  </div>
                  <div>
                    <div className="font-semibold text-white">NOI (Net Operating Income)</div>
                    <p className="text-slate-400">Rental income minus operating expenses (excluding mortgage)</p>
                  </div>
                  <div>
                    <div className="font-semibold text-white">The 1% Rule</div>
                    <p className="text-slate-400">Monthly rent should equal or exceed 1% of purchase price</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4 text-green-400">Example Success Story</h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                  Buy a $300K property with $60K down (20%). Monthly rent of $2,500 generates $30K annually. 
                  After expenses (~$15K including mortgage), you net $15K/year in cash flow.
                </p>
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-slate-400">Investment:</div>
                      <div className="font-semibold text-white">$60,000</div>
                    </div>
                    <div>
                      <div className="text-slate-400">Annual Cash Flow:</div>
                      <div className="font-semibold text-green-400">$15,000</div>
                    </div>
                    <div>
                      <div className="text-slate-400">CoC Return:</div>
                      <div className="font-semibold text-green-400">25%</div>
                    </div>
                    <div>
                      <div className="text-slate-400">Plus Equity:</div>
                      <div className="font-semibold text-blue-400">Build wealth</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* 1031 EXCHANGE CALCULATOR PAGE */}
        {currentPage === '1031-exchange' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">1031 Exchange Calculator</h1>
              <p className="text-slate-300 text-lg">
                Defer capital gains taxes and maximize wealth building through like-kind property exchanges.
              </p>
            </div>

            {/* How to Use Section */}
            <div className="mb-8 bg-green-500/10 border border-green-500/30 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-3 flex items-center text-green-400">
                <BookOpen className="w-5 h-5 mr-2" />
                What is a 1031 Exchange?
              </h3>
              <div className="text-sm text-slate-300 space-y-3">
                <p>
                  A <strong>1031 Exchange</strong> (named after IRC Section 1031) allows real estate investors to defer paying capital gains taxes 
                  by exchanging one investment property for another "like-kind" property. This powerful tax strategy lets you defer 100% of capital 
                  gains and depreciation recapture taxes, keeping more capital working for you.
                </p>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                    <p className="text-xs font-semibold text-green-400">Key Benefits:</p>
                    <ul className="text-xs mt-1 space-y-1">
                      <li>• <strong>Defer ALL capital gains taxes</strong></li>
                      <li>• <strong>Defer depreciation recapture (25% rate)</strong></li>
                      <li>• Keep 100% of equity working for you</li>
                      <li>• Build wealth faster through compounding</li>
                      <li>• Can exchange indefinitely (til death = step-up)</li>
                    </ul>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                    <p className="text-xs font-semibold text-yellow-400">Critical Rules:</p>
                    <ul className="text-xs mt-1 space-y-1">
                      <li>• <strong>45 days</strong> to identify replacement property</li>
                      <li>• <strong>180 days</strong> to close on replacement</li>
                      <li>• Must use Qualified Intermediary (QI)</li>
                      <li>• Must reinvest ALL proceeds (no boot)</li>
                      <li>• Must maintain or increase debt</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Calculator */}
            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              {/* Inputs */}
              <div className="space-y-6">
                {/* Relinquished Property */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Building2 className="w-5 h-5 mr-2 text-red-400" />
                    Relinquished Property (Selling)
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Original Purchase Price
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-400">$</span>
                        <input
                          type="number"
                          value={exchange1031Inputs.originalPurchasePrice}
                          onChange={(e) => setExchange1031Inputs({...exchange1031Inputs, originalPurchasePrice: parseFloat(e.target.value) || 0})}
                          className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-green-500"
                          step="10000"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Sale Price
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-400">$</span>
                        <input
                          type="number"
                          value={exchange1031Inputs.salePrice}
                          onChange={(e) => setExchange1031Inputs({...exchange1031Inputs, salePrice: parseFloat(e.target.value) || 0})}
                          className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-green-500"
                          step="10000"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Depreciation Taken
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-400">$</span>
                        <input
                          type="number"
                          value={exchange1031Inputs.depreciationTaken}
                          onChange={(e) => setExchange1031Inputs({...exchange1031Inputs, depreciationTaken: parseFloat(e.target.value) || 0})}
                          className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-green-500"
                          step="1000"
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        Total depreciation claimed over ownership period
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Selling Costs (Realtor, Closing, etc.)
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-400">$</span>
                        <input
                          type="number"
                          value={exchange1031Inputs.sellingCosts}
                          onChange={(e) => setExchange1031Inputs({...exchange1031Inputs, sellingCosts: parseFloat(e.target.value) || 0})}
                          className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-green-500"
                          step="1000"
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        Typically 5-7% of sale price
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Current Mortgage Balance
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-400">$</span>
                        <input
                          type="number"
                          value={exchange1031Inputs.mortgageBalance}
                          onChange={(e) => setExchange1031Inputs({...exchange1031Inputs, mortgageBalance: parseFloat(e.target.value) || 0})}
                          className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-green-500"
                          step="10000"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Replacement Property */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Home className="w-5 h-5 mr-2 text-green-400" />
                    Replacement Property (Buying)
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Purchase Price
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-400">$</span>
                        <input
                          type="number"
                          value={exchange1031Inputs.replacementPurchasePrice}
                          onChange={(e) => setExchange1031Inputs({...exchange1031Inputs, replacementPurchasePrice: parseFloat(e.target.value) || 0})}
                          className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-green-500"
                          step="10000"
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        Must be ≥ sale price of relinquished property
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        New Mortgage Amount
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-400">$</span>
                        <input
                          type="number"
                          value={exchange1031Inputs.replacementMortgage}
                          onChange={(e) => setExchange1031Inputs({...exchange1031Inputs, replacementMortgage: parseFloat(e.target.value) || 0})}
                          className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-green-500"
                          step="10000"
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        Must be ≥ old mortgage to avoid debt boot
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Closing Costs on New Property
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-400">$</span>
                        <input
                          type="number"
                          value={exchange1031Inputs.replacementClosingCosts}
                          onChange={(e) => setExchange1031Inputs({...exchange1031Inputs, replacementClosingCosts: parseFloat(e.target.value) || 0})}
                          className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-green-500"
                          step="1000"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Qualified Intermediary Fee
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-400">$</span>
                        <input
                          type="number"
                          value={exchange1031Inputs.qualifiedIntermediaryFee}
                          onChange={(e) => setExchange1031Inputs({...exchange1031Inputs, qualifiedIntermediaryFee: parseFloat(e.target.value) || 0})}
                          className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-green-500"
                          step="100"
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        Typically $2,000-$4,000
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tax Rates */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Percent className="w-5 h-5 mr-2 text-yellow-400" />
                    Tax Rates
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Federal Capital Gains Rate
                      </label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="range"
                          min="15"
                          max="20"
                          step="5"
                          value={exchange1031Inputs.federalCapitalGainsTax}
                          onChange={(e) => setExchange1031Inputs({...exchange1031Inputs, federalCapitalGainsTax: parseFloat(e.target.value)})}
                          className="flex-1"
                        />
                        <span className="text-lg font-bold text-green-400 w-12">{exchange1031Inputs.federalCapitalGainsTax}%</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        + 3.8% NIIT (Net Investment Income Tax)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        State Tax Rate
                      </label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="range"
                          min="0"
                          max="13.3"
                          step="0.1"
                          value={exchange1031Inputs.stateTaxRate}
                          onChange={(e) => setExchange1031Inputs({...exchange1031Inputs, stateTaxRate: parseFloat(e.target.value)})}
                          className="flex-1"
                        />
                        <span className="text-lg font-bold text-green-400 w-16">{exchange1031Inputs.stateTaxRate.toFixed(1)}%</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        CA: 13.3% | NY: 10.9% | TX/FL: 0%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="space-y-6">
                {/* Tax Without 1031 */}
                <div className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center text-red-400">
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    Tax if Sold Outright (No 1031)
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-sm text-slate-400">Total Gain</span>
                      <span className="font-bold">${parseInt(exchange1031Results.totalGain).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-sm text-slate-400">Federal Cap Gains Tax</span>
                      <span className="font-bold text-red-400">${parseInt(exchange1031Results.federalCapGainsTax).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-sm text-slate-400">State Tax</span>
                      <span className="font-bold text-red-400">${parseInt(exchange1031Results.stateCapGainsTax).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-sm text-slate-400">Depreciation Recapture (25%)</span>
                      <span className="font-bold text-red-400">${parseInt(exchange1031Results.depreciationRecaptureTax).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2">
                      <span className="font-semibold">Total Tax Owed</span>
                      <span className="text-2xl font-bold text-red-400">${parseInt(exchange1031Results.totalTaxWithout1031).toLocaleString()}</span>
                    </div>
                    <div className="mt-4 pt-4 border-t border-red-500/30">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-400">Net Proceeds After Tax</span>
                        <span className="font-bold">${parseInt(exchange1031Results.netProceedsWithout1031).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 1031 Exchange Requirements */}
                <div className={`border-2 rounded-xl p-6 ${
                  exchange1031Results.meetsValueRequirement && exchange1031Results.meetsDebtRequirement
                    ? 'bg-green-500/10 border-green-500'
                    : 'bg-yellow-500/10 border-yellow-500'
                }`}>
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Shield className="w-5 h-5 mr-2" />
                    1031 Exchange Requirements
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Value Requirement</span>
                      <span className={`font-semibold ${exchange1031Results.meetsValueRequirement ? 'text-green-400' : 'text-yellow-400'}`}>
                        {exchange1031Results.meetsValueRequirement ? '✓ Meets' : '✗ Below'} 
                        <span className="text-xs ml-1">(${parseInt(exchange1031Inputs.replacementPurchasePrice).toLocaleString()} ≥ ${parseInt(exchange1031Inputs.salePrice).toLocaleString()})</span>
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Debt Requirement</span>
                      <span className={`font-semibold ${exchange1031Results.meetsDebtRequirement ? 'text-green-400' : 'text-yellow-400'}`}>
                        {exchange1031Results.meetsDebtRequirement ? '✓ Meets' : '✗ Below'}
                        <span className="text-xs ml-1">(${parseInt(exchange1031Inputs.replacementMortgage).toLocaleString()} ≥ ${parseInt(exchange1031Inputs.mortgageBalance).toLocaleString()})</span>
                      </span>
                    </div>
                    <div className="pt-3 border-t border-slate-700">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-slate-400">Cash from Relinquished</span>
                        <span className="font-bold">${parseInt(exchange1031Results.cashFromRelinquished).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-slate-400">Cash Needed for Replacement</span>
                        <span className="font-bold">${parseInt(exchange1031Results.cashNeededForReplacement).toLocaleString()}</span>
                      </div>
                      {parseInt(exchange1031Results.cashShortfall) > 0 && (
                        <div className="flex justify-between items-center pt-2 border-t border-yellow-500/30">
                          <span className="text-sm text-yellow-400">Additional Cash Needed</span>
                          <span className="font-bold text-yellow-400">${parseInt(exchange1031Results.cashShortfall).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tax Benefit */}
                <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-2 border-green-500 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4">1031 Exchange Tax Benefit</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-400">Tax Without 1031</span>
                      <span className="text-xl font-bold line-through text-slate-400">
                        ${parseInt(exchange1031Results.totalTaxWithout1031).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-400">Tax With 1031</span>
                      <span className="text-xl font-bold text-green-400">
                        ${parseInt(exchange1031Results.taxOnBoot).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-green-500/30">
                      <span className="font-semibold text-green-400">Tax Deferred</span>
                      <span className="text-3xl font-bold text-green-400">
                        ${parseInt(exchange1031Results.taxDeferred).toLocaleString()}
                      </span>
                    </div>
                    <div className="mt-4 pt-4 border-t border-green-500/30">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-400">ROI on QI Fee</span>
                        <span className="text-2xl font-bold text-green-400">
                          {exchange1031Results.roiOn1031}x
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-2">
                        You save ${parseInt(exchange1031Results.taxDeferred).toLocaleString()} for a ${parseInt(exchange1031Inputs.qualifiedIntermediaryFee).toLocaleString()} QI fee
                      </p>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Clock className="w-5 h-5 mr-2 text-purple-400" />
                    Critical Deadlines
                  </h3>
                  <div className="space-y-3">
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-semibold text-purple-400">Identification Deadline</span>
                        <span className="text-lg font-bold">{exchange1031Results.identificationDeadline}</span>
                      </div>
                      <p className="text-xs text-slate-400">
                        45 days from sale to identify up to 3 replacement properties
                      </p>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-semibold text-purple-400">Closing Deadline</span>
                        <span className="text-lg font-bold">{exchange1031Results.closingDeadline}</span>
                      </div>
                      <p className="text-xs text-slate-400">
                        180 days from sale (or tax return due date, whichever is earlier)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Multi-Year Wealth Projection */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <TrendingUp className="w-6 h-6 mr-2 text-green-400" />
                10-Year Wealth Projection (5% Annual Appreciation)
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900/50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Year</th>
                      <th className="px-4 py-3 text-right font-semibold">With 1031 Exchange</th>
                      <th className="px-4 py-3 text-right font-semibold">If Sold Outright</th>
                      <th className="px-4 py-3 text-right font-semibold">Additional Wealth</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {exchange1031Results.projectedYears.map((projection, index) => (
                      <tr key={index} className={index === 0 ? 'bg-green-500/5' : ''}>
                        <td className="px-4 py-3 font-semibold">{projection.year}</td>
                        <td className="px-4 py-3 text-right font-semibold text-green-400">
                          ${(parseInt(projection.value1031) / 1000000).toFixed(2)}M
                        </td>
                        <td className="px-4 py-3 text-right">
                          ${(parseInt(projection.valueSold) / 1000000).toFixed(2)}M
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-green-400">
                          +${(parseInt(projection.additionalWealth) / 1000).toLocaleString()}K
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 text-xs text-slate-400">
                <strong>Note:</strong> This projection assumes 5% annual appreciation and reinvestment of tax savings. 
                The 1031 route keeps your full equity working for you, compounding over time.
              </div>
            </div>

            {/* Educational Content */}
            <div className="space-y-8">
              {/* The 3 Identification Rules */}
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-2xl font-bold mb-4 flex items-center">
                  <BookOpen className="w-6 h-6 mr-2 text-blue-400" />
                  The 3 Identification Rules (Pick One)
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-400 mb-2">1. Three-Property Rule</h4>
                    <p className="text-sm text-slate-300">
                      Identify up to <strong>3 properties</strong> of any value. Most common and flexible option.
                    </p>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-400 mb-2">2. 200% Rule</h4>
                    <p className="text-sm text-slate-300">
                      Identify unlimited properties, but <strong>total value ≤ 200%</strong> of relinquished property value.
                    </p>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-400 mb-2">3. 95% Rule</h4>
                    <p className="text-sm text-slate-300">
                      Identify unlimited properties of any value, but must close on <strong>95% of identified value</strong>.
                    </p>
                  </div>
                </div>
              </div>

              {/* What is Boot */}
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center text-red-400">
                  <AlertCircle className="w-6 h-6 mr-2" />
                  Understanding "Boot" (The Taxable Portion)
                </h3>
                <div className="text-slate-300 space-y-4">
                  <p>
                    <strong>"Boot"</strong> is any value received that is NOT like-kind property. Boot is immediately taxable.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <h4 className="font-semibold text-red-400 mb-2">Types of Boot:</h4>
                      <ul className="text-sm space-y-2">
                        <li>• <strong>Cash Boot:</strong> Taking cash from the exchange</li>
                        <li>• <strong>Debt Boot:</strong> Reducing mortgage debt (old debt &gt; new debt)</li>
                        <li>• <strong>Personal Property:</strong> Furniture, equipment received</li>
                        <li>• <strong>Net Value Boot:</strong> Replacement value &lt; relinquished value</li>
                      </ul>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <h4 className="font-semibold text-green-400 mb-2">How to Avoid Boot:</h4>
                      <ul className="text-sm space-y-2">
                        <li>• <strong>Buy Up:</strong> Replacement value ≥ relinquished value</li>
                        <li>• <strong>Maintain Debt:</strong> New mortgage ≥ old mortgage</li>
                        <li>• <strong>Reinvest All:</strong> Don't take any cash out</li>
                        <li>• <strong>Add Cash:</strong> Add your own funds if needed</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Common Mistakes */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center text-yellow-400">
                  <AlertTriangle className="w-6 h-6 mr-2" />
                  Common 1031 Exchange Mistakes
                </h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-300">
                  <div>
                    <h4 className="font-semibold text-white mb-2">Mistakes That Kill the Exchange:</h4>
                    <ul className="space-y-1">
                      <li>• Taking possession of funds (must use QI)</li>
                      <li>• Missing 45-day identification deadline</li>
                      <li>• Missing 180-day closing deadline</li>
                      <li>• Buying personal use property (primary residence)</li>
                      <li>• Reducing debt on replacement property</li>
                      <li>• Taking cash out ("boot")</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-2">How to Succeed:</h4>
                    <ul className="space-y-1">
                      <li>• Start planning 6+ months before sale</li>
                      <li>• Line up QI before closing on relinquished</li>
                      <li>• Identify properties within 30 days (not 45)</li>
                      <li>• Have backup properties identified</li>
                      <li>• Work with 1031-experienced real estate attorney</li>
                      <li>• Keep detailed records of all transactions</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* When NOT to Do a 1031 */}
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4">When NOT to Do a 1031 Exchange</h3>
                <div className="text-sm text-slate-300 space-y-3">
                  <p>A 1031 exchange isn't always the right choice. Consider selling outright if:</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <h4 className="font-semibold text-white mb-2">Tax Considerations:</h4>
                      <ul className="space-y-1">
                        <li>• You're in a low tax bracket now (15% LTCG)</li>
                        <li>• You expect to be in a higher bracket later</li>
                        <li>• You have large capital loss carryforwards</li>
                        <li>• Your heirs get step-up in basis soon anyway</li>
                      </ul>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <h4 className="font-semibold text-white mb-2">Practical Considerations:</h4>
                      <ul className="space-y-1">
                        <li>• You need the cash for personal use</li>
                        <li>• Can't find suitable replacement property</li>
                        <li>• Market conditions unfavorable</li>
                        <li>• You're done with real estate investing</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="mt-12 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Ready to Execute a 1031 Exchange?</h3>
              <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                A 1031 exchange can save you hundreds of thousands in taxes, but requires careful planning and execution. 
                Always work with experienced professionals: a qualified intermediary, real estate attorney, and CPA.
              </p>
              <div className="flex justify-center gap-4 flex-wrap">
                <button
                  onClick={() => setCurrentPage('rental')}
                  className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
                >
                  Rental Property ROI
                </button>
                <button
                  onClick={() => setCurrentPage('mortgage')}
                  className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
                >
                  Mortgage Calculator
                </button>
                <button
                  onClick={() => setCurrentPage('tax')}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-600 transition-all"
                >
                  More Tax Strategies
                </button>
              </div>
            </div>
          </section>
        )}

        {/* WITHDRAWAL STRATEGY CALCULATOR PAGE */}
        {currentPage === 'withdrawal-strategy' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>

            {/* Hero Section */}
            <div className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border border-purple-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg">
                  <PiggyBank className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold">Retirement Withdrawal Strategy Calculator</h2>
                  <p className="text-slate-300 mt-2">Optimize your retirement withdrawals to minimize lifetime taxes</p>
                </div>
              </div>
              <div className="mt-6 bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <p className="text-sm text-purple-300">
                  💡 <strong>Tax Optimization:</strong> The order you withdraw from retirement accounts can save you $100K+ in taxes. 
                  This calculator shows you the most tax-efficient withdrawal strategy based on your situation.
                </p>
              </div>
            </div>

            {/* Strategy Selector */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4">Select Withdrawal Strategy</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <button
                  onClick={() => updateWithdrawalInput('withdrawalStrategy', 'proportional')}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    withdrawalInputs.withdrawalStrategy === 'proportional'
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                  }`}
                >
                  <h4 className="font-bold text-lg mb-2">Proportional</h4>
                  <p className="text-sm text-slate-300">Withdraw proportionally from all accounts</p>
                  <div className="mt-3 text-xs text-slate-400">✓ Simple | ⚠️ Not tax-optimized</div>
                </button>
                <button
                  onClick={() => updateWithdrawalInput('withdrawalStrategy', 'tax-efficient')}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    withdrawalInputs.withdrawalStrategy === 'tax-efficient'
                      ? 'border-green-500 bg-green-500/10'
                      : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                  }`}
                >
                  <h4 className="font-bold text-lg mb-2">Tax-Efficient</h4>
                  <p className="text-sm text-slate-300">Optimal withdrawal order to minimize taxes</p>
                  <div className="mt-3 text-xs text-green-400">✓ Recommended for most retirees</div>
                </button>
                <button
                  onClick={() => updateWithdrawalInput('withdrawalStrategy', 'roth-conversion')}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    withdrawalInputs.withdrawalStrategy === 'roth-conversion'
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                  }`}
                >
                  <h4 className="font-bold text-lg mb-2">Roth Conversion</h4>
                  <p className="text-sm text-slate-300">Convert traditional to Roth strategically</p>
                  <div className="mt-3 text-xs text-purple-400">✓ Best for long-term tax savings</div>
                </button>
              </div>
            </div>

            {/* How to Use This Calculator */}
            <div className="mb-12 bg-slate-800/30 border border-slate-700 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <span className="mr-2">📖</span> How to Use This Calculator
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-white mb-2">Step 1: Select Strategy</h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• <strong>Proportional:</strong> Simple but not tax-optimized</li>
                    <li>• <strong>Tax-Efficient:</strong> Best order for most retirees</li>
                    <li>• <strong>Roth Conversion:</strong> Strategic conversions for long-term savings</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">Step 2: Enter Your Details</h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• <strong>Account Balances:</strong> Traditional, Roth, taxable accounts</li>
                    <li>• <strong>Annual Expenses:</strong> How much you need per year</li>
                    <li>• <strong>Tax Rates:</strong> Current and expected future rates</li>
                    <li>• <strong>Time Horizon:</strong> Years until 72 (RMD starts)</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                <p className="text-xs text-purple-300">
                  💡 <strong>Quick Tip:</strong> Tax-Efficient strategy typically withdraws taxable first, then traditional, then Roth last to maximize tax-free growth.
                </p>
              </div>
            </div>

            {/* Input Sections */}
            <div className="grid lg:grid-cols-2 gap-8 mb-8">
              {/* Retirement Accounts */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-6 flex items-center">
                  <PiggyBank className="w-5 h-5 mr-2 text-purple-400" />
                  Retirement Account Balances
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">
                      Traditional 401(k): ${withdrawalInputs.traditional401k.toLocaleString()}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="2000000"
                      step="10000"
                      value={withdrawalInputs.traditional401k}
                      onChange={(e) => updateWithdrawalInput('traditional401k', parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">
                      Traditional IRA: ${withdrawalInputs.traditionalIRA.toLocaleString()}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1000000"
                      step="10000"
                      value={withdrawalInputs.traditionalIRA}
                      onChange={(e) => updateWithdrawalInput('traditionalIRA', parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">
                      Roth 401(k): ${withdrawalInputs.roth401k.toLocaleString()}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1000000"
                      step="10000"
                      value={withdrawalInputs.roth401k}
                      onChange={(e) => updateWithdrawalInput('roth401k', parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">
                      Roth IRA: ${withdrawalInputs.rothIRA.toLocaleString()}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1000000"
                      step="10000"
                      value={withdrawalInputs.rothIRA}
                      onChange={(e) => updateWithdrawalInput('rothIRA', parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">
                      Taxable Brokerage: ${withdrawalInputs.taxableBrokerage.toLocaleString()}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="2000000"
                      step="10000"
                      value={withdrawalInputs.taxableBrokerage}
                      onChange={(e) => updateWithdrawalInput('taxableBrokerage', parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Personal Info & Income */}
              <div className="space-y-6">
                {/* Personal Info */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-6">Personal Information</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">Current Age</label>
                        <input
                          type="number"
                          value={withdrawalInputs.currentAge}
                          onChange={(e) => updateWithdrawalInput('currentAge', parseFloat(e.target.value))}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">Life Expectancy</label>
                        <input
                          type="number"
                          value={withdrawalInputs.lifeExpectancy}
                          onChange={(e) => updateWithdrawalInput('lifeExpectancy', parseFloat(e.target.value))}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={withdrawalInputs.married}
                          onChange={(e) => updateWithdrawalInput('married', e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-slate-300">Married</span>
                      </label>
                    </div>
                    {withdrawalInputs.married && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-slate-400 mb-2">Spouse Age</label>
                          <input
                            type="number"
                            value={withdrawalInputs.spouseAge}
                            onChange={(e) => updateWithdrawalInput('spouseAge', parseFloat(e.target.value))}
                            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-slate-400 mb-2">Spouse Life Exp.</label>
                          <input
                            type="number"
                            value={withdrawalInputs.spouseLifeExpectancy}
                            onChange={(e) => updateWithdrawalInput('spouseLifeExpectancy', parseFloat(e.target.value))}
                            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Income & Expenses */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-6">Income & Expenses</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Social Security (Monthly): ${withdrawalInputs.socialSecurityMonthly.toLocaleString()}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="5000"
                        step="100"
                        value={withdrawalInputs.socialSecurityMonthly}
                        onChange={(e) => updateWithdrawalInput('socialSecurityMonthly', parseFloat(e.target.value))}
                        className="w-full"
                      />
                    </div>
                    {withdrawalInputs.married && (
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">
                          Spouse SS (Monthly): ${withdrawalInputs.spouseSocialSecurity.toLocaleString()}
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="5000"
                          step="100"
                          value={withdrawalInputs.spouseSocialSecurity}
                          onChange={(e) => updateWithdrawalInput('spouseSocialSecurity', parseFloat(e.target.value))}
                          className="w-full"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Annual Expenses: ${withdrawalInputs.annualExpenses.toLocaleString()}
                      </label>
                      <input
                        type="range"
                        min="30000"
                        max="200000"
                        step="5000"
                        value={withdrawalInputs.annualExpenses}
                        onChange={(e) => updateWithdrawalInput('annualExpenses', parseFloat(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Results Dashboard */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-6">Your Withdrawal Strategy Results</h3>
              
              {/* Key Metrics */}
              <div className="grid md:grid-cols-4 gap-4 mb-8">
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6">
                  <div className="text-sm text-slate-400 mb-1">Total Portfolio</div>
                  <div className="text-2xl font-bold text-blue-400">${(parseInt(withdrawalResults.totalPortfolio) / 1000000).toFixed(2)}M</div>
                </div>
                <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-6">
                  <div className="text-sm text-slate-400 mb-1">Annual Withdrawal Needed</div>
                  <div className="text-2xl font-bold text-green-400">${(parseInt(withdrawalResults.annualWithdrawalNeeded) / 1000).toFixed(0)}K</div>
                </div>
                <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-6">
                  <div className="text-sm text-slate-400 mb-1">Estimated Annual Taxes</div>
                  <div className="text-2xl font-bold text-red-400">${(parseInt(withdrawalResults.estimatedAnnualTaxes) / 1000).toFixed(0)}K</div>
                  <div className="text-xs text-slate-400 mt-1">{withdrawalResults.effectiveTaxRate}% effective rate</div>
                </div>
                <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border border-purple-500/30 rounded-xl p-6">
                  <div className="text-sm text-slate-400 mb-1">Lifetime Tax Savings</div>
                  <div className="text-2xl font-bold text-purple-400">${(parseInt(withdrawalResults.lifetimeTaxSavings) / 1000).toFixed(0)}K</div>
                  <div className="text-xs text-slate-400 mt-1">vs. proportional</div>
                </div>
              </div>

              {/* Withdrawal Breakdown */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-lg font-bold mb-4">Annual Withdrawal Breakdown</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">From Traditional Accounts</span>
                      <span className="font-bold text-orange-400">${(parseInt(withdrawalResults.traditionalWithdrawal) / 1000).toFixed(0)}K</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">From Roth Accounts</span>
                      <span className="font-bold text-green-400">${(parseInt(withdrawalResults.rothWithdrawal) / 1000).toFixed(0)}K</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">From Taxable Brokerage</span>
                      <span className="font-bold text-blue-400">${(parseInt(withdrawalResults.taxableWithdrawal) / 1000).toFixed(0)}K</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-slate-600 pt-3">
                      <span className="text-slate-300">Social Security</span>
                      <span className="font-bold text-cyan-400">${(parseInt(withdrawalResults.socialSecurityAnnual) / 1000).toFixed(0)}K</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-slate-600 pt-3">
                      <span className="font-semibold">After-Tax Income</span>
                      <span className="font-bold text-emerald-400 text-lg">${(parseInt(withdrawalResults.afterTaxIncome) / 1000).toFixed(0)}K</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-lg font-bold mb-4">Strategy Analysis</h4>
                  <div className="space-y-3 text-sm">
                    {withdrawalInputs.withdrawalStrategy === 'tax-efficient' && (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                        <p className="text-green-300 font-semibold mb-1">✓ Tax-Efficient Strategy Active</p>
                        <p className="text-xs text-slate-300">
                          Withdrawing in optimal order: Taxable → Traditional (up to low bracket) → Roth. 
                          This minimizes your tax burden while preserving tax-free Roth growth.
                        </p>
                      </div>
                    )}
                    {withdrawalInputs.withdrawalStrategy === 'proportional' && (
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                        <p className="text-yellow-300 font-semibold mb-1">⚠️ Proportional Strategy</p>
                        <p className="text-xs text-slate-300">
                          This strategy is simple but not tax-optimized. Consider switching to Tax-Efficient 
                          strategy to save ${(parseInt(withdrawalResults.lifetimeTaxSavings) / 1000).toFixed(0)}K+ in lifetime taxes.
                        </p>
                      </div>
                    )}
                    {withdrawalResults.rmdRequired && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                        <p className="text-red-300 font-semibold mb-1">⚠️ RMD Required</p>
                        <p className="text-xs text-slate-300">
                          At age {withdrawalInputs.currentAge}, you must take ${(parseInt(withdrawalResults.rmdAmount) / 1000).toFixed(0)}K 
                          in Required Minimum Distributions from traditional accounts.
                        </p>
                      </div>
                    )}
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                      <p className="text-blue-300 font-semibold mb-1">Portfolio Longevity</p>
                      <p className="text-xs text-slate-300">
                        Your portfolio is projected to last {withdrawalResults.yearsPortfolioLasts} years 
                        based on {withdrawalInputs.investmentReturn}% annual returns and {withdrawalInputs.inflationRate}% inflation.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Portfolio Projection Chart */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h4 className="text-lg font-bold mb-4">30-Year Portfolio Projection</h4>
                <div className="overflow-x-auto">
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={withdrawalResults.portfolioProjection.slice(0, 31)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="age" stroke="#94a3b8" />
                      <YAxis stroke="#94a3b8" tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                        formatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                      />
                      <Legend />
                      <Area type="monotone" dataKey="traditional" stackId="1" stroke="#f97316" fill="#f97316" fillOpacity={0.6} name="Traditional" />
                      <Area type="monotone" dataKey="roth" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.6} name="Roth" />
                      <Area type="monotone" dataKey="taxable" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} name="Taxable" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Educational Content */}
            <div className="space-y-6 mb-8">
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-2xl font-bold mb-4">Understanding Withdrawal Strategies</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-semibold text-purple-400 mb-2">1. Tax-Efficient Order</h4>
                    <p className="text-sm text-slate-300 mb-3">
                      Withdraw in this order to minimize taxes:
                    </p>
                    <ol className="text-sm text-slate-300 space-y-1 list-decimal list-inside">
                      <li>Taxable brokerage (capital gains tax)</li>
                      <li>Traditional 401(k)/IRA (up to low bracket)</li>
                      <li>Roth accounts (tax-free)</li>
                    </ol>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-400 mb-2">2. RMD Considerations</h4>
                    <p className="text-sm text-slate-300">
                      At age 73, you MUST take Required Minimum Distributions from traditional accounts. 
                      Plan ahead to avoid being forced into higher tax brackets.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-400 mb-2">3. Roth Conversions</h4>
                    <p className="text-sm text-slate-300">
                      Convert traditional IRA to Roth IRA in early retirement (ages 60-72) when income is low. 
                      Pay taxes now at 12-22% vs. 24%+ later with RMDs.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-3 text-emerald-400">Pro Tips for Tax-Efficient Withdrawals</h3>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li>✓ <strong>Fill the brackets:</strong> Withdraw from traditional accounts up to the top of the 12% or 22% bracket</li>
                  <li>✓ <strong>Time Social Security:</strong> Delay SS to age 70 if possible for 8% annual increases</li>
                  <li>✓ <strong>Tax-loss harvesting:</strong> Offset gains in taxable accounts by selling losers</li>
                  <li>✓ <strong>Qualified Charitable Distributions (QCDs):</strong> After age 70½, donate up to $100K from IRA directly to charity (tax-free and counts toward RMD)</li>
                  <li>✓ <strong>Roth ladder:</strong> Convert traditional to Roth in low-income years</li>
                </ul>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border border-purple-500/20 rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Complete Your Tax Strategy</h3>
              <p className="text-slate-300 mb-6">
                Pair your withdrawal strategy with other tax-smart calculators for comprehensive retirement planning.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => setCurrentPage('stock-options')}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all"
                >
                  Stock Sale Tax Calculator
                </button>
                <button
                  onClick={() => setCurrentPage('home')}
                  className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
                >
                  Back to Home
                </button>
              </div>
            </div>
          </section>
        )}

        {/* COST SEGREGATION CALCULATOR PAGE */}
        {currentPage === 'cost-seg' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Cost Segregation Calculator</h1>
              <p className="text-slate-300 text-lg">
                Accelerate depreciation deductions and maximize immediate tax savings on commercial and residential rental properties.
              </p>
            </div>

            {/* How to Use Section */}
            <div className="mb-8 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-3 flex items-center text-emerald-400">
                <BookOpen className="w-5 h-5 mr-2" />
                How Cost Segregation Works
              </h3>
              <div className="text-sm text-slate-300 space-y-3">
                <p>
                  Cost segregation is an <strong>engineering-based tax strategy</strong> that reclassifies property components 
                  from 27.5/39-year depreciation into shorter 5, 7, and 15-year categories. This accelerates deductions and 
                  creates massive upfront tax savings.
                </p>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                    <p className="text-xs font-semibold text-emerald-400">What Gets Reclassified:</p>
                    <ul className="text-xs mt-1 space-y-1">
                      <li>• <strong>5-7 Year:</strong> Carpeting, appliances, fixtures, cabinetry</li>
                      <li>• <strong>15 Year:</strong> Parking lots, landscaping, fencing, sidewalks</li>
                      <li>• <strong>27.5/39 Year:</strong> Structural elements (walls, roof, foundation)</li>
                    </ul>
                  </div>
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                    <p className="text-xs font-semibold text-emerald-400">Key Benefits:</p>
                    <ul className="text-xs mt-1 space-y-1">
                      <li>• <strong>Immediate Savings:</strong> $50K-$500K+ in year 1</li>
                      <li>• <strong>100% Bonus:</strong> 5 & 15-year property fully deductible in year 1</li>
                      <li>• <strong>High ROI:</strong> Study costs $5K-$15K with 300-1000% ROI</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Calculator */}
            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              {/* Inputs */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-6 flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-emerald-400" />
                  Property Details
                </h3>

                <div className="space-y-4">
                  {/* Property Price */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Property Purchase Price
                    </label>
                    <input
                      type="number"
                      value={costSegInputs.propertyPrice}
                      onChange={(e) => setCostSegInputs({...costSegInputs, propertyPrice: parseFloat(e.target.value) || 0})}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-emerald-500"
                      step="10000"
                    />
                    <div className="flex justify-between mt-2">
                      <button onClick={() => setCostSegInputs({...costSegInputs, propertyPrice: 300000})} className="text-xs text-slate-400 hover:text-emerald-400">$300K</button>
                      <button onClick={() => setCostSegInputs({...costSegInputs, propertyPrice: 500000})} className="text-xs text-slate-400 hover:text-emerald-400">$500K</button>
                      <button onClick={() => setCostSegInputs({...costSegInputs, propertyPrice: 1000000})} className="text-xs text-slate-400 hover:text-emerald-400">$1M</button>
                      <button onClick={() => setCostSegInputs({...costSegInputs, propertyPrice: 2000000})} className="text-xs text-slate-400 hover:text-emerald-400">$2M</button>
                    </div>
                  </div>

                  {/* Property Type */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Property Type
                    </label>
                    <select
                      value={costSegInputs.propertyType}
                      onChange={(e) => setCostSegInputs({...costSegInputs, propertyType: e.target.value})}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-emerald-500"
                    >
                      <option value="residential">Residential Rental (27.5 year)</option>
                      <option value="commercial">Commercial (39 year)</option>
                    </select>
                    <p className="text-xs text-slate-400 mt-1">
                      Residential: Apartments, SFR, multifamily. Commercial: Office, retail, industrial.
                    </p>
                  </div>

                  {/* Land Value */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Land Value (Non-Depreciable)
                    </label>
                    <input
                      type="number"
                      value={costSegInputs.landValue}
                      onChange={(e) => setCostSegInputs({...costSegInputs, landValue: parseFloat(e.target.value) || 0})}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-emerald-500"
                      step="5000"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Typically 15-25% of purchase price. Check county tax assessor.
                    </p>
                  </div>

                  {/* Tax Bracket */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Tax Bracket (Federal + State)
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="range"
                        min="20"
                        max="50"
                        value={costSegInputs.taxBracket}
                        onChange={(e) => setCostSegInputs({...costSegInputs, taxBracket: parseInt(e.target.value)})}
                        className="flex-1"
                      />
                      <span className="text-lg font-bold text-emerald-400 w-16">{costSegInputs.taxBracket}%</span>
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 mt-1">
                      <span>20%</span>
                      <span>35%</span>
                      <span>50%</span>
                    </div>
                  </div>

                  {/* Study Cost */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Cost Segregation Study Cost
                    </label>
                    <input
                      type="number"
                      value={costSegInputs.studyCost}
                      onChange={(e) => setCostSegInputs({...costSegInputs, studyCost: parseFloat(e.target.value) || 0})}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-emerald-500"
                      step="500"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Typical range: $5K-$15K depending on property size/complexity.
                    </p>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="space-y-6">
                {/* Component Breakdown */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Target className="w-5 h-5 mr-2 text-emerald-400" />
                    Component Breakdown
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-sm text-slate-400">Land (Non-Depreciable)</span>
                      <span className="font-bold">${parseInt(costSegResults.landValue).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <div>
                        <span className="text-sm text-slate-400">Personal Property</span>
                        <span className="text-xs text-emerald-400 ml-2">(5-7 year)</span>
                      </div>
                      <span className="font-bold text-emerald-400">${parseInt(costSegResults.personalProperty).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <div>
                        <span className="text-sm text-slate-400">Land Improvements</span>
                        <span className="text-xs text-emerald-400 ml-2">(15 year)</span>
                      </div>
                      <span className="font-bold text-emerald-400">${parseInt(costSegResults.landImprovements).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-sm text-slate-400">Building Structure</span>
                        <span className="text-xs text-slate-400 ml-2">({costSegResults.traditionalYears} year)</span>
                      </div>
                      <span className="font-bold">${parseInt(costSegResults.buildingStructure).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Total Property Value</span>
                      <span className="text-xl font-bold text-emerald-400">${costSegInputs.propertyPrice.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Year 1 Savings */}
                <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 border-2 border-emerald-500 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold">Year 1 Tax Savings</h3>
                    <DollarSign className="w-8 h-8 text-emerald-400" />
                  </div>
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm text-slate-400 mb-1">Traditional Depreciation</div>
                      <div className="text-2xl font-bold text-slate-400 line-through">
                        ${parseInt(costSegResults.traditionalYear1Savings).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-emerald-400 mb-1">With Cost Segregation</div>
                      <div className="text-4xl font-bold text-emerald-400">
                        ${parseInt(costSegResults.year1TaxSavings).toLocaleString()}
                      </div>
                    </div>
                    <div className="pt-3 border-t border-emerald-500/30">
                      <div className="text-sm text-emerald-300 mb-1">Additional Year 1 Benefit</div>
                      <div className="text-3xl font-bold text-emerald-300">
                        +${parseInt(costSegResults.additionalYear1Savings).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ROI Metrics */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4">Study ROI Analysis</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-400">Study Cost</span>
                      <span className="font-bold text-red-400">-${costSegInputs.studyCost.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-400">Year 1 Additional Savings</span>
                      <span className="font-bold text-emerald-400">+${parseInt(costSegResults.additionalYear1Savings).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-slate-700">
                      <span className="font-semibold">Net Year 1 Benefit</span>
                      <span className="text-xl font-bold text-emerald-400">${parseInt(costSegResults.netBenefit).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-slate-700">
                      <span className="font-semibold">ROI on Study</span>
                      <span className="text-2xl font-bold text-emerald-400">{costSegResults.roi}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 5-Year Comparison */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <BarChart3 className="w-6 h-6 mr-2 text-emerald-400" />
                5-Year Depreciation Comparison
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Traditional */}
                <div>
                  <h4 className="text-lg font-semibold mb-4 text-slate-400">Traditional Depreciation</h4>
                  <div className="space-y-3">
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="text-sm text-slate-400 mb-1">Annual Deduction</div>
                      <div className="text-2xl font-bold">${parseInt(costSegResults.traditionalAnnual).toLocaleString()}</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="text-sm text-slate-400 mb-1">5-Year Total Deductions</div>
                      <div className="text-2xl font-bold">${parseInt(costSegResults.traditional5Year).toLocaleString()}</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="text-sm text-slate-400 mb-1">5-Year Tax Savings</div>
                      <div className="text-2xl font-bold text-slate-400">${parseInt(costSegResults.traditional5YearSavings).toLocaleString()}</div>
                    </div>
                  </div>
                </div>

                {/* Accelerated */}
                <div>
                  <h4 className="text-lg font-semibold mb-4 text-emerald-400">With Cost Segregation</h4>
                  <div className="space-y-3">
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                      <div className="text-sm text-emerald-400 mb-1">Year 1 Deduction (w/ Bonus)</div>
                      <div className="text-2xl font-bold text-emerald-400">${parseInt(costSegResults.acceleratedYear1).toLocaleString()}</div>
                      <div className="text-xs text-emerald-300 mt-1">{costSegResults.bonusDepPercent}% of property in year 1!</div>
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                      <div className="text-sm text-emerald-400 mb-1">5-Year Total Deductions</div>
                      <div className="text-2xl font-bold text-emerald-400">${parseInt(costSegResults.accelerated5Year).toLocaleString()}</div>
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                      <div className="text-sm text-emerald-400 mb-1">5-Year Tax Savings</div>
                      <div className="text-2xl font-bold text-emerald-300">${parseInt(costSegResults.total5YearSavings).toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Benefit Summary */}
              <div className="mt-6 pt-6 border-t border-slate-700">
                <div className="bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/30 rounded-lg p-6">
                  <div className="grid md:grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-sm text-slate-400 mb-2">Additional 5-Year Savings</div>
                      <div className="text-3xl font-bold text-emerald-400">
                        +${parseInt(costSegResults.additional5YearSavings).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-slate-400 mb-2">NPV of Benefits</div>
                      <div className="text-3xl font-bold text-emerald-400">
                        ${parseInt(costSegResults.npvNetBenefit).toLocaleString()}
                      </div>
                      <div className="text-xs text-emerald-300 mt-1">(7% discount rate)</div>
                    </div>
                    <div>
                      <div className="text-sm text-slate-400 mb-2">Effective ROI</div>
                      <div className="text-3xl font-bold text-emerald-400">
                        {((parseInt(costSegResults.npvNetBenefit) / costSegInputs.studyCost) * 100).toFixed(0)}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Educational Content */}
            <div className="space-y-8">
              {/* What is Cost Segregation */}
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-2xl font-bold mb-4 flex items-center">
                  <BookOpen className="w-6 h-6 mr-2 text-emerald-400" />
                  What is Cost Segregation?
                </h3>
                <div className="text-slate-300 space-y-4">
                  <p>
                    Cost segregation is an <strong>IRS-approved tax deferral strategy</strong> that identifies property components 
                    eligible for accelerated depreciation. Instead of depreciating your entire building over 27.5 or 39 years, 
                    you reclassify components into shorter 5, 7, and 15-year categories.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <h4 className="font-semibold text-emerald-400 mb-2">Who Benefits Most?</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Properties purchased/built for $500K+</li>
                        <li>• High-income owners (35%+ tax bracket)</li>
                        <li>• Recent acquisitions (last 15 years)</li>
                        <li>• Renovated/improved properties</li>
                        <li>• Commercial buildings with equipment</li>
                      </ul>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <h4 className="font-semibold text-emerald-400 mb-2">Key Requirements:</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Property must be income-producing</li>
                        <li>• Detailed engineering-based study</li>
                        <li>• IRS Form 3115 for retroactive studies</li>
                        <li>• Proper documentation and reporting</li>
                        <li>• CPA coordination for tax filing</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Component Examples */}
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-2xl font-bold mb-4">Component Classification Examples</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                    <h4 className="font-semibold text-emerald-400 mb-3 flex items-center">
                      <span className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-sm mr-2">5-7</span>
                      Personal Property
                    </h4>
                    <ul className="text-sm space-y-1 text-slate-300">
                      <li>• Carpeting & flooring</li>
                      <li>• Appliances & equipment</li>
                      <li>• Cabinets & countertops</li>
                      <li>• Lighting fixtures</li>
                      <li>• Security systems</li>
                      <li>• Decorative elements</li>
                    </ul>
                  </div>
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                    <h4 className="font-semibold text-emerald-400 mb-3 flex items-center">
                      <span className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-sm mr-2">15</span>
                      Land Improvements
                    </h4>
                    <ul className="text-sm space-y-1 text-slate-300">
                      <li>• Parking lots & driveways</li>
                      <li>• Landscaping & irrigation</li>
                      <li>• Fencing & gates</li>
                      <li>• Sidewalks & pathways</li>
                      <li>• Outdoor lighting</li>
                      <li>• Signage</li>
                    </ul>
                  </div>
                  <div className="bg-slate-700/30 border border-slate-600 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-400 mb-3 flex items-center">
                      <span className="w-8 h-8 bg-slate-600 rounded-full flex items-center justify-center text-sm mr-2">{costSegInputs.propertyType === 'residential' ? '27.5' : '39'}</span>
                      Building Structure
                    </h4>
                    <ul className="text-sm space-y-1 text-slate-300">
                      <li>• Foundation & framing</li>
                      <li>• Walls & roofing</li>
                      <li>• Windows & doors</li>
                      <li>• HVAC ductwork</li>
                      <li>• Plumbing & electrical (in walls)</li>
                      <li>• Elevators</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Process & Timeline */}
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-2xl font-bold mb-4">The Cost Seg Process</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Hire Qualified Firm</h4>
                      <p className="text-sm text-slate-300">Engage IRS-qualified cost segregation firm with engineers. Get proposal with estimated savings and study cost.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Site Visit & Analysis</h4>
                      <p className="text-sm text-slate-300">Engineer inspects property, reviews blueprints, invoices. Typically takes 4-8 weeks to complete detailed report.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Receive Report</h4>
                      <p className="text-sm text-slate-300">Get detailed breakdown showing component allocations, depreciation schedules, and IRS-compliant documentation.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">File with CPA</h4>
                      <p className="text-sm text-slate-300">CPA files Form 3115 (if retroactive) and adjusts depreciation schedules. Claim savings on current/amended returns.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Important Considerations */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center text-yellow-400">
                  <AlertCircle className="w-6 h-6 mr-2" />
                  Important Considerations
                </h3>
                <div className="text-sm text-slate-300 space-y-3">
                  <div>
                    <strong className="text-white">Recapture on Sale:</strong> Accelerated depreciation is "recaptured" 
                    as ordinary income when you sell (25% rate for real estate). However, the time value of money makes 
                    this worthwhile—tax savings now vs. recapture later.
                  </div>
                  <div>
                    <strong className="text-white">Passive Activity Limits:</strong> Depreciation deductions are passive 
                    losses. May be limited if you don't qualify as a real estate professional ($150K+ AGI limits apply).
                  </div>
                  <div>
                    <strong className="text-white">Bonus Depreciation:</strong> 100% bonus depreciation for 5 & 15-year 
                    property placed in service before 2023. Phases down 20%/year starting 2023 (80% in 2023, 60% in 2024, etc.).
                  </div>
                  <div>
                    <strong className="text-white">Study Quality Matters:</strong> Use IRS-qualified firms with engineers. 
                    Poor studies can trigger audits. Expect to pay $5K-$15K for quality work.
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="mt-12 bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/30 rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Ready to Accelerate Your Depreciation?</h3>
              <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Cost segregation delivers immediate, massive tax savings for property owners. With typical ROIs of 300-1000%, 
                the study cost pays for itself many times over in year one alone.
              </p>
              <div className="flex justify-center gap-4 flex-wrap">
                <button
                  onClick={() => setCurrentPage('rental')}
                  className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
                >
                  Rental Property Calculator
                </button>
                <button
                  onClick={() => setCurrentPage('tax')}
                  className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg font-semibold hover:from-emerald-600 hover:to-green-600 transition-all"
                >
                  Explore Tax Strategies
                </button>
              </div>
            </div>
          </section>
        )}

        {/* 83(B) ELECTION CALCULATOR PAGE */}
        {currentPage === '83b-election' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>

            {/* Hero Section */}
            <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
                  <FileText className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold">83(b) Election Calculator</h2>
                  <p className="text-slate-300 mt-2">Should you file an 83(b) election for your restricted stock?</p>
                </div>
              </div>
              <div className="mt-6 bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
                <p className="text-sm text-indigo-300">
                  ⏰ <strong>Critical Deadline:</strong> You have only 30 days from grant to file an 83(b) election with the IRS. 
                  This calculator shows whether filing can save you tens or hundreds of thousands in taxes.
                </p>
              </div>
            </div>

            {/* What is 83(b) Section */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6">📋 What is an 83(b) Election?</h3>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
                <p className="text-slate-300 mb-6">
                  When you receive <strong>restricted stock</strong> that vests over time, the IRS normally taxes you as it vests. 
                  An <strong>83(b) election</strong> lets you choose to pay taxes on the FULL value NOW (at grant), even though it hasn't vested yet.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
                    <h4 className="font-bold text-red-400 mb-3">WITHOUT 83(b) Election (Default)</h4>
                    <ul className="text-sm text-slate-300 space-y-2">
                      <li>• Pay <strong>ordinary income tax</strong> on value as shares vest</li>
                      <li>• If stock appreciates, you pay tax on higher value</li>
                      <li>• Only appreciation after vesting is capital gains</li>
                      <li>• Can result in massive tax bills during vesting</li>
                    </ul>
                  </div>

                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6">
                    <h4 className="font-bold text-green-400 mb-3">WITH 83(b) Election (Elected)</h4>
                    <ul className="text-sm text-slate-300 space-y-2">
                      <li>• Pay <strong>ordinary income tax</strong> on value NOW (at grant)</li>
                      <li>• ALL future appreciation is <strong>capital gains</strong></li>
                      <li>• Lock in low valuation for tax purposes</li>
                      <li>• Can save 6-figures if company succeeds</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* How to Use This Calculator */}
            <div className="mb-12 bg-slate-800/30 border border-slate-700 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <span className="mr-2">📖</span> How to Use This Calculator
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-white mb-2">Step 1: Enter Grant Details</h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• <strong>Shares Granted:</strong> Total number of restricted shares</li>
                    <li>• <strong>Strike Price:</strong> What you pay per share (often $0 for RSUs)</li>
                    <li>• <strong>FMV at Grant:</strong> Fair market value (409A valuation)</li>
                    <li>• <strong>Vesting Period:</strong> Years until fully vested (typically 4)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">Step 2: Enter Exit Scenario</h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• <strong>Expected FMV at Exit:</strong> Price per share at IPO/acquisition</li>
                    <li>• <strong>Years to Exit:</strong> Time horizon for liquidity event</li>
                    <li>• <strong>Tax Rates:</strong> Your federal and state tax brackets</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-3">
                <p className="text-xs text-indigo-300">
                  💡 <strong>Quick Tip:</strong> The calculator compares tax WITH vs WITHOUT filing 83(b). Green recommendation = file. Red = don't file. Remember: You have only 30 days!
                </p>
              </div>
            </div>

            {/* Calculator Section */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6">🧮 Calculate Your Scenario</h3>
              
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Inputs */}
                <div className="space-y-6">
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                    <h4 className="text-lg font-bold mb-4">Grant Details</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">
                          Number of Shares Granted
                        </label>
                        <input
                          type="number"
                          value={election83bInputs.sharesGranted}
                          onChange={(e) => updateElection83bInput('sharesGranted', e.target.value)}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">
                          Strike Price (What You Pay): ${election83bInputs.strikePrice}
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="10"
                          step="0.10"
                          value={election83bInputs.strikePrice}
                          onChange={(e) => updateElection83bInput('strikePrice', e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">
                          FMV at Grant (409A Value): ${election83bInputs.fmvAtGrant}
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="25"
                          step="0.50"
                          value={election83bInputs.fmvAtGrant}
                          onChange={(e) => updateElection83bInput('fmvAtGrant', e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">
                          Vesting Period: {election83bInputs.vestingYears} years
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="6"
                          step="1"
                          value={election83bInputs.vestingYears}
                          onChange={(e) => updateElection83bInput('vestingYears', e.target.value)}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                    <h4 className="text-lg font-bold mb-4">Exit Scenario</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">
                          Expected FMV at Exit: ${election83bInputs.expectedFMVAtExit}
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="5"
                          value={election83bInputs.expectedFMVAtExit}
                          onChange={(e) => updateElection83bInput('expectedFMVAtExit', e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">
                          Years Until Exit: {election83bInputs.yearsToExit}
                        </label>
                        <input
                          type="range"
                          min="2"
                          max="10"
                          step="1"
                          value={election83bInputs.yearsToExit}
                          onChange={(e) => updateElection83bInput('yearsToExit', e.target.value)}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                    <h4 className="text-lg font-bold mb-4">Tax Rates</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">
                          Ordinary Income Tax Rate: {election83bInputs.ordinaryIncomeTaxRate}%
                        </label>
                        <input
                          type="range"
                          min="22"
                          max="37"
                          step="1"
                          value={election83bInputs.ordinaryIncomeTaxRate}
                          onChange={(e) => updateElection83bInput('ordinaryIncomeTaxRate', e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">
                          State Tax Rate: {election83bInputs.stateTaxRate}%
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="13.3"
                          step="0.5"
                          value={election83bInputs.stateTaxRate}
                          onChange={(e) => updateElection83bInput('stateTaxRate', e.target.value)}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Results */}
                <div className="space-y-6">
                  {/* Recommendation */}
                  <div className={`bg-gradient-to-br ${
                    election83bResults.recommendation.includes('Strong YES') 
                      ? 'from-green-500/20 to-emerald-500/20 border-green-500/40' 
                      : election83bResults.recommendation.includes('YES')
                      ? 'from-green-500/10 to-emerald-500/10 border-green-500/30'
                      : election83bResults.recommendation.includes('Neutral')
                      ? 'from-yellow-500/10 to-amber-500/10 border-yellow-500/30'
                      : 'from-red-500/10 to-orange-500/10 border-red-500/30'
                  } border rounded-xl p-8`}>
                    <h4 className="text-sm text-slate-400 mb-2">Recommendation</h4>
                    <div className={`text-3xl font-bold mb-4 ${election83bResults.recommendationColor}`}>
                      {election83bResults.recommendation}
                    </div>
                    <div className="text-sm text-slate-300">
                      {election83bResults.recommendation.includes('YES') ? (
                        <p>Filing an 83(b) election could save you <strong>${(parseInt(election83bResults.taxSavings) / 1000).toFixed(0)}K</strong> in taxes 
                        ({election83bResults.savingsPercentage}% of your tax bill).</p>
                      ) : election83bResults.recommendation.includes('NO') ? (
                        <p>Based on your scenario, the upfront tax cost outweighs potential savings. Consider not filing.</p>
                      ) : (
                        <p>The benefit is marginal. Decision depends on your risk tolerance and cash availability.</p>
                      )}
                    </div>
                  </div>

                  {/* Tax Comparison */}
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                    <h4 className="text-lg font-bold mb-4">Tax Comparison</h4>
                    
                    <div className="space-y-4">
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                        <div className="text-xs text-slate-400 mb-2">WITHOUT 83(b) Election</div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-300">Tax at Vesting</span>
                            <span className="text-white font-semibold">${(parseInt(election83bResults.totalTaxAtVestingWithout) / 1000).toFixed(0)}K</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-300">Tax at Exit (Cap Gains)</span>
                            <span className="text-white font-semibold">${(parseInt(election83bResults.capitalGainsTaxWithout) / 1000).toFixed(0)}K</span>
                          </div>
                          <div className="flex justify-between border-t border-red-500/30 pt-2">
                            <span className="text-slate-300 font-semibold">Total Tax</span>
                            <span className="text-red-400 font-bold text-lg">${(parseInt(election83bResults.totalTaxWithout83b) / 1000).toFixed(0)}K</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                        <div className="text-xs text-slate-400 mb-2">WITH 83(b) Election</div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-300">Tax at Grant (NOW)</span>
                            <span className="text-white font-semibold">${(parseInt(election83bResults.taxAtGrant) / 1000).toFixed(0)}K</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-300">Tax at Exit (Cap Gains)</span>
                            <span className="text-white font-semibold">${(parseInt(election83bResults.capitalGainsTaxWith) / 1000).toFixed(0)}K</span>
                          </div>
                          <div className="flex justify-between border-t border-green-500/30 pt-2">
                            <span className="text-slate-300 font-semibold">Total Tax</span>
                            <span className="text-green-400 font-bold text-lg">${(parseInt(election83bResults.totalTaxWith83b) / 1000).toFixed(0)}K</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-300 font-semibold">Tax Savings with 83(b)</span>
                          <span className="text-purple-400 font-bold text-xl">${(parseInt(election83bResults.taxSavings) / 1000).toFixed(0)}K</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Net Proceeds */}
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                    <h4 className="text-lg font-bold mb-4">Net Proceeds at Exit</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Total Value at Exit</span>
                        <span className="text-white font-semibold">${(parseInt(election83bResults.totalValueAtExit) / 1000).toFixed(0)}K</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Without 83(b)</span>
                        <span className="text-red-400 font-semibold">${(parseInt(election83bResults.netProceedsWithout) / 1000).toFixed(0)}K</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">With 83(b)</span>
                        <span className="text-green-400 font-bold text-lg">${(parseInt(election83bResults.netProceedsWith) / 1000).toFixed(0)}K</span>
                      </div>
                    </div>
                  </div>

                  {/* Risk Assessment */}
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                    <h4 className="text-lg font-bold mb-4">Risk Assessment</h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-300">Upfront Tax Cost</span>
                        <span className="text-white font-semibold">${(parseInt(election83bResults.upfrontTaxCost) / 1000).toFixed(1)}K</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Risk Level</span>
                        <span className={`font-semibold ${
                          election83bResults.riskLevel === 'High' ? 'text-red-400' : 
                          election83bResults.riskLevel === 'Medium' ? 'text-yellow-400' : 'text-green-400'
                        }`}>{election83bResults.riskLevel}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Break-Even Price</span>
                        <span className="text-white font-semibold">${election83bResults.breakEvenPrice}</span>
                      </div>
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 mt-3">
                        <p className="text-xs text-amber-200">
                          ⚠️ <strong>Risk:</strong> If the company fails, you lose the upfront tax you paid. 
                          The stock must reach ${election83bResults.breakEvenPrice}/share for 83(b) to break even.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Educational Content */}
            <div className="space-y-8 mb-12">
              {/* When to File */}
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-2xl font-bold mb-4">✅ When to File an 83(b) Election</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2 text-sm text-slate-300">
                    <p><strong className="text-green-400">✓ Early-stage startup</strong> - Company valuation is still low</p>
                    <p><strong className="text-green-400">✓ High growth potential</strong> - You believe stock will appreciate significantly</p>
                    <p><strong className="text-green-400">✓ Low current FMV</strong> - 409A valuation is close to strike price</p>
                    <p><strong className="text-green-400">✓ Can afford upfront tax</strong> - You have cash to pay the tax now</p>
                    <p><strong className="text-green-400">✓ Long time horizon</strong> - Planning to stay 5+ years</p>
                  </div>
                  <div className="space-y-2 text-sm text-slate-300">
                    <p><strong className="text-red-400">✗ Late-stage company</strong> - High current valuation = high upfront tax</p>
                    <p><strong className="text-red-400">✗ Uncertain about company</strong> - High risk of failure</p>
                    <p><strong className="text-red-400">✗ Cash-strapped</strong> - Can't afford to pay tax now</p>
                    <p><strong className="text-red-400">✗ Planning to leave soon</strong> - May forfeit unvested shares</p>
                    <p><strong className="text-red-400">✗ FMV >> Strike Price</strong> - Large spread = large upfront tax</p>
                  </div>
                </div>
              </div>

              {/* How to File */}
              <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-6">
                <h3 className="text-2xl font-bold mb-4 text-amber-400">📝 How to File an 83(b) Election</h3>
                <div className="space-y-3 text-sm text-slate-300">
                  <div className="flex items-start space-x-3">
                    <span className="text-amber-400 font-bold">1.</span>
                    <p><strong>Download the form:</strong> Search "83(b) election form" or use IRS sample language</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-amber-400 font-bold">2.</span>
                    <p><strong>Fill it out:</strong> Include your name, SSN, grant date, # of shares, FMV, amount paid</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-amber-400 font-bold">3.</span>
                    <p><strong>Mail to IRS:</strong> Send via certified mail within 30 days of grant to: Internal Revenue Service, [Your IRS Center]</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-amber-400 font-bold">4.</span>
                    <p><strong>Send copy to employer:</strong> Give a copy to your company's finance/HR team</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-amber-400 font-bold">5.</span>
                    <p><strong>Attach to tax return:</strong> Include a copy when you file your taxes for that year</p>
                  </div>
                </div>
                <div className="mt-4 bg-red-500/20 border border-red-500/40 rounded-lg p-4">
                  <p className="text-sm text-red-200">
                    ⏰ <strong>CRITICAL DEADLINE:</strong> You have exactly 30 days from the date you received the restricted stock. 
                    If you miss this deadline, you CANNOT file an 83(b) election for that grant. No exceptions.
                  </p>
                </div>
              </div>

              {/* Real Examples */}
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-2xl font-bold mb-4">📊 Real-World Examples</h3>
                <div className="space-y-6">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <h4 className="font-bold text-green-400 mb-2">Example 1: Early Employee at Startup (SUCCESS)</h4>
                    <div className="text-sm text-slate-300 space-y-1">
                      <p>• <strong>Granted:</strong> 100,000 shares at $0.10 strike, $0.50 FMV</p>
                      <p>• <strong>83(b) tax paid:</strong> $14,000 (37% + 9.3% on $40K spread)</p>
                      <p>• <strong>7 years later:</strong> Company IPOs at $50/share</p>
                      <p>• <strong>Total value:</strong> $5,000,000</p>
                      <p>• <strong>Without 83(b):</strong> ~$2.3M in taxes (ordinary income on vesting + cap gains)</p>
                      <p>• <strong>With 83(b):</strong> ~$1.5M in taxes (just cap gains on appreciation)</p>
                      <p className="text-green-400 font-bold mt-2">💰 Saved: $800,000 by filing 83(b)</p>
                    </div>
                  </div>

                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <h4 className="font-bold text-red-400 mb-2">Example 2: Late-Stage Join (FAILURE)</h4>
                    <div className="text-sm text-slate-300 space-y-1">
                      <p>• <strong>Granted:</strong> 50,000 shares at $5 strike, $15 FMV</p>
                      <p>• <strong>83(b) tax paid:</strong> $230,000 (46.3% on $500K spread)</p>
                      <p>• <strong>2 years later:</strong> Company shuts down, stock worth $0</p>
                      <p className="text-red-400 font-bold mt-2">💸 Lost: $230,000 in upfront tax paid</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Complete Your Tax Strategy</h3>
              <p className="text-slate-300 mb-6">
                Explore other tax calculators to optimize your entire startup compensation package.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => setCurrentPage('stock-options')}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all"
                >
                  Stock Options & QSBS
                </button>
                <button
                  onClick={() => setCurrentPage('stock-tax')}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-lg font-semibold hover:from-purple-600 hover:to-indigo-600 transition-all"
                >
                  Stock Sale Tax Calculator
                </button>
                <button
                  onClick={() => setCurrentPage('home')}
                  className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
                >
                  Back to Home
                </button>
              </div>
            </div>
          </section>
        )}

        {/* STOCK OPTIONS & QSBS CALCULATOR PAGE */}
        {currentPage === 'stock-options' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Stock Options & QSBS Calculator</h1>
              <p className="text-slate-300 text-lg">
                Multi-year projections with $10M QSBS exclusion optimization for startup employees and founders.
              </p>
            </div>

            {/* How to Use Section */}
            <div className="mb-8 bg-purple-500/10 border border-purple-500/30 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-3 flex items-center text-purple-400">
                <BookOpen className="w-5 h-5 mr-2" />
                How QSBS Can Save You Millions
              </h3>
              <div className="text-sm text-slate-300 space-y-3">
                <p>
                  <strong>Qualified Small Business Stock (QSBS)</strong> under IRC Section 1202 allows you to 
                  <strong className="text-purple-400"> exclude up to $10 MILLION in capital gains</strong> from federal taxes 
                  when you sell startup stock. This is one of the most powerful tax benefits available to startup employees and founders.
                </p>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                    <p className="text-xs font-semibold text-purple-400">QSBS Requirements:</p>
                    <ul className="text-xs mt-1 space-y-1">
                      <li>• <strong>C-Corporation:</strong> Must be structured as C-corp (not LLC/S-corp)</li>
                      <li>• <strong>$50M Test:</strong> Assets &lt; $50M when stock issued</li>
                      <li>• <strong>5-Year Hold:</strong> Hold stock for 5+ years from exercise</li>
                      <li>• <strong>Active Business:</strong> 80%+ assets in active business use</li>
                    </ul>
                  </div>
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                    <p className="text-xs font-semibold text-purple-400">Tax Savings Example:</p>
                    <ul className="text-xs mt-1 space-y-1">
                      <li>• <strong>Sale Price:</strong> $15M</li>
                      <li>• <strong>Without QSBS:</strong> $3.57M tax (23.8% LTCG)</li>
                      <li>• <strong>With QSBS:</strong> $1.19M tax (only $5M taxable)</li>
                      <li>• <strong>Savings:</strong> $2.38M saved!</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Calculator */}
            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              {/* Inputs */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-6 flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2 text-purple-400" />
                  Stock Options Details
                </h3>

                <div className="space-y-4">
                  {/* Option Type */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Option Type
                    </label>
                    <select
                      value={stockOptionsInputs.optionType}
                      onChange={(e) => setStockOptionsInputs({...stockOptionsInputs, optionType: e.target.value})}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                    >
                      <option value="ISO">ISO (Incentive Stock Options)</option>
                      <option value="NSO">NSO (Non-Qualified Stock Options)</option>
                    </select>
                    <p className="text-xs text-slate-400 mt-1">
                      ISOs: Better tax treatment but AMT risk. NSOs: Ordinary income at exercise.
                    </p>
                  </div>

                  {/* Shares Granted */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Shares Granted
                    </label>
                    <input
                      type="number"
                      value={stockOptionsInputs.sharesGranted}
                      onChange={(e) => setStockOptionsInputs({...stockOptionsInputs, sharesGranted: parseFloat(e.target.value) || 0})}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                      step="1000"
                    />
                  </div>

                  {/* Strike Price */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Strike Price (per share)
                    </label>
                    <div className="flex items-center space-x-2">
                      <span className="text-slate-400">$</span>
                      <input
                        type="number"
                        value={stockOptionsInputs.strikePrice}
                        onChange={(e) => setStockOptionsInputs({...stockOptionsInputs, strikePrice: parseFloat(e.target.value) || 0})}
                        className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                        step="0.01"
                      />
                    </div>
                  </div>

                  {/* Current FMV */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Current Fair Market Value (per share)
                    </label>
                    <div className="flex items-center space-x-2">
                      <span className="text-slate-400">$</span>
                      <input
                        type="number"
                        value={stockOptionsInputs.currentFMV}
                        onChange={(e) => setStockOptionsInputs({...stockOptionsInputs, currentFMV: parseFloat(e.target.value) || 0})}
                        className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                        step="0.50"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      Current 409A valuation from most recent preferred round
                    </p>
                  </div>

                  {/* Projected Sale Price */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Projected Sale Price (per share)
                    </label>
                    <div className="flex items-center space-x-2">
                      <span className="text-slate-400">$</span>
                      <input
                        type="number"
                        value={stockOptionsInputs.projectedSalePrice}
                        onChange={(e) => setStockOptionsInputs({...stockOptionsInputs, projectedSalePrice: parseFloat(e.target.value) || 0})}
                        className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                        step="1"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      Estimated exit price at IPO or acquisition
                    </p>
                  </div>

                  {/* Exercise Year */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Exercise Year
                    </label>
                    <input
                      type="number"
                      value={stockOptionsInputs.exerciseYear}
                      onChange={(e) => setStockOptionsInputs({...stockOptionsInputs, exerciseYear: parseInt(e.target.value) || 2025})}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                    />
                  </div>

                  {/* Expected Sale Year */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Expected Sale Year (IPO/Acquisition)
                    </label>
                    <input
                      type="number"
                      value={stockOptionsInputs.expectedSaleYear}
                      onChange={(e) => setStockOptionsInputs({...stockOptionsInputs, expectedSaleYear: parseInt(e.target.value) || 2030})}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Need 5+ years after exercise for QSBS eligibility
                    </p>
                  </div>

                  {/* QSBS Eligible */}
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={stockOptionsInputs.qsbsEligible}
                        onChange={(e) => setStockOptionsInputs({...stockOptionsInputs, qsbsEligible: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium">Company meets QSBS requirements</span>
                    </label>
                    <p className="text-xs text-slate-400 mt-1">
                      C-corp with &lt;$50M assets when stock issued
                    </p>
                  </div>

                  {/* Tax Bracket */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Federal Tax Bracket
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="range"
                        min="24"
                        max="37"
                        value={stockOptionsInputs.taxBracket}
                        onChange={(e) => setStockOptionsInputs({...stockOptionsInputs, taxBracket: parseInt(e.target.value)})}
                        className="flex-1"
                      />
                      <span className="text-lg font-bold text-purple-400 w-12">{stockOptionsInputs.taxBracket}%</span>
                    </div>
                  </div>

                  {/* State Tax Rate */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      State Tax Rate
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="range"
                        min="0"
                        max="13.3"
                        step="0.1"
                        value={stockOptionsInputs.stateTaxRate}
                        onChange={(e) => setStockOptionsInputs({...stockOptionsInputs, stateTaxRate: parseFloat(e.target.value)})}
                        className="flex-1"
                      />
                      <span className="text-lg font-bold text-purple-400 w-16">{stockOptionsInputs.stateTaxRate.toFixed(1)}%</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      CA: 13.3% | NY: 10.9% | TX/FL: 0% | Most states: 4-6%
                    </p>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="space-y-6">
                {/* Exercise Cost & Tax */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <DollarSign className="w-5 h-5 mr-2 text-purple-400" />
                    Exercise Economics
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-sm text-slate-400">Cost to Exercise</span>
                      <span className="font-bold">${parseInt(stockOptionsResults.exerciseCost).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-sm text-slate-400">Bargain Element (Spread)</span>
                      <span className="font-bold text-purple-400">${parseInt(stockOptionsResults.exerciseSpread).toLocaleString()}</span>
                    </div>
                    {stockOptionsInputs.optionType === 'ISO' ? (
                      <>
                        <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                          <span className="text-sm text-slate-400">AMT Liability (28%)</span>
                          <span className="font-bold text-orange-400">${parseInt(stockOptionsResults.amtLiability).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-400">Regular Tax at Exercise</span>
                          <span className="font-bold text-green-400">$0</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-400">Ordinary Income Tax</span>
                        <span className="font-bold text-red-400">${parseInt(stockOptionsResults.exerciseTaxNSO).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* QSBS Eligibility */}
                <div className={`border-2 rounded-xl p-6 ${
                  stockOptionsResults.meetsHoldingPeriod && stockOptionsInputs.qsbsEligible
                    ? 'bg-green-500/10 border-green-500'
                    : 'bg-yellow-500/10 border-yellow-500'
                }`}>
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Shield className="w-5 h-5 mr-2" />
                    QSBS Eligibility Status
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Holding Period</span>
                      <span className={`font-semibold ${stockOptionsResults.meetsHoldingPeriod ? 'text-green-400' : 'text-yellow-400'}`}>
                        {stockOptionsResults.holdingPeriodYears} years {stockOptionsResults.meetsHoldingPeriod ? '✓' : `(need ${5 - stockOptionsResults.holdingPeriodYears} more)`}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Qualified Corporation</span>
                      <span className={`font-semibold ${stockOptionsResults.isQualifiedCorporation ? 'text-green-400' : 'text-red-400'}`}>
                        {stockOptionsResults.isQualifiedCorporation ? '✓ Yes' : '✗ No'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">QSBS Election</span>
                      <span className={`font-semibold ${stockOptionsInputs.qsbsEligible ? 'text-green-400' : 'text-red-400'}`}>
                        {stockOptionsInputs.qsbsEligible ? '✓ Enabled' : '✗ Disabled'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* QSBS Tax Savings */}
                <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-2 border-purple-500 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4">QSBS Tax Savings</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-400">Total Gain at Sale</span>
                      <span className="text-2xl font-bold">${(parseInt(stockOptionsResults.totalGain) / 1000000).toFixed(1)}M</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-purple-300">QSBS Excludable Gain</span>
                      <span className="text-2xl font-bold text-purple-300">
                        ${(parseInt(stockOptionsResults.qsbsExcludableGain) / 1000000).toFixed(1)}M
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-purple-500/30">
                      <span className="text-sm text-slate-400">Taxable Gain</span>
                      <span className="text-xl font-bold">${(parseInt(stockOptionsResults.taxableGain) / 1000000).toFixed(1)}M</span>
                    </div>
                    <div className="mt-4 pt-4 border-t border-purple-500/30">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-slate-400">Tax Without QSBS</span>
                        <span className="text-lg font-bold text-slate-400 line-through">
                          ${(parseInt(stockOptionsResults.totalTaxWithoutQSBS) / 1000000).toFixed(2)}M
                        </span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-purple-300">Tax With QSBS</span>
                        <span className="text-2xl font-bold text-purple-300">
                          ${(parseInt(stockOptionsResults.totalTaxWithQSBS) / 1000000).toFixed(2)}M
                        </span>
                      </div>
                      <div className="flex justify-between items-center pt-3 border-t border-purple-500/30">
                        <span className="font-semibold text-green-400">QSBS Tax Savings</span>
                        <span className="text-3xl font-bold text-green-400">
                          ${(parseInt(stockOptionsResults.qsbsTaxSavings) / 1000000).toFixed(2)}M
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Net Proceeds */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4">Net Proceeds After Tax</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-400">Without QSBS</span>
                      <span className="text-xl font-bold">${(parseInt(stockOptionsResults.netProceedsWithoutQSBS) / 1000000).toFixed(2)}M</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-slate-700">
                      <span className="font-semibold text-purple-400">With QSBS</span>
                      <span className="text-3xl font-bold text-purple-400">
                        ${(parseInt(stockOptionsResults.netProceedsWithQSBS) / 1000000).toFixed(2)}M
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 mt-2">
                      Effective tax rate: {stockOptionsResults.effectiveTaxRate}%
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Multi-Year Projection Table */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <BarChart3 className="w-6 h-6 mr-2 text-purple-400" />
                Multi-Year Projection
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900/50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Year</th>
                      <th className="px-4 py-3 text-right font-semibold">Est. Price/Share</th>
                      <th className="px-4 py-3 text-right font-semibold">Portfolio Value</th>
                      <th className="px-4 py-3 text-right font-semibold">Unrealized Gain</th>
                      <th className="px-4 py-3 text-center font-semibold">Holding Years</th>
                      <th className="px-4 py-3 text-center font-semibold">QSBS Status</th>
                      <th className="px-4 py-3 text-right font-semibold">Tax if Sold</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {stockOptionsResults.yearlyProjections.map((projection, index) => (
                      <tr key={index} className={projection.qsbsQualified ? 'bg-green-500/5' : ''}>
                        <td className="px-4 py-3 font-semibold">{projection.year}</td>
                        <td className="px-4 py-3 text-right">${projection.estimatedPrice}</td>
                        <td className="px-4 py-3 text-right font-semibold">
                          ${(parseInt(projection.estimatedValue) / 1000000).toFixed(2)}M
                        </td>
                        <td className="px-4 py-3 text-right text-purple-400">
                          ${(parseInt(projection.unrealizedGain) / 1000000).toFixed(2)}M
                        </td>
                        <td className="px-4 py-3 text-center">{projection.holdingYears}</td>
                        <td className="px-4 py-3 text-center">
                          {projection.qsbsQualified ? (
                            <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded">✓ QSBS</span>
                          ) : (
                            <span className="px-2 py-1 bg-slate-700 text-slate-400 text-xs rounded">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">
                          ${(parseInt(projection.potentialTax) / 1000000).toFixed(2)}M
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 text-xs text-slate-400">
                <strong>Note:</strong> QSBS exclusion kicks in after 5-year holding period. Table assumes linear price appreciation.
              </div>
            </div>

            {/* Optimal Strategy */}
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-6 mb-12">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <Target className="w-5 h-5 mr-2 text-purple-400" />
                Recommended Strategy
              </h3>
              <div className="text-lg font-semibold text-purple-300 mb-4">
                {stockOptionsResults.optimalStrategy}
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Key Actions:</h4>
                  <ul className="space-y-1 text-slate-300">
                    <li>• Exercise before options expire (typically 10 years from grant)</li>
                    <li>• Hold for 5+ years after exercise for QSBS</li>
                    <li>• Keep detailed records of exercise dates and costs</li>
                    <li>• File 83(b) election within 30 days if early exercising</li>
                  </ul>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Tax Considerations:</h4>
                  <ul className="space-y-1 text-slate-300">
                    <li>• ISOs: Watch out for AMT on exercise spread</li>
                    <li>• AMT credit can offset regular tax in future years</li>
                    <li>• QSBS only applies to original issuance (not secondary)</li>
                    <li>• Consult tax professional before exercising</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Educational Content */}
            <div className="space-y-8">
              {/* Understanding QSBS */}
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-2xl font-bold mb-4 flex items-center">
                  <BookOpen className="w-6 h-6 mr-2 text-purple-400" />
                  Understanding QSBS (Section 1202)
                </h3>
                <div className="text-slate-300 space-y-4">
                  <p>
                    <strong>Qualified Small Business Stock (QSBS)</strong> is one of the most powerful tax benefits in the tax code, 
                    allowing founders and early employees to exclude up to <strong className="text-purple-400">$10 MILLION</strong> in 
                    capital gains from federal taxes when they sell their startup stock.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <h4 className="font-semibold text-purple-400 mb-2">The 5 QSBS Requirements:</h4>
                      <ol className="text-sm space-y-2 list-decimal list-inside">
                        <li><strong>C-Corporation:</strong> Must be a domestic C-corp (not LLC, partnership, or S-corp)</li>
                        <li><strong>$50M Asset Test:</strong> Gross assets &lt; $50M before AND immediately after stock issuance</li>
                        <li><strong>Active Business:</strong> 80%+ of assets must be in active conduct of qualified trade/business</li>
                        <li><strong>Original Issuance:</strong> Stock must be acquired from company (not secondary market)</li>
                        <li><strong>5-Year Hold:</strong> Hold stock for minimum 5 years from date of acquisition</li>
                      </ol>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <h4 className="font-semibold text-purple-400 mb-2">What Businesses Qualify?</h4>
                      <div className="text-sm space-y-2">
                        <div>
                          <strong className="text-green-400">✓ Qualified:</strong>
                          <ul className="ml-4 mt-1 space-y-1">
                            <li>• SaaS/Software companies</li>
                            <li>• E-commerce & marketplaces</li>
                            <li>• Hardware/Manufacturing</li>
                            <li>• Biotech & life sciences</li>
                            <li>• Most technology startups</li>
                          </ul>
                        </div>
                        <div>
                          <strong className="text-red-400">✗ Excluded:</strong>
                          <ul className="ml-4 mt-1 space-y-1">
                            <li>• Financial services (banking, VC)</li>
                            <li>• Law/accounting/consulting</li>
                            <li>• Hospitality (hotels, restaurants)</li>
                            <li>• Farming & natural resources</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ISO vs NSO */}
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-2xl font-bold mb-4">ISO vs NSO: Which is Better?</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-400 mb-3 flex items-center">
                      <span className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-sm mr-2">ISO</span>
                      Incentive Stock Options
                    </h4>
                    <div className="text-sm space-y-2 text-slate-300">
                      <div>
                        <strong className="text-white">Tax Treatment:</strong>
                        <ul className="ml-4 mt-1 space-y-1">
                          <li>• No regular tax at exercise</li>
                          <li>• AMT on bargain element (28%)</li>
                          <li>• Long-term capital gains at sale (23.8%)</li>
                        </ul>
                      </div>
                      <div>
                        <strong className="text-white">Requirements:</strong>
                        <ul className="ml-4 mt-1 space-y-1">
                          <li>• Employees only (not contractors)</li>
                          <li>• $100K/year exercise limit</li>
                          <li>• Must hold 2 years from grant, 1 year from exercise</li>
                        </ul>
                      </div>
                      <div className="mt-3 p-2 bg-green-500/10 border border-green-500/30 rounded">
                        <strong className="text-green-400">Best for:</strong> Early employees expecting large gains who can afford AMT
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                    <h4 className="font-semibold text-orange-400 mb-3 flex items-center">
                      <span className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-sm mr-2">NSO</span>
                      Non-Qualified Stock Options
                    </h4>
                    <div className="text-sm space-y-2 text-slate-300">
                      <div>
                        <strong className="text-white">Tax Treatment:</strong>
                        <ul className="ml-4 mt-1 space-y-1">
                          <li>• Ordinary income tax at exercise (up to 50.3%)</li>
                          <li>• No AMT concerns</li>
                          <li>• Long-term capital gains on appreciation after exercise</li>
                        </ul>
                      </div>
                      <div>
                        <strong className="text-white">Requirements:</strong>
                        <ul className="ml-4 mt-1 space-y-1">
                          <li>• Available to employees, contractors, advisors</li>
                          <li>• No $100K limit</li>
                          <li>• More flexible exercise timing</li>
                        </ul>
                      </div>
                      <div className="mt-3 p-2 bg-green-500/10 border border-green-500/30 rounded">
                        <strong className="text-green-400">Best for:</strong> Advisors/contractors or when AMT risk too high
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* AMT Trap */}
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center text-red-400">
                  <AlertTriangle className="w-6 h-6 mr-2" />
                  The AMT Trap (For ISOs)
                </h3>
                <div className="text-sm text-slate-300 space-y-3">
                  <p>
                    <strong>Alternative Minimum Tax (AMT)</strong> is a parallel tax system that can hit ISO holders hard. 
                    When you exercise ISOs, the bargain element (FMV - strike price) is treated as income for AMT purposes, 
                    even though you haven't sold yet.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <strong className="text-red-400">Example of AMT Trap:</strong>
                      <ul className="text-xs mt-2 space-y-1">
                        <li>• Exercise 50,000 ISOs at $1 strike</li>
                        <li>• Current FMV: $10 per share</li>
                        <li>• Bargain element: $450,000</li>
                        <li>• <strong>AMT owed: $126,000</strong></li>
                        <li>• Stock later goes to $0 = You still owe AMT!</li>
                      </ul>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <strong className="text-green-400">How to Avoid:</strong>
                      <ul className="text-xs mt-2 space-y-1">
                        <li>• Early exercise when FMV = strike price (83(b) election)</li>
                        <li>• Exercise only what you can afford AMT on</li>
                        <li>• Spread exercises across multiple years</li>
                        <li>• Track AMT credit for future offset</li>
                        <li>• Consider NSOs if AMT risk too high</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Important Timing Considerations */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center text-yellow-400">
                  <AlertCircle className="w-6 h-6 mr-2" />
                  Critical Timing Considerations
                </h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-300">
                  <div>
                    <h4 className="font-semibold text-white mb-2">Exercise Timing:</h4>
                    <ul className="space-y-1">
                      <li>• <strong>Early Exercise:</strong> File 83(b) within 30 days or lose QSBS eligibility</li>
                      <li>• <strong>Post-Termination:</strong> Usually 90 days to exercise after leaving company</li>
                      <li>• <strong>Expiration:</strong> Options typically expire 10 years from grant date</li>
                      <li>• <strong>Start 5-Year Clock:</strong> QSBS holding period starts at exercise, not grant</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-2">Sale Timing:</h4>
                    <ul className="space-y-1">
                      <li>• <strong>5-Year Rule:</strong> Must hold 5 years from exercise for QSBS</li>
                      <li>• <strong>IPO Lockup:</strong> Usually 180-day lockup after IPO</li>
                      <li>• <strong>Secondary Sales:</strong> Don't qualify for QSBS (must be original issuance)</li>
                      <li>• <strong>Stacking Gains:</strong> Can use $10M exclusion per person (spouse = $20M)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="mt-12 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Ready to Optimize Your Stock Options?</h3>
              <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                The QSBS $10M exclusion can save you millions in taxes, but requires careful planning and timing. 
                Always consult with a qualified tax attorney or CPA before making exercise decisions.
              </p>
              <div className="flex justify-center gap-4 flex-wrap">
                <button
                  onClick={() => setCurrentPage('stock-tax')}
                  className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
                >
                  Stock Sale Tax Calculator
                </button>
                <button
                  onClick={() => setCurrentPage('tax')}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all"
                >
                  Explore Tax Strategies
                </button>
              </div>
            </div>
          </section>
        )}


        {/* STOCK SALE TAX CALCULATOR PAGE */}
        {currentPage === 'stock-tax' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Stock Sale Tax Calculator</h1>
              <p className="text-slate-300 text-lg">
                Calculate capital gains taxes on your stock sales with accurate federal and state tax rates.
              </p>
            </div>

            {/* How to Use Section */}
            <div className="mb-8 bg-green-500/10 border border-green-500/30 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-3 flex items-center text-green-400">
                <BookOpen className="w-5 h-5 mr-2" />
                How to Use This Calculator
              </h3>
              <div className="text-sm text-slate-300 space-y-3">
                <div>
                  <h4 className="font-semibold text-white mb-2">Simple Steps:</h4>
                  <ol className="space-y-1 list-decimal list-inside ml-2">
                    <li>Enter purchase price, sale price, and number of shares</li>
                    <li>Select holding period (Short-term &lt; 1 year, Long-term &gt; 1 year)</li>
                    <li>Choose your filing status and annual income</li>
                    <li>Add your state tax rate (0% for states with no income tax)</li>
                    <li>Include any capital loss carryover from prior years (optional)</li>
                  </ol>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                    <p className="text-xs font-semibold text-green-400">💰 Tax Rates 2025:</p>
                    <ul className="text-xs mt-1 space-y-1">
                      <li>• <strong>Short-term:</strong> 10-37% (ordinary income)</li>
                      <li>• <strong>Long-term:</strong> 0%, 15%, or 20% (preferential)</li>
                      <li>• <strong>NIIT:</strong> +3.8% for high earners ($200K+)</li>
                    </ul>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                    <p className="text-xs font-semibold text-green-400">💡 Pro Tips:</p>
                    <ul className="text-xs mt-1 space-y-1">
                      <li>• Hold &gt;1 year for lower tax rates</li>
                      <li>• Harvest losses to offset gains</li>
                      <li>• Avoid wash sales (30-day rule)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Calculator Grid */}
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Input Section */}
              <div className="space-y-6">
                {/* Trade Details Card */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <BarChart3 className="w-5 h-5 mr-2 text-blue-400" />
                    Stock Sale Details
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Purchase Price</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                          <input
                            type="number"
                            step="0.01"
                            value={stockTaxInputs.purchasePrice}
                            onChange={(e) => setStockTaxInputs({...stockTaxInputs, purchasePrice: parseFloat(e.target.value) || 0})}
                            className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                            placeholder="50.00"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Sale Price</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                          <input
                            type="number"
                            step="0.01"
                            value={stockTaxInputs.salePrice}
                            onChange={(e) => setStockTaxInputs({...stockTaxInputs, salePrice: parseFloat(e.target.value) || 0})}
                            className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                            placeholder="75.00"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Number of Shares</label>
                      <input
                        type="number"
                        value={stockTaxInputs.shares}
                        onChange={(e) => setStockTaxInputs({...stockTaxInputs, shares: parseFloat(e.target.value) || 0})}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                        placeholder="100"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Holding Period</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setStockTaxInputs({...stockTaxInputs, holdingPeriod: 'short'})}
                          className={`py-3 rounded-lg font-medium transition-all ${
                            stockTaxInputs.holdingPeriod === 'short'
                              ? 'bg-red-500 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          Short-Term
                          <div className="text-xs opacity-75">&lt; 1 year</div>
                        </button>
                        <button
                          onClick={() => setStockTaxInputs({...stockTaxInputs, holdingPeriod: 'long'})}
                          className={`py-3 rounded-lg font-medium transition-all ${
                            stockTaxInputs.holdingPeriod === 'long'
                              ? 'bg-green-500 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          Long-Term
                          <div className="text-xs opacity-75">&gt; 1 year</div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tax Profile Card */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Percent className="w-5 h-5 mr-2 text-green-400" />
                    Your Tax Profile
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Filing Status</label>
                      <select
                        value={stockTaxInputs.filingStatus}
                        onChange={(e) => setStockTaxInputs({...stockTaxInputs, filingStatus: e.target.value})}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                      >
                        <option value="single">Single</option>
                        <option value="married">Married Filing Jointly</option>
                        <option value="hoh">Head of Household</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Annual Income</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                        <input
                          type="number"
                          value={stockTaxInputs.annualIncome}
                          onChange={(e) => setStockTaxInputs({...stockTaxInputs, annualIncome: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                          placeholder="75000"
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">Used to determine tax bracket</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">State Tax Rate</label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.1"
                          value={stockTaxInputs.stateTaxRate}
                          onChange={(e) => setStockTaxInputs({...stockTaxInputs, stateTaxRate: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-4 pr-8 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                          placeholder="5.0"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">Varies by state (0-13.3%)</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Capital Loss Carryover</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                        <input
                          type="number"
                          value={stockTaxInputs.capitalLossCarryover}
                          onChange={(e) => setStockTaxInputs({...stockTaxInputs, capitalLossCarryover: parseFloat(e.target.value) || 0})}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
                          placeholder="0"
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">Prior year losses to offset gains</p>
                    </div>
                  </div>
                </div>

                {/* Quick Scenarios */}
                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-6">
                  <h4 className="font-semibold mb-3 text-purple-400">Example Scenarios</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setStockTaxInputs({
                        purchasePrice: 100,
                        salePrice: 150,
                        shares: 50,
                        holdingPeriod: 'long',
                        filingStatus: 'single',
                        annualIncome: 50000,
                        stateTaxRate: 5,
                        capitalLossCarryover: 0
                      })}
                      className="px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-all"
                    >
                      Moderate Gain
                    </button>
                    <button
                      onClick={() => setStockTaxInputs({
                        purchasePrice: 50,
                        salePrice: 200,
                        shares: 100,
                        holdingPeriod: 'short',
                        filingStatus: 'single',
                        annualIncome: 150000,
                        stateTaxRate: 7,
                        capitalLossCarryover: 0
                      })}
                      className="px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-all"
                    >
                      Big Win
                    </button>
                    <button
                      onClick={() => setStockTaxInputs({
                        purchasePrice: 75,
                        salePrice: 50,
                        shares: 200,
                        holdingPeriod: 'long',
                        filingStatus: 'married',
                        annualIncome: 100000,
                        stateTaxRate: 6,
                        capitalLossCarryover: 0
                      })}
                      className="px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-all"
                    >
                      Loss Scenario
                    </button>
                    <button
                      onClick={() => setStockTaxInputs({
                        purchasePrice: 100,
                        salePrice: 125,
                        shares: 100,
                        holdingPeriod: 'long',
                        filingStatus: 'single',
                        annualIncome: 40000,
                        stateTaxRate: 0,
                        capitalLossCarryover: 0
                      })}
                      className="px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-all"
                    >
                      0% Tax Rate
                    </button>
                  </div>
                </div>
              </div>

              {/* Results Section */}
              <div className="space-y-6">
                {/* Main Results Card */}
                <div className={`border-2 rounded-xl p-8 ${
                  stockTaxResults.isGain
                    ? 'bg-green-500/10 border-green-500/30'
                    : 'bg-red-500/10 border-red-500/30'
                }`}>
                  <div className="text-center mb-6">
                    <div className="inline-block px-4 py-1.5 bg-slate-900/50 border border-slate-700 rounded-full mb-4">
                      <span className="text-sm text-slate-300 font-semibold">
                        {stockTaxResults.isGain ? 'Capital Gain' : 'Capital Loss'}
                      </span>
                    </div>
                    <div className={`text-5xl font-bold mb-2 ${
                      stockTaxResults.isGain ? 'text-green-400' : 'text-red-400'
                    }`}>
                      ${Math.abs(parseFloat(stockTaxResults.capitalGain)).toLocaleString()}
                    </div>
                    <p className="text-slate-300 text-sm">
                      ${stockTaxResults.gainPerShare} per share ({stockTaxResults.returnPercent}%)
                    </p>
                  </div>

                  <div className="space-y-3 bg-slate-900/50 rounded-lg p-4 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Sale Proceeds:</span>
                      <span className="font-semibold text-green-400">${parseFloat(stockTaxResults.saleProceeds).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Cost Basis:</span>
                      <span className="font-semibold text-red-400">-${parseFloat(stockTaxResults.costBasis).toLocaleString()}</span>
                    </div>
                    {parseFloat(stockTaxResults.carryoverUsed) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400">Loss Carryover Used:</span>
                        <span className="font-semibold text-blue-400">-${parseFloat(stockTaxResults.carryoverUsed).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-2 border-t border-slate-700">
                      <span className="text-white font-semibold">Taxable Gain:</span>
                      <span className="font-bold text-green-400">${parseFloat(stockTaxResults.netCapitalGain).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Tax Breakdown Card */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-lg font-bold mb-4">Tax Breakdown</h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center pb-3 border-b border-slate-700">
                      <div>
                        <div className="text-slate-300">Federal Tax</div>
                        <div className="text-xs text-slate-400">
                          {stockTaxInputs.holdingPeriod === 'long' ? 'Long-term' : 'Short-term'} rate: {stockTaxResults.federalTaxRate}%
                          {parseFloat(stockTaxResults.niitRate) > 0 && ` + ${stockTaxResults.niitRate}% NIIT`}
                        </div>
                      </div>
                      <span className="font-semibold text-red-400">${parseFloat(stockTaxResults.federalTax).toLocaleString()}</span>
                    </div>
                    
                    <div className="flex justify-between items-center pb-3 border-b border-slate-700">
                      <div>
                        <div className="text-slate-300">State Tax</div>
                        <div className="text-xs text-slate-400">
                          Rate: {stockTaxInputs.stateTaxRate}%
                        </div>
                      </div>
                      <span className="font-semibold text-red-400">${parseFloat(stockTaxResults.stateTax).toLocaleString()}</span>
                    </div>
                    
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-white font-bold">Total Tax Owed</span>
                      <span className="text-2xl font-bold text-red-400">${parseFloat(stockTaxResults.totalTax).toLocaleString()}</span>
                    </div>

                    <div className="flex justify-between items-center pt-3 border-t border-slate-700">
                      <span className="text-slate-400">Effective Tax Rate</span>
                      <span className="font-semibold">{stockTaxResults.effectiveTaxRate}%</span>
                    </div>
                  </div>
                </div>

                {/* Net Proceeds Card */}
                <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-2 border-blue-500/40 rounded-xl p-6">
                  <h3 className="text-lg font-bold mb-4">After-Tax Results</h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-lg">
                      <span className="text-slate-300">Net Proceeds:</span>
                      <span className="font-bold text-blue-400">${parseFloat(stockTaxResults.netProceeds).toLocaleString()}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Net Gain:</span>
                      <span className={`font-bold ${parseFloat(stockTaxResults.netGain) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        ${parseFloat(stockTaxResults.netGain).toLocaleString()}
                      </span>
                    </div>

                    {parseFloat(stockTaxResults.remainingCarryover) > 0 && (
                      <div className="flex justify-between items-center pt-3 border-t border-blue-500/30">
                        <span className="text-slate-300">Remaining Carryover:</span>
                        <span className="font-semibold text-blue-400">${parseFloat(stockTaxResults.remainingCarryover).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Break-Even Card */}
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
                  <h3 className="text-lg font-bold mb-3 flex items-center">
                    <Target className="w-5 h-5 mr-2 text-yellow-400" />
                    Break-Even Analysis
                  </h3>
                  <p className="text-sm text-slate-300 mb-3">
                    To break even after taxes, you need to sell at:
                  </p>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-yellow-400 mb-1">
                      ${stockTaxResults.breakEvenPrice}
                    </div>
                    <p className="text-xs text-slate-400">per share</p>
                  </div>
                </div>

                {/* Tax Tips Card */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
                  <h4 className="font-semibold mb-3 flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-blue-400" />
                    Tax Optimization Tips
                  </h4>
                  <ul className="text-sm text-slate-300 space-y-2">
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>Hold stocks &gt;1 year for lower long-term capital gains rates</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>Harvest losses to offset gains (tax-loss harvesting)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>Avoid wash sales (don't rebuy within 30 days of selling at a loss)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>Use specific lot identification to optimize which shares you sell</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>Consider Roth IRA for tax-free growth and withdrawals</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Educational Section */}
            <div className="mt-12 grid md:grid-cols-3 gap-6">
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4 text-green-400">Short-Term vs Long-Term</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="font-semibold text-white">Short-Term (&lt; 1 year)</div>
                    <p className="text-slate-400">Taxed as ordinary income at your regular tax bracket (10-37%)</p>
                  </div>
                  <div>
                    <div className="font-semibold text-white">Long-Term (&gt; 1 year)</div>
                    <p className="text-slate-400">Preferential rates: 0%, 15%, or 20% based on income</p>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 mt-4">
                    <p className="text-xs text-green-400 font-semibold">💡 Pro Tip:</p>
                    <p className="text-xs text-slate-300 mt-1">
                      Holding just one extra day past the 1-year mark can save you 10-20% in taxes!
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4 text-blue-400">2025 Long-Term Rates</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="font-semibold text-white">0% Rate</div>
                    <p className="text-slate-400">Single: Up to $47,025</p>
                    <p className="text-slate-400">Married: Up to $94,050</p>
                  </div>
                  <div>
                    <div className="font-semibold text-white">15% Rate</div>
                    <p className="text-slate-400">Single: $47,026-$518,900</p>
                    <p className="text-slate-400">Married: $94,051-$583,750</p>
                  </div>
                  <div>
                    <div className="font-semibold text-white">20% Rate</div>
                    <p className="text-slate-400">Single: Over $518,900</p>
                    <p className="text-slate-400">Married: Over $583,750</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4 text-purple-400">Important Notes</h3>
                <div className="space-y-2 text-sm text-slate-300">
                  <div className="flex items-start">
                    <span className="text-purple-400 mr-2">⚠️</span>
                    <span>3.8% NIIT applies to high earners ($200K+ single, $250K+ married)</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-400 mr-2">📝</span>
                    <span>Report all stock sales on Form 8949 and Schedule D</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-400 mr-2">💰</span>
                    <span>Capital losses can offset gains plus $3,000 of ordinary income annually</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-400 mr-2">🔄</span>
                    <span>Unused losses carry forward indefinitely to future years</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mt-8 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
              <p className="text-sm text-slate-300">
                <strong className="text-yellow-400">⚠️ Disclaimer:</strong> This calculator provides estimates for educational purposes only. 
                Tax laws are complex and vary by individual circumstances. Always consult with a qualified tax professional (CPA or EA) 
                for personalized tax advice. This is not tax advice.
              </p>
            </div>
          </section>
        )}


        {/* TEMPLATE LIBRARY PAGE */}
        {currentPage === 'templates' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg">
                  <ClipboardCheck className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold">Calculator Templates Hub</h2>
                  <p className="text-slate-300 mt-2">Pre-built scenarios for all calculators - financial planning, mortgages, rentals & more</p>
                </div>
              </div>
              <div className="mt-6 bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                <p className="text-sm text-cyan-300">
                  💡 <strong>Quick Start:</strong> Choose a template that matches your situation. 
                  Each template loads pre-filled values into the appropriate calculator - fully customizable!
                </p>
              </div>
            </div>

            {/* Section Navigation - Sticky */}
            <div className="mb-12 sticky top-0 z-10 bg-slate-900/95 backdrop-blur-sm border-y border-slate-700 py-4">
              <div className="flex flex-wrap gap-2 justify-center">
                <button
                  onClick={() => {
                    const element = document.getElementById('financial-templates');
                    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="px-4 py-2 bg-cyan-500/20 border border-cyan-500/30 rounded-lg text-sm font-semibold text-cyan-400 hover:bg-cyan-500/30 transition-all"
                >
                  💰 Financial Planning (9)
                </button>
                <button
                  onClick={() => {
                    const element = document.getElementById('mortgage-templates');
                    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-lg text-sm font-semibold text-emerald-400 hover:bg-emerald-500/30 transition-all"
                >
                  🏠 Mortgage (7)
                </button>
                <button
                  onClick={() => {
                    const element = document.getElementById('rental-templates');
                    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-lg text-sm font-semibold text-purple-400 hover:bg-purple-500/30 transition-all"
                >
                  🏘️ Rental Property (5)
                </button>
                <button
                  onClick={() => {
                    const element = document.getElementById('1031-templates');
                    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="px-4 py-2 bg-teal-500/20 border border-teal-500/30 rounded-lg text-sm font-semibold text-teal-400 hover:bg-teal-500/30 transition-all"
                >
                  🔄 1031 Exchange (7)
                </button>
                <button
                  onClick={() => {
                    const element = document.getElementById('trading-templates');
                    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-lg text-sm font-semibold text-orange-400 hover:bg-orange-500/30 transition-all"
                >
                  📈 Leverage Trading (5)
                </button>
                <button
                  onClick={() => {
                    const element = document.getElementById('stock-templates');
                    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-lg text-sm font-semibold text-green-400 hover:bg-green-500/30 transition-all"
                >
                  💵 Stock Sale Tax (5)
                </button>
              </div>
              <p className="text-center text-xs text-slate-500 mt-3">
                Click any category to jump to that section • 38 total templates
              </p>
            </div>

            {/* FINANCIAL PLANNING TEMPLATES SECTION */}
            <div id="financial-templates" className="mb-16 scroll-mt-24">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg">
                  <Target className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Financial Planning Templates</h3>
                  <p className="text-slate-400 text-sm">Complete life planning scenarios with income, expenses, and long-term goals</p>
                </div>
              </div>

            {/* Templates Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {/* Template 1: Recent Graduate */}
              <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-emerald-500/20 transition-all group">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-emerald-500/20 rounded-lg">
                    <GraduationCap className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-bold text-emerald-400">Recent Graduate</h3>
                </div>
                <p className="text-sm text-slate-300 mb-4">
                  Just starting your career with student loans and limited savings. Focus on building emergency fund and paying off debt.
                </p>
                <div className="space-y-2 mb-6 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Income:</span>
                    <span className="text-white font-semibold">$55,000/yr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Monthly Expenses:</span>
                    <span className="text-white font-semibold">$2,800</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Primary Goal:</span>
                    <span className="text-white font-semibold">Emergency Fund</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setPlanningInputs({
                      annualSalary: 55000, sideIncome: 0, investmentIncome: 0,
                      housing: 1200, transportation: 350, food: 400, utilities: 150,
                      insurance: 150, healthcare: 100, discretionary: 300, otherExpenses: 150,
                      emergencyFundGoal: 10000, emergencyFundCurrent: 500,
                      homeDownPayment: 40000, homeDownPaymentCurrent: 0,
                      retirementGoal: 1000000, retirementCurrent: 5000,
                      customGoalName: 'Student Loan Payoff', customGoalAmount: 30000, customGoalCurrent: 5000,
                      inflationRate: 3, investmentReturn: 7, salaryGrowth: 5, taxRate: 22, yearsToRetirement: 40
                    });
                    setCurrentPage('planning');
                  }}
                  className="w-full py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-all group-hover:shadow-lg"
                >
                  Use This Template →
                </button>
              </div>

              {/* Template 2: Young Professional */}
              <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-blue-500/20 transition-all group">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold text-blue-400">Young Professional</h3>
                </div>
                <p className="text-sm text-slate-300 mb-4">
                  Established career, building wealth. Focus on retirement investing and saving for major purchases.
                </p>
                <div className="space-y-2 mb-6 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Income:</span>
                    <span className="text-white font-semibold">$85,000/yr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Monthly Expenses:</span>
                    <span className="text-white font-semibold">$3,800</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Primary Goal:</span>
                    <span className="text-white font-semibold">Home Purchase</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setPlanningInputs({
                      annualSalary: 85000, sideIncome: 5000, investmentIncome: 2000,
                      housing: 1600, transportation: 500, food: 600, utilities: 200,
                      insurance: 300, healthcare: 150, discretionary: 400, otherExpenses: 50,
                      emergencyFundGoal: 18000, emergencyFundCurrent: 10000,
                      homeDownPayment: 80000, homeDownPaymentCurrent: 15000,
                      retirementGoal: 1500000, retirementCurrent: 40000,
                      customGoalName: 'New Car', customGoalAmount: 35000, customGoalCurrent: 5000,
                      inflationRate: 3, investmentReturn: 8, salaryGrowth: 4, taxRate: 24, yearsToRetirement: 35
                    });
                    setCurrentPage('planning');
                  }}
                  className="w-full py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-all group-hover:shadow-lg"
                >
                  Use This Template →
                </button>
              </div>

              {/* Template 3: Family of 4 */}
              <div className="bg-gradient-to-br from-pink-500/10 to-rose-500/10 border border-pink-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-pink-500/20 transition-all group">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-pink-500/20 rounded-lg">
                    <Heart className="w-6 h-6 text-pink-400" />
                  </div>
                  <h3 className="text-xl font-bold text-pink-400">Family of 4</h3>
                </div>
                <p className="text-sm text-slate-300 mb-4">
                  Dual income household with children. Balancing current expenses with long-term savings and education goals.
                </p>
                <div className="space-y-2 mb-6 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Income:</span>
                    <span className="text-white font-semibold">$135,000/yr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Monthly Expenses:</span>
                    <span className="text-white font-semibold">$6,500</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Primary Goal:</span>
                    <span className="text-white font-semibold">College Fund</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setPlanningInputs({
                      annualSalary: 135000, sideIncome: 0, investmentIncome: 3000,
                      housing: 2500, transportation: 800, food: 1200, utilities: 350,
                      insurance: 600, healthcare: 400, discretionary: 500, otherExpenses: 150,
                      emergencyFundGoal: 30000, emergencyFundCurrent: 20000,
                      homeDownPayment: 0, homeDownPaymentCurrent: 0,
                      retirementGoal: 2000000, retirementCurrent: 120000,
                      customGoalName: 'College Fund (529)', customGoalAmount: 150000, customGoalCurrent: 25000,
                      inflationRate: 3, investmentReturn: 7, salaryGrowth: 3, taxRate: 28, yearsToRetirement: 25
                    });
                    setCurrentPage('planning');
                  }}
                  className="w-full py-3 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 transition-all group-hover:shadow-lg"
                >
                  Use This Template →
                </button>
              </div>

              {/* Template 4: Mid-Career Professional */}
              <div className="bg-gradient-to-br from-purple-500/10 to-violet-500/10 border border-purple-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-purple-500/20 transition-all group">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <Building2 className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-bold text-purple-400">Mid-Career Pro</h3>
                </div>
                <p className="text-sm text-slate-300 mb-4">
                  Peak earning years with established assets. Focus on maximizing retirement contributions and investments.
                </p>
                <div className="space-y-2 mb-6 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Income:</span>
                    <span className="text-white font-semibold">$175,000/yr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Monthly Expenses:</span>
                    <span className="text-white font-semibold">$8,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Primary Goal:</span>
                    <span className="text-white font-semibold">Wealth Building</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setPlanningInputs({
                      annualSalary: 175000, sideIncome: 15000, investmentIncome: 12000,
                      housing: 3000, transportation: 900, food: 1000, utilities: 300,
                      insurance: 800, healthcare: 500, discretionary: 1000, otherExpenses: 500,
                      emergencyFundGoal: 45000, emergencyFundCurrent: 45000,
                      homeDownPayment: 0, homeDownPaymentCurrent: 0,
                      retirementGoal: 3000000, retirementCurrent: 400000,
                      customGoalName: 'Investment Property', customGoalAmount: 100000, customGoalCurrent: 30000,
                      inflationRate: 3, investmentReturn: 8, salaryGrowth: 3, taxRate: 32, yearsToRetirement: 18
                    });
                    setCurrentPage('planning');
                  }}
                  className="w-full py-3 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-600 transition-all group-hover:shadow-lg"
                >
                  Use This Template →
                </button>
              </div>

              {/* Template 5: Early Retirement */}
              <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-amber-500/20 transition-all group">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-amber-500/20 rounded-lg">
                    <PiggyBank className="w-6 h-6 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-bold text-amber-400">Early Retirement</h3>
                </div>
                <p className="text-sm text-slate-300 mb-4">
                  Aggressive saver targeting FIRE (Financial Independence, Retire Early). High savings rate and optimized expenses.
                </p>
                <div className="space-y-2 mb-6 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Income:</span>
                    <span className="text-white font-semibold">$120,000/yr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Monthly Expenses:</span>
                    <span className="text-white font-semibold">$3,500</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Primary Goal:</span>
                    <span className="text-white font-semibold">FIRE by 45</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setPlanningInputs({
                      annualSalary: 120000, sideIncome: 10000, investmentIncome: 8000,
                      housing: 1400, transportation: 300, food: 500, utilities: 200,
                      insurance: 350, healthcare: 250, discretionary: 300, otherExpenses: 200,
                      emergencyFundGoal: 25000, emergencyFundCurrent: 25000,
                      homeDownPayment: 0, homeDownPaymentCurrent: 0,
                      retirementGoal: 1500000, retirementCurrent: 300000,
                      customGoalName: 'Travel Fund', customGoalAmount: 30000, customGoalCurrent: 10000,
                      inflationRate: 3, investmentReturn: 9, salaryGrowth: 2, taxRate: 24, yearsToRetirement: 12
                    });
                    setCurrentPage('planning');
                  }}
                  className="w-full py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-all group-hover:shadow-lg"
                >
                  Use This Template →
                </button>
              </div>

              {/* Template 6: Aggressive Wealth Builder */}
              <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-red-500/20 transition-all group">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-red-500/20 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-red-400" />
                  </div>
                  <h3 className="text-xl font-bold text-red-400">Aggressive Saver</h3>
                </div>
                <p className="text-sm text-slate-300 mb-4">
                  High income earner with very high savings rate. Maximizing tax-advantaged accounts and investment opportunities.
                </p>
                <div className="space-y-2 mb-6 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Income:</span>
                    <span className="text-white font-semibold">$250,000/yr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Monthly Expenses:</span>
                    <span className="text-white font-semibold">$7,500</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Primary Goal:</span>
                    <span className="text-white font-semibold">Wealth Max</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setPlanningInputs({
                      annualSalary: 250000, sideIncome: 30000, investmentIncome: 20000,
                      housing: 3500, transportation: 800, food: 1000, utilities: 300,
                      insurance: 1000, healthcare: 400, discretionary: 400, otherExpenses: 100,
                      emergencyFundGoal: 50000, emergencyFundCurrent: 50000,
                      homeDownPayment: 0, homeDownPaymentCurrent: 0,
                      retirementGoal: 5000000, retirementCurrent: 600000,
                      customGoalName: 'Real Estate Fund', customGoalAmount: 200000, customGoalCurrent: 80000,
                      inflationRate: 3, investmentReturn: 10, salaryGrowth: 5, taxRate: 35, yearsToRetirement: 20
                    });
                    setCurrentPage('planning');
                  }}
                  className="w-full py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all group-hover:shadow-lg"
                >
                  Use This Template →
                </button>
              </div>

              {/* Template 7: First-Time Homebuyer */}
              <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-emerald-500/20 transition-all group">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-emerald-500/20 rounded-lg">
                    <Home className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-bold text-emerald-400">First-Time Homebuyer</h3>
                </div>
                <p className="text-sm text-slate-300 mb-4">
                  Ready to buy your first home. Learn about mortgage leverage, down payments, and the full cost of homeownership.
                </p>
                <div className="space-y-2 mb-6 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Income:</span>
                    <span className="text-white font-semibold">$90,000/yr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Monthly Expenses:</span>
                    <span className="text-white font-semibold">$3,200</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Primary Goal:</span>
                    <span className="text-white font-semibold">Down Payment</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setPlanningInputs({
                      annualSalary: 90000, sideIncome: 3000, investmentIncome: 1500,
                      housing: 1500, transportation: 450, food: 550, utilities: 180,
                      insurance: 250, healthcare: 200, discretionary: 350, otherExpenses: 220,
                      emergencyFundGoal: 15000, emergencyFundCurrent: 12000,
                      homeDownPayment: 60000, homeDownPaymentCurrent: 25000,
                      retirementGoal: 1200000, retirementCurrent: 35000,
                      customGoalName: 'Closing Costs', customGoalAmount: 12000, customGoalCurrent: 5000,
                      inflationRate: 3, investmentReturn: 7, salaryGrowth: 4, taxRate: 22, yearsToRetirement: 35
                    });
                    setCurrentPage('planning');
                  }}
                  className="w-full py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-all group-hover:shadow-lg"
                >
                  Use This Template →
                </button>
              </div>

              {/* Template 8: Small Business Owner */}
              <div className="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 border border-cyan-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-cyan-500/20 transition-all group">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-cyan-500/20 rounded-lg">
                    <Building2 className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-bold text-cyan-400">Small Business Owner</h3>
                </div>
                <p className="text-sm text-slate-300 mb-4">
                  Running your own business. Leverage business loans, optimize cash flow, and separate business from personal finances.
                </p>
                <div className="space-y-2 mb-6 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Income:</span>
                    <span className="text-white font-semibold">$110,000/yr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Monthly Expenses:</span>
                    <span className="text-white font-semibold">$5,200</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Primary Goal:</span>
                    <span className="text-white font-semibold">Business Growth</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setPlanningInputs({
                      annualSalary: 110000, sideIncome: 0, investmentIncome: 5000,
                      housing: 2200, transportation: 700, food: 800, utilities: 250,
                      insurance: 600, healthcare: 350, discretionary: 400, otherExpenses: 900,
                      emergencyFundGoal: 35000, emergencyFundCurrent: 20000,
                      homeDownPayment: 0, homeDownPaymentCurrent: 0,
                      retirementGoal: 2000000, retirementCurrent: 85000,
                      customGoalName: 'Business Expansion Fund', customGoalAmount: 75000, customGoalCurrent: 15000,
                      inflationRate: 3, investmentReturn: 9, salaryGrowth: 6, taxRate: 26, yearsToRetirement: 28
                    });
                    setCurrentPage('planning');
                  }}
                  className="w-full py-3 bg-cyan-500 text-white font-semibold rounded-lg hover:bg-cyan-600 transition-all group-hover:shadow-lg"
                >
                  Use This Template →
                </button>
              </div>

              {/* Template 9: Tax Optimization Focus */}
              <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border border-violet-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-violet-500/20 transition-all group">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-violet-500/20 rounded-lg">
                    <PiggyBank className="w-6 h-6 text-violet-400" />
                  </div>
                  <h3 className="text-xl font-bold text-violet-400">Tax Optimization Plan</h3>
                </div>
                <p className="text-sm text-slate-300 mb-4">
                  High earner focused on minimizing tax burden. Maximize deductions, tax-advantaged accounts, and strategic income planning.
                </p>
                <div className="space-y-2 mb-6 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Income:</span>
                    <span className="text-white font-semibold">$200,000/yr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Monthly Expenses:</span>
                    <span className="text-white font-semibold">$8,500</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Primary Goal:</span>
                    <span className="text-white font-semibold">Tax Savings</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setPlanningInputs({
                      annualSalary: 200000, sideIncome: 20000, investmentIncome: 15000,
                      housing: 3500, transportation: 850, food: 1000, utilities: 350,
                      insurance: 900, healthcare: 500, discretionary: 800, otherExpenses: 600,
                      emergencyFundGoal: 40000, emergencyFundCurrent: 40000,
                      homeDownPayment: 0, homeDownPaymentCurrent: 0,
                      retirementGoal: 3500000, retirementCurrent: 500000,
                      customGoalName: 'Backdoor Roth Fund', customGoalAmount: 50000, customGoalCurrent: 20000,
                      inflationRate: 3, investmentReturn: 8, salaryGrowth: 4, taxRate: 35, yearsToRetirement: 22
                    });
                    setCurrentPage('planning');
                  }}
                  className="w-full py-3 bg-violet-500 text-white font-semibold rounded-lg hover:bg-violet-600 transition-all group-hover:shadow-lg"
                >
                  Use This Template →
                </button>
              </div>
            </div>
            </div>

            {/* MORTGAGE TEMPLATES SECTION */}
            <div id="mortgage-templates" className="mb-16 mt-16 scroll-mt-24">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg">
                  <Home className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Mortgage Calculator Templates</h3>
                  <p className="text-slate-400 text-sm">Pre-configured scenarios for different homebuying situations</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Template 1: First-Time Buyer (20% Down) */}
                <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-emerald-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-emerald-500/20 rounded-lg">
                      <Home className="w-6 h-6 text-emerald-400" />
                    </div>
                    <h3 className="text-xl font-bold text-emerald-400">First-Time Buyer (20% Down)</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Traditional first home purchase with 20% down payment. Avoid PMI and get best rates.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Income:</span>
                      <span className="text-white font-semibold">$90,000/yr</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Home Price:</span>
                      <span className="text-white font-semibold">$300,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Down Payment:</span>
                      <span className="text-white font-semibold">$60,000 (20%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">DTI Ratio:</span>
                      <span className="text-green-400 font-semibold">28%</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setMortgageInputs({
                        annualIncome: 90000,
                        monthlyDebts: 400,
                        homePrice: 300000,
                        downPayment: 60000,
                        interestRate: 7.0,
                        loanTerm: 30,
                        propertyTax: 3600,
                        homeInsurance: 1200,
                        hoa: 0,
                        pmi: 0
                      });
                      setCurrentPage('mortgage');
                    }}
                    className="w-full py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 2: First-Time Buyer (FHA 3.5% Down) */}
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-blue-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <Home className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-blue-400">FHA Loan (3.5% Down)</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Lower savings, FHA benefits. Perfect for first-time buyers with limited down payment funds.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Income:</span>
                      <span className="text-white font-semibold">$75,000/yr</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Home Price:</span>
                      <span className="text-white font-semibold">$250,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Down Payment:</span>
                      <span className="text-white font-semibold">$8,750 (3.5%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">DTI Ratio:</span>
                      <span className="text-yellow-400 font-semibold">35%</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setMortgageInputs({
                        annualIncome: 75000,
                        monthlyDebts: 350,
                        homePrice: 250000,
                        downPayment: 8750,
                        interestRate: 7.2,
                        loanTerm: 30,
                        propertyTax: 3000,
                        homeInsurance: 1000,
                        hoa: 0,
                        pmi: 150
                      });
                      setCurrentPage('mortgage');
                    }}
                    className="w-full py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 3: Move-Up Buyer */}
                <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border border-purple-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-purple-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-purple-400" />
                    </div>
                    <h3 className="text-xl font-bold text-purple-400">Move-Up Buyer</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Selling current home and trading up. Large down payment from home sale equity.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Income:</span>
                      <span className="text-white font-semibold">$140,000/yr</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Home Price:</span>
                      <span className="text-white font-semibold">$500,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Down Payment:</span>
                      <span className="text-white font-semibold">$150,000 (30%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">DTI Ratio:</span>
                      <span className="text-green-400 font-semibold">25%</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setMortgageInputs({
                        annualIncome: 140000,
                        monthlyDebts: 600,
                        homePrice: 500000,
                        downPayment: 150000,
                        interestRate: 6.8,
                        loanTerm: 30,
                        propertyTax: 6000,
                        homeInsurance: 1800,
                        hoa: 150,
                        pmi: 0
                      });
                      setCurrentPage('mortgage');
                    }}
                    className="w-full py-3 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 4: High-Income Professional */}
                <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-amber-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-amber-500/20 rounded-lg">
                      <Briefcase className="w-6 h-6 text-amber-400" />
                    </div>
                    <h3 className="text-xl font-bold text-amber-400">High-Income Professional</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Jumbo loan territory. Strong income, large down payment, luxury property purchase.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Income:</span>
                      <span className="text-white font-semibold">$250,000/yr</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Home Price:</span>
                      <span className="text-white font-semibold">$900,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Down Payment:</span>
                      <span className="text-white font-semibold">$180,000 (20%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">DTI Ratio:</span>
                      <span className="text-green-400 font-semibold">20%</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setMortgageInputs({
                        annualIncome: 250000,
                        monthlyDebts: 1200,
                        homePrice: 900000,
                        downPayment: 180000,
                        interestRate: 7.2,
                        loanTerm: 30,
                        propertyTax: 10800,
                        homeInsurance: 2500,
                        hoa: 300,
                        pmi: 0
                      });
                      setCurrentPage('mortgage');
                    }}
                    className="w-full py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 5: Aggressive Buyer (10% Down) */}
                <div className="bg-gradient-to-br from-red-500/10 to-pink-500/10 border border-red-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-red-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-red-500/20 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-red-400" />
                    </div>
                    <h3 className="text-xl font-bold text-red-400">Aggressive Buyer (10% Down)</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Stretching budget for appreciation. Lower down payment, betting on market growth.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Income:</span>
                      <span className="text-white font-semibold">$100,000/yr</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Home Price:</span>
                      <span className="text-white font-semibold">$400,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Down Payment:</span>
                      <span className="text-white font-semibold">$40,000 (10%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">DTI Ratio:</span>
                      <span className="text-red-400 font-semibold">40%</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setMortgageInputs({
                        annualIncome: 100000,
                        monthlyDebts: 500,
                        homePrice: 400000,
                        downPayment: 40000,
                        interestRate: 7.4,
                        loanTerm: 30,
                        propertyTax: 4800,
                        homeInsurance: 1400,
                        hoa: 100,
                        pmi: 200
                      });
                      setCurrentPage('mortgage');
                    }}
                    className="w-full py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 6: House Hacker (Multi-Unit) */}
                <div className="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 border border-cyan-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-cyan-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-cyan-500/20 rounded-lg">
                      <Building2 className="w-6 h-6 text-cyan-400" />
                    </div>
                    <h3 className="text-xl font-bold text-cyan-400">House Hacker (Multi-Unit)</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Live in one unit, rent others. Rental income offsets mortgage payment significantly.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Income:</span>
                      <span className="text-white font-semibold">$70,000/yr</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Home Price:</span>
                      <span className="text-white font-semibold">$350,000 (Duplex)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Down Payment:</span>
                      <span className="text-white font-semibold">$52,500 (15%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Rental Income:</span>
                      <span className="text-green-400 font-semibold">+$1,500/mo</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setMortgageInputs({
                        annualIncome: 70000,
                        monthlyDebts: 300,
                        homePrice: 350000,
                        downPayment: 52500,
                        interestRate: 7.5,
                        loanTerm: 30,
                        propertyTax: 4200,
                        homeInsurance: 1500,
                        hoa: 0,
                        pmi: 100
                      });
                      setCurrentPage('mortgage');
                    }}
                    className="w-full py-3 bg-cyan-500 text-white font-semibold rounded-lg hover:bg-cyan-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 7: Investment Property Purchase */}
                <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border border-violet-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-violet-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-violet-500/20 rounded-lg">
                      <PiggyBank className="w-6 h-6 text-violet-400" />
                    </div>
                    <h3 className="text-xl font-bold text-violet-400">Investment Property</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Second property for rental. Higher down payment required, focused on cash flow.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Income:</span>
                      <span className="text-white font-semibold">$120,000/yr</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Home Price:</span>
                      <span className="text-white font-semibold">$300,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Down Payment:</span>
                      <span className="text-white font-semibold">$75,000 (25%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">DTI Ratio:</span>
                      <span className="text-yellow-400 font-semibold">35%</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setMortgageInputs({
                        annualIncome: 120000,
                        monthlyDebts: 800,
                        homePrice: 300000,
                        downPayment: 75000,
                        interestRate: 8.0,
                        loanTerm: 30,
                        propertyTax: 3600,
                        homeInsurance: 1200,
                        hoa: 0,
                        pmi: 0
                      });
                      setCurrentPage('mortgage');
                    }}
                    className="w-full py-3 bg-violet-500 text-white font-semibold rounded-lg hover:bg-violet-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>
              </div>
            </div>

            {/* RENTAL PROPERTY TEMPLATES SECTION */}
            <div id="rental-templates" className="mb-16 mt-16 scroll-mt-24">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg">
                  <Building2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Rental Property ROI Templates</h3>
                  <p className="text-slate-400 text-sm">Investment property scenarios with cash flow and return analysis</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Template 1: Single-Family Starter */}
                <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-blue-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <Home className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-blue-400">Single-Family Starter</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Entry-level investment property. Midwest/South market focus with strong cash flow potential.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Purchase Price:</span>
                      <span className="text-white font-semibold">$300,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Down Payment:</span>
                      <span className="text-white font-semibold">$60,000 (20%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Monthly Rent:</span>
                      <span className="text-green-400 font-semibold">$2,200</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Cap Rate:</span>
                      <span className="text-green-400 font-semibold">~7%</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setRentalInputs({
                        purchasePrice: 300000,
                        downPayment: 60000,
                        interestRate: 7.5,
                        loanTerm: 30,
                        monthlyRent: 2200,
                        vacancy: 5,
                        propertyTax: 3600,
                        insurance: 1200,
                        maintenance: 2400,
                        propertyMgmt: 10
                      });
                      setCurrentPage('rental');
                    }}
                    className="w-full py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 2: Multi-Unit House Hack */}
                <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-emerald-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-emerald-500/20 rounded-lg">
                      <Building2 className="w-6 h-6 text-emerald-400" />
                    </div>
                    <h3 className="text-xl font-bold text-emerald-400">Multi-Unit House Hack</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Live in one unit, rent the others. Lower down payment, tenant income covers your mortgage.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Purchase Price:</span>
                      <span className="text-white font-semibold">$450,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Down Payment:</span>
                      <span className="text-white font-semibold">$67,500 (15%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Monthly Rent:</span>
                      <span className="text-green-400 font-semibold">$3,500</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Strategy:</span>
                      <span className="text-cyan-400 font-semibold">House Hacking</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setRentalInputs({
                        purchasePrice: 450000,
                        downPayment: 67500,
                        interestRate: 7.25,
                        loanTerm: 30,
                        monthlyRent: 3500,
                        vacancy: 4,
                        propertyTax: 5400,
                        insurance: 1600,
                        maintenance: 3600,
                        propertyMgmt: 0
                      });
                      setCurrentPage('rental');
                    }}
                    className="w-full py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 3: Luxury Condo (Appreciation Play) */}
                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-purple-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-purple-400" />
                    </div>
                    <h3 className="text-xl font-bold text-purple-400">Luxury Condo</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Coastal city appreciation play. Lower cash flow but strong long-term appreciation potential.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Purchase Price:</span>
                      <span className="text-white font-semibold">$600,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Down Payment:</span>
                      <span className="text-white font-semibold">$180,000 (30%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Monthly Rent:</span>
                      <span className="text-yellow-400 font-semibold">$3,200</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Strategy:</span>
                      <span className="text-purple-400 font-semibold">Appreciation</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setRentalInputs({
                        purchasePrice: 600000,
                        downPayment: 180000,
                        interestRate: 7.0,
                        loanTerm: 30,
                        monthlyRent: 3200,
                        vacancy: 6,
                        propertyTax: 7200,
                        insurance: 1800,
                        maintenance: 4800,
                        propertyMgmt: 10
                      });
                      setCurrentPage('rental');
                    }}
                    className="w-full py-3 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 4: Out-of-State Cash Flow */}
                <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-amber-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-amber-500/20 rounded-lg">
                      <DollarSign className="w-6 h-6 text-amber-400" />
                    </div>
                    <h3 className="text-xl font-bold text-amber-400">Out-of-State Cash Flow</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Midwest/South high-yield property. Strong cash-on-cash returns, managed remotely.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Purchase Price:</span>
                      <span className="text-white font-semibold">$180,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Down Payment:</span>
                      <span className="text-white font-semibold">$45,000 (25%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Monthly Rent:</span>
                      <span className="text-green-400 font-semibold">$1,600</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Cap Rate:</span>
                      <span className="text-green-400 font-semibold">~9%</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setRentalInputs({
                        purchasePrice: 180000,
                        downPayment: 45000,
                        interestRate: 8.0,
                        loanTerm: 30,
                        monthlyRent: 1600,
                        vacancy: 6,
                        propertyTax: 2160,
                        insurance: 900,
                        maintenance: 1440,
                        propertyMgmt: 10
                      });
                      setCurrentPage('rental');
                    }}
                    className="w-full py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 5: Short-Term Rental (Airbnb) */}
                <div className="bg-gradient-to-br from-red-500/10 to-rose-500/10 border border-red-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-red-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-red-500/20 rounded-lg">
                      <Globe className="w-6 h-6 text-red-400" />
                    </div>
                    <h3 className="text-xl font-bold text-red-400">Short-Term Rental</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Airbnb/VRBO property in tourist area. Higher revenue potential but more management intensive.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Purchase Price:</span>
                      <span className="text-white font-semibold">$400,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Down Payment:</span>
                      <span className="text-white font-semibold">$100,000 (25%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Monthly Revenue:</span>
                      <span className="text-green-400 font-semibold">$4,500</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Strategy:</span>
                      <span className="text-red-400 font-semibold">STR</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setRentalInputs({
                        purchasePrice: 400000,
                        downPayment: 100000,
                        interestRate: 8.25,
                        loanTerm: 30,
                        monthlyRent: 4500,
                        vacancy: 15,
                        propertyTax: 4800,
                        insurance: 2000,
                        maintenance: 6000,
                        propertyMgmt: 20
                      });
                      setCurrentPage('rental');
                    }}
                    className="w-full py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>
              </div>
            </div>

            {/* 1031 EXCHANGE TEMPLATES SECTION */}
            <div id="1031-templates" className="mb-16 mt-16 scroll-mt-24">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-lg">
                  <RefreshCw className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">1031 Exchange Calculator Templates</h3>
                  <p className="text-slate-400 text-sm">Tax-deferred property exchange scenarios and strategies</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Template 1: Basic 1031 (Single to Single) */}
                <div className="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 border border-cyan-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-cyan-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-cyan-500/20 rounded-lg">
                      <RefreshCw className="w-6 h-6 text-cyan-400" />
                    </div>
                    <h3 className="text-xl font-bold text-cyan-400">Basic 1031 Exchange</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Standard like-kind exchange. Sell one property, buy similar property, defer all capital gains taxes.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Sell Property:</span>
                      <span className="text-white font-semibold">$500K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Buy Property:</span>
                      <span className="text-white font-semibold">$600K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Gain:</span>
                      <span className="text-green-400 font-semibold">$227K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Tax Deferred:</span>
                      <span className="text-cyan-400 font-semibold">$88K</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setExchange1031Inputs({
                        originalPurchasePrice: 350000,
                        originalPurchaseYear: 2015,
                        salePrice: 500000,
                        saleYear: 2025,
                        depreciationTaken: 63636,
                        sellingCosts: 35000,
                        mortgageBalance: 150000,
                        replacementPurchasePrice: 600000,
                        replacementMortgage: 200000,
                        replacementClosingCosts: 12000,
                        federalCapitalGainsTax: 20,
                        niitRate: 3.8,
                        stateTaxRate: 9.3,
                        depreciationRecaptureRate: 25,
                        bootReceived: 0,
                        qualifiedIntermediaryFee: 2500,
                        identificationDeadlineDays: 45,
                        closingDeadlineDays: 180
                      });
                      setCurrentPage('exchange1031');
                    }}
                    className="w-full py-3 bg-cyan-500 text-white font-semibold rounded-lg hover:bg-cyan-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 2: Trading Up (Significant Upgrade) */}
                <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-blue-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-blue-400">Trading Up Strategy</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Significant upgrade into larger property. Consolidate equity into premium asset with higher value.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Sell Property:</span>
                      <span className="text-white font-semibold">$400K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Buy Property:</span>
                      <span className="text-white font-semibold">$900K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Gain:</span>
                      <span className="text-green-400 font-semibold">$172K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Tax Deferred:</span>
                      <span className="text-blue-400 font-semibold">$67K</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setExchange1031Inputs({
                        originalPurchasePrice: 300000,
                        originalPurchaseYear: 2016,
                        salePrice: 400000,
                        saleYear: 2025,
                        depreciationTaken: 54545,
                        sellingCosts: 28000,
                        mortgageBalance: 100000,
                        replacementPurchasePrice: 900000,
                        replacementMortgage: 550000,
                        replacementClosingCosts: 18000,
                        federalCapitalGainsTax: 20,
                        niitRate: 3.8,
                        stateTaxRate: 9.3,
                        depreciationRecaptureRate: 25,
                        bootReceived: 0,
                        qualifiedIntermediaryFee: 2500,
                        identificationDeadlineDays: 45,
                        closingDeadlineDays: 180
                      });
                      setCurrentPage('exchange1031');
                    }}
                    className="w-full py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 3: Multiple Properties (Diversification) */}
                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-purple-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <LayoutGrid className="w-6 h-6 text-purple-400" />
                    </div>
                    <h3 className="text-xl font-bold text-purple-400">Diversification Play</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Sell one large property, buy multiple smaller properties. Spread risk across different markets.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Sell Property:</span>
                      <span className="text-white font-semibold">$800K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Buy Multiple:</span>
                      <span className="text-white font-semibold">$500K + $400K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Gain:</span>
                      <span className="text-green-400 font-semibold">$373K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Tax Deferred:</span>
                      <span className="text-purple-400 font-semibold">$145K</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setExchange1031Inputs({
                        originalPurchasePrice: 500000,
                        originalPurchaseYear: 2014,
                        salePrice: 800000,
                        saleYear: 2025,
                        depreciationTaken: 72727,
                        sellingCosts: 56000,
                        mortgageBalance: 200000,
                        replacementPurchasePrice: 900000,
                        replacementMortgage: 350000,
                        replacementClosingCosts: 18000,
                        federalCapitalGainsTax: 20,
                        niitRate: 3.8,
                        stateTaxRate: 9.3,
                        depreciationRecaptureRate: 25,
                        bootReceived: 0,
                        qualifiedIntermediaryFee: 3000,
                        identificationDeadlineDays: 45,
                        closingDeadlineDays: 180
                      });
                      setCurrentPage('exchange1031');
                    }}
                    className="w-full py-3 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 4: Cash-Out Refinance Before 1031 */}
                <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-emerald-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-emerald-500/20 rounded-lg">
                      <DollarSign className="w-6 h-6 text-emerald-400" />
                    </div>
                    <h3 className="text-xl font-bold text-emerald-400">Cash-Out Then Exchange</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Refinance to pull tax-free cash before sale, then 1031 remaining equity. Smart wealth extraction.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Sell Property:</span>
                      <span className="text-white font-semibold">$600K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Buy Property:</span>
                      <span className="text-white font-semibold">$700K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Cash Extracted:</span>
                      <span className="text-emerald-400 font-semibold">$150K (refi)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Tax Deferred:</span>
                      <span className="text-emerald-400 font-semibold">$92K</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setExchange1031Inputs({
                        originalPurchasePrice: 400000,
                        originalPurchaseYear: 2017,
                        salePrice: 600000,
                        saleYear: 2025,
                        depreciationTaken: 58182,
                        sellingCosts: 42000,
                        mortgageBalance: 350000,
                        replacementPurchasePrice: 700000,
                        replacementMortgage: 300000,
                        replacementClosingCosts: 14000,
                        federalCapitalGainsTax: 20,
                        niitRate: 3.8,
                        stateTaxRate: 9.3,
                        depreciationRecaptureRate: 25,
                        bootReceived: 0,
                        qualifiedIntermediaryFee: 2500,
                        identificationDeadlineDays: 45,
                        closingDeadlineDays: 180
                      });
                      setCurrentPage('exchange1031');
                    }}
                    className="w-full py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 5: Partial 1031 (Taking Boot) */}
                <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-amber-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-amber-500/20 rounded-lg">
                      <PiggyBank className="w-6 h-6 text-amber-400" />
                    </div>
                    <h3 className="text-xl font-bold text-amber-400">Partial 1031 (Boot)</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Take some cash out (boot), pay taxes on that portion only. Defer taxes on reinvested equity.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Sell Property:</span>
                      <span className="text-white font-semibold">$700K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Buy Property:</span>
                      <span className="text-white font-semibold">$550K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Cash Kept (Boot):</span>
                      <span className="text-yellow-400 font-semibold">$100K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Tax on Boot:</span>
                      <span className="text-red-400 font-semibold">~$39K</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setExchange1031Inputs({
                        originalPurchasePrice: 450000,
                        originalPurchaseYear: 2016,
                        salePrice: 700000,
                        saleYear: 2025,
                        depreciationTaken: 65455,
                        sellingCosts: 49000,
                        mortgageBalance: 180000,
                        replacementPurchasePrice: 550000,
                        replacementMortgage: 200000,
                        replacementClosingCosts: 11000,
                        federalCapitalGainsTax: 20,
                        niitRate: 3.8,
                        stateTaxRate: 9.3,
                        depreciationRecaptureRate: 25,
                        bootReceived: 100000,
                        qualifiedIntermediaryFee: 2500,
                        identificationDeadlineDays: 45,
                        closingDeadlineDays: 180
                      });
                      setCurrentPage('exchange1031');
                    }}
                    className="w-full py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 6: Reverse 1031 (Buy First) */}
                <div className="bg-gradient-to-br from-red-500/10 to-rose-500/10 border border-red-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-red-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-red-500/20 rounded-lg">
                      <ArrowLeftRight className="w-6 h-6 text-red-400" />
                    </div>
                    <h3 className="text-xl font-bold text-red-400">Reverse 1031</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Buy replacement property first, then sell old property within 180 days. For competitive markets.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Buy First:</span>
                      <span className="text-white font-semibold">$750K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Then Sell:</span>
                      <span className="text-white font-semibold">$600K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Timeline:</span>
                      <span className="text-red-400 font-semibold">180 days max</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Tax Deferred:</span>
                      <span className="text-red-400 font-semibold">$75K</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setExchange1031Inputs({
                        originalPurchasePrice: 420000,
                        originalPurchaseYear: 2017,
                        salePrice: 600000,
                        saleYear: 2025,
                        depreciationTaken: 61091,
                        sellingCosts: 42000,
                        mortgageBalance: 220000,
                        replacementPurchasePrice: 750000,
                        replacementMortgage: 380000,
                        replacementClosingCosts: 15000,
                        federalCapitalGainsTax: 20,
                        niitRate: 3.8,
                        stateTaxRate: 9.3,
                        depreciationRecaptureRate: 25,
                        bootReceived: 0,
                        qualifiedIntermediaryFee: 4000,
                        identificationDeadlineDays: 45,
                        closingDeadlineDays: 180
                      });
                      setCurrentPage('exchange1031');
                    }}
                    className="w-full py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 7: Vacation to Commercial */}
                <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border border-violet-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-violet-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-violet-500/20 rounded-lg">
                      <Building2 className="w-6 h-6 text-violet-400" />
                    </div>
                    <h3 className="text-xl font-bold text-violet-400">Property Type Change</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Exchange vacation rental for commercial property. Different property types can qualify for 1031.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Sell Vacation:</span>
                      <span className="text-white font-semibold">$500K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Buy Commercial:</span>
                      <span className="text-white font-semibold">$600K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Gain:</span>
                      <span className="text-green-400 font-semibold">$182K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Tax Deferred:</span>
                      <span className="text-violet-400 font-semibold">$71K</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setExchange1031Inputs({
                        originalPurchasePrice: 380000,
                        originalPurchaseYear: 2018,
                        salePrice: 500000,
                        saleYear: 2025,
                        depreciationTaken: 55455,
                        sellingCosts: 35000,
                        mortgageBalance: 180000,
                        replacementPurchasePrice: 600000,
                        replacementMortgage: 250000,
                        replacementClosingCosts: 12000,
                        federalCapitalGainsTax: 20,
                        niitRate: 3.8,
                        stateTaxRate: 9.3,
                        depreciationRecaptureRate: 25,
                        bootReceived: 0,
                        qualifiedIntermediaryFee: 2500,
                        identificationDeadlineDays: 45,
                        closingDeadlineDays: 180
                      });
                      setCurrentPage('exchange1031');
                    }}
                    className="w-full py-3 bg-violet-500 text-white font-semibold rounded-lg hover:bg-violet-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>
              </div>
            </div>

            {/* LEVERAGE TRADING TEMPLATES SECTION */}
            <div id="trading-templates" className="mb-16 mt-16 scroll-mt-24">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Leverage Trading Calculator Templates</h3>
                  <p className="text-slate-400 text-sm">Pre-configured trading scenarios with different leverage levels and risk profiles</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Template 1: Conservative Swing Trade (3x) */}
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-blue-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-blue-400">Conservative Swing (3x)</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Lower leverage swing trading. Hold for days/weeks with wider stop losses. Lower risk, steady returns.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Capital:</span>
                      <span className="text-white font-semibold">$10,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Leverage:</span>
                      <span className="text-blue-400 font-semibold">3x</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Stop Loss:</span>
                      <span className="text-yellow-400 font-semibold">5%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Position Size:</span>
                      <span className="text-cyan-400 font-semibold">$30,000</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setCalcInputs({
                        capital: 10000,
                        leverage: 3,
                        entryPrice: 100,
                        exitPrice: 105,
                        position: 'long',
                        feePercent: 0.1
                      });
                      setCurrentPage('home');
                      setTimeout(() => {
                        document.getElementById('leverage-calculator')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    className="w-full py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 2: Day Trading Setup (10x) */}
                <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border border-purple-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-purple-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <BarChart3 className="w-6 h-6 text-purple-400" />
                    </div>
                    <h3 className="text-xl font-bold text-purple-400">Day Trading (10x)</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Active intraday trading with moderate leverage. Quick entries and exits, tight stop losses.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Capital:</span>
                      <span className="text-white font-semibold">$5,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Leverage:</span>
                      <span className="text-purple-400 font-semibold">10x</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Stop Loss:</span>
                      <span className="text-yellow-400 font-semibold">2%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Position Size:</span>
                      <span className="text-cyan-400 font-semibold">$50,000</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setCalcInputs({
                        capital: 5000,
                        leverage: 10,
                        entryPrice: 50000,
                        exitPrice: 51000,
                        position: 'long',
                        feePercent: 0.08
                      });
                      setCurrentPage('home');
                      setTimeout(() => {
                        document.getElementById('leverage-calculator')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    className="w-full py-3 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 3: Crypto Futures (20x) */}
                <div className="bg-gradient-to-br from-orange-500/10 to-amber-500/10 border border-orange-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-orange-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-orange-500/20 rounded-lg">
                      <Bitcoin className="w-6 h-6 text-orange-400" />
                    </div>
                    <h3 className="text-xl font-bold text-orange-400">Crypto Futures (20x)</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    High leverage crypto positioning. Volatile markets, tight risk management essential. High risk/reward.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Capital:</span>
                      <span className="text-white font-semibold">$2,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Leverage:</span>
                      <span className="text-orange-400 font-semibold">20x</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Stop Loss:</span>
                      <span className="text-red-400 font-semibold">1%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Position Size:</span>
                      <span className="text-cyan-400 font-semibold">$40,000</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setCalcInputs({
                        capital: 2000,
                        leverage: 20,
                        entryPrice: 95000,
                        exitPrice: 96900,
                        position: 'long',
                        feePercent: 0.05
                      });
                      setCurrentPage('home');
                      setTimeout(() => {
                        document.getElementById('leverage-calculator')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    className="w-full py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 4: Options Strategy (50x Effective) */}
                <div className="bg-gradient-to-br from-red-500/10 to-pink-500/10 border border-red-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-red-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-red-500/20 rounded-lg">
                      <Target className="w-6 h-6 text-red-400" />
                    </div>
                    <h3 className="text-xl font-bold text-red-400">Options Play (50x)</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    High leverage via options. Defined risk, massive upside potential. Suitable for experienced traders.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Capital:</span>
                      <span className="text-white font-semibold">$1,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Leverage:</span>
                      <span className="text-red-400 font-semibold">50x</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Risk:</span>
                      <span className="text-red-400 font-semibold">100% (premium)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Effective Size:</span>
                      <span className="text-cyan-400 font-semibold">$50,000</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setCalcInputs({
                        capital: 1000,
                        leverage: 50,
                        entryPrice: 450,
                        exitPrice: 470,
                        position: 'long',
                        feePercent: 0.5
                      });
                      setCurrentPage('home');
                      setTimeout(() => {
                        document.getElementById('leverage-calculator')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    className="w-full py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 5: Portfolio Hedging (Short) */}
                <div className="bg-gradient-to-br from-slate-500/10 to-gray-500/10 border border-slate-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-slate-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-slate-500/20 rounded-lg">
                      <Shield className="w-6 h-6 text-slate-400" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-300">Portfolio Hedge (Short)</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Protective short position with 2x leverage. Hedge long portfolio against market downturns.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Capital:</span>
                      <span className="text-white font-semibold">$20,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Leverage:</span>
                      <span className="text-slate-400 font-semibold">2x Short</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Portfolio:</span>
                      <span className="text-emerald-400 font-semibold">$100K Long</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Hedge Size:</span>
                      <span className="text-cyan-400 font-semibold">$40,000</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setCalcInputs({
                        capital: 20000,
                        leverage: 2,
                        entryPrice: 4500,
                        exitPrice: 4320,
                        position: 'short',
                        feePercent: 0.1
                      });
                      setCurrentPage('home');
                      setTimeout(() => {
                        document.getElementById('leverage-calculator')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    className="w-full py-3 bg-slate-500 text-white font-semibold rounded-lg hover:bg-slate-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>
              </div>
            </div>

            {/* STOCK SALE TAX TEMPLATES SECTION */}
            <div id="stock-templates" className="mb-16 mt-16 scroll-mt-24">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Stock Sale Tax Calculator Templates</h3>
                  <p className="text-slate-400 text-sm">Pre-configured scenarios for capital gains tax calculations</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Template 1: Short-Term Gain (High Earner) */}
                <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-red-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-red-500/20 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-red-400" />
                    </div>
                    <h3 className="text-xl font-bold text-red-400">Short-Term Gain</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Held less than 1 year. Taxed as ordinary income at high earner rates. Higher tax burden.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Shares:</span>
                      <span className="text-white font-semibold">500</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Gain per Share:</span>
                      <span className="text-green-400 font-semibold">$25</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Total Gain:</span>
                      <span className="text-green-400 font-semibold">$12,500</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Tax Rate:</span>
                      <span className="text-red-400 font-semibold">37% Fed</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setStockTaxInputs({
                        purchasePrice: 50,
                        salePrice: 75,
                        shares: 500,
                        holdingPeriod: 'short',
                        filingStatus: 'single',
                        annualIncome: 600000,
                        stateTaxRate: 9.3,
                        capitalLossCarryover: 0
                      });
                      setCurrentPage('tax');
                    }}
                    className="w-full py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 2: Long-Term Gain (Moderate Income) */}
                <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-green-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-green-500/20 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-green-400" />
                    </div>
                    <h3 className="text-xl font-bold text-green-400">Long-Term Gain</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Held over 1 year. Preferential tax rates (15% for most). Lower tax burden, buy & hold benefit.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Shares:</span>
                      <span className="text-white font-semibold">200</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Gain per Share:</span>
                      <span className="text-green-400 font-semibold">$40</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Total Gain:</span>
                      <span className="text-green-400 font-semibold">$8,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Tax Rate:</span>
                      <span className="text-green-400 font-semibold">15% Fed</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setStockTaxInputs({
                        purchasePrice: 60,
                        salePrice: 100,
                        shares: 200,
                        holdingPeriod: 'long',
                        filingStatus: 'married',
                        annualIncome: 120000,
                        stateTaxRate: 5,
                        capitalLossCarryover: 0
                      });
                      setCurrentPage('tax');
                    }}
                    className="w-full py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 3: Tax Loss Harvesting */}
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-blue-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <TrendingDown className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-blue-400">Tax Loss Harvesting</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Realize losses to offset gains. Strategic tax planning to reduce tax bill using capital loss carryover.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Shares:</span>
                      <span className="text-white font-semibold">300</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Capital Loss:</span>
                      <span className="text-red-400 font-semibold">-$3,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Carryover:</span>
                      <span className="text-yellow-400 font-semibold">$5,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Tax Savings:</span>
                      <span className="text-blue-400 font-semibold">Reduced</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setStockTaxInputs({
                        purchasePrice: 85,
                        salePrice: 75,
                        shares: 300,
                        holdingPeriod: 'long',
                        filingStatus: 'single',
                        annualIncome: 150000,
                        stateTaxRate: 7,
                        capitalLossCarryover: 5000
                      });
                      setCurrentPage('tax');
                    }}
                    className="w-full py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 4: ESPP/RSU Sale */}
                <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border border-purple-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-purple-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <Briefcase className="w-6 h-6 text-purple-400" />
                    </div>
                    <h3 className="text-xl font-bold text-purple-400">ESPP/RSU Sale</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Employee stock compensation sale. Mix of ordinary income (at vest) and capital gains (appreciation).
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Shares:</span>
                      <span className="text-white font-semibold">150</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Vest Price:</span>
                      <span className="text-slate-300 font-semibold">$100</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Sale Price:</span>
                      <span className="text-green-400 font-semibold">$130</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Cap Gain:</span>
                      <span className="text-purple-400 font-semibold">$4,500</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setStockTaxInputs({
                        purchasePrice: 100,
                        salePrice: 130,
                        shares: 150,
                        holdingPeriod: 'long',
                        filingStatus: 'married',
                        annualIncome: 200000,
                        stateTaxRate: 6,
                        capitalLossCarryover: 0
                      });
                      setCurrentPage('tax');
                    }}
                    className="w-full py-3 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>

                {/* Template 5: Day Trader (Multiple Short-Term) */}
                <div className="bg-gradient-to-br from-orange-500/10 to-amber-500/10 border border-orange-500/30 rounded-xl p-6 hover:shadow-xl hover:shadow-orange-500/20 transition-all group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-orange-500/20 rounded-lg">
                      <BarChart3 className="w-6 h-6 text-orange-400" />
                    </div>
                    <h3 className="text-xl font-bold text-orange-400">Day Trader Gains</h3>
                  </div>
                  <p className="text-sm text-slate-300 mb-4">
                    Frequent short-term trades. All gains taxed as ordinary income. Higher volume, higher taxes.
                  </p>
                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Shares:</span>
                      <span className="text-white font-semibold">1,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Gain per Share:</span>
                      <span className="text-green-400 font-semibold">$5</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Total Gain:</span>
                      <span className="text-green-400 font-semibold">$5,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Tax Rate:</span>
                      <span className="text-orange-400 font-semibold">32% Fed</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setStockTaxInputs({
                        purchasePrice: 45,
                        salePrice: 50,
                        shares: 1000,
                        holdingPeriod: 'short',
                        filingStatus: 'single',
                        annualIncome: 250000,
                        stateTaxRate: 8,
                        capitalLossCarryover: 0
                      });
                      setCurrentPage('tax');
                    }}
                    className="w-full py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-all group-hover:shadow-lg"
                  >
                    Use This Template →
                  </button>
                </div>
              </div>
            </div>

            {/* Comparison Table */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6">Template Comparison</h3>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left p-4 text-slate-400 font-semibold">Template</th>
                        <th className="text-left p-4 text-slate-400 font-semibold">Annual Income</th>
                        <th className="text-left p-4 text-slate-400 font-semibold">Monthly Expenses</th>
                        <th className="text-left p-4 text-slate-400 font-semibold">Savings Rate</th>
                        <th className="text-left p-4 text-slate-400 font-semibold">Best For</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-slate-700 hover:bg-slate-700/30 transition-colors">
                        <td className="p-4 text-white font-semibold">Recent Graduate</td>
                        <td className="p-4 text-slate-300">$55,000</td>
                        <td className="p-4 text-slate-300">$2,800</td>
                        <td className="p-4 text-green-400">~25%</td>
                        <td className="p-4 text-slate-300 text-sm">Entry-level career, building foundation</td>
                      </tr>
                      <tr className="border-b border-slate-700 hover:bg-slate-700/30 transition-colors">
                        <td className="p-4 text-white font-semibold">Young Professional</td>
                        <td className="p-4 text-slate-300">$92,000</td>
                        <td className="p-4 text-slate-300">$3,800</td>
                        <td className="p-4 text-green-400">~40%</td>
                        <td className="p-4 text-slate-300 text-sm">Established career, home savings</td>
                      </tr>
                      <tr className="border-b border-slate-700 hover:bg-slate-700/30 transition-colors">
                        <td className="p-4 text-white font-semibold">Family of 4</td>
                        <td className="p-4 text-slate-300">$138,000</td>
                        <td className="p-4 text-slate-300">$6,500</td>
                        <td className="p-4 text-yellow-400">~30%</td>
                        <td className="p-4 text-slate-300 text-sm">Dual income, children, balanced goals</td>
                      </tr>
                      <tr className="border-b border-slate-700 hover:bg-slate-700/30 transition-colors">
                        <td className="p-4 text-white font-semibold">Mid-Career Pro</td>
                        <td className="p-4 text-slate-300">$202,000</td>
                        <td className="p-4 text-slate-300">$8,000</td>
                        <td className="p-4 text-green-400">~48%</td>
                        <td className="p-4 text-slate-300 text-sm">Peak earnings, wealth accumulation</td>
                      </tr>
                      <tr className="border-b border-slate-700 hover:bg-slate-700/30 transition-colors">
                        <td className="p-4 text-white font-semibold">Early Retirement</td>
                        <td className="p-4 text-slate-300">$138,000</td>
                        <td className="p-4 text-slate-300">$3,500</td>
                        <td className="p-4 text-emerald-400">~65%</td>
                        <td className="p-4 text-slate-300 text-sm">FIRE strategy, optimized lifestyle</td>
                      </tr>
                      <tr className="hover:bg-slate-700/30 transition-colors">
                        <td className="p-4 text-white font-semibold">Aggressive Saver</td>
                        <td className="p-4 text-slate-300">$300,000</td>
                        <td className="p-4 text-slate-300">$7,500</td>
                        <td className="p-4 text-emerald-400">~70%</td>
                        <td className="p-4 text-slate-300 text-sm">High earner, maximum growth</td>
                      </tr>
                      <tr className="border-t border-slate-700 hover:bg-slate-700/30 transition-colors">
                        <td className="p-4 text-white font-semibold">First-Time Homebuyer</td>
                        <td className="p-4 text-slate-300">$94,500</td>
                        <td className="p-4 text-slate-300">$3,200</td>
                        <td className="p-4 text-green-400">~42%</td>
                        <td className="p-4 text-slate-300 text-sm">Down payment, mortgage leverage</td>
                      </tr>
                      <tr className="hover:bg-slate-700/30 transition-colors">
                        <td className="p-4 text-white font-semibold">Small Business Owner</td>
                        <td className="p-4 text-slate-300">$115,000</td>
                        <td className="p-4 text-slate-300">$5,200</td>
                        <td className="p-4 text-yellow-400">~32%</td>
                        <td className="p-4 text-slate-300 text-sm">Business leverage, cash flow focus</td>
                      </tr>
                      <tr className="hover:bg-slate-700/30 transition-colors">
                        <td className="p-4 text-white font-semibold">Tax Optimization Plan</td>
                        <td className="p-4 text-slate-300">$235,000</td>
                        <td className="p-4 text-slate-300">$8,500</td>
                        <td className="p-4 text-green-400">~50%</td>
                        <td className="p-4 text-slate-300 text-sm">Tax-advantaged strategies, deductions</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Customization Guide */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-xl p-6">
                <h4 className="font-semibold text-blue-400 mb-3 flex items-center">
                  <Target className="w-5 h-5 mr-2" />
                  How to Use Templates
                </h4>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">1.</span>
                    <span>Choose the template that best matches your current situation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">2.</span>
                    <span>Click "Use This Template" to load it into the appropriate calculator</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">3.</span>
                    <span>Customize all values to match your actual income, expenses, and goals</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">4.</span>
                    <span>Review your projections and adjust as needed</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-6">
                <h4 className="font-semibold text-purple-400 mb-3 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  Important Notes
                </h4>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    <span>Templates are starting points - adjust all values to your situation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    <span>Income and expense figures are based on US national averages</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    <span>Investment returns assume diversified portfolios - actual results vary</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    <span>Review and update your plan regularly as circumstances change</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* CTA to start planning */}
            <div className="mt-12 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Don't See Your Situation?</h3>
              <p className="text-slate-300 mb-6">
                Start with a blank slate and build your personalized financial plan from scratch
              </p>
              <button
                onClick={() => setCurrentPage('planning')}
                className="px-8 py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-all shadow-lg shadow-emerald-500/20"
              >
                Create Custom Plan →
              </button>
            </div>
          </section>
        )}

        {/* LEVERAGE PRODUCTS PAGE (30+) */}
        {currentPage === 'products' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                  <BarChart3 className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold">31+ Leverage Products Available to Retail</h2>
                  <p className="text-slate-300 mt-2">A comprehensive guide to every leverage product you can access</p>
                </div>
              </div>
              <div className="mt-6 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-300">
                  💡 <strong>Know Your Options:</strong> From mortgages to crypto perpetuals, understand what leverage products exist, 
                  how they work, typical leverage ratios, and who can access them. Knowledge is power.
                </p>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mb-12 bg-amber-500/10 border-l-4 border-amber-500 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-bold text-amber-400 mb-2">⚠️ Educational Overview Only</h4>
                  <p className="text-xs text-amber-200/90">
                    This page describes leverage products for educational purposes. Listing a product here does NOT constitute a recommendation. 
                    Many of these products are extremely risky. Always consult licensed professionals and understand the risks before using leverage.
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-blue-400 mb-1">10+</div>
                <div className="text-sm text-slate-400">Trading Products</div>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-emerald-400 mb-1">8+</div>
                <div className="text-sm text-slate-400">Real Estate Products</div>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-purple-400 mb-1">7+</div>
                <div className="text-sm text-slate-400">Business Products</div>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-orange-400 mb-1">6+</div>
                <div className="text-sm text-slate-400">Consumer Products</div>
              </div>
            </div>

            {/* CATEGORY 1: TRADING & INVESTING LEVERAGE */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold">Trading & Investing Leverage (10 Products)</h3>
              </div>

              <div className="space-y-4">
                {/* Stock Margin */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">1. Stock Margin Trading</h4>
                      <p className="text-sm text-slate-400">Borrow against your stock portfolio to buy more stocks</p>
                    </div>
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">2x MAX</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">All brokerages (Fidelity, Schwab, IBKR, Robinhood)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">$2,000 minimum, margin account approval</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">6-13% APR (varies by broker & amount)</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Low leverage (safer)</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Liquid markets</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Margin calls</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Interest costs</span>
                  </div>
                </div>

                {/* Stock Options */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">2. Stock Options (Calls & Puts)</h4>
                      <p className="text-sm text-slate-400">Contracts giving the right to buy/sell at fixed price</p>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">50-100x</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">All major brokerages (approval levels required)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">Options approval (Level 2+), experience</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">Premium paid upfront, $0.65/contract fee</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Limited downside (premium)</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Defined risk</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Expires worthless</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Time decay</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Complex</span>
                  </div>
                </div>

                {/* Futures */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">3. Futures Contracts</h4>
                      <p className="text-sm text-slate-400">Standardized contracts to buy/sell at future date (ES, NQ, CL, Gold)</p>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">10-50x</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">IBKR, TD Ameritrade, NinjaTrader, AMP Futures</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">$500-$5,000 minimum (varies by contract)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">$1-$10/contract, no interest (futures fee)</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ High liquidity</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ No decay</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ High leverage risk</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Expiration dates</span>
                  </div>
                </div>

                {/* Forex */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">4. Forex Trading (FX)</h4>
                      <p className="text-sm text-slate-400">Currency pairs (EUR/USD, GBP/JPY, etc.)</p>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">50-500x</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">OANDA, Forex.com, IBKR, IG Group</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">$100-$1,000 minimum (U.S. max 50x leverage)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">Spread (0.5-3 pips), overnight swap fees</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ 24/5 trading</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ High liquidity</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Extremely high leverage</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Swap fees</span>
                  </div>
                </div>

                {/* Crypto Perpetuals */}
                <div className="bg-slate-800/50 border border-orange-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">5. Crypto Perpetual Futures (Perps)</h4>
                      <p className="text-sm text-slate-400">Never-expiring crypto futures with funding rates</p>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">10-125x</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">Hyperliquid, Lighter, Coinbase, Binance, Bybit</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">$50-$100 minimum, KYC varies by platform</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">Funding rate (±0.01% per 8hrs), trading fees (0.02-0.1%)</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ 24/7/365 trading</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ No expiration</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Easy shorting</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Instant liquidation</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Funding costs</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Exchange risk</span>
                  </div>
                  <div className="mt-3 bg-orange-500/10 border border-orange-500/30 rounded p-3">
                    <p className="text-xs text-orange-300">
                      <strong>⚠️ Warning:</strong> Crypto perps are the most volatile leverage product available to retail. 
                      $2B+ in liquidations can happen in a single day. Highly addictive. Not recommended for beginners.
                    </p>
                  </div>
                </div>

                {/* CFDs */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">6. CFDs (Contracts for Difference)</h4>
                      <p className="text-sm text-slate-400">Derivative contracts on stocks, indices, commodities (not available in U.S.)</p>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">5-30x</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">IG Group, Plus500, eToro (outside U.S.)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">$100-$500 minimum</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">Spread + overnight financing fees</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Access global markets</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Not available in U.S.</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Financing costs</span>
                  </div>
                </div>

                {/* Portfolio Margin */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">7. Portfolio Margin</h4>
                      <p className="text-sm text-slate-400">Risk-based margin allowing higher leverage for sophisticated traders</p>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">4-6x</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">IBKR, Schwab, Fidelity (for qualified accounts)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">$125,000 minimum, options approval</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">Same as reg T margin (6-13% APR)</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Lower margin requirements</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ High minimum</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Complex calculations</span>
                  </div>
                </div>

                {/* LEAPS */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">8. LEAPS (Long-term Options)</h4>
                      <p className="text-sm text-slate-400">Options with 1-3 year expiration (poor man's covered call strategy)</p>
                    </div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full">5-20x</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">All brokerages with options (Level 2+)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">Options approval, $2,000+ recommended</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">Premium (higher for longer dates)</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Less time decay</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Lower leverage (safer)</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Expensive premium</span>
                  </div>
                </div>

                {/* Leveraged ETFs */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">9. Leveraged ETFs</h4>
                      <p className="text-sm text-slate-400">ETFs that provide 2x or 3x daily returns (TQQQ, UPRO, SQQQ)</p>
                    </div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full">2-3x</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">ProShares, Direxion (trade like stocks)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">Any brokerage account, $0 minimum</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">0.6-1% expense ratio, rebalancing decay</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Easy access</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Decay over time</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Not for long-term holding</span>
                  </div>
                </div>

                {/* Box Spreads */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">9. Box Spread Financing</h4>
                      <p className="text-sm text-slate-400">Options arbitrage strategy used to borrow at Treasury rates (~3-5%)</p>
                    </div>
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">CUSTOM</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">Any broker with European options (SPX)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">$100,000+, options expertise, portfolio margin</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">~3-5% (Treasury rate + spread)</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Cheapest leverage</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Complex execution</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ High minimum</span>
                  </div>
                </div>
              </div>
            </div>

            {/* CATEGORY 2: REAL ESTATE LEVERAGE */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg">
                  <Home className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold">Real Estate Leverage (8 Products)</h3>
              </div>

              <div className="space-y-4">
                {/* Conventional Mortgage */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">11. Conventional Mortgage</h4>
                      <p className="text-sm text-slate-400">Standard home loan with 20% down payment</p>
                    </div>
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">4x</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">All banks, credit unions, mortgage lenders</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">20% down, 620+ credit score, income verification</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">6-8% APR (30-year fixed), closing costs 2-5%</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Low rates</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Tax deductible interest</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Large down payment</span>
                  </div>
                </div>

                {/* FHA Loan */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">12. FHA Loan</h4>
                      <p className="text-sm text-slate-400">Government-backed mortgage with 3.5% down</p>
                    </div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full">28x</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">FHA-approved lenders (most banks)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">3.5% down, 580+ credit score, primary residence</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">6-8% APR + MIP (mortgage insurance 0.85%)</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Low down payment</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Mortgage insurance required</span>
                  </div>
                </div>

                {/* VA Loan */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">13. VA Loan</h4>
                      <p className="text-sm text-slate-400">0% down mortgage for veterans and active military</p>
                    </div>
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">∞ (0% down)</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">VA-approved lenders</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">Military service, certificate of eligibility</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">5.5-7.5% APR, 2.15% funding fee (one-time)</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ No down payment</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ No PMI</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Military only</span>
                  </div>
                </div>

                {/* HELOC */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">14. HELOC (Home Equity Line of Credit)</h4>
                      <p className="text-sm text-slate-400">Revolving credit line secured by home equity</p>
                    </div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full">VARIABLE</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">Banks, credit unions (Chase, Wells Fargo, BofA)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">20%+ home equity, 640+ credit score</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">8-11% APR (variable), $0-$500 closing costs</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Flexible draw</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Tax deductible (if used for home)</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Variable rate</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Home at risk</span>
                  </div>
                </div>

                {/* Cash-Out Refinance */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">15. Cash-Out Refinance</h4>
                      <p className="text-sm text-slate-400">Refinance mortgage for more than you owe, take difference in cash</p>
                    </div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full">VARIES</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">All mortgage lenders</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">20%+ equity remaining, 620+ credit score</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">6-8% APR (30-year fixed), 2-5% closing costs</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Fixed rate</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Large lump sum</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Restart mortgage term</span>
                  </div>
                </div>

                {/* Bridge Loan */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">16. Bridge Loan</h4>
                      <p className="text-sm text-slate-400">Short-term financing to buy new home before selling old one</p>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">SHORT-TERM</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">Banks, hard money lenders</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">Equity in current home, strong credit</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">8.5-10.5% APR, 1.5-3% origination fee, 6-12 month term</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Buy before selling</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Expensive</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Two mortgages temporarily</span>
                  </div>
                </div>

                {/* Hard Money Loan */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">17. Hard Money Loan (Real Estate)</h4>
                      <p className="text-sm text-slate-400">Asset-based financing for flippers and investors</p>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">4-9x</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">Private lenders, hard money funds</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">Property value, 10-30% down</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">9-15% APR, 2-5 points upfront, 6-18 month term</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Fast approval</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Asset-based (not credit)</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Very expensive</span>
                  </div>
                </div>

                {/* Commercial Real Estate Loan */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">18. Commercial Real Estate Loan</h4>
                      <p className="text-sm text-slate-400">Financing for investment properties (5+ units) and commercial buildings</p>
                    </div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full">3-4x</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">Commercial banks, credit unions</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">20-30% down, 1.25x DSCR, business plan</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">7-10% APR, 1-3 points, 5-25 year amortization</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Scale portfolio</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Large down payment</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Personal guarantee often required</span>
                  </div>
                </div>
              </div>
            </div>

            {/* CATEGORY 3: BUSINESS LEVERAGE */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg">
                  <Briefcase className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold">Business Leverage (7 Products)</h3>
              </div>

              <div className="space-y-4">
                {/* SBA Loan */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">19. SBA Loan (7(a) & 504)</h4>
                      <p className="text-sm text-slate-400">Government-backed loans for small businesses</p>
                    </div>
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">10x (90% LTV)</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">SBA-approved lenders (banks, credit unions)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">10% down, 2 years operating, good credit</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">7-10% APR, guarantee fee 0.25-3.75%, up to 25 year term</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Low down payment</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Long terms</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Slow approval (60-90 days)</span>
                  </div>
                </div>

                {/* Business Line of Credit */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">20. Business Line of Credit</h4>
                      <p className="text-sm text-slate-400">Revolving credit for working capital and cash flow</p>
                    </div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full">REVOLVING</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">Banks, online lenders (Bluevine, Fundbox)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">$50,000+ annual revenue, 600+ credit score</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">8-25% APR, $0-$500 annual fee</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Flexible</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Only pay for what you use</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Variable rate</span>
                  </div>
                </div>

                {/* Equipment Financing */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">21. Equipment Financing</h4>
                      <p className="text-sm text-slate-400">Secured loans for machinery, vehicles, tech equipment</p>
                    </div>
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">4-10x</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">Banks, equipment vendors, specialty lenders</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">10-20% down, equipment as collateral</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">6-12% APR, 3-7 year term</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Equipment secured</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Tax benefits (depreciation)</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Equipment depreciates</span>
                  </div>
                </div>

                {/* Invoice Factoring */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">22. Invoice Factoring / AR Financing</h4>
                      <p className="text-sm text-slate-400">Sell invoices for immediate cash (advance against receivables)</p>
                    </div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full">80-90%</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">Factoring companies (BlueVine, Fundbox, Triumph)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">B2B invoices, creditworthy customers</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">1-5% per invoice, advance 80-90%</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Fast cash</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ No debt on balance sheet</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Expensive (effective APR 24-60%)</span>
                  </div>
                </div>

                {/* Merchant Cash Advance */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">23. Merchant Cash Advance (MCA)</h4>
                      <p className="text-sm text-slate-400">Advance against future credit card sales</p>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">HIGH COST</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">MCA companies (OnDeck, Rapid Finance)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">$5,000+/month credit card revenue</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">Factor rate 1.2-1.5x (effective APR 40-200%+)</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Fast funding (1-3 days)</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Extremely expensive</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Daily repayment from sales</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Predatory</span>
                  </div>
                </div>

                {/* Venture Debt */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">24. Venture Debt</h4>
                      <p className="text-sm text-slate-400">Loans for VC-backed startups (extends runway between equity rounds)</p>
                    </div>
                    <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-xs font-bold rounded-full">VARIES</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">Specialty lenders (Silicon Valley Bank, Hercules Capital)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">VC backing, strong growth metrics, 12+ month runway</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">8-15% interest + warrants (0.1-0.5% equity)</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Less dilutive than equity</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Extend runway</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ VC-backed only</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Warrants dilute</span>
                  </div>
                </div>

                {/* Asset-Based Lending */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">25. Asset-Based Lending (ABL)</h4>
                      <p className="text-sm text-slate-400">Loans secured by business assets (inventory, equipment, AR)</p>
                    </div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full">50-85% LTV</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">Banks, specialty finance companies</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">$2M+ revenue, substantial assets</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">Prime + 2-4% (7-10% total), audit fees</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Large credit lines</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Audits required</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Complex</span>
                  </div>
                </div>
              </div>
            </div>

            {/* CATEGORY 4: CONSUMER LEVERAGE */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg">
                  <CreditCard className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold">Consumer Leverage (6 Products)</h3>
              </div>

              <div className="space-y-4">
                {/* Credit Cards */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">26. Credit Cards</h4>
                      <p className="text-sm text-slate-400">Revolving unsecured credit for purchases</p>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">VARIABLE</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">All banks (Chase, Amex, Capital One, Citi)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">18+, income, 600+ credit score (varies)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">16-29% APR if carrying balance, $0-$695 annual fee</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Rewards (cashback, points)</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ No interest if paid monthly</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Very high APR</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Easy to overspend</span>
                  </div>
                </div>

                {/* Personal Loans */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">27. Personal Loans (Unsecured)</h4>
                      <p className="text-sm text-slate-400">Fixed-term loans for consolidation, purchases, emergencies</p>
                    </div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full">FIXED</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">Banks, credit unions, online lenders (SoFi, Marcus)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">640+ credit score, income verification</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">6-36% APR, $0-5% origination fee, 2-7 year term</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Fixed rate & payment</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ High APR for poor credit</span>
                  </div>
                </div>

                {/* Auto Loans */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">28. Auto Loans</h4>
                      <p className="text-sm text-slate-400">Secured loans for vehicle purchases</p>
                    </div>
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">4-10x</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">Banks, credit unions, dealer financing</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">10-20% down, 620+ credit score</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">4-18% APR (varies by credit), 3-7 year term</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Vehicle secured</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Depreciating asset</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Negative equity risk</span>
                  </div>
                </div>

                {/* Student Loans */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">29. Student Loans (Federal & Private)</h4>
                      <p className="text-sm text-slate-400">Loans for college tuition and expenses</p>
                    </div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full">HIGH LTV</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">Federal gov't, banks (Sallie Mae, SoFi, Discover)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">Enrollment, FAFSA (federal), credit check (private)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">Federal: 5.5-8.05%, Private: 4-14%, 10-30 year term</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Access education</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Deferred payment (in school)</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Non-dischargeable in bankruptcy</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Long repayment</span>
                  </div>
                </div>

                {/* Buy Now Pay Later */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">30. Buy Now Pay Later (BNPL)</h4>
                      <p className="text-sm text-slate-400">Split purchases into 4 installments (Affirm, Klarna, Afterpay)</p>
                    </div>
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">0% (often)</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">Affirm, Klarna, Afterpay, PayPal</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">18+, debit/credit card, soft credit check</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">0-30% APR (varies by merchant/term), late fees $7-25</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Often 0% APR</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ No hard credit check</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Encourages overspending</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Late fees add up</span>
                  </div>
                </div>

                {/* Payday Loans */}
                <div className="bg-slate-800/50 border border-red-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-red-400 mb-1">31. Payday Loans ⚠️ AVOID</h4>
                      <p className="text-sm text-slate-400">Short-term high-interest loans against next paycheck</p>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">PREDATORY</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Who Offers:</div>
                      <div className="text-slate-300">Payday loan stores, online lenders</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Requirements:</div>
                      <div className="text-slate-300">Income verification, bank account</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Costs:</div>
                      <div className="text-slate-300">$15 per $100 borrowed (APR 400%+), 2-4 week term</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Insanely expensive (400%+ APR)</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Debt trap cycle</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Predatory by design</span>
                  </div>
                  <div className="mt-3 bg-red-500/10 border border-red-500/30 rounded p-3">
                    <p className="text-xs text-red-300">
                      <strong>WARNING:</strong> Payday loans are designed to trap borrowers in debt cycles. If you need $500, you'll pay $575 in 2 weeks 
                      (391% APR). 80% of borrowers roll over loans, paying more fees. Avoid at all costs. Better options: ask family, sell items, negotiate with creditors.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Section */}
            <div className="mb-12 bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center text-blue-400">
                <span className="mr-3">💡</span>
                Key Takeaways: Using Leverage Wisely
              </h3>

              <div className="space-y-4 text-sm text-slate-300">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">1. Lower Leverage = Safer</h4>
                  <p>Real estate mortgages (4x) are safer than crypto perpetuals (125x). Match leverage to asset volatility.</p>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">2. Only Leverage Appreciating Assets</h4>
                  <p>Good: real estate, stocks, businesses. Bad: cars, consumer goods, depreciating assets.</p>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">3. Understand Total Cost</h4>
                  <p>Factor in interest, fees, opportunity cost. A 0% APR car loan isn't free—you're paying in depreciation.</p>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">4. Always Have an Exit Plan</h4>
                  <p>Know your liquidation price, margin call threshold, and when you'll cut losses. Never assume "it'll come back."</p>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-bold text-white mb-2">5. Avoid Predatory Products</h4>
                  <p>Payday loans (400% APR), merchant cash advances (100%+ APR), and aggressive margin calls destroy wealth. If it feels predatory, it probably is.</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-4">Ready to Calculate Your Leverage?</h3>
              <p className="text-slate-300 mb-6">
                Use our professional calculators to model different scenarios before committing capital.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => setCurrentPage('leverage-trading')}
                  className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-600 transition-all"
                >
                  Trading Calculator
                </button>
                <button
                  onClick={() => setCurrentPage('mortgage')}
                  className="px-8 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
                >
                  Mortgage Calculator
                </button>
              </div>
            </div>
          </section>
        )}

        {/* CRYPTO PROTOCOLS PAGE */}
        {currentPage === 'crypto-protocols' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                  <Bitcoin className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold">Crypto Protocols</h2>
                  <p className="text-slate-300 mt-2">Leading DeFi protocols across all major categories</p>
                </div>
              </div>
              <div className="mt-6 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-300">
                  💡 <strong>Market Overview:</strong> Total DeFi TVL: ~$130B (Dec 2025). These protocols represent sector leaders 
                  with dominant market positions. Smart contract-based—you maintain custody but assume smart contract risk.
                </p>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mb-12 bg-red-500/10 border-l-4 border-red-500 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-bold text-red-400 mb-2">⚠️ CRITICAL WARNINGS</h4>
                  <ul className="text-xs text-red-200/90 space-y-1">
                    <li>• <strong>Smart contract risk:</strong> Protocols can be hacked or exploited</li>
                    <li>• <strong>No insurance:</strong> Unlike CEXs, no FDIC or investor protection</li>
                    <li>• <strong>Liquidation is automatic:</strong> No support desk to help you</li>
                    <li>• <strong>Gas fees:</strong> Ethereum transactions can be expensive during congestion</li>
                    <li>• <strong>Regulatory risk:</strong> Some protocols may have geographic restrictions</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* CATEGORY 1: LENDING & BORROWING */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg">
                    <DollarSign className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Lending & Borrowing</h3>
                    <p className="text-slate-400 text-sm">~$78B TVL | 60% Share</p>
                  </div>
                </div>
              </div>
              <p className="text-slate-400 mb-6 text-sm">
                Multi-chain credit markets; Aave dominates with unmatched scale across 15+ networks.
              </p>

              <div className="space-y-4">
                {/* Aave */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">Aave</h4>
                      <p className="text-sm text-slate-400">Multi-chain credit hub with flash loans & isolation mode</p>
                    </div>
                    <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 text-xs font-bold rounded-full">Market Leader</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Chains:</div>
                      <div className="text-slate-300"><strong>Ethereum</strong>, Polygon, Arbitrum, Optimism, Avalanche, Base + 10 more</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">TVL:</div>
                      <div className="text-slate-300"><strong>$40B</strong></div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Key Assets:</div>
                      <div className="text-slate-300">ETH, WBTC, stablecoins, 100+ tokens</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">How It Works:</div>
                    <div className="text-slate-300 text-sm bg-slate-900/50 rounded p-3">
                      Supply assets to earn yield → Borrow against collateral (up to 75% LTV) → Flash loans available (instant uncollateralized loans repaid in same transaction). Isolation mode protects from risky asset contagion.
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">Use Cases:</div>
                    <div className="text-slate-300 text-sm">
                      • <strong>Leverage:</strong> Supply $10K ETH → Borrow $7.5K USDC → Buy more ETH → Repeat for 3x leverage<br/>
                      • <strong>Arbitrage:</strong> Use flash loans to profit from price differences across DEXs<br/>
                      • <strong>Yield:</strong> Earn 2-8% APY on supplied assets while maintaining liquidity
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ $40B TVL (most trusted)</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ 15+ chains</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Flash loans</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Complex for beginners</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-400">
                    <strong>Website:</strong> aave.com | <strong>Share:</strong> Dominant lending leader
                  </div>
                </div>
              </div>
            </div>

            {/* CATEGORY 2: LIQUID STAKING */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
                    <Shield className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Liquid Staking</h3>
                    <p className="text-slate-400 text-sm">~$38B TVL | 29% Share</p>
                  </div>
                </div>
              </div>
              <p className="text-slate-400 mb-6 text-sm">
                Unlock staked assets; Lido owns Ethereum staking, Jito dominates Solana with MEV optimization.
              </p>

              <div className="space-y-4">
                {/* Lido */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">Lido</h4>
                      <p className="text-sm text-slate-400">ETH security backbone with stETH liquid staking</p>
                    </div>
                    <span className="px-3 py-1 bg-indigo-500/20 text-indigo-400 text-xs font-bold rounded-full">ETH Leader</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Chains:</div>
                      <div className="text-slate-300"><strong>Ethereum</strong>, Polygon, Solana</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">TVL:</div>
                      <div className="text-slate-300"><strong>$32B</strong></div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Key Assets:</div>
                      <div className="text-slate-300">ETH → stETH, SOL → stSOL</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">How It Works:</div>
                    <div className="text-slate-300 text-sm bg-slate-900/50 rounded p-3">
                      Stake ETH → Receive stETH (1:1 liquid token) → stETH earns ~3-4% APR staking rewards → Use stETH in DeFi (Aave, Curve, etc.) while earning staking rewards.
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">Use Cases:</div>
                    <div className="text-slate-300 text-sm">
                      • <strong>Liquid staking:</strong> Earn ETH staking rewards without locking assets for months<br/>
                      • <strong>DeFi collateral:</strong> Use stETH in Aave for borrowing while earning staking yield<br/>
                      • <strong>Yield stacking:</strong> Stake ETH → stETH → Supply to Curve → Earn staking + LP fees
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ $32B TVL</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Liquid (tradable)</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ DeFi integrations</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Centralization concerns</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-400">
                    <strong>Website:</strong> lido.fi | <strong>Share:</strong> 80%+ ETH liquid staking
                  </div>
                </div>

                {/* Jito */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">Jito</h4>
                      <p className="text-sm text-slate-400">Solana liquid staking with MEV optimization</p>
                    </div>
                    <span className="px-3 py-1 bg-indigo-500/20 text-indigo-400 text-xs font-bold rounded-full">SOL Leader</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Chain:</div>
                      <div className="text-slate-300"><strong>Solana</strong></div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">TVL:</div>
                      <div className="text-slate-300"><strong>$2.4B</strong></div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Key Assets:</div>
                      <div className="text-slate-300">SOL → JitoSOL</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">How It Works:</div>
                    <div className="text-slate-300 text-sm bg-slate-900/50 rounded p-3">
                      Stake SOL → Receive JitoSOL → Earn staking rewards PLUS MEV (Maximal Extractable Value) rewards → Higher yields than standard Solana staking → Use JitoSOL across Solana DeFi.
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">Use Cases:</div>
                    <div className="text-slate-300 text-sm">
                      • <strong>Enhanced yields:</strong> Earn staking rewards + MEV rewards (higher APY than vanilla staking)<br/>
                      • <strong>DeFi efficiency:</strong> Use JitoSOL as collateral in lending protocols<br/>
                      • <strong>LP farming:</strong> Provide JitoSOL-SOL liquidity on Orca/Raydium for triple yields
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ MEV-optimized</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Higher APY</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ DeFi integrations</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Solana-only</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-400">
                    <strong>Website:</strong> jito.network | <strong>Share:</strong> Dominant Solana liquid staking
                  </div>
                </div>
              </div>
            </div>

            {/* CATEGORY 3: DEXs */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">DEXs (Trading & Liquidity)</h3>
                    <p className="text-slate-400 text-sm">~$20B TVL | 15% Share</p>
                  </div>
                </div>
              </div>
              <p className="text-slate-400 mb-6 text-sm">
                Spot swaps with deep liquidity; Jupiter leads Solana, Uniswap dominates Ethereum, Hyperliquid brings orderbook innovation.
              </p>

              <div className="space-y-4">
                {/* Jupiter */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">Jupiter</h4>
                      <p className="text-sm text-slate-400">Solana volume king with intent-based routing & meme support</p>
                    </div>
                    <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded-full">Solana #1</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Chain:</div>
                      <div className="text-slate-300"><strong>Solana</strong></div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">TVL / Volume:</div>
                      <div className="text-slate-300"><strong>$10B TVL</strong> / $3.2B daily</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Key Assets:</div>
                      <div className="text-slate-300">SOL, meme tokens, SPL tokens</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">How It Works:</div>
                    <div className="text-slate-300 text-sm bg-slate-900/50 rounded p-3">
                      Intent-based aggregator routes trades across multiple Solana DEXs → Finds best prices automatically → Ultra-fast execution (400ms avg) → Supports meme tokens with high slippage tolerance.
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">Use Cases:</div>
                    <div className="text-slate-300 text-sm">
                      • <strong>Speed trading:</strong> Execute swaps in 400ms with best prices across all Solana DEXs<br/>
                      • <strong>Meme coins:</strong> Trade high-volatility tokens with slippage protection<br/>
                      • <strong>DCA/Limit orders:</strong> Set automated buy/sell orders without constant monitoring
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Fastest execution</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ $3.2B daily volume</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Best prices</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Solana downtime risk</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-400">
                    <strong>Website:</strong> jup.ag | <strong>Share:</strong> 85%+ Solana DEX volume
                  </div>
                </div>

                {/* Uniswap */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">Uniswap</h4>
                      <p className="text-sm text-slate-400">Ethereum liquidity standard with V4 custom AMMs</p>
                    </div>
                    <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded-full">ETH #1</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Chains:</div>
                      <div className="text-slate-300"><strong>Ethereum</strong>, Arbitrum, Optimism, Polygon, Base, L2s</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">TVL / Volume:</div>
                      <div className="text-slate-300"><strong>$5.2B TVL</strong> / $2.1B daily</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Key Assets:</div>
                      <div className="text-slate-300">ETH, WBTC, stablecoins, ERC-20s</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">How It Works:</div>
                    <div className="text-slate-300 text-sm bg-slate-900/50 rounded p-3">
                      V4 introduces custom hooks (programmable logic) → Concentrated liquidity (LPs earn more with less capital) → Multi-chain deployment for seamless cross-L2 trading.
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">Use Cases:</div>
                    <div className="text-slate-300 text-sm">
                      • <strong>Blue-chip swaps:</strong> Trade ETH, WBTC, major tokens with deepest liquidity<br/>
                      • <strong>LP earnings:</strong> Provide liquidity to earn 0.05-1% fees on every trade<br/>
                      • <strong>Cross-chain:</strong> Swap across Ethereum L2s with canonical bridges
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Most trusted DEX</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Multi-chain</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Deep liquidity</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ ETH gas fees</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-400">
                    <strong>Website:</strong> uniswap.org | <strong>Share:</strong> #1 Ethereum DEX
                  </div>
                </div>

                {/* Hyperliquid Spot */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">Hyperliquid (Spot)</h4>
                      <p className="text-sm text-slate-400">Permissionless orderbook; no listing fees, direct perp integration</p>
                    </div>
                    <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-xs font-bold rounded-full">L1 Innovation</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Chain:</div>
                      <div className="text-slate-300"><strong>Hyperliquid L1</strong></div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">TVL / Volume:</div>
                      <div className="text-slate-300"><strong>$164M TVL</strong> / $260M daily</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Key Assets:</div>
                      <div className="text-slate-300">HYPE, spot tokens (permissionless)</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">How It Works:</div>
                    <div className="text-slate-300 text-sm bg-slate-900/50 rounded p-3">
                      On-chain orderbook with zero listing fees → Anyone can create spot markets → Direct integration with perpetuals for arbitrage → Orderbook execution like CEXs but self-custody.
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">Use Cases:</div>
                    <div className="text-slate-300 text-sm">
                      • <strong>New token trading:</strong> Trade tokens before CEX listings (no listing fees)<br/>
                      • <strong>Orderbook execution:</strong> Professional trading with limit orders & market depth<br/>
                      • <strong>Spot-perp arb:</strong> Exploit price differences between spot and perpetual markets
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ No listing fees</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Orderbook</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Perp integration</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ New L1 risk</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-400">
                    <strong>Website:</strong> hyperliquid.xyz | <strong>Innovation:</strong> Permissionless markets
                  </div>
                </div>
              </div>
            </div>

            {/* CATEGORY 4: REAL WORLD ASSETS */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Real World Assets (RWAs)</h3>
                    <p className="text-slate-400 text-sm">~$18B TVL | 14% Share</p>
                  </div>
                </div>
              </div>
              <p className="text-slate-400 mb-6 text-sm">
                Tokenized off-chain yields; Maker bridges TradFi to crypto with U.S. Treasury backing.
              </p>

              <div className="space-y-4">
                {/* MakerDAO Sky */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">MakerDAO (Sky)</h4>
                      <p className="text-sm text-slate-400">RWA-backed DAI; on-chain TradFi gateway</p>
                    </div>
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">RWA Leader</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Chain:</div>
                      <div className="text-slate-300"><strong>Ethereum</strong></div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">TVL:</div>
                      <div className="text-slate-300"><strong>$5.5B</strong></div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Key Assets:</div>
                      <div className="text-slate-300">U.S. Treasuries, corporate bonds, real estate</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">How It Works:</div>
                    <div className="text-slate-300 text-sm bg-slate-900/50 rounded p-3">
                      DAI stablecoin backed 50%+ by real-world assets (U.S. Treasuries, bonds) → Earn DSR (DAI Savings Rate) ~5% APY from RWA yields → Bridge between TradFi and DeFi.
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">Use Cases:</div>
                    <div className="text-slate-300 text-sm">
                      • <strong>Yield on stables:</strong> Hold DAI and earn 5% APY from U.S. Treasury yields<br/>
                      • <strong>TradFi exposure:</strong> Access traditional asset yields without leaving crypto<br/>
                      • <strong>Stablecoin minting:</strong> Mint DAI against ETH collateral (see CDPs category)
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ RWA-backed</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ 5% DSR yield</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ TradFi bridge</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Centralized custody</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-400">
                    <strong>Website:</strong> makerdao.com (sky.money) | <strong>Share:</strong> Leading RWA protocol
                  </div>
                </div>
              </div>
            </div>

            {/* CATEGORY 5: DERIVATIVES */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-red-500 to-pink-600 rounded-lg">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Derivatives (Leverage Products)</h3>
                    <p className="text-slate-400 text-sm">~$10B OI | 8% Share</p>
                  </div>
                </div>
              </div>
              <p className="text-slate-400 mb-6 text-sm">
                Perpetual futures for leveraged trading; Hyperliquid crushes with $5B daily volume and orderbook liquidity.
              </p>

              <div className="space-y-4">
                {/* Hyperliquid Perps */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">Hyperliquid (Perps)</h4>
                      <p className="text-sm text-slate-400">On-chain orderbook & 50x leverage; perp liquidity beast</p>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">Perp King</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Chain:</div>
                      <div className="text-slate-300"><strong>Hyperliquid L1</strong></div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">OI / Volume:</div>
                      <div className="text-slate-300"><strong>$1.1B OI</strong> / $5B daily volume</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Key Markets:</div>
                      <div className="text-slate-300">BTC, ETH, SOL, 100+ perpetuals</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">How It Works:</div>
                    <div className="text-slate-300 text-sm bg-slate-900/50 rounded p-3">
                      On-chain orderbook (no AMM) → Deposit collateral → Open leveraged positions up to 50x → Funding rates balance longs/shorts → CEX-like UI with DeFi custody.
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">Use Cases:</div>
                    <div className="text-slate-300 text-sm">
                      • <strong>Leverage trading:</strong> Trade BTC, ETH, alts with up to 50x leverage<br/>
                      • <strong>Hedging:</strong> Short perpetuals to hedge your spot crypto holdings<br/>
                      • <strong>Arbitrage:</strong> Exploit funding rate differences and spot-perp spreads
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ $5B daily volume</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ 50x leverage</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Orderbook</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ High liquidation risk</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-400">
                    <strong>Website:</strong> hyperliquid.xyz | <strong>Share:</strong> Leading perp DEX
                  </div>
                </div>
              </div>
            </div>

            {/* CATEGORY 6: CDPs */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg">
                    <CreditCard className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">CDPs (Collateralized Debt Positions)</h3>
                    <p className="text-slate-400 text-sm">~$8B TVL | 6% Share</p>
                  </div>
                </div>
              </div>
              <p className="text-slate-400 mb-6 text-sm">
                Mint stablecoins via collateral; Maker's ecosystem towers with decentralized DAI minting.
              </p>

              <div className="space-y-4">
                {/* MakerDAO Vaults */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">MakerDAO Vaults</h4>
                      <p className="text-sm text-slate-400">DAI vaults with RWAs; decentralized stablecoin minting</p>
                    </div>
                    <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-xs font-bold rounded-full">CDP Leader</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Chain:</div>
                      <div className="text-slate-300"><strong>Ethereum</strong></div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">TVL:</div>
                      <div className="text-slate-300"><strong>$6B</strong></div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Collateral:</div>
                      <div className="text-slate-300">ETH, WBTC, stETH, RWAs, LP tokens</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">How It Works:</div>
                    <div className="text-slate-300 text-sm bg-slate-900/50 rounded p-3">
                      Deposit collateral (e.g., $15K ETH) → Mint DAI (up to $10K at 150% ratio) → Pay stability fee (~2% APR) → Use DAI in DeFi → Repay DAI anytime to unlock collateral.
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">Use Cases:</div>
                    <div className="text-slate-300 text-sm">
                      • <strong>Unlock liquidity:</strong> Get cash (DAI) from ETH without selling (no taxable event)<br/>
                      • <strong>Decentralized minting:</strong> Create your own stablecoins backed by crypto<br/>
                      • <strong>Yield farming:</strong> Mint DAI → Deploy to DeFi yields → Repay loan + keep profits
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Decentralized</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Low fees (~2%)</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ RWA collateral</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Liquidation risk</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-400">
                    <strong>Website:</strong> makerdao.com | <strong>Share:</strong> Dominant CDP protocol
                  </div>
                </div>
              </div>
            </div>

            {/* CATEGORY 7: YIELD AGGREGATORS */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-lg">
                    <Target className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Yield Aggregators</h3>
                    <p className="text-slate-400 text-sm">~$5B TVL | 4% Share</p>
                  </div>
                </div>
              </div>
              <p className="text-slate-400 mb-6 text-sm">
                Auto-optimize yields; Pendle's innovation leads with yield tokenization and structured products.
              </p>

              <div className="space-y-4">
                {/* Pendle */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">Pendle</h4>
                      <p className="text-sm text-slate-400">Yield tokenization; advanced structured products</p>
                    </div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full">Yield Innovation</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Chains:</div>
                      <div className="text-slate-300"><strong>Ethereum</strong>, Arbitrum</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">TVL:</div>
                      <div className="text-slate-300"><strong>$12B</strong></div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Key Assets:</div>
                      <div className="text-slate-300">stETH, GLP, sDAI, yield tokens</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">How It Works:</div>
                    <div className="text-slate-300 text-sm bg-slate-900/50 rounded p-3">
                      Deposit yield-bearing assets → Split into PT (principal) + YT (yield) tokens → Trade yield futures or lock fixed rates → Advanced users speculate on yield direction.
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">Use Cases:</div>
                    <div className="text-slate-300 text-sm">
                      • <strong>Fixed yields:</strong> Buy PT tokens to lock 5% fixed APY on stETH (vs variable)<br/>
                      • <strong>Yield speculation:</strong> Buy YT tokens to bet on rising yields (leveraged exposure)<br/>
                      • <strong>Structured products:</strong> Create custom yield strategies for sophisticated DeFi users
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Fixed rates</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Yield innovation</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ $12B TVL</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Complex for beginners</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-400">
                    <strong>Website:</strong> pendle.finance | <strong>Share:</strong> Leading yield protocol
                  </div>
                </div>
              </div>
            </div>

            {/* CATEGORY 8: BRIDGES */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-lg">
                    <Globe className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Bridges (Cross-Chain Transfers)</h3>
                    <p className="text-slate-400 text-sm">Volume-Based | Enabling Layer</p>
                  </div>
                </div>
              </div>
              <p className="text-slate-400 mb-6 text-sm">
                Omnichain leader for scale/speed; LayerZero connects 70+ chains with $76B+ cumulative volume.
              </p>

              <div className="space-y-4">
                {/* LayerZero */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">LayerZero</h4>
                      <p className="text-sm text-slate-400">Omnichain messaging with ultra-low fees & NFT support</p>
                    </div>
                    <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 text-xs font-bold rounded-full">Bridge Leader</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Chains:</div>
                      <div className="text-slate-300"><strong>70+</strong> (Ethereum, Solana, Cosmos, L2s, EVM chains)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Volume:</div>
                      <div className="text-slate-300"><strong>$76.5B cumulative</strong> / $15.7B (30d)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Key Features:</div>
                      <div className="text-slate-300">Pooled liquidity, ultra-low fees, NFTs</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">How It Works:</div>
                    <div className="text-slate-300 text-sm bg-slate-900/50 rounded p-3">
                      Omnichain messaging protocol → Stargate pools liquidity across chains → Transfer assets between 70+ chains → Ultra-low fees via shared liquidity → Supports tokens + NFTs.
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">Use Cases:</div>
                    <div className="text-slate-300 text-sm">
                      • <strong>Chain hopping:</strong> Move USDC from Ethereum → Arbitrum → Solana in minutes<br/>
                      • <strong>Yield optimization:</strong> Bridge assets to chains with best yields (e.g., ETH → Avalanche)<br/>
                      • <strong>NFT transfers:</strong> Move NFTs cross-chain (rare capability)
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ 70+ chains</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ $76B+ volume</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ NFT support</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Bridge risk</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-400">
                    <strong>Website:</strong> layerzero.network | <strong>Share:</strong> Omnichain leader
                  </div>
                </div>
              </div>
            </div>

            {/* CATEGORY 9: PREDICTION MARKETS */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-pink-500 to-rose-600 rounded-lg">
                    <BarChart3 className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Prediction Markets</h3>
                    <p className="text-slate-400 text-sm">Volume-Based | Niche Utility</p>
                  </div>
                </div>
              </div>
              <p className="text-slate-400 mb-6 text-sm">
                Bet on events/outcomes; Polymarket leads DeFi volume, Kalshi for regulated real-money markets.
              </p>

              <div className="space-y-4">
                {/* Polymarket */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">Polymarket</h4>
                      <p className="text-sm text-slate-400">Oracle-backed bets; non-custodial, global crypto access</p>
                    </div>
                    <span className="px-3 py-1 bg-pink-500/20 text-pink-400 text-xs font-bold rounded-full">DeFi Leader</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Chains:</div>
                      <div className="text-slate-300"><strong>Polygon</strong>, Base (DeFi)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Weekly Volume:</div>
                      <div className="text-slate-300"><strong>$961M</strong></div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Markets:</div>
                      <div className="text-slate-300">Elections, sports, crypto, world events</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">How It Works:</div>
                    <div className="text-slate-300 text-sm bg-slate-900/50 rounded p-3">
                      Buy shares (yes/no) on event outcomes → Oracle resolves markets when events conclude → Winning shares worth $1, losing shares $0 → Non-custodial (crypto wallets) → Global access.
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">Use Cases:</div>
                    <div className="text-slate-300 text-sm">
                      • <strong>Political betting:</strong> Bet on elections, policy outcomes (accurate crowd wisdom)<br/>
                      • <strong>Hedging:</strong> Hedge real-world outcomes (e.g., bet on Fed rate hikes)<br/>
                      • <strong>Entertainment:</strong> Trade on sports, Twitter drama, crypto events
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ $961M weekly volume</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Non-custodial</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Global access</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ Regulatory uncertainty</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-400">
                    <strong>Website:</strong> polymarket.com | <strong>Type:</strong> Decentralized prediction market
                  </div>
                </div>

                {/* Kalshi */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white mb-1">Kalshi</h4>
                      <p className="text-sm text-slate-400">Real-money event trading; CFTC-regulated for U.S. users</p>
                    </div>
                    <span className="px-3 py-1 bg-pink-500/20 text-pink-400 text-xs font-bold rounded-full">Regulated</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Type:</div>
                      <div className="text-slate-300"><strong>Centralized</strong> (U.S. CFTC-regulated)</div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Weekly Volume:</div>
                      <div className="text-slate-300"><strong>~$800M</strong></div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-semibold mb-1">Markets:</div>
                      <div className="text-slate-300">Politics, sports, economics, weather</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">How It Works:</div>
                    <div className="text-slate-300 text-sm bg-slate-900/50 rounded p-3">
                      CFTC-regulated exchange → Buy/sell contracts on event outcomes → Real USD trading (bank transfers) → Verified U.S. users only → Broad markets (politics, sports, economics).
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-slate-500 font-semibold text-sm mb-1">Use Cases:</div>
                    <div className="text-slate-300 text-sm">
                      • <strong>Regulated betting:</strong> Legal event trading with CFTC oversight (U.S. only)<br/>
                      • <strong>Real USD:</strong> Trade with actual dollars, not crypto (easier for TradFi users)<br/>
                      • <strong>Broad markets:</strong> Access diverse prediction markets (weather, economics, sports)
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ CFTC-regulated</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Real USD</span>
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">✅ Legal clarity</span>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">❌ U.S. only</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-400">
                    <strong>Website:</strong> kalshi.com | <strong>Type:</strong> Regulated event trading
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-4 text-center">🎯 Explore the DeFi Ecosystem</h3>
              <p className="text-center text-slate-300 mb-6 max-w-2xl mx-auto">
                These 9 categories represent the dominant protocols across the entire DeFi landscape. Each protocol leads its sector 
                with strong fundamentals, deep liquidity, and proven track records. Always DYOR before interacting with any protocol.
              </p>
              <div className="flex justify-center gap-4 flex-wrap">
                <button
                  onClick={() => setCurrentPage('glossary')}
                  className="px-6 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all flex items-center space-x-2"
                >
                  <Search className="w-4 h-4" />
                  <span>Learn DeFi Terms</span>
                </button>
                <button
                  onClick={() => setCurrentPage('leverage-trading')}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-600 transition-all"
                >
                  Leverage Trading Guide
                </button>
              </div>
            </div>
          </section>
        )}
        {/* READING THE FINE PRINT PAGE */}
        {currentPage === 'reading-fine-print' && (
          <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Hero */}
            <div className="mb-12">
              <div className="flex items-center space-x-4 mb-6">
                <div className="p-3 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg">
                  <FileText className="w-8 h-8" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold">Reading the Fine Print</h1>
                  <p className="text-xl text-slate-300 mt-2">How to spot accounting tricks & red flags before you invest</p>
                </div>
              </div>
              
              <div className="bg-red-500/10 border-l-4 border-red-500 rounded-lg p-6">
                <p className="text-red-200">
                  <strong>🎯 Reality Check:</strong> Companies legally manipulate financial statements to look better than they are. 
                  This module teaches you the 16 most common red flags that separate winners from disasters.
                </p>
              </div>
            </div>

            {/* SECTION 1: THE 3 FINANCIAL STATEMENTS */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6 flex items-center">
                <span className="mr-3">📊</span>
                The 3 Financial Statements (In Order of Importance)
              </h2>

              <div className="space-y-6">
                {/* Cash Flow Statement */}
                <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-6">
                  <h3 className="text-2xl font-bold text-green-400 mb-4">1. Cash Flow Statement = TRUTH</h3>
                  <div className="space-y-3 text-slate-300">
                    <p className="font-semibold text-white">Why it matters most: <span className="text-green-400">Cash doesn't lie.</span></p>
                    <div className="bg-slate-900/50 rounded-lg p-4 space-y-2">
                      <p><strong>Operating Cash Flow (OCF):</strong> Cash generated from business operations</p>
                      <p><strong>Free Cash Flow (FCF):</strong> OCF - Capital Expenditures</p>
                      <p className="text-green-400 font-bold mt-3">✅ Rule: OCF should be &gt; Net Income (usually)</p>
                    </div>
                    <div className="bg-red-900/20 border border-red-500/30 rounded p-3">
                      <p className="text-sm"><strong>🚨 Red Flag:</strong> If Net Income is positive but OCF is negative → Company is burning cash despite "profits"</p>
                    </div>
                  </div>
                </div>

                {/* Balance Sheet */}
                <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6">
                  <h3 className="text-2xl font-bold text-blue-400 mb-4">2. Balance Sheet = HEALTH CHECK</h3>
                  <div className="space-y-3 text-slate-300">
                    <p className="font-semibold text-white">Asset Quality Hierarchy (Best → Worst):</p>
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-2">
                          <span className="text-green-400 font-bold">1. Cash</span>
                          <span className="text-xs text-slate-500">→ Can't fake it</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-blue-400 font-bold">2. Marketable Securities</span>
                          <span className="text-xs text-slate-500">→ Liquid, real value</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-cyan-400 font-bold">3. Accounts Receivable</span>
                          <span className="text-xs text-slate-500">→ Owed to you, but not paid yet</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-yellow-400 font-bold">4. Inventory</span>
                          <span className="text-xs text-slate-500">→ Can become obsolete</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-orange-400 font-bold">5. PP&E (Property/Equipment)</span>
                          <span className="text-xs text-slate-500">→ Hard to sell quickly</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-red-400 font-bold">6. Goodwill & Intangibles</span>
                          <span className="text-xs text-slate-500">→ Made-up value from acquisitions</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-red-900/20 border border-red-500/30 rounded p-3">
                      <p className="text-sm"><strong>🚨 Red Flag:</strong> Goodwill &gt; 30% of assets → Company overpaid for acquisitions</p>
                    </div>
                  </div>
                </div>

                {/* Income Statement */}
                <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-6">
                  <h3 className="text-2xl font-bold text-purple-400 mb-4">3. Income Statement = MOST MANIPULATED</h3>
                  <div className="space-y-3 text-slate-300">
                    <p className="font-semibold text-white">Why last: <span className="text-red-400">Easiest to fake with accounting tricks.</span></p>
                    <div className="bg-slate-900/50 rounded-lg p-4 space-y-2 text-sm">
                      <p><strong>Revenue:</strong> Can recognize early (channel stuffing)</p>
                      <p><strong>COGS:</strong> Can understate (underestimate warranty costs)</p>
                      <p><strong>EBITDA:</strong> "Earnings before all the bad stuff"</p>
                      <p><strong>Net Income:</strong> After all tricks are applied</p>
                    </div>
                    <div className="bg-yellow-900/20 border border-yellow-500/30 rounded p-3">
                      <p className="text-sm"><strong>⚠️ Pro Tip:</strong> Always compare Net Income to Operating Cash Flow. If they diverge significantly, dig deeper.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 2: THE 16 RED FLAGS */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6 flex items-center">
                <span className="mr-3">🚨</span>
                The 16 Red Flags (Short Seller's Checklist)
              </h2>

              {/* Accounting Tricks (8) */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-red-400 mb-4">Part 1: Accounting Tricks (8)</h3>
                <div className="space-y-4">
                  
                  {/* Red Flag 1 */}
                  <div className="bg-slate-800/50 border-l-4 border-red-500 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl font-bold text-red-400">1</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2">Revenue Growing Faster Than Cash Flow</h4>
                        <p className="text-sm text-slate-300 mb-2">Company books revenue, but customers aren't paying (or paying in stock/IOUs)</p>
                        <div className="text-xs text-slate-400">
                          <strong>Example:</strong> Revenue up 50%, Operating Cash Flow up 5% → Fake growth
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Red Flag 2 */}
                  <div className="bg-slate-800/50 border-l-4 border-red-500 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl font-bold text-red-400">2</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2">Receivables Growing Faster Than Revenue</h4>
                        <p className="text-sm text-slate-300 mb-2">"Sales" are actually just IOUs that may never be collected</p>
                        <div className="text-xs text-slate-400">
                          <strong>Check:</strong> Days Sales Outstanding (DSO) = (Receivables / Revenue) × 365. Rising DSO = bad.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Red Flag 3 */}
                  <div className="bg-slate-800/50 border-l-4 border-red-500 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl font-bold text-red-400">3</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2">Inventory Building Up</h4>
                        <p className="text-sm text-slate-300 mb-2">Products aren't selling, piling up in warehouses (will write down later)</p>
                        <div className="text-xs text-slate-400">
                          <strong>Check:</strong> Days Inventory Outstanding (DIO) = (Inventory / COGS) × 365. Rising DIO = trouble.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Red Flag 4 */}
                  <div className="bg-slate-800/50 border-l-4 border-red-500 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl font-bold text-red-400">4</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2">Aggressive Revenue Recognition</h4>
                        <p className="text-sm text-slate-300 mb-2">Booking future revenue today (multi-year contracts counted upfront)</p>
                        <div className="text-xs text-slate-400">
                          <strong>Look for:</strong> "Deferred revenue" shrinking = borrowed from future
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Red Flag 5 */}
                  <div className="bg-slate-800/50 border-l-4 border-red-500 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl font-bold text-red-400">5</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2">Capitalizing Expenses</h4>
                        <p className="text-sm text-slate-300 mb-2">Moving expenses to balance sheet (as "assets") instead of expensing immediately</p>
                        <div className="text-xs text-slate-400">
                          <strong>Trick:</strong> R&D labeled as "intangible asset" instead of expense → inflates earnings
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Red Flag 6 */}
                  <div className="bg-slate-800/50 border-l-4 border-red-500 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl font-bold text-red-400">6</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2">Frequent "One-Time" Charges</h4>
                        <p className="text-sm text-slate-300 mb-2">If "one-time" charges happen every quarter, they're not one-time</p>
                        <div className="text-xs text-slate-400">
                          <strong>GAAP vs Non-GAAP:</strong> Company highlights "adjusted" earnings (removes "bad" stuff)
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Red Flag 7 */}
                  <div className="bg-slate-800/50 border-l-4 border-red-500 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl font-bold text-red-400">7</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2">Goodwill Impairments</h4>
                        <p className="text-sm text-slate-300 mb-2">Admitting they overpaid for acquisitions (massive write-downs)</p>
                        <div className="text-xs text-slate-400">
                          <strong>Warning:</strong> One impairment often leads to more. Means management is bad at M&A.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Red Flag 8 */}
                  <div className="bg-slate-800/50 border-l-4 border-red-500 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl font-bold text-red-400">8</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2">Changing Accounting Methods</h4>
                        <p className="text-sm text-slate-300 mb-2">Switching depreciation methods, useful life estimates, etc. to boost earnings</p>
                        <div className="text-xs text-slate-400">
                          <strong>Red flag:</strong> Look for footnotes saying "change in accounting estimate"
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Operational Warnings (8) */}
              <div>
                <h3 className="text-2xl font-bold text-orange-400 mb-4">Part 2: Operational Warnings (8)</h3>
                <div className="space-y-4">
                  
                  {/* Red Flag 9 */}
                  <div className="bg-slate-800/50 border-l-4 border-orange-500 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl font-bold text-orange-400">9</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2">Customer Concentration Risk</h4>
                        <p className="text-sm text-slate-300 mb-2">&gt;20% of revenue from 1 customer = huge risk if they leave</p>
                        <div className="text-xs text-slate-400">
                          <strong>Check:</strong> Look in "Risk Factors" section for "significant customers"
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Red Flag 10 */}
                  <div className="bg-slate-800/50 border-l-4 border-orange-500 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl font-bold text-orange-400">10</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2">Executive Turnover</h4>
                        <p className="text-sm text-slate-300 mb-2">CFO or CEO leaves suddenly = rats fleeing sinking ship</p>
                        <div className="text-xs text-slate-400">
                          <strong>Especially bad:</strong> CFO departure right before earnings report
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Red Flag 11 */}
                  <div className="bg-slate-800/50 border-l-4 border-orange-500 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl font-bold text-orange-400">11</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2">Insider Selling</h4>
                        <p className="text-sm text-slate-300 mb-2">Executives dumping shares (especially after lockup expiration)</p>
                        <div className="text-xs text-slate-400">
                          <strong>Check:</strong> sec.gov/cgi-bin/browse-edgar → Look for Form 4 filings
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Red Flag 12 */}
                  <div className="bg-slate-800/50 border-l-4 border-orange-500 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl font-bold text-orange-400">12</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2">Related Party Transactions</h4>
                        <p className="text-sm text-slate-300 mb-2">Company doing business with CEO's wife's company = self-dealing</p>
                        <div className="text-xs text-slate-400">
                          <strong>Look for:</strong> "Related Party Transactions" footnote in 10-K
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Red Flag 13 */}
                  <div className="bg-slate-800/50 border-l-4 border-orange-500 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl font-bold text-orange-400">13</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2">Auditor Issues</h4>
                        <p className="text-sm text-slate-300 mb-2">Auditor resigns, gets fired, or issues "going concern" warning</p>
                        <div className="text-xs text-slate-400">
                          <strong>Death sentence:</strong> "Material weakness in internal controls" = accounting is broken
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Red Flag 14 */}
                  <div className="bg-slate-800/50 border-l-4 border-orange-500 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl font-bold text-orange-400">14</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2">Rising Debt Levels</h4>
                        <p className="text-sm text-slate-300 mb-2">Debt growing faster than revenue = borrowing to survive</p>
                        <div className="text-xs text-slate-400">
                          <strong>Check:</strong> Debt-to-Equity ratio &gt;2 = risky. &gt;5 = very risky.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Red Flag 15 */}
                  <div className="bg-slate-800/50 border-l-4 border-orange-500 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl font-bold text-orange-400">15</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2">Declining Gross Margins</h4>
                        <p className="text-sm text-slate-300 mb-2">Costs rising faster than prices = losing pricing power</p>
                        <div className="text-xs text-slate-400">
                          <strong>Formula:</strong> Gross Margin = (Revenue - COGS) / Revenue. Should be stable or rising.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Red Flag 16 */}
                  <div className="bg-slate-800/50 border-l-4 border-orange-500 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl font-bold text-orange-400">16</span>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-2">Guidance Cuts</h4>
                        <p className="text-sm text-slate-300 mb-2">Company lowers forecast = problems they can't hide anymore</p>
                        <div className="text-xs text-slate-400">
                          <strong>Watch for:</strong> "Guidance" or "Outlook" section in earnings calls
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* SECTION 3: QUICK REFERENCE */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6 flex items-center">
                <span className="mr-3">📋</span>
                Quick Reference Card
              </h2>

              <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4 text-cyan-400">30-Second Health Check</h3>
                
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div className="space-y-3">
                    <h4 className="font-bold text-green-400">✅ GREEN FLAGS (Good)</h4>
                    <ul className="space-y-1 text-slate-300">
                      <li>• Operating Cash Flow &gt; Net Income</li>
                      <li>• Revenue & cash growing together</li>
                      <li>• Low debt-to-equity (&lt;1)</li>
                      <li>• Stable/rising gross margins</li>
                      <li>• Insider buying</li>
                      <li>• Same auditor for years</li>
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-bold text-red-400">🚨 RED FLAGS (Bad)</h4>
                    <ul className="space-y-1 text-slate-300">
                      <li>• Net Income &gt; OCF (cash burning)</li>
                      <li>• Receivables growing faster than revenue</li>
                      <li>• Goodwill &gt;30% of assets</li>
                      <li>• Frequent "one-time" charges</li>
                      <li>• Executive turnover</li>
                      <li>• Auditor warnings/resignations</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-6 bg-cyan-500/10 border border-cyan-500/30 rounded p-4">
                  <p className="text-sm text-cyan-300">
                    <strong>Pro Tip:</strong> Read the 10-K/10-Q in this order: (1) Cash Flow Statement, (2) Risk Factors section, 
                    (3) Footnotes for related parties, (4) MD&A section, (5) Balance Sheet, (6) Income Statement last.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-4">Want to Practice?</h3>
              <p className="text-slate-300 mb-6">
                Download a real 10-K from SEC.gov and run through the 16-point checklist.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="https://www.sec.gov/edgar/searchedgar/companysearch.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-3 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg font-semibold hover:from-red-600 hover:to-orange-600 transition-all inline-flex items-center space-x-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Search SEC EDGAR</span>
                </a>
                <button
                  onClick={() => setCurrentPage('glossary')}
                  className="px-8 py-3 bg-slate-800 border border-slate-600 rounded-lg font-semibold hover:bg-slate-700 transition-all"
                >
                  Financial Terms Glossary
                </button>
              </div>
            </div>

            {/* Series Completion */}
            <div className="mt-12 mb-8 bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-2xl p-8 text-center">
              <div className="mb-4">
                <span className="text-4xl">🎓</span>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-amber-400">Street Finance Series Complete!</h3>
              <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                You've completed all 7 modules of the Street Finance series. You now have a comprehensive framework for understanding how finance 
                really works—from the system level down to reading financial statements and spotting red flags.
              </p>
              <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto text-sm mb-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <div className="font-bold text-amber-400 mb-2">Modules 1-2: Foundation</div>
                  <div className="text-xs text-slate-400">System understanding + Wealth strategies</div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <div className="font-bold text-emerald-400 mb-2">Modules 3-4: Mechanics</div>
                  <div className="text-xs text-slate-400">Psychology + Credit system</div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <div className="font-bold text-cyan-400 mb-2">Modules 5-7: Strategy</div>
                  <div className="text-xs text-slate-400">Information + Game theory + Reading fine print</div>
                </div>
              </div>
              <div className="text-sm text-slate-400">
                💡 <strong>What's Next:</strong> Apply these frameworks to analyze real companies, investments, and financial decisions. 
                The goal isn't just knowledge—it's better decision-making.
              </div>
            </div>

            {/* Bottom Navigation to All Modules */}
            <div className="flex flex-wrap justify-center gap-3 pt-8 border-t border-slate-700">
              <button
                onClick={() => setCurrentPage('real-finance-system')}
                className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-700 transition-all"
              >
                Module 1
              </button>
              <button
                onClick={() => setCurrentPage('wealth-games')}
                className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-700 transition-all"
              >
                Module 2
              </button>
              <button
                onClick={() => setCurrentPage('market-psychology')}
                className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-700 transition-all"
              >
                Module 3
              </button>
              <button
                onClick={() => setCurrentPage('credit-leverage')}
                className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-700 transition-all"
              >
                Module 4
              </button>
              <button
                onClick={() => setCurrentPage('narrative-economy')}
                className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-700 transition-all"
              >
                Module 5
              </button>
              <button
                onClick={() => setCurrentPage('game-theory')}
                className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-700 transition-all"
              >
                Module 6
              </button>
              <button
                onClick={() => setCurrentPage('home')}
                className="px-8 py-3 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg font-bold hover:from-red-600 hover:to-orange-600 transition-all"
              >
                🏠 Back to Home
              </button>
            </div>
          </section>
        )}

        {/* ABOUT PAGE */}
        {currentPage === 'about' && (
          <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
              <h1 className="text-4xl font-bold mb-4">About StreetFi</h1>
              <p className="text-xl text-slate-300">
                Free tools and education to help traders make smarter, safer decisions.
              </p>
            </div>

            <div className="prose prose-invert prose-slate max-w-none">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-8">
                <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
                <p className="text-slate-300 text-lg">
                  To democratize financial education and make professional-grade trading tools accessible to everyone, 
                  completely free. We believe that knowledge should not be gatekept by expensive subscriptions or locked 
                  behind paywalls.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-8">
                <h2 className="text-2xl font-bold text-white mb-4">Why We Built This</h2>
                <p className="text-slate-300 mb-4">
                  Leveraged trading is powerful but dangerous. Most retail traders lose money because they don't understand 
                  the risks, don't calculate position sizes properly, and don't have access to professional risk management tools.
                </p>
                <p className="text-slate-300">
                  We created StreetFi to change that. Every calculator, every tax strategy, every educational resource 
                  on this platform is completely free and always will be.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-8">
                <h2 className="text-2xl font-bold text-white mb-4">What Makes Us Different</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-blue-400 mb-2">Education First</h3>
                    <p className="text-slate-300 text-sm">
                      We prioritize learning over profits. Understanding risk is more important than maximizing returns.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-green-400 mb-2">Always Free</h3>
                    <p className="text-slate-300 text-sm">
                      No premium tiers, no hidden costs, no upsells. Quality financial education should be accessible to everyone.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-purple-400 mb-2">Privacy Focused</h3>
                    <p className="text-slate-300 text-sm">
                      No accounts required, no tracking, no data collection. Your financial information stays private.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-orange-400 mb-2">Transparent</h3>
                    <p className="text-slate-300 text-sm">
                      We show our calculations, cite our sources, and explain our reasoning. No black boxes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Who This Is For</h2>
                <ul className="space-y-3 text-slate-300">
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">✓</span>
                    <span><strong>Beginner traders</strong> who want to understand leverage before risking real money</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">✓</span>
                    <span><strong>Experienced traders</strong> looking for professional risk management tools</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">✓</span>
                    <span><strong>Investors</strong> who want to optimize their tax strategies and save thousands</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">✓</span>
                    <span><strong>Anyone</strong> who believes financial education should be free and accessible</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-12 text-center">
              <button
                onClick={() => setCurrentPage('home')}
                className="px-8 py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-all"
              >
                Explore Our Tools
              </button>
            </div>
          </section>
        )}

        {/* DISCLAIMER PAGE */}
        {currentPage === 'disclaimer' && (
          <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
              <h1 className="text-4xl font-bold mb-4">Disclaimer</h1>
              <p className="text-slate-400">Last updated: December 2025</p>
            </div>

            <div className="prose prose-invert prose-slate max-w-none">
              <div className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-6 mb-8">
                <h2 className="text-2xl font-bold text-red-400 mb-4">⚠️ Important Legal Notice</h2>
                <p className="text-slate-300 text-lg">
                  This platform provides educational tools and information only. Nothing on this website constitutes 
                  financial advice, investment advice, trading advice, or tax advice.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">Not Financial Advice</h2>
                <p className="text-slate-300">
                  All calculators, tools, strategies, and content on StreetFi are for <strong>educational and informational purposes only</strong>. 
                  They do not constitute financial, investment, legal, or tax advice, nor are they a recommendation to buy, sell, or hold any securities or financial instruments.
                </p>
                <p className="text-slate-300 mt-4">
                  You should not rely solely on the information or tools provided on this website to make financial decisions. 
                  Always consult with a qualified financial advisor, CPA, tax professional, or attorney before making any investment 
                  or financial decisions.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">High Risk Warning</h2>
                <p className="text-slate-300 mb-4">
                  <strong className="text-red-400">Leveraged trading is extremely risky.</strong> Most retail traders lose money trading 
                  with leverage. You can lose more than your initial investment in certain circumstances.
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li>• Leverage magnifies both gains AND losses</li>
                  <li>• Liquidation can result in total loss of capital</li>
                  <li>• Market volatility can lead to rapid, unexpected losses</li>
                  <li>• Past performance does not guarantee future results</li>
                  <li>• Never invest money you cannot afford to lose</li>
                </ul>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">Not Tax Advice</h2>
                <p className="text-slate-300">
                  The tax strategies and information provided on this website are for general educational purposes only. 
                  Tax laws are complex, vary by jurisdiction, and change frequently. Nothing on this website should be 
                  construed as personalized tax advice.
                </p>
                <p className="text-slate-300 mt-4">
                  <strong>Always consult with a qualified CPA, tax attorney, or Enrolled Agent before implementing any tax strategy.</strong> 
                  Tax consequences depend on your specific circumstances, and improper application of tax strategies can result 
                  in penalties, fines, or audits.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">Accuracy of Information</h2>
                <p className="text-slate-300">
                  While we strive to provide accurate and up-to-date information, we make no warranties or representations 
                  regarding the accuracy, completeness, or reliability of any content, tools, or calculators on this website.
                </p>
                <p className="text-slate-300 mt-4">
                  Financial markets, tax laws, and regulations change constantly. Information that was accurate when published 
                  may become outdated. Users are responsible for verifying all information independently.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">No Guarantees</h2>
                <p className="text-slate-300">
                  We do not guarantee any specific results, profits, or outcomes from using the tools or information on this website. 
                  Trading and investing involve substantial risk, and there is always the potential for loss.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
                <h2 className="text-xl font-bold text-white mb-4">Limitation of Liability</h2>
                <p className="text-slate-300">
                  To the maximum extent permitted by law, StreetFi and its creators shall not be liable for any direct, 
                  indirect, incidental, consequential, or punitive damages arising from your use of this website or reliance 
                  on any information, tools, or calculators provided.
                </p>
                <p className="text-slate-300 mt-4">
                  <strong>By using this website, you acknowledge that you understand these risks and agree to use the tools 
                  and information at your own risk.</strong>
                </p>
              </div>
            </div>
          </section>
        )}

        {/* PRIVACY POLICY PAGE */}
        {currentPage === 'privacy' && (
          <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
              <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
              <p className="text-slate-400">Last updated: December 2025</p>
            </div>

            <div className="prose prose-invert prose-slate max-w-none">
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 mb-8">
                <h2 className="text-2xl font-bold text-green-400 mb-4">🔒 Our Commitment to Privacy</h2>
                <p className="text-slate-300 text-lg">
                  StreetFi is built with privacy as a core principle. We do not require accounts, 
                  we do not track you, and we do not sell your data. Period.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">What We Don't Collect</h2>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start">
                    <span className="text-green-400 mr-2">✓</span>
                    <span>We don't require accounts or registration</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-2">✓</span>
                    <span>We don't track your browsing behavior across websites</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-2">✓</span>
                    <span>We don't store your calculator inputs or financial data</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-2">✓</span>
                    <span>We don't sell your information to third parties</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-2">✓</span>
                    <span>We don't use invasive cookies or fingerprinting</span>
                  </li>
                </ul>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">What We May Collect (Minimal)</h2>
                <p className="text-slate-300 mb-4">
                  We may collect basic, anonymous analytics to improve the website:
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li>• Page views (which pages are visited)</li>
                  <li>• General location (country/region level only)</li>
                  <li>• Device type (mobile vs desktop)</li>
                  <li>• Referral source (how you found us)</li>
                </ul>
                <p className="text-slate-300 mt-4">
                  This data is aggregated and anonymous. We cannot and do not identify individual users.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">Your Data Stays on Your Device</h2>
                <p className="text-slate-300">
                  All calculator inputs, financial planning data, and other information you enter remains on your device. 
                  We do not transmit this data to our servers, and we do not store it in any database.
                </p>
                <p className="text-slate-300 mt-4">
                  Your browser may store this data locally (in localStorage) for your convenience, but you can clear 
                  this at any time through your browser settings.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">Cookies</h2>
                <p className="text-slate-300">
                  We use minimal cookies for essential website functionality only. We do not use advertising cookies 
                  or tracking cookies. You can disable cookies in your browser settings, though some functionality may be limited.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">Third-Party Services</h2>
                <p className="text-slate-300">
                  We may use privacy-focused third-party services for website hosting and basic analytics. 
                  These services are chosen specifically for their commitment to user privacy and compliance with data protection regulations.
                </p>
                <p className="text-slate-300 mt-4">
                  We do not use Google Analytics, Facebook Pixel, or other invasive tracking services.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">Changes to This Policy</h2>
                <p className="text-slate-300">
                  We may update this Privacy Policy from time to time. Any changes will be posted on this page with 
                  an updated "Last updated" date. Continued use of the website after changes constitutes acceptance of the updated policy.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
                <h2 className="text-xl font-bold text-white mb-4">Contact</h2>
                <p className="text-slate-300">
                  If you have questions about this Privacy Policy, please contact us at:
                </p>
                <p className="text-blue-400 mt-2">hello@leverfinance.com</p>
              </div>
            </div>
          </section>
        )}

        {/* TERMS OF SERVICE PAGE */}
        {currentPage === 'terms' && (
          <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
              <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
              <p className="text-slate-400">Last updated: December 2025</p>
            </div>

            <div className="prose prose-invert prose-slate max-w-none">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
                <p className="text-slate-300">
                  By accessing and using StreetFi, you accept and agree to be bound by these Terms of Service. 
                  If you do not agree to these terms, please do not use this website.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">2. Use of Service</h2>
                <p className="text-slate-300 mb-4">
                  StreetFi provides educational tools and information about financial leverage, trading, and investing. You agree to use this service:
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li>• For lawful purposes only</li>
                  <li>• In a manner that does not infringe the rights of others</li>
                  <li>• In compliance with all applicable laws and regulations</li>
                  <li>• For personal, non-commercial educational use</li>
                </ul>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">3. Prohibited Uses</h2>
                <p className="text-slate-300 mb-4">You may not:</p>
                <ul className="space-y-2 text-slate-300">
                  <li>• Use this website to provide financial advice to others</li>
                  <li>• Misrepresent the tools or information as professional advice</li>
                  <li>• Scrape, copy, or redistribute content without permission</li>
                  <li>• Attempt to interfere with the website's functionality</li>
                  <li>• Use automated systems to access the website</li>
                </ul>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">4. Educational Purpose</h2>
                <p className="text-slate-300">
                  All content, calculators, and tools on StreetFi are provided strictly for educational and informational purposes. 
                  They do not constitute financial, investment, legal, or tax advice. You are solely responsible for any decisions you 
                  make based on information from this website.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">5. No Warranties</h2>
                <p className="text-slate-300">
                  This website and all content, tools, and calculators are provided "as is" without any warranties, express or implied. 
                  We do not warrant that:
                </p>
                <ul className="space-y-2 text-slate-300 mt-4">
                  <li>• The website will be available or uninterrupted</li>
                  <li>• Information will be accurate, complete, or current</li>
                  <li>• Tools and calculators will produce error-free results</li>
                  <li>• Defects will be corrected</li>
                </ul>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">6. Limitation of Liability</h2>
                <p className="text-slate-300">
                  To the maximum extent permitted by law, StreetFi and its creators shall not be liable for any damages 
                  whatsoever (including, without limitation, damages for loss of profits, data, or other intangible losses) 
                  arising from:
                </p>
                <ul className="space-y-2 text-slate-300 mt-4">
                  <li>• Your use or inability to use the service</li>
                  <li>• Reliance on any information, tools, or calculators</li>
                  <li>• Trading or investment losses</li>
                  <li>• Errors or inaccuracies in content</li>
                  <li>• Any other matter relating to the service</li>
                </ul>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">7. Intellectual Property</h2>
                <p className="text-slate-300">
                  All content, design, code, calculators, and tools on StreetFi are owned by or licensed to us and are 
                  protected by copyright and other intellectual property laws. You may not reproduce, distribute, or create 
                  derivative works without express written permission.
                </p>
                <p className="text-slate-300 mt-4">
                  You may use the tools for personal, non-commercial purposes only.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">8. Third-Party Links</h2>
                <p className="text-slate-300">
                  This website may contain links to third-party websites. We are not responsible for the content, accuracy, 
                  or practices of these external sites. Visiting these links is at your own risk.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">9. Indemnification</h2>
                <p className="text-slate-300">
                  You agree to indemnify and hold harmless StreetFi and its creators from any claims, losses, liabilities, 
                  damages, costs, or expenses (including legal fees) arising from your use of the website or violation of these terms.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">10. Changes to Terms</h2>
                <p className="text-slate-300">
                  We reserve the right to modify these Terms of Service at any time. Changes will be posted on this page 
                  with an updated "Last updated" date. Continued use of the website after changes constitutes acceptance 
                  of the modified terms.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">11. Governing Law</h2>
                <p className="text-slate-300">
                  These Terms of Service shall be governed by and construed in accordance with applicable laws, without regard 
                  to conflict of law principles.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
                <h2 className="text-xl font-bold text-white mb-4">12. Contact Information</h2>
                <p className="text-slate-300">
                  For questions about these Terms of Service, please contact:
                </p>
                <p className="text-blue-400 mt-2">hello@leverfinance.com</p>
              </div>
            </div>
          </section>
        )}

        {/* ENHANCED TAX SAVINGS PAGE */}
        {currentPage === 'tax' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center text-sm"
              >
                ← Back to Home
              </button>
            </div>
            {/* Tax Hero */}
            <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-8 mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                  <PiggyBank className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold">Tax Savings Strategies for Investors</h2>
                  <p className="text-slate-300 mt-2">Keep more of your trading profits with smart tax planning</p>
                </div>
              </div>
              <div className="mt-6 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-sm text-yellow-300">
                  ⚠️ <strong>Important Disclaimer:</strong> This is educational information only, not tax advice. 
                  Tax laws are complex and change frequently. Always consult a qualified CPA or tax professional 
                  before making tax-related decisions.
                </p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid sm:grid-cols-4 gap-6 mb-12">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <div className="text-3xl font-bold text-green-400 mb-2">0% - 20%</div>
                <div className="text-sm text-slate-400">Long-term Capital Gains Tax Rates</div>
                <div className="text-xs text-slate-500 mt-2">vs up to 37% ordinary income</div>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <div className="text-3xl font-bold text-blue-400 mb-2">$3,000</div>
                <div className="text-sm text-slate-400">Annual Loss Deduction Limit</div>
                <div className="text-xs text-slate-500 mt-2">Against ordinary income</div>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <div className="text-3xl font-bold text-purple-400 mb-2">20%</div>
                <div className="text-sm text-slate-400">QBI Deduction</div>
                <div className="text-xs text-slate-500 mt-2">For qualified business income</div>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <div className="text-3xl font-bold text-orange-400 mb-2">$500K</div>
                <div className="text-sm text-slate-400">Home Sale Exclusion</div>
                <div className="text-xs text-slate-500 mt-2">Married couples (tax-free!)</div>
              </div>
            </div>

            {/* Section Navigation Filters */}
            <div className="mb-12 sticky top-0 z-10 bg-slate-900/95 backdrop-blur-sm border-y border-slate-700 py-4">
              <div className="flex flex-wrap gap-2 justify-center">
                <button
                  onClick={() => {
                    const element = document.getElementById('investment-tax');
                    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="px-4 py-2 bg-blue-500/20 border border-blue-500/30 rounded-lg text-sm font-semibold text-blue-400 hover:bg-blue-500/30 transition-all cursor-pointer"
                >
                  📈 Investment & Trading (10)
                </button>
                <button
                  onClick={() => {
                    const element = document.getElementById('crypto-tax');
                    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-lg text-sm font-semibold text-orange-400 hover:bg-orange-500/30 transition-all cursor-pointer"
                >
                  🪙 Cryptocurrency (5)
                </button>
                <button
                  onClick={() => {
                    const element = document.getElementById('real-estate-tax');
                    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-lg text-sm font-semibold text-purple-400 hover:bg-purple-500/30 transition-all cursor-pointer"
                >
                  🏠 Real Estate (4)
                </button>
                <button
                  onClick={() => {
                    const element = document.getElementById('business-tax');
                    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-lg text-sm font-semibold text-green-400 hover:bg-green-500/30 transition-all cursor-pointer"
                >
                  💼 Business (9)
                </button>
                <button
                  onClick={() => {
                    const element = document.getElementById('general-tax');
                    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="px-4 py-2 bg-pink-500/20 border border-pink-500/30 rounded-lg text-sm font-semibold text-pink-400 hover:bg-pink-500/30 transition-all cursor-pointer"
                >
                  ⭐ General & Family (10)
                </button>
              </div>
              <p className="text-center text-xs text-slate-500 mt-3">
                Click any category to jump to that section • 38 total strategies
              </p>
            </div>

            {/* Investment & Financial Market Tax Breaks */}
            <div id="investment-tax" className="mb-12 scroll-mt-24">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <TrendingUp className="w-6 h-6 mr-3 text-blue-400" />
                Investment & Financial Market Tax Breaks
              </h3>
              
              <div className="space-y-6">
                {/* Capital Gains Advantage */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">💰 Long-Term Capital Gains Tax Advantages</h4>
                  <p className="text-slate-300 mb-4">
                    Assets held for more than 1 year qualify for preferential tax rates: 0%, 15%, or 20% 
                    (vs ordinary income tax up to 37%). This is the foundation of buy-and-hold investing.
                  </p>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-blue-400 mb-2">💡 Strategy for Leveraged Positions:</p>
                    <p className="text-sm text-slate-300">
                      If you're up significantly on a leveraged position, consider holding past 1 year to 
                      save up to 17% in taxes. Plan your exits around the 1-year mark. This works especially 
                      well with lower leverage (2-5x) where you can comfortably hold longer.
                    </p>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3 text-sm">
                    <div className="bg-slate-900/50 rounded p-3">
                      <div className="font-semibold text-green-400">0% Rate</div>
                      <div className="text-slate-400">Income under ~$44K (single)</div>
                      <div className="text-xs text-slate-500 mt-1">~$89K (married)</div>
                    </div>
                    <div className="bg-slate-900/50 rounded p-3">
                      <div className="font-semibold text-blue-400">15% Rate</div>
                      <div className="text-slate-400">Income $44K-$492K</div>
                      <div className="text-xs text-slate-500 mt-1">Most investors</div>
                    </div>
                    <div className="bg-slate-900/50 rounded p-3">
                      <div className="font-semibold text-orange-400">20% Rate</div>
                      <div className="text-slate-400">Income over $492K</div>
                      <div className="text-xs text-slate-500 mt-1">High earners only</div>
                    </div>
                  </div>
                </div>

                {/* Tax-Loss Harvesting */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">📉 Tax-Loss Harvesting</h4>
                  <p className="text-slate-300 mb-4">
                    Sell losing positions to offset gains. Can deduct up to $3,000 against ordinary income 
                    per year, with unlimited carryforward. This is one of the most powerful tax strategies 
                    for active traders.
                  </p>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">💡 Real Example:</p>
                    <p className="text-sm text-slate-300">
                      Made $20K on leveraged crypto trades but lost $15K on stocks? Sell the stocks to 
                      offset gains, paying tax on only $5K instead of $20K. <strong>Saves you ~$5,500 in taxes</strong> (at 37% bracket).
                      The remaining $5K can offset next year's gains or $3K of ordinary income.
                    </p>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm text-red-300 mb-2">
                      ⚠️ <strong>Wash Sale Warning (Stocks/Options ONLY):</strong>
                    </p>
                    <p className="text-sm text-slate-300">
                      Can't buy the same or substantially identical security within 30 days before or after 
                      the sale. This rule applies to stocks and options, but <strong>NOT to crypto</strong> (as of 2025).
                    </p>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">🎁 Crypto Advantage:</p>
                    <p className="text-sm text-slate-300">
                      Crypto has NO wash sale rule! You can sell Bitcoin at a loss and immediately buy it 
                      back, harvesting the tax loss while maintaining your position. This makes crypto 
                      ideal for tax-loss harvesting strategies.
                    </p>
                  </div>
                </div>

                {/* 0% Capital Gains for Low-Income */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🎯 0% Capital Gains for Lower-Income Investors</h4>
                  <p className="text-slate-300 mb-4">
                    For those in the 0%–12% tax bracket, <strong>long-term capital gains = 0% tax</strong>. 
                    Many retirees or lower-income investors use this to realize gains completely tax-free.
                  </p>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">💡 Strategic Application:</p>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Retire early and live off capital gains tax-free</li>
                      <li>• Students can trade with 0% tax on gains</li>
                      <li>• Harvest gains in low-income years</li>
                      <li>• Fill up the 0% bracket before it ends ($44K single / $89K married)</li>
                    </ul>
                  </div>
                </div>

                {/* Trader Tax Status */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🏢 Trader Tax Status (Section 475 Mark-to-Market)</h4>
                  <p className="text-slate-300 mb-4">
                    Active traders can elect "mark-to-market" accounting and qualify for business deductions. 
                    This is <strong>HUGE</strong> for frequent leverage traders who meet the requirements.
                  </p>
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-purple-400 mb-2">✅ Massive Benefits:</p>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• <strong>Deduct unlimited trading losses</strong> (no $3K limit!)</li>
                      <li>• <strong>Avoid wash sale rules completely</strong></li>
                      <li>• Deduct home office, equipment, software, data feeds, education</li>
                      <li>• <strong>QBI deduction (20% of trading income)</strong></li>
                      <li>• Section 179 deduction for equipment (computers, monitors, etc.)</li>
                      <li>• Convert capital gains to ordinary business income (good if you have losses)</li>
                    </ul>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-blue-400 mb-2">📋 Requirements to Qualify:</p>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Trade frequently (typically 4+ trades/day, 15+ days/month)</li>
                      <li>• Hold positions short-term (usually days, not weeks/months)</li>
                      <li>• Trading is substantial portion of your income</li>
                      <li>• <strong>Must elect by April 15</strong> of the year it applies (or by tax filing deadline for existing traders)</li>
                      <li>• Keep detailed records proving trader status</li>
                    </ul>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <p className="text-sm text-yellow-300">
                      💡 <strong>Worth it?</strong> If you're making 50+ trades per month with leverage, 
                      this election could save you <strong>$10K-$50K+ annually</strong>. The ability to deduct 
                      unlimited losses alone is game-changing. Consult a CPA specializing in trader taxation.
                    </p>
                  </div>
                </div>

                {/* Qualified Dividends */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">📊 Qualified Dividend Treatment</h4>
                  <p className="text-slate-300 mb-4">
                    Dividends from U.S. companies or qualified foreign companies are taxed at long-term 
                    capital gains rates (0%-20%) instead of ordinary income rates (up to 37%).
                  </p>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">💡 Leverage Strategy with Margin:</p>
                    <p className="text-sm text-slate-300 mb-2">
                      If using margin to buy dividend stocks, <strong>the interest you pay is tax-deductible</strong> 
                      against investment income (up to your net investment income). You're essentially:
                    </p>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Borrowing at ~8% (tax deductible, so effective rate ~5-6% after taxes)</li>
                      <li>• Earning qualified dividends at 15% tax rate (vs 37% for interest income)</li>
                      <li>• Plus potential stock appreciation taxed as capital gains</li>
                    </ul>
                  </div>
                </div>

                {/* Tax-Advantaged Accounts */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🎁 Tax-Advantaged Retirement Accounts</h4>
                  <p className="text-slate-300 mb-4">
                    These are among the most powerful tax breaks available to retail investors.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <h5 className="font-semibold text-blue-400 mb-2">Traditional IRA / 401(k)</h5>
                      <p className="text-sm text-slate-300 mb-2">
                        Contributions reduce taxable income NOW → pay tax later in retirement (presumably at lower rates)
                      </p>
                      <ul className="text-xs text-slate-400 space-y-1">
                        <li>• 401(k) limit: $23,500 (2025)</li>
                        <li>• IRA limit: $7,000 (2025)</li>
                        <li>• Immediate tax deduction</li>
                        <li>• Tax-deferred growth</li>
                      </ul>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <h5 className="font-semibold text-green-400 mb-2">Roth IRA / Roth 401(k)</h5>
                      <p className="text-sm text-slate-300 mb-2">
                        Pay tax upfront → growth & withdrawals are TAX-FREE forever. Perfect for young traders.
                      </p>
                      <ul className="text-xs text-slate-400 space-y-1">
                        <li>• Same contribution limits</li>
                        <li>• NO tax on gains EVER</li>
                        <li>• Withdraw contributions anytime</li>
                        <li>• No RMDs (Required Minimum Distributions)</li>
                      </ul>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                      <h5 className="font-semibold text-purple-400 mb-2">HSA (Health Savings Account)</h5>
                      <p className="text-sm text-slate-300 mb-2">
                        <strong>Triple tax advantage:</strong> Deductible contributions, tax-free growth, tax-free withdrawals for medical expenses
                      </p>
                      <ul className="text-xs text-slate-400 space-y-1">
                        <li>• $4,300 single / $8,550 family (2025)</li>
                        <li>• After 65, withdraw for ANY reason</li>
                        <li>• Best retirement account if maxed out</li>
                      </ul>
                    </div>
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                      <h5 className="font-semibold text-orange-400 mb-2">SEP IRA / Solo 401(k)</h5>
                      <p className="text-sm text-slate-300 mb-2">
                        For self-employed traders, these allow MASSIVE contributions (up to $70,000 in 2025)
                      </p>
                      <ul className="text-xs text-slate-400 space-y-1">
                        <li>• Shelter 25% of net SE income</li>
                        <li>• Solo 401(k): Employee + employer contributions</li>
                        <li>• Huge tax break for profitable traders</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Backdoor Roth Strategies */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🚪 Backdoor Roth & Mega Backdoor Roth</h4>
                  <p className="text-slate-300 mb-4">
                    High earners can't contribute directly to Roth IRAs, but these strategies provide workarounds 
                    to access tax-free growth. These are LEGAL loopholes explicitly allowed by the IRS.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <h5 className="font-semibold text-blue-400 mb-2">Backdoor Roth IRA</h5>
                      <p className="text-sm text-slate-300 mb-2">
                        <strong>Step 1:</strong> Contribute to Traditional IRA (non-deductible) <br/>
                        <strong>Step 2:</strong> Immediately convert to Roth IRA <br/>
                        <strong>Result:</strong> $7,000/year into Roth regardless of income
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        ⚠️ Watch out for "pro-rata rule" if you have existing traditional IRA balances
                      </p>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                      <h5 className="font-semibold text-purple-400 mb-2">Mega Backdoor Roth</h5>
                      <p className="text-sm text-slate-300 mb-2">
                        For those with 401(k) plans that allow after-tax contributions:<br/>
                        Contribute up to $69,000 total to 401(k), then convert to Roth<br/>
                        <strong>Result:</strong> Up to ~$46,000/year into Roth
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        💡 Check if your employer's plan allows: 1) after-tax contributions, 2) in-plan Roth conversions
                      </p>
                    </div>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mt-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">💰 Long-term Impact:</p>
                    <p className="text-sm text-slate-300">
                      Using these strategies for 20 years with 8% returns can create $500K-$2M+ in tax-free retirement 
                      funds. For high-earning traders, this is one of the most powerful wealth-building tools available.
                    </p>
                  </div>
                </div>

                {/* Qualified Charitable Distributions */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">📤 Qualified Charitable Distributions (QCD)</h4>
                  <p className="text-slate-300 mb-4">
                    If you're 70½ or older with a traditional IRA, you can donate up to $105,000/year directly to 
                    charity from your IRA. This counts toward your Required Minimum Distribution (RMD) but is NOT 
                    taxable income.
                  </p>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">💡 Why This is Powerful:</p>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• <strong>Avoid taxation on RMD:</strong> Normally RMDs are taxable; QCDs are not</li>
                      <li>• <strong>Lower AGI:</strong> Doesn't increase your adjusted gross income (helps with Medicare premiums, SS taxation)</li>
                      <li>• <strong>Charitable deduction not needed:</strong> Works even if you take standard deduction</li>
                      <li>• <strong>Perfect for retirees:</strong> Who don't need their RMD for living expenses</li>
                    </ul>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-blue-400 mb-2">📋 Example:</p>
                    <p className="text-sm text-slate-300">
                      RMD is $50K. You donate $30K via QCD and take $20K cash. Only the $20K is taxable income. 
                      <strong>Saves ~$11,000 in taxes</strong> (37% bracket) while supporting your favorite charity.
                    </p>
                  </div>
                </div>

                {/* Catch-Up Contributions */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🎂 Catch-Up Contributions (Age 50+)</h4>
                  <p className="text-slate-300 mb-4">
                    If you're 50 or older, you can contribute EXTRA to retirement accounts beyond normal limits. 
                    This accelerates retirement savings and provides additional tax deductions.
                  </p>
                  <div className="grid md:grid-cols-3 gap-4 mb-4">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <h5 className="font-semibold text-blue-400 mb-2">401(k) / 403(b)</h5>
                      <p className="text-sm text-slate-300 mb-1">
                        Normal limit: $23,000<br/>
                        <strong>Catch-up: +$7,500</strong><br/>
                        <strong>Total: $30,500</strong>
                      </p>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <h5 className="font-semibold text-green-400 mb-2">IRA (Traditional/Roth)</h5>
                      <p className="text-sm text-slate-300 mb-1">
                        Normal limit: $7,000<br/>
                        <strong>Catch-up: +$1,000</strong><br/>
                        <strong>Total: $8,000</strong>
                      </p>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                      <h5 className="font-semibold text-purple-400 mb-2">SIMPLE IRA</h5>
                      <p className="text-sm text-slate-300 mb-1">
                        Normal limit: $16,000<br/>
                        <strong>Catch-up: +$3,500</strong><br/>
                        <strong>Total: $19,500</strong>
                      </p>
                    </div>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">💰 Maximum Tax Savings:</p>
                    <p className="text-sm text-slate-300">
                      Max out 401(k) catch-up ($7,500) + IRA catch-up ($1,000) = $8,500 extra deduction. 
                      <strong>Saves $3,145 in taxes</strong> (37% bracket). Plus your investments grow tax-deferred!
                    </p>
                  </div>
                </div>

                {/* Investment Interest Deduction */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">💳 Investment Interest Expense Deduction</h4>
                  <p className="text-slate-300 mb-4">
                    If you use margin to buy stocks, bonds, or other investments, the margin interest you pay is 
                    TAX-DEDUCTIBLE up to your net investment income. This is huge for leveraged investors!
                  </p>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-blue-400 mb-2">💡 What Qualifies:</p>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• <strong>Margin interest</strong> on brokerage accounts</li>
                      <li>• Interest on loans used to purchase taxable investments</li>
                      <li>• Deductible up to your <strong>net investment income</strong> (interest, dividends, capital gains if you elect)</li>
                      <li>• Excess can be carried forward to future years</li>
                    </ul>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">💰 Real Example:</p>
                    <p className="text-sm text-slate-300">
                      You borrow $100,000 on margin at 8% = $8,000 interest expense. You have $15,000 in dividends 
                      and interest income. Deduct the full $8,000. <strong>Saves $2,960 in taxes</strong> (37% bracket). 
                      Your effective borrowing cost is only 5.04% after tax deduction!
                    </p>
                  </div>
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-purple-400 mb-2">⚡ Advanced Strategy:</p>
                    <p className="text-sm text-slate-300">
                      You can ELECT to treat long-term capital gains as ordinary investment income to increase your 
                      deduction limit. Trade-off: gains taxed at ordinary rates instead of preferential rates. 
                      Math it out with your CPA to see if beneficial.
                    </p>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mt-4">
                    <p className="text-sm text-yellow-300">
                      ⚠️ <strong>Limitations:</strong> Interest on margin for tax-exempt investments (muni bonds) is NOT deductible. 
                      Passive activity limitations may apply. Report on Schedule A (itemized deductions).
                    </p>
                  </div>
                </div>

                {/* Net Investment Income Tax */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">⚠️ Net Investment Income Tax (NIIT) - 3.8% Surtax</h4>
                  <p className="text-slate-300 mb-4">
                    High earners pay an ADDITIONAL 3.8% tax on investment income. This surtax kicks in at $200K (single) 
                    or $250K (married) and applies to the LESSER of: net investment income OR amount above threshold.
                  </p>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-red-400 mb-2">📊 What's Taxed:</p>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Interest, dividends, capital gains</li>
                      <li>• Rental & royalty income</li>
                      <li>• Passive business income</li>
                      <li>• Annuity distributions</li>
                      <li>• Trading profits (if not business income via 475 election)</li>
                    </ul>
                  </div>
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-orange-400 mb-2">💰 Example Impact:</p>
                    <p className="text-sm text-slate-300">
                      AGI: $300K (single). Net investment income: $80K. Threshold: $200K. <br/>
                      NIIT applies to: LESSER of $80K (investment income) or $100K (above threshold) = $80K.<br/>
                      <strong>NIIT = $80K × 3.8% = $3,040 extra tax</strong> on top of regular capital gains tax!
                    </p>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">🛡️ Strategies to Avoid/Reduce NIIT:</p>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• <strong>Section 475 election:</strong> Converts investment income to business income (not subject to NIIT!)</li>
                      <li>• <strong>Tax-loss harvesting:</strong> Reduce net investment income</li>
                      <li>• <strong>Opportunity Zone investing:</strong> Defer gains (temporarily avoid NIIT)</li>
                      <li>• <strong>Max out retirement accounts:</strong> Reduces MAGI below thresholds</li>
                      <li>• <strong>Municipal bonds:</strong> Interest exempt from NIIT</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Crypto-Specific Tax Strategies */}
            <div id="crypto-tax" className="mb-12 scroll-mt-24">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <Bitcoin className="w-6 h-6 mr-3 text-orange-400" />
                Cryptocurrency Tax Strategies
              </h3>
              
              <div className="space-y-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">💎 Crypto-Specific Tax Advantages</h4>
                  <p className="text-slate-300 mb-4">
                    Crypto has unique tax treatment that can be highly advantageous for active traders.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <p className="text-sm font-semibold text-green-400 mb-2">✅ No Wash Sale Rule</p>
                      <p className="text-sm text-slate-300">
                        Unlike stocks, you CAN immediately rebuy the same crypto after selling for a loss. 
                        Perfect for tax-loss harvesting while maintaining your position. This is crypto's 
                        biggest tax advantage.
                      </p>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <p className="text-sm font-semibold text-green-400 mb-2">✅ Long-Term Capital Gains Still Apply</p>
                      <p className="text-sm text-slate-300">
                        Hold crypto for 1+ year and you get the same 0%/15%/20% preferential rates as stocks. 
                        This is huge for buy-and-hold investors.
                      </p>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <p className="text-sm font-semibold text-blue-400 mb-2">💰 Mining/Staking Deductions</p>
                      <p className="text-sm text-slate-300">
                        Deduct electricity, hardware, and internet costs as business expenses if you mine 
                        or stake as a business. GPU miners can deduct equipment via Section 179.
                      </p>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <p className="text-sm font-semibold text-blue-400 mb-2">💰 DeFi Loan Loophole</p>
                      <p className="text-sm text-slate-300">
                        Borrowing against crypto (Aave, Compound, Maker) may not be a taxable event, letting 
                        you access cash without triggering capital gains. <em>Consult tax pro for current IRS position.</em>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">⚠️ Crypto Tax Reporting Requirements</h4>
                  <p className="text-slate-300 mb-4">
                    The IRS requires reporting of ALL crypto transactions. Every trade, even crypto-to-crypto, 
                    is a taxable event. Enforcement is increasing dramatically.
                  </p>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm text-red-300">
                      <strong>Track Everything:</strong> Use crypto tax software (CoinTracker, Koinly, CoinLedger, 
                      TokenTax) to automatically track cost basis across all exchanges and wallets. Manual tracking 
                      is nearly impossible with leveraged trading.
                    </p>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <p className="text-sm text-yellow-300">
                      💡 <strong>Pro Tip:</strong> Set up automatic API connections to your exchanges at the 
                      START of the year. Retroactively importing 1000+ trades is painful and error-prone.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🎯 Specific Strategies for Crypto Traders</h4>
                  <div className="space-y-3">
                    <div className="bg-slate-900/50 rounded p-4">
                      <p className="font-semibold text-blue-400 mb-2">Strategy #1: Year-End Tax-Loss Harvesting</p>
                      <p className="text-sm text-slate-300">
                        December 31 is the deadline. Sell losers to offset gains, then immediately rebuy 
                        (no wash sale!). This can save thousands in taxes while maintaining your positions.
                      </p>
                    </div>
                    <div className="bg-slate-900/50 rounded p-4">
                      <p className="font-semibold text-blue-400 mb-2">Strategy #2: Gifting Crypto to Family</p>
                      <p className="text-sm text-slate-300">
                        Gift up to $18,000/year per person tax-free. Great way to transfer wealth to children 
                        in lower tax brackets who can sell at 0% capital gains rate.
                      </p>
                    </div>
                    <div className="bg-slate-900/50 rounded p-4">
                      <p className="font-semibold text-blue-400 mb-2">Strategy #3: Donate Appreciated Crypto</p>
                      <p className="text-sm text-slate-300">
                        Donate crypto directly to charity (not cash). You get a deduction for fair market value 
                        AND avoid paying capital gains tax. This is extremely tax-efficient for big winners.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Real Estate Tax Breaks */}
            {/* Real Estate Tax Advantages */}
            <div id="real-estate-tax" className="mb-12 scroll-mt-24">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <Home className="w-6 h-6 mr-3 text-purple-400" />
                Real Estate Tax Advantages
              </h3>
              
              <div className="space-y-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🏠 Primary Residence Exclusion</h4>
                  <p className="text-slate-300 mb-4">
                    Exclude up to <strong>$250,000 (single)</strong> or <strong>$500,000 (married)</strong> of profit 
                    from selling your home if lived in 2 of the past 5 years. This is 100% TAX-FREE money.
                  </p>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">💡 Leverage Strategy (House Hacking):</p>
                    <p className="text-sm text-slate-300 mb-2">
                      Buy a home with a low down payment (3.5%-20% via FHA/conventional), live in it for 2+ years, 
                      then sell tax-free. This is one of the best leveraged, tax-free investments available. 
                      <strong>You can repeat every 2 years.</strong>
                    </p>
                    <p className="text-sm text-slate-300">
                      Example: Put $20K down on a $400K house (5%), sell 2 years later for $500K. 
                      $100K profit = tax-free. That's a 500% return on your $20K investment, completely tax-free.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🏢 Rental Property Depreciation</h4>
                  <p className="text-slate-300 mb-4">
                    Depreciate rental property over 27.5 years, creating "paper losses" that reduce taxable 
                    income while property (usually) appreciates. This is legal phantom income reduction.
                  </p>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-blue-400 mb-2">💡 Example:</p>
                    <p className="text-sm text-slate-300">
                      $300K rental property (excluding land value) = ~$11K/year depreciation deduction. 
                      If rental income is $15K/year, you only pay tax on $4K. Meanwhile, the property 
                      is likely appreciating in value.
                    </p>
                  </div>
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <p className="text-sm text-purple-300">
                      <strong>Advanced: Cost Segregation Studies</strong> - Hire specialists to reclassify 
                      building components (flooring, wiring, HVAC) as 5/7/15-year property instead of 27.5 years. 
                      This accelerates depreciation, front-loading deductions. Great for high-income years. 
                      Costs $5K-$15K but can save $50K+ in taxes.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🔄 1031 Exchange (Like-Kind Exchange)</h4>
                  <p className="text-slate-300 mb-4">
                    Defer capital gains tax by reinvesting sale proceeds into another "like-kind" property 
                    within specific timeframes (45 days to identify, 180 days to close). Can repeat indefinitely.
                  </p>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">💡 The Ultimate Power Move:</p>
                    <p className="text-sm text-slate-300">
                      Use leverage to buy property → let it appreciate → 1031 into bigger property with more 
                      debt → repeat. Build a real estate empire while deferring ALL capital gains taxes until 
                      death (step-up in basis for heirs eliminates tax entirely). This is how billionaires 
                      are built in real estate.
                    </p>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <p className="text-sm text-yellow-300">
                      ⚠️ <strong>Rules are strict:</strong> Must use qualified intermediary, can't touch the 
                      money, must identify replacement within 45 days. Mess it up and the entire gain is taxable.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">💳 Mortgage Interest Deduction</h4>
                  <p className="text-slate-300 mb-4">
                    Deduct mortgage interest on up to $750,000 of acquisition debt for primary and second homes 
                    (if itemizing). Only useful if your total itemized deductions exceed the standard deduction.
                  </p>
                  <div className="text-sm text-slate-400 mt-2 space-y-1">
                    <p>• Standard deduction 2025: $15,000 (single) / $30,000 (married)</p>
                    <p>• Most taxpayers take standard deduction, so mortgage interest doesn't help</p>
                    <p>• More valuable in high-tax states (SALT + mortgage interest can exceed standard)</p>
                    <p>• HELOC interest may be deductible if used for home improvements</p>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🏘️ Property Tax Deduction</h4>
                  <p className="text-slate-300 mb-4">
                    Deduct up to $10,000 combined cap for state/local income taxes PLUS property taxes (SALT cap). 
                    This hurts high-tax state residents.
                  </p>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-sm text-blue-300">
                      💡 <strong>Workaround for landlords:</strong> Property taxes on rental properties are 
                      deducted on Schedule E (no $10K cap). Only your PRIMARY residence is subject to SALT cap.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Small Business & Self-Employment */}
            <div id="business-tax" className="mb-12 scroll-mt-24">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <Building2 className="w-6 h-6 mr-3 text-green-400" />
                Small Business & Self-Employment Tax Breaks
              </h3>
              
              <div className="space-y-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">📊 QBI Deduction (20% of Business Income)</h4>
                  <p className="text-slate-300 mb-4">
                    Qualified Business Income deduction allows you to deduct 20% of net income from pass-through 
                    businesses (sole proprietorships, partnerships, S-corps, LLCs). This is MASSIVE.
                  </p>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">💡 Example for Traders:</p>
                    <p className="text-sm text-slate-300">
                      $100K in trading profits as a business (Section 475 election) = $20K QBI deduction. 
                      Only pay tax on $80K. <strong>Saves ~$7,400 in taxes</strong> (at 37% bracket). 
                      This is why trader tax status is so valuable.
                    </p>
                  </div>
                  <div className="text-sm text-slate-400">
                    <p><strong>Phase-out thresholds (2025):</strong></p>
                    <p>• Begins at $191,950 (single) / $383,900 (married)</p>
                    <p>• Fully phases out at $241,950 (single) / $483,900 (married)</p>
                    <p>• Specified Service Trade or Business (SSTB) limitations apply above thresholds</p>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">💼 Business Expense Deductions</h4>
                  <p className="text-slate-300 mb-4">
                    If you operate a trading or investment business, you can deduct ordinary and necessary expenses.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 rounded p-4">
                      <h5 className="font-semibold text-blue-400 mb-2">Technology & Software</h5>
                      <ul className="text-sm text-slate-300 space-y-1">
                        <li>• Computer equipment (Section 179)</li>
                        <li>• Trading software & platforms</li>
                        <li>• Data feeds & research tools</li>
                        <li>• Charting software</li>
                        <li>• VPN services for trading</li>
                      </ul>
                    </div>
                    <div className="bg-slate-900/50 rounded p-4">
                      <h5 className="font-semibold text-blue-400 mb-2">Office & Operations</h5>
                      <ul className="text-sm text-slate-300 space-y-1">
                        <li>• Home office deduction</li>
                        <li>• Internet & phone (business portion)</li>
                        <li>• Office furniture & supplies</li>
                        <li>• Business insurance</li>
                        <li>• Professional fees (CPA, lawyer)</li>
                      </ul>
                    </div>
                    <div className="bg-slate-900/50 rounded p-4">
                      <h5 className="font-semibold text-blue-400 mb-2">Education & Development</h5>
                      <ul className="text-sm text-slate-300 space-y-1">
                        <li>• Trading courses & education</li>
                        <li>• Books & publications</li>
                        <li>• Conference & seminar fees</li>
                        <li>• Travel to trading events</li>
                        <li>• Mentorship/coaching programs</li>
                      </ul>
                    </div>
                    <div className="bg-slate-900/50 rounded p-4">
                      <h5 className="font-semibold text-blue-400 mb-2">Other Deductions</h5>
                      <ul className="text-sm text-slate-300 space-y-1">
                        <li>• Business mileage</li>
                        <li>• Meals (50% deductible)</li>
                        <li>• Health insurance (self-employed)</li>
                        <li>• Retirement contributions</li>
                        <li>• Legal & accounting fees</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Augusta Rule */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🏠 Augusta Rule (Section 280A)</h4>
                  <p className="text-slate-300 mb-4">
                    One of the most underutilized tax loopholes: Rent your home to your business for up to 14 days 
                    per year, and the rental income is <strong>completely tax-free</strong>. Your business gets a 
                    deduction, but you pay ZERO tax on the income.
                  </p>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">💡 How Traders Use This:</p>
                    <p className="text-sm text-slate-300 mb-2">
                      If you operate a trading LLC or S-corp, you can:
                    </p>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Rent your home to your business for strategy meetings, business planning sessions</li>
                      <li>• Charge fair market rental rate (check hotel prices in your area - often $200-$500/day)</li>
                      <li>• Business deducts the expense; you receive tax-free income</li>
                      <li>• Maximum: 14 days per year</li>
                    </ul>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-blue-400 mb-2">💰 Real Numbers:</p>
                    <p className="text-sm text-slate-300">
                      14 days × $400/day = $5,600 in tax-free income. Your business gets $5,600 deduction 
                      (saves ~$2,072 in taxes at 37%), and you pay $0 tax on the income. 
                      <strong>Total benefit: ~$7,672</strong>
                    </p>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mt-4">
                    <p className="text-sm text-yellow-300">
                      ⚠️ <strong>Documentation is critical:</strong> Keep detailed records of business purpose, 
                      attendance, market rate research. This is legitimate but heavily scrutinized by IRS.
                    </p>
                  </div>
                </div>

                {/* Hiring Family Members */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">👨‍👩‍👧‍👦 Hiring Family Members</h4>
                  <p className="text-slate-300 mb-4">
                    If you run a trading business, hiring your children (or spouse) for legitimate work can create 
                    massive tax savings by income shifting to lower tax brackets.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <h5 className="font-semibold text-blue-400 mb-2">Hiring Your Children</h5>
                      <ul className="text-sm text-slate-300 space-y-1">
                        <li>• Pay them up to $15,000 (2025 standard deduction) - they pay $0 tax</li>
                        <li>• Under 18: No FICA taxes if sole proprietorship</li>
                        <li>• Your business deducts the wages</li>
                        <li>• They can contribute to Roth IRA (tax-free growth forever!)</li>
                        <li>• Must be legitimate work and reasonable pay</li>
                      </ul>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                      <h5 className="font-semibold text-purple-400 mb-2">Hiring Your Spouse</h5>
                      <ul className="text-sm text-slate-300 space-y-1">
                        <li>• Pay reasonable salary for actual work</li>
                        <li>• Deductible expense for business</li>
                        <li>• Enables health insurance deduction</li>
                        <li>• Can max out their retirement accounts</li>
                        <li>• Income still "in family" but tax-optimized</li>
                      </ul>
                    </div>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">💰 Example Savings:</p>
                    <p className="text-sm text-slate-300">
                      You're in 37% tax bracket. Pay your 16-year-old $14,600 to manage social media, data entry, 
                      research. They pay $0 tax (under standard deduction), you deduct $14,600. 
                      <strong>Saves $5,402 in taxes.</strong> Plus they can put $7,000 in Roth IRA for tax-free growth.
                    </p>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mt-4">
                    <p className="text-sm text-red-300">
                      ⚠️ <strong>IRS Requirements:</strong> Work must be legitimate, necessary for business, and 
                      compensation must be reasonable. Keep timesheets, job descriptions, and payment records. 
                      Paying your 8-year-old $50K/year will get audited.
                    </p>
                  </div>
                </div>

                {/* Self-Employed Health Insurance */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🏥 Self-Employed Health Insurance Deduction</h4>
                  <p className="text-slate-300 mb-4">
                    If you're self-employed (including full-time traders with Section 475 election), you can deduct 
                    100% of health insurance premiums for yourself, spouse, and dependents as an "above-the-line" 
                    deduction. This is HUGE and often overlooked.
                  </p>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">💡 What Qualifies:</p>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Health insurance premiums (medical, dental, vision)</li>
                      <li>• Long-term care insurance premiums (age-based limits)</li>
                      <li>• Medicare premiums (if self-employed and over 65)</li>
                      <li>• Coverage for spouse and dependents under 27</li>
                    </ul>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-blue-400 mb-2">💰 Example Savings:</p>
                    <p className="text-sm text-slate-300">
                      Family health insurance: $24,000/year. Self-employed trader deducts entire amount. 
                      <strong>Saves $8,880 in taxes</strong> (37% bracket) + $3,672 in self-employment tax (15.3%). 
                      Total savings: <strong>$12,552/year!</strong>
                    </p>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mt-4">
                    <p className="text-sm text-yellow-300">
                      ⚠️ <strong>Requirements:</strong> Cannot exceed your net self-employment income. If you have 
                      access to employer-sponsored coverage through a spouse, you may not be eligible.
                    </p>
                  </div>
                </div>

                {/* Professional Development & Subscriptions */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">📚 Professional Development & Trading Subscriptions</h4>
                  <p className="text-slate-300 mb-4">
                    Deduct education and subscription costs directly related to your trading business. This includes 
                    courses, data feeds, research services, and professional tools.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <h5 className="font-semibold text-blue-400 mb-2">Deductible Education</h5>
                      <ul className="text-sm text-slate-300 space-y-1">
                        <li>• Trading courses & bootcamps</li>
                        <li>• Technical analysis certifications (CMT)</li>
                        <li>• Options/futures education programs</li>
                        <li>• Seminars & conferences (travel included!)</li>
                        <li>• Trading mentorship programs</li>
                      </ul>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                      <h5 className="font-semibold text-purple-400 mb-2">Deductible Subscriptions</h5>
                      <ul className="text-sm text-slate-300 space-y-1">
                        <li>• Bloomberg Terminal ($24K/year)</li>
                        <li>• Level 2 market data</li>
                        <li>• TradingView Pro ($600/year)</li>
                        <li>• Seeking Alpha Premium</li>
                        <li>• Wall Street Journal, Barron's, FT</li>
                        <li>• Research services (Morningstar, etc.)</li>
                      </ul>
                    </div>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">💰 Real Trader Example:</p>
                    <p className="text-sm text-slate-300">
                      TradingView Pro ($600) + Bloomberg Terminal ($24,000) + Options Course ($2,000) + Conference 
                      with travel ($3,000) = $29,600 in deductions. <strong>Saves $10,952 in taxes</strong> (37% bracket). 
                      These tools pay for themselves through tax savings alone!
                    </p>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mt-4">
                    <p className="text-sm text-yellow-300">
                      ⚠️ <strong>Must be business-related:</strong> Education must maintain or improve skills in your 
                      current trade/business. Can't deduct education to enter a new field.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🏡 Home Office Deduction</h4>
                  <p className="text-slate-300 mb-4">
                    If you trade from home as a business, you can deduct a portion of your home expenses.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <p className="text-sm font-semibold text-blue-400 mb-2">Simplified Method</p>
                      <p className="text-sm text-slate-300 mb-2">
                        $5 per square foot of home office space, up to 300 sq ft = max $1,500/year deduction
                      </p>
                      <p className="text-xs text-slate-400">Easy, no tracking required</p>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <p className="text-sm font-semibold text-green-400 mb-2">Actual Expense Method</p>
                      <p className="text-sm text-slate-300 mb-2">
                        Deduct percentage of: mortgage interest, property taxes, utilities, repairs, insurance, 
                        depreciation based on % of home used for business
                      </p>
                      <p className="text-xs text-slate-400">More work, but potentially much larger deduction</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* General & Often-Overlooked Tax Breaks */}
            <div id="general-tax" className="mb-12 scroll-mt-24">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <Heart className="w-6 h-6 mr-3 text-pink-400" />
                General & Often-Overlooked Tax Breaks
              </h3>
              
              <div className="space-y-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">❤️ Charitable Contributions (Donating Stock)</h4>
                  <p className="text-slate-300 mb-4">
                    One of the most tax-efficient strategies: donate appreciated stock directly to charity instead of cash.
                  </p>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">💡 Double Tax Benefit:</p>
                    <ol className="text-sm text-slate-300 space-y-2">
                      <li>1. <strong>Get a deduction</strong> for the full fair market value of the stock</li>
                      <li>2. <strong>Avoid paying capital gains tax</strong> on the appreciation</li>
                    </ol>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-blue-400 mb-2">💡 Example:</p>
                    <p className="text-sm text-slate-300">
                      You bought stock for $10K, now worth $50K. If you sell and donate cash, you pay $6K-$8K 
                      in capital gains tax. If you donate the stock directly, you get a $50K deduction AND 
                      avoid the capital gains tax entirely. <strong>This saves $15K-$20K in taxes</strong> (37% bracket).
                    </p>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🎯 Saver's Credit</h4>
                  <p className="text-slate-300 mb-4">
                    Low to moderate-income earners can get a tax CREDIT (not deduction) of up to $1,000 (single) 
                    or $2,000 (married) for contributing to retirement accounts.
                  </p>
                  <div className="grid sm:grid-cols-3 gap-3 text-sm">
                    <div className="bg-slate-900/50 rounded p-3">
                      <div className="font-semibold text-green-400">50% Credit</div>
                      <div className="text-slate-400">AGI under $23,000 (single)</div>
                    </div>
                    <div className="bg-slate-900/50 rounded p-3">
                      <div className="font-semibold text-blue-400">20% Credit</div>
                      <div className="text-slate-400">AGI $23,000-$25,000</div>
                    </div>
                    <div className="bg-slate-900/50 rounded p-3">
                      <div className="font-semibold text-orange-400">10% Credit</div>
                      <div className="text-slate-400">AGI $25,000-$38,000</div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    This is a CREDIT (dollar-for-dollar reduction in tax owed), not a deduction. Very valuable 
                    for lower-income traders starting out.
                  </p>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">⚡ Energy & Green Energy Credits</h4>
                  <p className="text-slate-300 mb-4">
                    Federal tax credits for energy-efficient improvements and vehicles.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <p className="text-sm font-semibold text-green-400 mb-2">🏠 Residential Clean Energy Credit</p>
                      <ul className="text-sm text-slate-300 space-y-1">
                        <li>• Solar panels: 30% tax credit</li>
                        <li>• Battery storage: 30% credit</li>
                        <li>• Geothermal heat pumps: 30% credit</li>
                        <li>• No dollar limit!</li>
                      </ul>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <p className="text-sm font-semibold text-blue-400 mb-2">🚗 EV Tax Credit</p>
                      <ul className="text-sm text-slate-300 space-y-1">
                        <li>• New EVs: up to $7,500 credit</li>
                        <li>• Used EVs: up to $4,000 credit</li>
                        <li>• Must meet income & price limits</li>
                        <li>• Point-of-sale credit (2024+)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🎓 Education Tax Breaks</h4>
                  <p className="text-slate-300 mb-4">
                    Credits and deductions for education expenses (can apply to trading education!).
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                      <h5 className="font-semibold text-blue-400 mb-2">American Opportunity Credit</h5>
                      <p className="text-xs text-slate-300 mb-2">
                        Up to $2,500 per student for first 4 years of college
                      </p>
                      <p className="text-xs text-slate-400">40% refundable</p>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                      <h5 className="font-semibold text-purple-400 mb-2">Lifetime Learning Credit</h5>
                      <p className="text-xs text-slate-300 mb-2">
                        Up to $2,000 per tax return for any post-secondary education
                      </p>
                      <p className="text-xs text-slate-400">Not refundable</p>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                      <h5 className="font-semibold text-green-400 mb-2">529 Plans</h5>
                      <p className="text-xs text-slate-300 mb-2">
                        Tax-free growth & withdrawals for education expenses
                      </p>
                      <p className="text-xs text-slate-400">Some states give deduction</p>
                    </div>
                  </div>
                </div>

                {/* Student Loan Interest Deduction */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🎓 Student Loan Interest Deduction</h4>
                  <p className="text-slate-300 mb-4">
                    Deduct up to $2,500 in student loan interest even if you don't itemize deductions. This is an 
                    "above-the-line" deduction that reduces your adjusted gross income.
                  </p>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-blue-400 mb-2">💡 Key Benefits:</p>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Available even if you take standard deduction</li>
                      <li>• Covers interest on undergraduate & graduate loans</li>
                      <li>• Both federal and private loans qualify</li>
                      <li>• Can be claimed for loans taken for spouse or dependents</li>
                    </ul>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <p className="text-sm text-slate-300">
                      <strong>Income Limits (2025):</strong> Phases out between $80K-$95K (single) or $165K-$195K (married). 
                      For young traders with student debt, this deduction can save $550-$925/year in taxes.
                    </p>
                  </div>
                </div>

                {/* Bunching Charitable Donations */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">📚 Bunching Charitable Donations</h4>
                  <p className="text-slate-300 mb-4">
                    Advanced strategy: Combine 2-3 years of charitable giving into one year to exceed the standard 
                    deduction threshold, then take standard deduction in other years.
                  </p>
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-purple-400 mb-2">💡 How Bunching Works:</p>
                    <p className="text-sm text-slate-300 mb-2">
                      <strong>Without bunching:</strong> Donate $10K/year for 3 years = $30K total donations, but take 
                      standard deduction each year ($13,850 single). Get no benefit from donations.
                    </p>
                    <p className="text-sm text-slate-300">
                      <strong>With bunching:</strong> Donate $30K in Year 1, $0 in Years 2-3. Year 1: itemize and 
                      deduct $30K. Years 2-3: standard deduction. <strong>Extra tax savings: ~$6,000</strong>
                    </p>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-blue-400 mb-2">💰 Pro Strategy:</p>
                    <p className="text-sm text-slate-300">
                      Use a Donor-Advised Fund (DAF) to make the large donation in one year, get immediate deduction, 
                      then distribute to charities over multiple years. Best of both worlds!
                    </p>
                  </div>
                </div>

                {/* Earned Income Tax Credit */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">💵 Earned Income Tax Credit (EITC)</h4>
                  <p className="text-slate-300 mb-4">
                    One of the most powerful credits for low-to-moderate income workers. This is a REFUNDABLE credit 
                    (you get money even if you owe $0 tax). Up to $7,430 for families with 3+ children.
                  </p>
                  <div className="grid md:grid-cols-4 gap-3 mb-4">
                    <div className="bg-slate-900/50 rounded p-3">
                      <div className="font-semibold text-green-400 mb-1">No Kids</div>
                      <div className="text-xs text-slate-400">Up to $600</div>
                      <div className="text-xs text-slate-500">Income under $18K</div>
                    </div>
                    <div className="bg-slate-900/50 rounded p-3">
                      <div className="font-semibold text-blue-400 mb-1">1 Child</div>
                      <div className="text-xs text-slate-400">Up to $3,995</div>
                      <div className="text-xs text-slate-500">Income under $48K</div>
                    </div>
                    <div className="bg-slate-900/50 rounded p-3">
                      <div className="font-semibold text-purple-400 mb-1">2 Children</div>
                      <div className="text-xs text-slate-400">Up to $6,604</div>
                      <div className="text-xs text-slate-500">Income under $54K</div>
                    </div>
                    <div className="bg-slate-900/50 rounded p-3">
                      <div className="font-semibold text-orange-400 mb-1">3+ Children</div>
                      <div className="text-xs text-slate-400">Up to $7,430</div>
                      <div className="text-xs text-slate-500">Income under $57K</div>
                    </div>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <p className="text-sm text-slate-300">
                      💡 <strong>For traders:</strong> If you had a down year and income is low, make sure to claim 
                      this! Many eligible taxpayers miss out on thousands because they don't know about EITC. It's 
                      refundable, meaning you get a check even if you owe no tax.
                    </p>
                  </div>
                </div>

                {/* Dependent Care FSA */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">👶 Dependent Care FSA</h4>
                  <p className="text-slate-300 mb-4">
                    Contribute up to $5,000/year pre-tax for childcare expenses if you and your spouse work. 
                    This is BETTER than the Child & Dependent Care Credit for most taxpayers.
                  </p>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-blue-400 mb-2">💡 Why FSA is Usually Better:</p>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• <strong>Pre-tax contribution</strong> = save on income tax + FICA (total ~30-40%)</li>
                      <li>• Child Care Credit = 20-35% but calculated on after-tax dollars</li>
                      <li>• FSA saves $1,500-$2,000 in taxes for most people</li>
                      <li>• Can't claim both FSA and full credit (reduced)</li>
                    </ul>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">📋 Example:</p>
                    <p className="text-sm text-slate-300">
                      You pay $12,000/year for daycare. Contribute $5,000 to Dependent Care FSA. You save ~$1,850 
                      in taxes (37% bracket). Remaining $7,000 may qualify for partial Child Care Credit. 
                      <strong>Total savings: $2,000-$2,500 vs doing nothing.</strong>
                    </p>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">👨‍👩‍👧 Family Tax Credits</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                      <p className="text-sm font-semibold text-purple-400 mb-2">Child Tax Credit</p>
                      <p className="text-sm text-slate-300">
                        Up to $2,000 per qualifying child under 17. $1,600 is refundable. 
                        Phases out at high incomes.
                      </p>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <p className="text-sm font-semibold text-blue-400 mb-2">Child & Dependent Care Credit</p>
                      <p className="text-sm text-slate-300">
                        20-35% of childcare expenses (up to $3K per child, $6K max) if you work or 
                        look for work. Essential for working parents.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Estimated Tax Payments Strategy */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">📅 Estimated Tax Payment Strategy (Avoid Penalties!)</h4>
                  <p className="text-slate-300 mb-4">
                    If you have significant trading income or self-employment income, you must make quarterly estimated 
                    tax payments or face penalties. But there are smart strategies to minimize or avoid these penalties.
                  </p>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-blue-400 mb-2">🗓️ Payment Deadlines:</p>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Q1 (Jan 1 - Mar 31): Due April 15</li>
                      <li>• Q2 (Apr 1 - May 31): Due June 15</li>
                      <li>• Q3 (Jun 1 - Aug 31): Due September 15</li>
                      <li>• Q4 (Sep 1 - Dec 31): Due January 15 (next year)</li>
                    </ul>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">🛡️ Safe Harbor Rules (Avoid Penalties):</p>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• <strong>Option 1:</strong> Pay 90% of current year's tax liability</li>
                      <li>• <strong>Option 2:</strong> Pay 100% of prior year's tax liability (110% if AGI > $150K)</li>
                      <li>• <strong>Pro Tip:</strong> If you had a big winning year, use Option 2 and pay based on LAST year's taxes (much lower!)</li>
                    </ul>
                  </div>
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-purple-400 mb-2">💡 Advanced Strategy for Traders:</p>
                    <p className="text-sm text-slate-300">
                      Had a huge trading year? Instead of quarterly payments, increase W-2 withholding in Q4 
                      (if you have a job). <strong>IRS treats withholding as paid evenly throughout the year</strong> 
                      even if you do it all in December! Avoids underpayment penalties retroactively.
                    </p>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mt-4">
                    <p className="text-sm text-red-300">
                      ⚠️ <strong>Penalty:</strong> Underpayment penalty is ~7% annually (IRS short-term rate + 3%). 
                      On $50K underpayment, that's $3,500/year in penalties. Pay on time!
                    </p>
                  </div>
                </div>

                {/* Record Keeping for Traders */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">📊 Record Keeping & Documentation (Critical for Audits!)</h4>
                  <p className="text-slate-300 mb-4">
                    The IRS WILL audit traders with significant gains or losses. Proper record keeping is your best 
                    defense and can save you hundreds of thousands in disallowed deductions during an audit.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <h5 className="font-semibold text-blue-400 mb-2">Must-Keep Records:</h5>
                      <ul className="text-sm text-slate-300 space-y-1">
                        <li>• All trade confirmations & statements</li>
                        <li>• Cost basis documentation (CRITICAL!)</li>
                        <li>• Form 1099-B from brokers</li>
                        <li>• Wire transfers, deposits, withdrawals</li>
                        <li>• Crypto wallet addresses & transaction history</li>
                        <li>• Gifts, inheritances affecting cost basis</li>
                      </ul>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                      <h5 className="font-semibold text-purple-400 mb-2">Business Expense Records:</h5>
                      <ul className="text-sm text-slate-300 space-y-1">
                        <li>• Receipts for all equipment purchases</li>
                        <li>• Subscription invoices (TradingView, etc.)</li>
                        <li>• Home office square footage & expenses</li>
                        <li>• Trading journal (proves trader status)</li>
                        <li>• Business mileage logs</li>
                        <li>• Education course certificates & receipts</li>
                      </ul>
                    </div>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-green-400 mb-2">🔧 Best Tools for Traders:</p>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• <strong>Crypto:</strong> CoinTracker, Koinly, CoinLedger, TokenTax (API integration with exchanges)</li>
                      <li>• <strong>Stocks/Options:</strong> TradeLog, GainsKeeper (automatic cost basis tracking)</li>
                      <li>• <strong>Expenses:</strong> QuickBooks Self-Employed, Expensify, Excel spreadsheet</li>
                      <li>• <strong>Mileage:</strong> MileIQ, Everlance (GPS tracking apps)</li>
                    </ul>
                  </div>
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                    <p className="text-sm font-semibold text-orange-400 mb-2">⏰ Retention Period:</p>
                    <p className="text-sm text-slate-300">
                      Keep all records for <strong>at least 7 years</strong>. IRS typically audits 3 years back, but 
                      can go 6 years for substantial underreporting (>25% of income). For securities, keep records 
                      showing cost basis FOREVER or until 3 years after you sell and report the sale.
                    </p>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mt-4">
                    <p className="text-sm text-red-300">
                      ⚠️ <strong>Audit Horror Story:</strong> Trader claimed $500K in trading losses without proper 
                      documentation. IRS disallowed ALL losses. Tax bill: $185K + penalties + interest = $250K+. 
                      Keep. Your. Records.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-white mb-3">🏛️ State-Specific Tax Breaks</h4>
                  <p className="text-slate-300 mb-4">
                    Many states offer additional tax breaks beyond federal. Check your state for:
                  </p>
                  <ul className="text-sm text-slate-300 space-y-2">
                    <li>• <strong>529 plan contributions:</strong> Often deductible on state returns (varies by state)</li>
                    <li>• <strong>Property tax credits:</strong> Many states offer credits/rebates for seniors, veterans, or low-income</li>
                    <li>• <strong>First-time homebuyer credits:</strong> Some states still offer these</li>
                    <li>• <strong>Angel investor tax credits:</strong> Some states give credits for investing in local startups</li>
                    <li>• <strong>Film/entertainment tax credits:</strong> Invest in local productions for credits</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Summary Table */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6">📋 Quick Reference: Tax Breaks by Investor Type</h3>
              <div className="overflow-x-auto">
                <table className="w-full bg-slate-800/50 border border-slate-700 rounded-xl">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left p-4 text-slate-400 font-semibold">Investor Type</th>
                      <th className="text-left p-4 text-slate-400 font-semibold">Top Tax Breaks to Use</th>
                      <th className="text-left p-4 text-slate-400 font-semibold">Potential Annual Savings</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-700">
                      <td className="p-4 text-white font-semibold">W-2 Worker (Side Trading)</td>
                      <td className="p-4 text-slate-300 text-sm">
                        Tax-loss harvesting, 401(k) max, HSA max, long-term capital gains
                      </td>
                      <td className="p-4 text-green-400">$5K-$15K</td>
                    </tr>
                    <tr className="border-b border-slate-700">
                      <td className="p-4 text-white font-semibold">Active Day Trader</td>
                      <td className="p-4 text-slate-300 text-sm">
                        Section 475 election, QBI deduction, business expense deductions
                      </td>
                      <td className="p-4 text-green-400">$10K-$50K+</td>
                    </tr>
                    <tr className="border-b border-slate-700">
                      <td className="p-4 text-white font-semibold">Crypto Trader</td>
                      <td className="p-4 text-slate-300 text-sm">
                        Tax-loss harvesting (no wash sale!), long-term gains, donate crypto
                      </td>
                      <td className="p-4 text-green-400">$3K-$20K</td>
                    </tr>
                    <tr className="border-b border-slate-700">
                      <td className="p-4 text-white font-semibold">Real Estate Investor</td>
                      <td className="p-4 text-slate-300 text-sm">
                        Depreciation, 1031 exchanges, cost segregation, primary residence exclusion
                      </td>
                      <td className="p-4 text-green-400">$15K-$100K+</td>
                    </tr>
                    <tr className="border-b border-slate-700">
                      <td className="p-4 text-white font-semibold">Self-Employed Trader</td>
                      <td className="p-4 text-slate-300 text-sm">
                        QBI 20%, Solo 401(k) ($69K limit), SEP IRA, home office, equipment deductions
                      </td>
                      <td className="p-4 text-green-400">$20K-$60K+</td>
                    </tr>
                    <tr>
                      <td className="p-4 text-white font-semibold">High Net Worth</td>
                      <td className="p-4 text-slate-300 text-sm">
                        Donate appreciated assets, charitable trusts, 1031 exchanges, life insurance strategies
                      </td>
                      <td className="p-4 text-green-400">$50K-$500K+</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Action Items */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6">✅ Action Items: Start Saving on Taxes Today</h3>
              <div className="space-y-4">
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
                  <h4 className="font-semibold text-blue-400 mb-3">📅 Before End of Year (December 31 Deadline)</h4>
                  <ul className="text-sm text-slate-300 space-y-2">
                    <li>✓ Tax-loss harvest losing positions (especially crypto!)</li>
                    <li>✓ Max out retirement contributions (401k, IRA, HSA)</li>
                    <li>✓ Donate appreciated stock to charity</li>
                    <li>✓ Make estimated tax payments if self-employed</li>
                    <li>✓ Buy equipment for business (Section 179 deduction)</li>
                    <li>✓ Bunch charitable contributions if itemizing</li>
                  </ul>
                </div>

                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
                  <h4 className="font-semibold text-green-400 mb-3">🗓️ Q1 Next Year (Before April 15)</h4>
                  <ul className="text-sm text-slate-300 space-y-2">
                    <li>✓ File Section 475 election if qualifying as trader (must file with prior year tax return or by April 15)</li>
                    <li>✓ Make IRA contributions for prior year (deadline = tax day)</li>
                    <li>✓ File extension if needed for complex situations</li>
                    <li>✓ Organize all trading records (exchanges, cost basis, etc.)</li>
                    <li>✓ Use crypto tax software to generate tax forms</li>
                  </ul>
                </div>

                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-6">
                  <h4 className="font-semibold text-purple-400 mb-3">📚 Ongoing Throughout Year</h4>
                  <ul className="text-sm text-slate-300 space-y-2">
                    <li>✓ Track ALL trades and expenses in real-time</li>
                    <li>✓ Make quarterly estimated tax payments</li>
                    <li>✓ Document business activities (trading journal, records)</li>
                    <li>✓ Meet with CPA/tax strategist (at least annually)</li>
                    <li>✓ Review tax situation after major life changes</li>
                    <li>✓ Stay updated on tax law changes</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Final CTA */}
            <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">💰 Ready to Keep More of Your Profits?</h3>
              <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Tax planning is just as important as your trading strategy. The best traders aren't just good 
                at making money – they're excellent at keeping it. 
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-6">
                <button
                  onClick={() => setCurrentPage('trade-calculator')}
                  className="px-6 py-3 bg-blue-500 rounded-lg font-semibold hover:bg-blue-600 transition-all"
                >
                  Calculate Your Leverage
                </button>
                <button
                  onClick={() => setCurrentPage('quiz')}
                  className="px-6 py-3 bg-purple-500 rounded-lg font-semibold hover:bg-purple-600 transition-all"
                >
                  Take Risk Assessment
                </button>
              </div>
              <p className="text-sm text-slate-400">
                <strong>Remember:</strong> Always consult a qualified tax professional (CPA or EA) before 
                implementing tax strategies. This guide is for educational purposes only.
              </p>
            </div>

            {/* Resources */}
            <div className="mt-12 bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              <h4 className="text-lg font-semibold mb-4">📚 Recommended Tax Resources</h4>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h5 className="font-semibold text-blue-400 mb-2">Tax Software for Traders:</h5>
                  <ul className="text-slate-300 space-y-1">
                    <li>• TradeLog - Stock/options tracking</li>
                    <li>• CoinTracker - Crypto tax software</li>
                    <li>• Koinly - Crypto DeFi tracking</li>
                    <li>• TokenTax - Advanced crypto tax</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-green-400 mb-2">Find Tax Professionals:</h5>
                  <ul className="text-slate-300 space-y-1">
                    <li>• Green & Company CPAs (trader specialists)</li>
                    <li>• Local EA (Enrolled Agent)</li>
                    <li>• CPA specializing in traders</li>
                    <li>• Check credentials at IRS.gov</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-6 gap-8 mb-8">
            {/* Brand Column */}
            <div className="md:col-span-2">
              <button 
                onClick={() => setCurrentPage('home')}
                className="flex items-center space-x-2 mb-4 hover:opacity-80 transition-opacity cursor-pointer"
              >
                <TrendingUp className="w-6 h-6 text-blue-400" />
                <span className="text-lg font-bold">StreetFi</span>
              </button>
              <p className="text-slate-400 text-sm mb-4">
                Professional-grade financial calculators and education—completely free. Master leverage strategies, tax optimization, DeFi protocols, and real-world finance. Wall Street tools without the gatekeepers.
              </p>
              <div className="text-sm text-slate-500">
                <p>© 2025 StreetFi</p>
                <p>Educational platform - Not financial advice</p>
              </div>
            </div>

            {/* Learn Basics Column */}
            <div>
              <h4 className="font-semibold text-white mb-3">Learn Basics</h4>
              <div className="space-y-2">
                <button onClick={() => setCurrentPage('real-estate-leverage')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Real Estate Leverage
                </button>
                <button onClick={() => setCurrentPage('investment-leverage')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Investment Leverage
                </button>
                <button onClick={() => setCurrentPage('crypto-leverage')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Crypto & DeFi Basics
                </button>
                <button onClick={() => setCurrentPage('business-leverage')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Business Leverage
                </button>
              </div>
            </div>

            {/* Learn Advanced Column */}
            <div>
              <h4 className="font-semibold text-white mb-3">Learn Advanced</h4>
              <div className="space-y-2">
                <button onClick={() => setCurrentPage('real-finance-system')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  How Finance Works
                </button>
                <button onClick={() => setCurrentPage('wealth-games')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Wealth Games
                </button>
                <button onClick={() => setCurrentPage('market-psychology')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Market Psychology
                </button>
                <button onClick={() => setCurrentPage('credit-leverage')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Credit & Leverage
                </button>
                <button onClick={() => setCurrentPage('narrative-economy')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Information & Narrative
                </button>
                <button onClick={() => setCurrentPage('game-theory')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Game Theory
                </button>
                <button onClick={() => setCurrentPage('reading-fine-print')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Reading Fine Print
                </button>
              </div>
            </div>

            {/* Calculators Column */}
            <div>
              <h4 className="font-semibold text-white mb-3">Calculators</h4>
              <div className="space-y-2">
                <button onClick={() => setCurrentPage('leverage-trading')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Leverage Trading
                </button>
                <button onClick={() => setCurrentPage('stock-options')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Stock Options & QSBS
                </button>
                <button onClick={() => setCurrentPage('stock-tax')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Stock Sale Tax
                </button>
                <button onClick={() => setCurrentPage('mortgage')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Mortgage Calculator
                </button>
                <button onClick={() => setCurrentPage('rental')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Rental Property ROI
                </button>
                <button onClick={() => setCurrentPage('planning')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Financial Planning
                </button>
              </div>
            </div>

            {/* Resources & Legal Column */}
            <div>
              <h4 className="font-semibold text-white mb-3">Resources</h4>
              <div className="space-y-2">
                <button onClick={() => setCurrentPage('glossary')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Trading Glossary
                </button>
                <button onClick={() => setCurrentPage('products')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Leverage Products
                </button>
                <button onClick={() => setCurrentPage('crypto-protocols')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Crypto Protocols
                </button>
                <button onClick={() => setCurrentPage('tax')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Tax Strategies
                </button>
                <button onClick={() => setCurrentPage('quiz')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  Risk Assessment
                </button>
                <button onClick={() => setCurrentPage('about')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                  About
                </button>
              </div>
              <div className="mt-6">
                <h4 className="font-semibold text-white mb-3 text-sm">Legal</h4>
                <div className="space-y-2">
                  <button onClick={() => setCurrentPage('disclaimer')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                    Disclaimer
                  </button>
                  <button onClick={() => setCurrentPage('privacy')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                    Privacy Policy
                  </button>
                  <button onClick={() => setCurrentPage('terms')} className="block w-full text-left text-slate-400 hover:text-white text-sm transition-colors">
                    Terms of Service
                  </button>
                </div>
              </div>
              <div className="mt-6">
                <h4 className="font-semibold text-white mb-3 text-sm">Connect</h4>
                <div className="space-y-2">
                  <a href="mailto:hello@leverfinance.com" className="block text-slate-400 hover:text-white text-sm transition-colors">
                    Contact Us
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Disclaimer */}
          <div className="pt-8 border-t border-slate-800">
            <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-4">
              <p className="text-slate-400 text-xs text-center">
                <strong className="text-yellow-400">⚠️ Important:</strong> This platform provides educational tools only. Not financial, investment, or tax advice. 
                Trading with leverage is extremely risky and most traders lose money. Always consult qualified professionals before making financial decisions.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
